import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { registerUndoScope, setUndoArbiterToast } from './hooks/useUndoArbiter';
import {
    HistoryEntry, RedoEntry, ReparentedChild, toStructuralProjection, applyUndo, applyRedo,
} from './utils/undoHistory';
import { flushSync } from 'react-dom';
import * as Sentry from '@sentry/react';
import {
    FolderOpen, Edit3, Trash2, Plus, LogOut,
    Layout, BookOpen, GraduationCap, Bookmark, Moon, Sun, Monitor,
    Menu, Grid, Network, ZoomIn, ZoomOut, Download, Upload, Cloud, Settings, HardDrive, AlertCircle, CheckCircle2, Loader2, UserMinus, LogIn,
    AlignLeft, GitGraph, BoxSelect, FileText, ChevronDown, CheckSquare, X, Info, Crown, Lock, BrainCircuit, Sparkles, Unlock, Target, Bell,
    Minimize, Maximize, MoveHorizontal, Square, Columns, Search, Minimize2, Maximize2, Check, Share2, Undo, Redo, List, ArrowLeft, GripVertical, AlertTriangle, Copy, Map as MapIcon,
    Link as LinkIcon, Link2, PlusCircle, GitCommit, GitPullRequest, Hash, GalleryVertical, GitMerge, ListTree, Zap, ScanLine, ImagePlus, Sigma, MoreHorizontal, Eye, Clock, Shield, RotateCcw, ScanSearch, Pin, GitBranchPlus, Github, RefreshCw, LayoutGrid, ExternalLink, Radio, Circle, Youtube, ChevronLeft, ChevronRight
} from 'lucide-react';
import { NodeCard } from './components/NodeCard';
import { SocialPostModal } from './components/SocialPostModal';
import { LearningShareAdvisorModal } from './components/LearningShareAdvisorModal';
import { scoreNoteForThreads } from './services/socialService';
import { YouTubeManagerModal } from './components/YouTubeManagerModal';
import { CollectionsHubModal } from './components/CollectionsHubModal';
import { ChatWidget } from './components/ChatWidget';
import NotebookIndexMenuItem from './components/NotebookIndexMenuItem';
import { MediaPanel } from './components/MediaPanel';
import { LoginScreen } from './components/LoginScreen';
import { WaitlistScreen } from './components/WaitlistScreen';
import { QuizModal } from './components/QuizModal';
import { SettingsModal } from './components/SettingsModal';
import { BlogView } from './components/BlogView';
import { FlowView, FlowViewHandle } from './components/FlowView';
import { ExpandedNoteModal } from './components/ExpandedNoteModal';
import { OutlinePanel } from './components/OutlinePanel';
import { UpgradeModal } from './components/UpgradeModal';
import { ShareModal } from './components/ShareModal';
import { MarketplaceModal } from './components/MarketplaceModal';
import { SnapshotDecisionModal } from './components/SnapshotDecisionModal';
import { AskAIModal } from './components/AskAIModal';
import { GuidedReadingPanel } from './components/GuidedReadingPanel';
import { callGuidedReading, GuidedReadingResult, ReadingSegment } from './services/geminiService';
import { QuizSelectionModal } from './components/QuizSelectionModal';
import { LinkSelectorModal } from './components/LinkSelectorModal';
import { ReviewSessionModal } from './components/ReviewSessionModal'; // NEW
import { ReviewFeedPanel } from './components/ReviewFeedPanel'; // NEW
import { NoteQuestionsModal } from './components/NoteQuestionsModal';
import { BackfillQuestionsModal } from './components/BackfillQuestionsModal';
import { NotebookSizeModal } from './components/NotebookSizeModal';
import { ExportModal } from './components/ExportModal';
import { EmptyNotebookOnboarding } from './components/EmptyNotebookOnboarding';
import { FirstCaptureNudge } from './components/FirstCaptureNudge';
import { useIsMobile } from './hooks/useIsMobile';
import { MobileImageViewer } from './components/MobileImageViewer';
import { TagPreviewStrip } from './components/TagPreviewStrip';
import {
    MindMapNode, Notebook, User, Language, NodeLink, RelatedVideoRef, Quiz, NodeWidth, Stroke, NodeType, FeatureFlags, NoteStyle, AIBehaviorSettings, Position
} from './types';
import {
    analyzeNodeUnderstanding, pinpointError, generateQuizFromNodes, generateTitleForNode, autoGenerateTitleFromText, generateSubTopics, generateStudyQuestions, generateReviewQuestions, setSimulateFreeUser as setGeminiSimulateFreeUser, setActiveNotebookAISettings, extractImageHashtags, callIdentifyDocument,
    generateNodeEmbedding, semanticSearchNotes, searchAcrossNotebooks, buildSearchIndex,
    createCheckoutSession, createPortalSession,
    getMaintenanceStatus, checkMaintenanceAccess, joinWaitlist,
} from './services/geminiService';
import { setNotebookPatternsEnabled } from './utils/mathSymbols';
import { initGoogleDrive, connectDrive, disconnectDrive, saveZipToDrive, downloadDriveBackup, DriveBackupFile } from './services/googleDriveService';
import { DebugOverlay } from './components/DebugOverlay';
import { ReAuthModal } from './components/ReAuthModal';
import { TrashModal } from './components/TrashModal';
import { NodeImageSlider } from './components/NodeImageSlider';
import { subscribeToNoteTextChanges, acceptRemoteText, isNoteTextAtBaseline, onTextSaveResult, setTextConflicts, notebookLoadedAt, type RemoteTextChange, subscribeToAnnotations, onAnnotationsMerged } from './services/storageService';
import { mergeAnnotations, sameAnnotations, type Annotations } from './utils/annotationMerge';
import { mergeNoteText, type NoteText, type TextConflictCopy } from './utils/textMerge';
import { subscribeToUserData, saveUserData, deleteNotebookData, loadSharedNotebook, setNotebookPublic, updatePublicNotebookTitle, onDebugLog, saveNotebookAISettings, saveNotebookPatternsEnabled, saveUserProfile, restoreNotebookData, permanentlyDeleteNotebookData, saveUserPrefs, loadUserPrefs, saveNotebookDescription, saveNotebookGroup, loadNotebookNodes, loadNotebookAnnotations } from './services/storageService';
import { useVideoScoreDisplay } from './services/videoScoreDisplay';
import { saveOrphanVideos, countVideos } from './services/orphanVideos';
import { safeUpdateDoc } from './services/videoPatchWrite';
import ClipJobsWatcher, { PendingClip } from './components/ClipJobsWatcher';
import { db, auth } from './services/firebase';
import { doc, deleteDoc, onSnapshot, updateDoc, collection, getDocs, getDoc } from 'firebase/firestore';
import { calculateLayout, LayoutType } from './utils/layoutUtils';
import { subscribeToAuth, logout, signInWithGoogle } from './services/authService';
import { backupService } from './services/backupService';
import { saveSnapshot, listSnapshots, restoreSnapshot, isDestructiveChange, SnapshotMeta } from './services/snapshotService';
import { getSizeLevelWarning, formatBytes } from './utils/sizeUtils';
import { parseMarkdown } from './utils/markdownUtils';
import { refreshOnboardingIfNeeded } from './utils/onboardingService';
import { getAdjacentNoteIds } from './utils/noteSequence';
import posthog from 'posthog-js';
import { GitHubStatus, linkGitHubInstallation, updateGitHubRepoConfig, clearGitHubConfig as clearGitHubConfigFn, getGitHubInstallUrl, publishNotebookToGitHub as publishNotebookToGitHubService } from './services/githubService';
import { GitHubPublishModal } from './components/GitHubPublishModal';
import { YouTubeUploadModal } from './components/YouTubeUploadModal';
import { QuickDrawLayer } from './components/QuickDrawLayer';
import { GuideBuilderModal } from './components/GuideBuilderModal';
import { initYouTube, isYouTubeConnected, buildVideoAppendPatch } from './services/youtubeService';
import { GitHubHubModal } from './components/GitHubHubModal';
import { FindReplacePanel } from './components/FindReplacePanel';
import { PodcastHub, FloatingPodcastPlayer } from './components/PodcastHub';
import { NewVersionNotice } from './components/NewVersionNotice';

// --- APP VERSION CONTROL ---
// One record/export/upload flow. Several can exist at once (one recording at a time, any number
// processing or waiting for review); each has its own YouTubeUploadModal instance.
interface UploadSession {
    sid: string;
    nodeId: string;
    notebookId: string;
    slot: number; // keeps two flows on the same note from sharing recovery storage
    initialFile?: File;
    sourceJobId?: string;
    // A spin-off clip of a recording (see ClipJobsWatcher): pre-filled title, and the export job of
    // the full video it was marked in.
    initialTitle?: string;
    clipOfJobId?: string;
    // Fallbacks in case the note/notebook is unloaded from memory while its flow is still running.
    nodeSnapshot?: MindMapNode;
    notebookSnapshot?: Notebook;
}

const APP_VERSION_NAME = "4.3.7";

// Notebook structural undo/redo (§5.2) lives in utils/undoHistory.ts as pure,
// unit-tested logic — see tests/undoHistory.test.ts.

function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h.toString(36);
}
const VERSION_STRING = `v${APP_VERSION_NAME}`;

// Notebook limit removed — no cap on free notebooks
const ADMIN_EMAIL = 'han.vas2018@gmail.com';

const generateId = () => Math.random().toString(36).substring(2, 9);

const FEATURED_NOTEBOOKS = [
    { uid: 'B9vR650VTGZL36LvnXujA7Hka072', id: 'knsupyj', title: 'Statistical Inference — Casella & Berger' },
    { uid: 'B9vR650VTGZL36LvnXujA7Hka072', id: 'y3o8ofk', title: 'Pattern Recognition & ML — Bishop' },
    { uid: 'B9vR650VTGZL36LvnXujA7Hka072', id: '1g19y5q', title: 'Numerical Optimization — Nocedal & Wright' },
];

const createInitialNotebook = (): Notebook => ({
    id: generateId(),
    title: 'Welcome to Study Board',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    nodeWidth: 'standard',
    nodes: [],
    links: [],
    pdfAnnotations: {},
    isPublic: false,
    _createdLocally: true
});

const Tip: React.FC<{ text: string; children: React.ReactNode; side?: 'top' | 'bottom' | 'left'; align?: 'center' | 'right' }> = ({ text, children, side = 'top', align = 'center' }) => (
  <div className="relative group/tip inline-flex">
    {children}
    {side === 'left' ? (
      <div className="pointer-events-none absolute right-full mr-2 top-1/2 -translate-y-1/2 px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-[9999] shadow-lg w-max max-w-[220px] text-center whitespace-pre-wrap">
        {text}
        <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900 dark:border-l-gray-700" />
      </div>
    ) : (
      <div className={`pointer-events-none absolute ${side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} ${align === 'right' ? 'right-0' : 'left-1/2 -translate-x-1/2'} px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-snug rounded-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150 z-[9999] shadow-lg w-max max-w-[220px] text-center whitespace-pre-wrap`}>
        {text}
        <span className={`absolute ${side === 'top' ? 'top-full border-t-gray-900 dark:border-t-gray-700' : 'bottom-full border-b-gray-900 dark:border-b-gray-700'} ${align === 'right' ? 'right-3' : 'left-1/2 -translate-x-1/2'} border-4 border-transparent`} />
      </div>
    )}
  </div>
);

function PaymentSuccessPage({ language }: { language: string }) {
    const [status, setStatus] = React.useState<'waiting' | 'success' | 'timeout'>('waiting');
    const isVi = language === 'vietnamese';

    React.useEffect(() => {
        const timeout = setTimeout(() => setStatus('timeout'), 30000);
        const unsubAuth = auth.onAuthStateChanged((user: any) => {
            if (!user) return;
            const unsub = onSnapshot(doc(db, 'users', user.uid), (snap: any) => {
                if (snap.data()?.isPremium === true) {
                    clearTimeout(timeout);
                    setStatus('success');
                    setTimeout(() => { window.location.href = '/'; }, 2500);
                }
            });
            return () => unsub();
        });
        return () => { clearTimeout(timeout); unsubAuth(); };
    }, []);

    return (
        <div className="fixed inset-0 bg-white dark:bg-slate-900 flex flex-col items-center justify-center gap-6 font-sans">
            {status === 'waiting' && (
                <>
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                            {isVi ? 'Đang kích hoạt tài khoản...' : 'Activating your account...'}
                        </h2>
                        <p className="text-sm text-gray-500">{isVi ? 'Vui lòng chờ vài giây.' : 'Please wait a moment.'}</p>
                    </div>
                </>
            )}
            {status === 'success' && (
                <>
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                            {isVi ? 'Thanh toán thành công!' : 'Payment successful!'}
                        </h2>
                        <p className="text-sm text-gray-500">{isVi ? 'Chào mừng bạn đến với Premium ✦' : 'Welcome to Premium ✦'}</p>
                    </div>
                </>
            )}
            {status === 'timeout' && (
                <div className="text-center max-w-sm px-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {isVi ? 'Đang xử lý...' : 'Processing...'}
                    </h2>
                    <p className="text-sm text-gray-500 mb-4">
                        {isVi ? 'Thanh toán đang được xử lý. Vui lòng chờ vài phút rồi quay lại app.' : 'Your payment is being processed. Please wait a few minutes and return to the app.'}
                    </p>
                    <a href="/" className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-colors">
                        {isVi ? 'Về trang chủ' : 'Go to app'}
                    </a>
                </div>
            )}
        </div>
    );
}

export default function App() {
    const [user, setUser] = useState<User | null>(null);
    const [sessionExpired, setSessionExpired] = useState(false);
    const isExplicitLogoutRef = useRef(false);
    const hadUserRef = useRef(false);
    // Front door for the "no new sign-ups while we fix security holes" lockdown — see
    // functions/src/index.ts's MAINTENANCE_MODE. null = not checked yet (treat as closed to be
    // safe rather than flash the real login screen). showLoginDuringMaintenance is the escape
    // hatch behind the WaitlistScreen's small "Already have an account? Log in" link.
    const [maintenanceMode, setMaintenanceMode] = useState<boolean | null>(null);
    const [showLoginDuringMaintenance, setShowLoginDuringMaintenance] = useState(false);
    const [waitlistBlockedEmail, setWaitlistBlockedEmail] = useState<string | null>(null);
    useEffect(() => {
        getMaintenanceStatus()
            .then(res => setMaintenanceMode(!!res.maintenanceMode))
            .catch(() => setMaintenanceMode(false)); // status check itself failing shouldn't lock everyone out
    }, []);
    // Safety net: someone used the "Log in" escape hatch and signed in during maintenance but
    // isn't actually allowlisted. Bounce them back to the waitlist screen instead of letting them
    // into an app that assertNotInMaintenance (the real, server-side gate) will error on at every
    // turn — and auto-join them since we now know their email.
    useEffect(() => {
        if (!user || !user.email || maintenanceMode !== true) return;
        let cancelled = false;
        checkMaintenanceAccess().then(res => {
            if (cancelled || res.allowed) return;
            joinWaitlist(user.email!, 'auto-login-attempt').catch(() => {});
            setWaitlistBlockedEmail(user.email!);
            setShowLoginDuringMaintenance(false);
            logout().finally(() => setUser(null));
        }).catch(() => { /* if the check itself fails, don't lock the admin out over a network blip */ });
        return () => { cancelled = true; };
    }, [user, maintenanceMode]);
    const [notebooks, _setNotebooks] = useState<Notebook[]>([]);
    // Wrapper that keeps nodeCount in sync with the actual nodes array on every update.
    // This is the foundation for Stage 2 lazy loading: when non-active notebooks are loaded
    // without their full nodes array, nodeCount will still reflect the true count from Firestore.
    const setNotebooks = useCallback((updater: Notebook[] | ((prev: Notebook[]) => Notebook[])) => {
        _setNotebooks(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            // Only recompute nodeCount for notebooks whose nodes array actually changed length
            return next.map(nb => {
                const prevNb = prev.find(p => p.id === nb.id);
                const nodesChanged = !prevNb || prevNb.nodes !== nb.nodes;
                if (nodesChanged && !nb._isPartiallyLoaded) {
                    return { ...nb, nodeCount: nb.nodes.length };
                }
                return nb;
            });
        });
    }, []);
    const [activeNotebookId, setActiveNotebookId] = useState<string | null>(null);
    const [showTrashModal, setShowTrashModal] = useState(false);

    // --- DATA INTEGRITY STATES ---
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const isInitialLoadCompleteRef = useRef(false);
    const isSavingCriticalOpRef = useRef(false); // Separate flag for save locking

    const notebooksRef = useRef(notebooks);
    const lastSavedNotebooksRef = useRef<Notebook[]>([]);
    const lastCloudSyncTimeRef = useRef<number>(0); // Track last successful cloud sync time
    type NodeSearchStub = { id: string; title: string; content: string; tags: string[] };
    // Lightweight search stubs for all notebooks — survives eviction of full nodes
    const crossSearchIndex = useRef<Map<string, NodeSearchStub[]>>(new Map());
    const cloudSyncDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Stage 3 lazy-load: track current + previous active notebook ID for eviction
    const activeNotebookIdRef = useRef<string | null>(null);
    const prevActiveNotebookIdRef = useRef<string | null>(null);
    // Monotonic counter to cancel stale loadNotebookNodes responses when switching rapidly
    const loadRequestCountRef = useRef(0);
    // Explicit deletion tracking: only node IDs that the user intentionally deleted go here.
    // Autosave uses this instead of diff-based deletion to avoid treating load failures as deletions.
    const pendingDeletionsRef = useRef<Map<string, Set<string>>>(new Map());
    // Known real node counts from last successful load — used to detect silent empty-load bugs
    const knownNodeCountRef = useRef<Map<string, number>>(new Map());
    // True while the active notebook hasn't finished its lazy-load yet (or its grace period hasn't elapsed).
    // Prevents the empty-notebook onboarding card from flashing prematurely.
    const [notebookLoadPending, setNotebookLoadPending] = useState(false);
    const notebookLoadPendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        notebooksRef.current = notebooks;
    }, [notebooks]);

    useEffect(() => {
        isInitialLoadCompleteRef.current = isInitialLoadComplete;
    }, [isInitialLoadComplete]);

    const [historyPast, setHistoryPast] = useState<HistoryEntry[]>([]);
    const [historyFuture, setHistoryFuture] = useState<RedoEntry[]>([]);
    const tempSnapshotRef = useRef<MindMapNode[] | null>(null);
    const MAX_HISTORY = 20;

    const isMobile = useIsMobile();

    const [language, setLanguage] = useState<Language>(() => {
        const saved = localStorage.getItem('ms_language');
        return saved === 'vietnamese' ? 'vietnamese' : 'english';
    });

    useEffect(() => {
        localStorage.setItem('ms_language', language);
        if (user && user.id !== 'guest') saveUserPrefs(user.id, { language });
    }, [language, user]);

    const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('sb_sidebar_open') !== 'false');
    const [mediaPanelOpen, setMediaPanelOpen] = useState(() => localStorage.getItem('sb_media_panel_open') === 'true');
    const [mediaPanelOpenedForPdf, setMediaPanelOpenedForPdf] = useState(false);
    const pendingRootNoteIdRef = useRef<string | null>(null);
    const pendingPdfFilenameRef = useRef<string>('');
    const pdfUtilsRef = useRef<import('./components/PdfViewer').PdfDocumentUtils | null>(null);
    const [showFirstCaptureNudge, setShowFirstCaptureNudge] = useState(false);
    const [highlightCaptureButton, setHighlightCaptureButton] = useState(false);
    const [outlinePanelOpen, setOutlinePanelOpen] = useState(() => localStorage.getItem('sb_outline_open') === 'true');
    const [outlinePanelWidth, setOutlinePanelWidth] = useState(() => parseInt(localStorage.getItem('sb_outline_width') || '280', 10));
    const [outlineCollapsedIds, setOutlineCollapsedIds] = useState<Set<string>>(new Set());
    const [isResizingOutline, setIsResizingOutline] = useState(false);
    const [mediaPanelWidth, setMediaPanelWidth] = useState(() => parseInt(localStorage.getItem('sb_media_width') || '450', 10));
    const [isResizingPanel, setIsResizingPanel] = useState(false);
    // Refs for direct DOM resize (avoids re-render on every mousemove)
    const mediaPanelWidthRef = useRef(parseInt(localStorage.getItem('sb_media_width') || '450', 10));
    const outlinePanelWidthRef = useRef(parseInt(localStorage.getItem('sb_outline_width') || '280', 10));
    const mediaPanelElRef = useRef<HTMLDivElement>(null);
    const mediaToolbarRef = useRef<HTMLDivElement>(null);
    const mediaActionBarRef = useRef<HTMLDivElement>(null);
    const outlinePanelElRef = useRef<HTMLDivElement>(null);

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsInitialTab, setSettingsInitialTab] = useState<'general' | 'ai' | 'math' | 'premium' | 'data'>('general');
    const [settingsInitialAIScope, setSettingsInitialAIScope] = useState<string>('global');
    const [settingsDataTabNotebookId, setSettingsDataTabNotebookId] = useState<string | undefined>(undefined);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [marketplaceOpen, setMarketplaceOpen] = useState(false);
    const [driveConnected, setDriveConnected] = useState(false);
    const [driveBackupRecords, setDriveBackupRecords] = useState<DriveBackupFile[]>(() => {
        try { return JSON.parse(localStorage.getItem('sb_drive_backups') ?? '[]'); } catch { return []; }
    });
    const [isPremium, setIsPremium] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<string | undefined>(undefined);
    const [simulateFreeUser, setSimulateFreeUser] = useState(false);

    // --- FEATURE FLAGS ---
    const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(() => {
        const saved = localStorage.getItem('ms_feature_flags');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Failed to parse feature flags:', e);
            }
        }
        return {
            enableCleanup: false,
            enableSizeWarnings: false,
            enableSubcollections: false,
            debugMode: false
        };
    });

    // Persist feature flags to localStorage
    useEffect(() => {
        localStorage.setItem('ms_feature_flags', JSON.stringify(featureFlags));
        if (featureFlags.debugMode) {
            const enabledFlags = Object.entries(featureFlags).filter(([_, value]) => value === true);
            console.log('🚩 Feature Flags (Enabled):', Object.fromEntries(enabledFlags));
            console.log('   All flags:', featureFlags);
        }
    }, [featureFlags]);

    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    const [nodeToDeleteId, setNodeToDeleteId] = useState<string | null>(null);
    const [deleteNodeHasChildren, setDeleteNodeHasChildren] = useState(false);
    const [notebookToDeleteId, setNotebookToDeleteId] = useState<string | null>(null);

    const [pendingSnapshot, setPendingSnapshot] = useState<string | null>(null);
    const [pendingSnapshotText, setPendingSnapshotText] = useState<string>('');
    const [pdfReturnPending, setPdfReturnPending] = useState(false);

    const [activePreviewTag, setActivePreviewTag] = useState<string | null>(null);
    const [tagNavPrevNodeId, setTagNavPrevNodeId] = useState<string | null>(null);
    const [askAIOpen, setAskAIOpen] = useState(false);
    const [askAISnapshot, setAskAISnapshot] = useState<string | null>(null);
    const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
    const [askAIContext, setAskAIContext] = useState<{
        boardTitle?: string; pdfName?: string; pageNumber?: number;
        selectedNote?: { title: string; content: string }; recentNotesTitles?: string[];
    } | undefined>(undefined);

    const [guidedReadingData, setGuidedReadingData] = useState<GuidedReadingResult | null>(null);
    const [guidedReadingLoading, setGuidedReadingLoading] = useState(false);
    const [guidedReadingHighlights, setGuidedReadingHighlights] = useState<{ text: string; colorIndex: number }[]>([]);
    const [guidedReadingHighlightsVisible, setGuidedReadingHighlightsVisible] = useState(true);

    const [syncStatus, setSyncStatus] = useState<{
        state: 'saved' | 'saving' | 'error',
        message?: string
    }>({ state: 'saved' });
    const [syncJustSaved, setSyncJustSaved] = useState(false);
    const prevSyncStateRef = useRef<'saved' | 'saving' | 'error'>('saved');

    const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
    const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false); // Track if local has changes not in cloud
    const [authSessionExpired, setAuthSessionExpired] = useState(false);
    const [showReAuthModal, setShowReAuthModal] = useState(false);
    const [recoveryInfo, setRecoveryInfo] = useState<{ count: number; notebookTitle: string } | null>(null);

    const isSavingRef = useRef(false);
    const [isLocalLoading, setIsLocalLoading] = useState(false);

    const [importTask, setImportTask] = useState<{
        isActive: boolean;
        progress: number;
        fileName: string;
        status: 'processing' | 'success' | 'error';
    }>({ isActive: false, progress: 0, fileName: '', status: 'processing' });

    const [exportTask, setExportTask] = useState<{
        isActive: boolean;
        progress: number;
        status: 'processing' | 'success' | 'error' | 'cancelled';
    }>({ isActive: false, progress: 0, status: 'processing' });
    const exportAbortControllerRef = useRef<AbortController | null>(null);
    const nodeExpandCyclesRef = useRef<Record<string, 0 | 1 | 2>>({});
    const layoutRecalcTimerRef = useRef<NodeJS.Timeout | null>(null);
    const nodeActualHeightsRef = useRef<Record<string, number>>({});
    const wasSkeletonModeRef = useRef(false);

    // NEW: State for Export Modal
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [podcastHubOpen, setPodcastHubOpen] = useState(false);
    const [activePodcast, setActivePodcast] = useState<import('./types').Podcast | null>(null);
    const [podcastQueue, setPodcastQueue] = useState<import('./types').Podcast[]>([]);

    const [driveConfig, setDriveConfig] = useState({ apiKey: '', clientId: '' });

    const [layoutMode, setLayoutMode] = useState<'map' | 'board' | 'blog' | 'stream' | 'flow'>(() => {
        // Map and Flow View aren't offered as mobile menu options (too little space to be useful
        // there), so a narrow viewport should never land in either — even from a stale URL param
        // or a value saved on a wider screen.
        const isMobileInit = typeof window !== 'undefined' && window.innerWidth < 768;

        const urlParams = new URLSearchParams(window.location.search);
        const urlLayout = urlParams.get('layout');
        if (urlLayout === 'blog' || urlLayout === 'stream' || urlLayout === 'map' || urlLayout === 'flow') {
            if (isMobileInit && (urlLayout === 'map' || urlLayout === 'flow')) return 'stream';
            return urlLayout as any;
        }

        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('ms_layout_mode');
            if (saved === 'map' || saved === 'board' || saved === 'blog' || saved === 'stream' || saved === 'flow') {
                if (isMobileInit && (saved === 'map' || saved === 'flow')) return 'stream';
                return saved;
            }
        }
        return isMobileInit ? 'stream' : 'map';
    });

    const [mapLayoutStyle, setMapLayoutStyle] = useState<LayoutType>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('ms_layout_style');
            if (saved === 'free' || saved === 'horizontal' || saved === 'horizontal-right' || saved === 'vertical' || saved === 'vertical-both') return saved as LayoutType;
        }
        return 'vertical';
    });

    const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);

    // Context menu for bulk actions
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; notebookId: string } | null>(null);
    const [nodeContextMenu, setNodeContextMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null);
    const [shareNodeId, setShareNodeId] = useState<string | null>(null);
    const [videoEditNodeId, setVideoEditNodeId] = useState<string | null>(null);
    const [videoEditDraft, setVideoEditDraft] = useState({ url: '', title: '', duration: '' });
    const [uploadSessions, setUploadSessions] = useState<UploadSession[]>([]);
    useEffect(() => { uploadOpenRef.current = uploadSessions.length > 0; if (uploadSessions.length) setQuickDrawOpen(false); }, [uploadSessions.length]);
    const [foregroundSid, setForegroundSid] = useState<string | null>(null);
    const [recordingSids, setRecordingSids] = useState<string[]>([]);

    const layoutMountedRef = useRef(false);
    useEffect(() => {
        localStorage.setItem('ms_layout_mode', layoutMode);
        if (layoutMountedRef.current) posthog.capture('layout_changed', { layout: layoutMode });
        else layoutMountedRef.current = true;
    }, [layoutMode]);

    useEffect(() => {
        localStorage.setItem('ms_layout_style', mapLayoutStyle);
    }, [mapLayoutStyle]);

    const [nodeWidth, setNodeWidth] = useState<NodeWidth>('standard');
    const [widthDropdownOpen, setWidthDropdownOpen] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [searchChips, setSearchChips] = useState<string[]>([]);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [canvasSearchMode, setCanvasSearchMode] = useState<'keyword' | 'semantic'>('keyword');
    const [canvasSearchScope, setCanvasSearchScope] = useState<'all' | 'current'>('all');
    const [canvasSearchResults, setCanvasSearchResults] = useState<import('./services/geminiService').CrossNotebookSearchResult[]>([]);
    const [canvasSemanticResults, setCanvasSemanticResults] = useState<import('./types').SemanticSearchResult[]>([]);
    const [canvasSearchLoading, setCanvasSearchLoading] = useState(false);
    const [canvasSearchError, setCanvasSearchError] = useState<string | null>(null);
    const [canvasSearchedQuery, setCanvasSearchedQuery] = useState<string>('');
    const canvasSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [findReplaceOpen, setFindReplaceOpen] = useState(false);
    const [findReplaceScope, setFindReplaceScope] = useState<'notebook' | 'note'>('notebook');
    const [findReplaceVersion, setFindReplaceVersion] = useState(0);
    const [findReplaceQuery, setFindReplaceQuery] = useState('');
    const [searchPeekNode, setSearchPeekNode] = useState<{ node: import('./types').MindMapNode; notebookId: string } | null>(null);
    const [hoverPreview, setHoverPreview] = useState<{ node: import('./types').MindMapNode; notebookId: string; top: number; left: number } | null>(null);
    const [searchHoverExpanded, setSearchHoverExpanded] = useState(false);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [searchHistory, setSearchHistory] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem('sb_search_history') || '[]'); } catch { return []; } });
    const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);

    const [renamingNotebookId, setRenamingNotebookId] = useState<string | null>(null);
    const [renamingTitle, setRenamingTitle] = useState("");

    const [linkingSourceId, setLinkingSourceId] = useState<string | null>(null);
    const [isLinkSelectorOpen, setIsLinkSelectorOpen] = useState(false);
    const [linkDecision, setLinkDecision] = useState<{
        sourceId: string;
        targetId: string;
        position: { x: number; y: number }
    } | null>(null);

    const [quiz, setQuiz] = useState<Quiz | null>(null);
    const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);

    const [isQuizConfigMode, setIsQuizConfigMode] = useState(false);
    const [quizSelectedNodeIds, setQuizSelectedNodeIds] = useState<Set<string>>(new Set());

    const [quizQuestionCount, setQuizQuestionCount] = useState(5);

    // Track if we've auto-focused for current notebook to prevent repeated centering
    const hasAutoFocusedRef = useRef<string | null>(null);
    // Stores a nodeId to jump to after cross-notebook navigation completes
    const pendingJumpNodeRef = useRef<string | null>(null);

    const [pdfUrl, setPdfUrl] = useState<string | null>(null);

    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    const viewportSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const streamScrollSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const lastActiveNodeIdRef = useRef<string | null>(null);
    const [magicExpandingNodeId, setMagicExpandingNodeId] = useState<string | null>(null);

    const [dropCandidateId, setDropCandidateId] = useState<string | null>(null);
    const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
    const [pendingCrossNav, setPendingCrossNav] = useState<{ notebookId: string; nodeId: string } | null>(null);
    const [mobileImageSrc, setMobileImageSrc] = useState<string | null>(null);

    const [globalExpandCycle, setGlobalExpandCycle] = useState<0 | 1 | 2 | 3 | 4>(() => {
        const saved = localStorage.getItem('globalExpandCycle');
        const n = saved !== null ? parseInt(saved) : 1;
        return (n >= 0 && n <= 4 ? n : 1) as 0 | 1 | 2 | 3 | 4;
    });
    // The "Display mode" control that changes this is desktop-only (hidden on mobile — too little
    // space to be useful there), so mobile always renders as if it's on the default, "Expand Text".
    const effectiveExpandCycle = isMobile ? 1 : globalExpandCycle;
    const [showExpandMenu, setShowExpandMenu] = useState(false);
    const [canvasBg, setCanvasBg] = useState<{ color: string; pattern: string }>(() => {
        try { const s = localStorage.getItem('canvasBg'); return s ? JSON.parse(s) : { color: '#f8fafc', pattern: 'dots-sm' }; } catch { return { color: '#f8fafc', pattern: 'dots-sm' }; }
    });
    const [savedCustomColors, setSavedCustomColors] = useState<string[]>(() => {
        try { const s = localStorage.getItem('canvasBgCustomColors'); return s ? JSON.parse(s) : []; } catch { return []; }
    });
    const [canvasBgMenu, setCanvasBgMenu] = useState<{ x: number; y: number } | null>(null);
    const [isSurveyMode, setIsSurveyMode] = useState(false);
    // Canvas score map: every card shows its video-potential score, coloured as in YouTube Manager's map tab.
    const [showScoreMap, setShowScoreMap] = useState(false);
    const videoScoreDisplay = useVideoScoreDisplay(); // switched on in YouTube Manager's map tab
    const [surveyPreview, setSurveyPreview] = useState<{ node: MindMapNode; rect: DOMRect } | null>(null);
    const [outlineLinkMode, setOutlineLinkMode] = useState(false);
    // Synced from Firestore (users/{uid}/meta/githubStatus) — the token itself never lives here,
    // only Cloud Functions can read/write it. See services/githubService.ts + functions/src/github.ts.
    const [githubStatus, setGithubStatus] = useState<GitHubStatus | null>(null);
    const [githubPublishNotebookId, setGithubPublishNotebookId] = useState<string | null>(null);
    const [showGitHubHub, setShowGitHubHub] = useState(false);
    const [showGuideBuilder, setShowGuideBuilder] = useState(false);
    const [showThreadsAdvisor, setShowThreadsAdvisor] = useState(false);
    const [showYouTubeManager, setShowYouTubeManager] = useState(false);
    const [showCollectionsHub, setShowCollectionsHub] = useState(false);
    const [advisorDraft, setAdvisorDraft] = useState<{ postText: string; hashtags: string[]; nodeId: string | null } | null>(null);
    const [isGuideSelectMode, setIsGuideSelectMode] = useState(false);
    const [guideSelectedNodeIds, setGuideSelectedNodeIds] = useState<Set<string>>(new Set());
    const [guideInitialIds, setGuideInitialIds] = useState<string[]>([]);
    const prevPublishingRef = useRef<boolean>(false);
    const [bulkPublishQueue, setBulkPublishQueue] = useState<{ ids: string[]; mode: 'full' | 'safe'; commitMessage?: string; idx: number } | null>(null);
    const [draggingLinkId, setDraggingLinkId] = useState<string | null>(null);
    const [selectedLinkId, setSelectedLinkId] = useState<{ id: string; sx: number; sy: number } | null>(null);
    const [linkHoverNodeId, setLinkHoverNodeId] = useState<string | null>(null);
    const draggingLinkStartRef = useRef<{ mouseX: number; mouseY: number; dx: number; dy: number } | null>(null);

    const [isFlying, setIsFlying] = useState(false);
    const flyCorridorBoundsRef = useRef<{ minX: number; maxX: number; minY: number; maxY: number } | null>(null);
    const flyScaleRef = useRef<number | null>(null);

    const [activeReviewSessionNodeId, setActiveReviewSessionNodeId] = useState<string | null>(null);
    const [reviewFeedPanelOpen, setReviewFeedPanelOpen] = useState(false);
    const [isAutoTitling, setIsAutoTitling] = useState(false);
    const [autoTitleProgress, setAutoTitleProgress] = useState({ current: 0, total: 0 });
    const [isDragOverCanvas, setIsDragOverCanvas] = useState(false);
    const [pickParentForNodeId, setPickParentForNodeId] = useState<string | null>(null);
    const autoTitleCancelRef = useRef(false);
    const [userApiKeys] = useState<{ gemini?: string; openai?: string }>(() => ({
        gemini: localStorage.getItem('sb_user_gemini_key') || undefined,
        openai: localStorage.getItem('sb_user_openai_key') || undefined,
    }));
    const [autoTitleEnabled, setAutoTitleEnabled] = useState(() => localStorage.getItem('autoTitleEnabled') === 'true');
    const [bgAutoTitleStatus, setBgAutoTitleStatus] = useState<string | null>(null);
    const bgAutoTitleRunningRef = useRef(false);
    const [reviewFeedWidth, setReviewFeedWidth] = useState(320);
    const [isResizingReviewFeed, setIsResizingReviewFeed] = useState(false);
    const [noteQuestionsNodeId, setNoteQuestionsNodeId] = useState<string | null>(null);
    const [backfillModalOpen, setBackfillModalOpen] = useState(false);
    const [isGeneratingMissing, setIsGeneratingMissing] = useState(false); // NEW: batch generation loading
    const [aiMenuOpen, setAiMenuOpen] = useState(false); // NEW: AI action menu FAB
    const [isRecording, setIsRecording] = useState(false);
    const [isRecordingPaused, setIsRecordingPaused] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunksRef = useRef<Blob[]>([]);
    const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [pendingRecordingFile, setPendingRecordingFile] = useState<File | null>(null);
    const [recordingTargetNodeId, setRecordingTargetNodeId] = useState<string | null>(null);

    const [sizeModalNotebookId, setSizeModalNotebookId] = useState<string | null>(null); // NEW: Size analysis modal
    const [pendingImportNotebook, setPendingImportNotebook] = useState<Notebook | null>(null);
    const [importPickMode, setImportPickMode] = useState<{ notebookId: string; importedNotebook: Notebook } | null>(null);

    const [showDeployHint, setShowDeployHint] = useState(false);
    const [notebookOrder, setNotebookOrder] = useState<string[]>(() => {
        try { return JSON.parse(localStorage.getItem('notebookOrder') || '[]'); } catch { return []; }
    });
    const [draggingNbId, setDraggingNbId] = useState<string | null>(null);
    const [dragOverNbId, setDragOverNbId] = useState<string | null>(null);

    const isReadOnlyMode = new URLSearchParams(window.location.search).get('mode') === 'view';
    const [testViewMode, setTestViewMode] = useState(false);
    const isEffectiveReadOnly = isReadOnlyMode || testViewMode;

    useEffect(() => {
        const isStaging = window.location.hostname.includes('run.app') || window.location.hostname.includes('web.app');
        if (isStaging && !isEffectiveReadOnly && user) {
            setShowDeployHint(true);
        }
    }, [isEffectiveReadOnly, user]);

    // PREMUM SYNC
    useEffect(() => {
        if (!user || user.id === 'guest') {
            setIsPremium(false);
            return;
        }

        const unsubscribe = onSnapshot(doc(db, 'users', user.id), (docSnapshot) => {
            const data = docSnapshot.data();
            setIsPremium(data?.isPremium === true);
            setSubscriptionStatus(data?.subscriptionStatus);
        }, (error) => {
            console.error("Error syncing premium:", error);
        });

        return () => unsubscribe();
    }, [user]);

    // GITHUB STATUS SYNC — read-only mirror of the server-held config (never the token itself),
    // so "configured on my MacBook yesterday" now shows up correctly on any other device too.
    useEffect(() => {
        if (!user || user.id === 'guest') {
            setGithubStatus(null);
            return;
        }

        const unsubscribe = onSnapshot(doc(db, 'users', user.id, 'meta', 'githubStatus'), (docSnapshot) => {
            const data = docSnapshot.data();
            setGithubStatus(data ? (data as GitHubStatus) : null);
        }, (error) => {
            console.error("Error syncing GitHub status:", error);
        });

        return () => unsubscribe();
    }, [user]);

    // Show toast when background publish completes
    useEffect(() => {
        const wasPublishing = prevPublishingRef.current;
        const isPublishing = !!githubStatus?.publishing;
        if (wasPublishing && !isPublishing) {
            if (githubStatus?.lastPublishResult === 'success') {
                showToast(language === 'vietnamese' ? '✓ Publish lên GitHub thành công!' : '✓ Published to GitHub!', 'success');
            } else if (githubStatus?.lastPublishResult === 'error') {
                showToast(language === 'vietnamese' ? 'Publish thất bại: ' + (githubStatus.lastPublishError || 'unknown error') : 'Publish failed: ' + (githubStatus?.lastPublishError || 'unknown error'), 'error');
            }
        }
        prevPublishingRef.current = isPublishing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [githubStatus?.publishing, githubStatus?.lastPublishResult]);

    // Sequential bulk publish — runs in App.tsx so modal can close immediately
    useEffect(() => {
        if (!bulkPublishQueue) return;
        const { ids, mode, idx } = bulkPublishQueue;
        if (idx >= ids.length) {
            showToast(language === 'vietnamese' ? `✓ Đã publish ${ids.length} notebooks!` : `✓ Published ${ids.length} notebooks!`, 'success');
            setBulkPublishQueue(null);
            return;
        }
        const id = ids[idx];
        const nb = notebooks.find(n => n.id === id);
        publishNotebookToGitHubService(id, mode, bulkPublishQueue.commitMessage)
            .then(result => {
                const msg = result.commitMessage || `✓ ${nb?.title || 'Notebook'}`;
                showToast(msg, 'success');
                posthog.capture('github_published', { mode });
                setNotebooks(nbs => nbs.map(n =>
                    n.id === id ? { ...n, lastPublishedNodeCount: n.nodeCount ?? n.nodes.length, lastPublishedAt: Date.now() } : n
                ));
            })
            .catch(() => {
                showToast((language === 'vietnamese' ? '✗ Lỗi: ' : '✗ Error: ') + (nb?.title || 'Notebook'), 'error');
            })
            .finally(() => {
                setBulkPublishQueue(q => q ? { ...q, idx: q.idx + 1 } : null);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bulkPublishQueue?.idx]);

    // Handle GitHub App installation callback — GitHub redirects back to studyboard.app?installation_id=XXX
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const installationId = params.get('installation_id');
        const setupAction = params.get('setup_action');
        if (!installationId || !user || user.id === 'guest') return;

        // Clean URL immediately so refresh doesn't retrigger
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        linkGitHubInstallation(Number(installationId))
            .then(() => {
                // Open Settings to GitHub tab so user can pick repo
                setSettingsOpen(true);
                setSettingsInitialTab('data');
            })
            .catch(e => console.error('Failed to link GitHub installation:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const canvasRef = useRef<HTMLDivElement>(null);
    const innerCanvasRef = useRef<HTMLDivElement>(null);
    const flowViewRef = useRef<FlowViewHandle>(null);
    const activePointers = useRef<Map<number, { x: number, y: number, type?: string }>>(new Map());
    const prevPinchDist = useRef<number | null>(null);
    const isDraggingCanvas = useRef(false);
    const draggingNodeId = useRef<string | null>(null);
    const lastMousePos = useRef({ x: 0, y: 0 });
    const panRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    const scaleRef = useRef(1);
    panRef.current = pan;
    scaleRef.current = scale;

    // Block browser zoom on trackpad pinch (Mac) - but allow scroll in text fields
    useEffect(() => {
        const handleNativeWheel = (e: WheelEvent) => {
            // Allow normal scroll inside text inputs/textareas
            const target = e.target as HTMLElement;
            if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable) {
                return;
            }

            // Pinch-to-zoom sends wheel events with ctrlKey=true
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
            }
        };

        // Use capture phase + document level to intercept BEFORE browser zoom
        document.addEventListener('wheel', handleNativeWheel, { passive: false, capture: true });
        return () => document.removeEventListener('wheel', handleNativeWheel, { capture: true });
    }, []);

    // Warn user before closing tab if there are unsaved changes
    useEffect(() => {
        if (isEffectiveReadOnly) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            // Only warn if we have unsynced changes
            if (hasUnsyncedChanges) {
                e.preventDefault();
                // Modern browsers ignore custom messages, but we still need to set returnValue
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsyncedChanges, isEffectiveReadOnly]);

    // Persist UI state to localStorage
    useEffect(() => { localStorage.setItem('sb_sidebar_open', String(sidebarOpen)); }, [sidebarOpen]);
    useEffect(() => { localStorage.setItem('sb_media_panel_open', String(mediaPanelOpen)); }, [mediaPanelOpen]);
    useEffect(() => { localStorage.setItem('sb_outline_open', String(outlinePanelOpen)); }, [outlinePanelOpen]);
    useEffect(() => { localStorage.setItem('sb_outline_width', String(outlinePanelWidth)); }, [outlinePanelWidth]);
    useEffect(() => { localStorage.setItem('sb_media_width', String(mediaPanelWidth)); }, [mediaPanelWidth]);
    useEffect(() => { if (activeNotebookId) localStorage.setItem('sb_active_notebook', activeNotebookId); }, [activeNotebookId]);
    useEffect(() => { setOutlineCollapsedIds(new Set()); }, [activeNotebookId]);

    const isGuest = user?.id === 'guest';
    const isAdmin = user?.email?.toLowerCase().trim() === ADMIN_EMAIL.toLowerCase();    const effectiveIsPremium = isPremium && !simulateFreeUser;

    const handleSimulateFreeUserChange = useCallback((v: boolean) => {
        setSimulateFreeUser(v);
        setGeminiSimulateFreeUser(v);
    }, []);
    const activeNotebook = notebooks.find(nb => nb.id === activeNotebookId);
    // A drawing saved while recording goes to the note open in the expanded view (not the note the
    // recording started from) — only in the user's own, editable notebook.
    const drawingTargetNote = useMemo(() => {
        if (!expandedNodeId || !activeNotebook || isEffectiveReadOnly) return null;
        const n = activeNotebook.nodes.find(x => x.id === expandedNodeId);
        return n ? { notebookId: activeNotebook.id, nodeId: n.id, title: n.title || '' } : null;
    }, [expandedNodeId, activeNotebook, isEffectiveReadOnly]);
    // The note open in the expanded view, for the virtual classmate who follows along while recording.
    // Draw on the screen while just taking notes (the recording's drawing board, no recording).
    const [quickDrawOpen, setQuickDrawOpen] = useState(false);
    const liveExpandedNote = useMemo(() => {
        if (!expandedNodeId || !activeNotebook) return null;
        const n = activeNotebook.nodes.find(x => x.id === expandedNodeId);
        return n ? { nodeId: n.id, title: n.title || '', content: n.content || '', attachments: n.attachments } : null;
    }, [expandedNodeId, activeNotebook]);
    // Ctrl/⌘+Shift+D (the recording's drawing key, if remapped) opens it. While an upload/recording
    // window exists, that window owns the key and its own board, so this one steps aside.
    const uploadOpenRef = useRef(false);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!e.shiftKey || e.altKey || !(e.ctrlKey || e.metaKey) || uploadOpenRef.current) return;
            let key = 'd';
            try { key = (JSON.parse(localStorage.getItem('draw_hotkeys') || '{}').toggleOverlay || 'd').toLowerCase(); } catch {}
            if (e.key.toLowerCase() !== key) return;
            e.preventDefault();
            setQuickDrawOpen(v => !v);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);
    const activeNotebooks = useMemo(() => {
        const list = notebooks.filter(nb => !nb.isDeleted);
        if (notebookOrder.length === 0) return list;
        const orderMap = new Map(notebookOrder.map((id, i) => [id, i]));
        return [...list].sort((a, b) => {
            const ai = orderMap.get(a.id) ?? Infinity;
            const bi = orderMap.get(b.id) ?? Infinity;
            return ai - bi;
        });
    }, [notebooks, notebookOrder]);

    // Persist which note is expanded so a refresh (web or mobile) reopens it — mirrors sb_viewport_ per-notebook pattern.
    // Deliberately keyed only on the ids (not `activeNotebook`) so this doesn't re-fire on every node edit.
    useEffect(() => {
        if (!activeNotebookId) return;
        if (expandedNodeId && activeNotebook?.nodes.some(n => n.id === expandedNodeId)) {
            localStorage.setItem(`sb_expanded_node_${activeNotebookId}`, expandedNodeId);
        } else if (!expandedNodeId) {
            localStorage.removeItem(`sb_expanded_node_${activeNotebookId}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedNodeId, activeNotebookId]);

    const isUntitledNode = (n: { title?: string; titleSource?: string; autoTitleAttempts?: number }) => {
        if (n.titleSource === 'user' || n.titleSource === 'ai') return false;
        const t = n.title?.trim() ?? '';
        return t === '' || t === 'Tiêu đề...' || t.toLowerCase() === 'untitled';
    };
    const autoTitlePendingCount = useMemo(() =>
        (activeNotebook?.nodes ?? []).filter(n => isUntitledNode(n) && (n.autoTitleAttempts ?? 0) < 2 && (n.content?.trim().length ?? 0) >= 20).length,
        [activeNotebook]);
    const autoTitleStuckCount = useMemo(() =>
        (activeNotebook?.nodes ?? []).filter(n => isUntitledNode(n) && (n.autoTitleAttempts ?? 0) >= 2).length,
        [activeNotebook]);
    const trashedNotebooks = useMemo(() => notebooks.filter(nb => nb.isDeleted), [notebooks]);

    // Memoize notebook stats to prevent recalculation on every render
    const notebookStats = useMemo(() => {
        return activeNotebooks.map(nb => ({
            id: nb.id,
            totalCount: nb.nodes.length,
            untitledCount: nb.nodes.filter(n => !n.title?.trim() && (n.content || n.imageBase64)).length
        }));
    }, [notebooks]);

    // --- PERFORMANCE OPTIMIZATION: VIEWPORT MATH ENGINE ---
    const viewportBounds = useMemo(() => {
        const minX = -pan.x / scale;
        const minY = -pan.y / scale;
        const maxX = (window.innerWidth - pan.x) / scale;
        const maxY = (window.innerHeight - pan.y) / scale;
        const buffer = 300;

        return {
            minX: minX - buffer,
            minY: minY - buffer,
            maxX: maxX + buffer,
            maxY: maxY + buffer
        };
    }, [pan.x, pan.y, scale]);

    // DEBUG OVERLAY STATE
    const [debugLogs, setDebugLogs] = useState<string[]>([]);
    useEffect(() => {
        return onDebugLog((msg) => {
            setDebugLogs(prev => [...prev.slice(-19), msg]); // Keep last 20 logs
        });
    }, []);

    // Debounce: update debouncedSearchQuery 200ms after user stops typing
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 200);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Track the last meaningful query in a ref — save to history only when search closes
    const pendingSearchHistoryRef = useRef<string>('');
    useEffect(() => {
        if (debouncedSearchQuery.trim().length >= 2) {
            pendingSearchHistoryRef.current = debouncedSearchQuery.trim();
        } else {
            pendingSearchHistoryRef.current = '';
        }
    }, [debouncedSearchQuery]);

    useEffect(() => {
        if (!isSearchFocused && pendingSearchHistoryRef.current) {
            saveSearchHistory(pendingSearchHistoryRef.current);
            pendingSearchHistoryRef.current = '';
        }
    }, [isSearchFocused]);

    // Auto-search for keyword mode as user types (200ms debounce via debouncedSearchQuery)
    // "Notebook này" scope uses chip-based Enter-to-search — no auto-search on typing
    useEffect(() => {
        if (!isSearchFocused || canvasSearchMode !== 'keyword' || canvasSearchScope === 'current') return;
        const q = debouncedSearchQuery.trim();
        if (!q) {
            setCanvasSearchResults([]);
            setCanvasSearchedQuery('');
            setCanvasSearchError(null);
            return;
        }
        {
            setCanvasSearchLoading(true);
            setCanvasSearchError(null);
            setCanvasSearchedQuery(q);
            searchAcrossNotebooks(q)
                .then(results => setCanvasSearchResults(results ?? []))
                .catch((err: any) => setCanvasSearchError(err?.message || 'Tìm kiếm thất bại'))
                .finally(() => setCanvasSearchLoading(false));
        }
    }, [debouncedSearchQuery, isSearchFocused, canvasSearchMode, canvasSearchScope, activeNotebook, activeNotebookId]);

    // Pre-built search index — O(1) lookup per node, rebuilt only when notebooks change
    const searchIndex = useMemo(() => {
        const map = new Map<string, { titleLower: string; tagsLower: string; contentLower: string }>();
        for (const nb of notebooks) {
            for (const n of nb.nodes) {
                map.set(n.id, {
                    titleLower: (n.title ?? '').toLowerCase(),
                    tagsLower: (n.tags || []).join(' ').toLowerCase(),
                    contentLower: (n.content ?? '').toLowerCase(),
                });
            }
        }
        return map;
    }, [notebooks]);

    const visibleNodes = useMemo(() => {
        if (!activeNotebook) return [];
        const query = debouncedSearchQuery.toLowerCase().trim();
        return activeNotebook.nodes.filter(node => {
            if (query.length >= 2) {
                const idx = searchIndex.get(node.id);
                if (idx && (idx.titleLower.includes(query) || idx.tagsLower.includes(query) || idx.contentLower.includes(query))) return true;
            }
            const bounds = isFlying && flyCorridorBoundsRef.current
                ? flyCorridorBoundsRef.current
                : viewportBounds;
            return (
                node.position.x >= bounds.minX &&
                node.position.x <= bounds.maxX &&
                node.position.y >= bounds.minY &&
                node.position.y <= bounds.maxY
            );
        });
    }, [activeNotebook?.nodes, viewportBounds, debouncedSearchQuery, searchIndex, isFlying]);


    const visibleLinks = useMemo(() => {
        if (!activeNotebook || !activeNotebook.links) return [];
        const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
        return activeNotebook.links.filter(link =>
            visibleNodeIds.has(link.fromId) || visibleNodeIds.has(link.toId)
        );
    }, [activeNotebook?.links, visibleNodes]);

    // Provides `nodes` for each notebook to modals (ExpandedNoteModal, ReviewFeedPanel, etc.).
    // For partially-loaded notebooks, substitutes lightweight stubs from crossSearchIndex so
    // cross-notebook search and link creation still work after non-active nodes are evicted.
    const allNotebooksForModal = useMemo(() =>
        notebooks.map(nb => ({
            id: nb.id,
            title: nb.title,
            nodes: nb._isPartiallyLoaded
                ? (crossSearchIndex.current.get(nb.id) ?? []).map(stub => ({
                    id: stub.id, title: stub.title, content: stub.content, tags: stub.tags,
                    position: { x: 0, y: 0 }, imageBase64: null, imageMimeType: null,
                    analysis: null, isAnalyzing: false,
                } as MindMapNode))
                : nb.nodes,
        }))
    , [notebooks]);

    const handleNodeSelect = useCallback((id: string, e: React.MouseEvent | React.PointerEvent) => {
        e.stopPropagation();
        if (isQuizConfigMode) {
            setQuizSelectedNodeIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        } else if (pickParentForNodeId && id !== pickParentForNodeId) {
            // Assign clicked node as parent of the newly dropped image node
            const childId = pickParentForNodeId;
            setPickParentForNodeId(null);
            setNotebooks(nbs => nbs.map(nb => {
                if (nb.id !== activeNotebookId) return nb;
                const parentNode = nb.nodes.find(n => n.id === id);
                if (!parentNode) return nb;
                const siblings = nb.nodes.filter(n => n.parentId === id);
                const updatedNodes = nb.nodes.map(n => n.id === childId
                    ? { ...n, parentId: id, updatedAt: Date.now(), position: { x: parentNode.position.x + 320, y: parentNode.position.y + siblings.length * 200 } }
                    : n
                );
                const newLink: NodeLink = { id: generateId(), fromId: id, toId: childId, createdAt: Date.now() };
                const updatedLinks = [...(nb.links || []), newLink];
                const finalNodes = mapLayoutStyle !== 'free'
                    ? calculateLayout(updatedNodes, updatedLinks, mapLayoutStyle, nodeWidth)
                    : updatedNodes;
                return { ...nb, nodes: finalNodes, links: updatedLinks, updatedAt: Date.now() };
            }));
            setSelectedNodeId(childId);
        } else if (linkingSourceId) {
            setLinkDecision({
                sourceId: linkingSourceId,
                targetId: id,
                position: { x: e.clientX, y: e.clientY }
            });
            setLinkingSourceId(null);
        } else {
            setSelectedNodeId(id);
        }
    }, [isQuizConfigMode, pickParentForNodeId, activeNotebookId, mapLayoutStyle, nodeWidth, linkingSourceId]);

    const handleNodeClick = useCallback((id: string) => {
        if (!isQuizConfigMode && !linkingSourceId) {
            const node = notebooksRef.current.find(nb => nb.id === activeNotebookId)?.nodes.find(n => n.id === id);
            if (node) {
                const containerW = canvasRef.current?.clientWidth || window.innerWidth;
                const containerH = canvasRef.current?.clientHeight || window.innerHeight;
                setPan({
                    x: (containerW / 2) - (node.position.x * scale),
                    y: (containerH / 2) - (node.position.y * scale)
                });
            }
        }
    }, [activeNotebookId, isQuizConfigMode, linkingSourceId, scale]);

    // Track last selected node so image drop can attach to it even after deselection
    useEffect(() => {
        if (selectedNodeId) lastActiveNodeIdRef.current = selectedNodeId;
    }, [selectedNodeId]);

    const handleTagSearch = useCallback((tag: string) => {
        setActivePreviewTag(tag);
    }, []);

    const handleJumpToNode = useCallback((id: string, opts?: { instant?: boolean }) => {
        setSelectedNodeId(id);
        if (layoutMode === 'stream') {
            const element = document.getElementById(`node-card-inner-${id}`);
            if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            const node = notebooksRef.current.find(nb => nb.id === activeNotebookId)?.nodes.find(n => n.id === id);
            if (node) {
                if (opts?.instant) snapToCanvasNode(node.position.x, node.position.y);
                else flyToCanvasNode(node.position.x, node.position.y);
            }
        }
    }, [layoutMode, activeNotebookId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Instant jump — used by NodeCard crosslinks, parent button, arrow keys (no fly animation)
    const handleJumpToNodeInstant = useCallback((id: string) => {
        handleJumpToNode(id, { instant: true });
    }, [handleJumpToNode]);

    const saveSearchHistory = useCallback((query: string) => {
        if (!query.trim() || query.trim().length < 2) return;
        setSearchHistory(prev => {
            const next = [query.trim(), ...prev.filter(h => h !== query.trim())].slice(0, 8);
            localStorage.setItem('sb_search_history', JSON.stringify(next));
            return next;
        });
    }, []);

    const handleGlobalSearchSelect = useCallback((notebookId: string, nodeId: string) => {
        saveSearchHistory(searchQuery);
        posthog.capture('search_used', { query_length: searchQuery.trim().length });
        setSearchQuery('');
        setIsSearchFocused(false);
        if (notebookId === activeNotebookId) {
            handleJumpToNode(nodeId);
        } else {
            pendingJumpNodeRef.current = nodeId;
            setActiveNotebookId(notebookId);
        }
    }, [activeNotebookId, handleJumpToNode, saveSearchHistory, searchQuery]);

    useEffect(() => {
        if (!searchQuery.trim()) {
            // Chip-based local search (current-notebook scope) clears searchQuery right after
            // committing a chip and running the search — don't wipe the results it just found.
            if (searchChips.length === 0) {
                setCanvasSearchResults([]);
                setCanvasSearchedQuery('');
            }
            setCanvasSemanticResults([]);
        } else {
            // Clear stale semantic results whenever the query changes
            setCanvasSemanticResults([]);
        }
    }, [searchQuery, searchChips.length]);

    const handleCanvasSearch = useCallback(async (mode?: 'keyword' | 'semantic', scope?: 'all' | 'current', chips?: string[]) => {
        const activeMode = mode ?? canvasSearchMode;
        const activeScope = scope ?? canvasSearchScope;
        const activeChips = chips ?? searchChips;
        const q = searchQuery.trim();
        if (!q && activeChips.length === 0) return;

        if (activeMode === 'keyword') {
            if (activeScope === 'current') {
                // Local search on current notebook — instant, full content, works with unsaved changes
                // AND logic: all chips must match
                const allChips = activeChips.length > 0 ? activeChips : [q];
                if (allChips.every(c => c.length < 2)) return;
                setCanvasSearchError(null);
                try {
                type LocalResult = import('./services/geminiService').CrossNotebookSearchResult;
                const results: LocalResult[] = [];
                const nodes = activeNotebook?.nodes ?? [];
                for (const node of nodes) {
                  try {
                    const titleLower = (node.title ?? '').toLowerCase();
                    // tags is expected to be string[], but guard against any legacy/malformed
                    // shape (e.g. a stray string) so one bad node can't throw and silently
                    // abort the whole search — that used to leave the UI stuck on "no results yet".
                    const tagsLower = (Array.isArray(node.tags) ? node.tags.join(' ') : (node.tags ? String(node.tags) : '')).toLowerCase();
                    const contentLower = (node.content ?? '').toLowerCase();
                    // sourceExtractedText holds OCR/transcribed text from photographed notes, PDF
                    // snapshots, etc. — the actual readable content for image-based notes often
                    // lives here rather than in `content`, so it must be searched too.
                    const sourceLower = (node.sourceExtractedText ?? '').toLowerCase();
                    // All chips must match somewhere
                    const allMatch = allChips.every(chip => {
                        const cl = chip.toLowerCase();
                        return titleLower.includes(cl) || tagsLower.includes(cl) || contentLower.includes(cl) || sourceLower.includes(cl);
                    });
                    if (!allMatch) continue;
                    // Excerpt from first chip that matches content, falling back to source text
                    let excerpt = '';
                    const contentMatchChip = allChips.find(chip => contentLower.includes(chip.toLowerCase()));
                    const sourceMatchChip = allChips.find(chip => sourceLower.includes(chip.toLowerCase()));
                    if (contentMatchChip) {
                        const raw = node.content ?? '';
                        const idx = contentLower.indexOf(contentMatchChip.toLowerCase());
                        const start = Math.max(0, idx - 60);
                        const end = Math.min(raw.length, idx + contentMatchChip.length + 120);
                        excerpt = (start > 0 ? '…' : '') +
                            raw.slice(start, end).replace(/\*\*|__|\*|_|~~|`{1,3}|#{1,6}\s?|\$\$?|<[^>]*>/g, '').trim() +
                            (end < raw.length ? '…' : '');
                    } else if (sourceMatchChip) {
                        const raw = node.sourceExtractedText ?? '';
                        const idx = sourceLower.indexOf(sourceMatchChip.toLowerCase());
                        const start = Math.max(0, idx - 60);
                        const end = Math.min(raw.length, idx + sourceMatchChip.length + 120);
                        excerpt = (start > 0 ? '…' : '') +
                            raw.slice(start, end).replace(/\*\*|__|\*|_|~~|`{1,3}|#{1,6}\s?|\$\$?|<[^>]*>/g, '').trim() +
                            (end < raw.length ? '…' : '');
                    }
                    const inTitle = allChips.some(c => titleLower.includes(c.toLowerCase()));
                    const inTags = allChips.some(c => tagsLower.includes(c.toLowerCase()));
                    results.push({
                        notebookId: activeNotebookId ?? '',
                        notebookTitle: activeNotebook?.title ?? '',
                        nodeId: node.id,
                        nodeTitle: node.title ?? '',
                        excerpt,
                        score: (inTitle ? 4 : 0) + (inTags ? 2 : 0) + 1,
                    });
                  } catch (nodeErr) {
                    console.error('Canvas search: skipping node due to error', node.id, nodeErr);
                  }
                }
                results.sort((a, b) => b.score - a.score);
                setCanvasSearchResults(results.slice(0, 50));
                setCanvasSearchedQuery(allChips.join(' + '));
                } catch (searchErr) {
                    // Never leave the UI stuck on "press Enter to search" — surface a real error instead.
                    console.error('Canvas search failed', searchErr);
                    setCanvasSearchResults([]);
                    setCanvasSearchedQuery(allChips.join(' + '));
                    setCanvasSearchError(language === 'vietnamese' ? 'Lỗi khi tìm kiếm — thử lại' : 'Search failed — try again');
                }
            } else {
                // Cloud search across all notebooks (other notebooks are lazy-loaded, need Firestore)
                setCanvasSearchLoading(true);
                setCanvasSearchError(null);
                setCanvasSearchedQuery(q);
                try {
                    const results = await searchAcrossNotebooks(q);
                    setCanvasSearchResults(results ?? []);
                } catch (err: any) {
                    setCanvasSearchError(err?.message || 'Tìm kiếm thất bại. Vui lòng thử lại.');
                } finally {
                    setCanvasSearchLoading(false);
                }
            }
        } else {
            // Semantic always uses cloud (needs embeddings regardless of scope)
            setCanvasSearchLoading(true);
            setCanvasSearchError(null);
            setCanvasSearchedQuery(q);
            try {
                const results = await semanticSearchNotes(q);
                setCanvasSemanticResults(results ?? []);
            } catch (err: any) {
                setCanvasSearchError(err?.message || 'Tìm kiếm thất bại. Vui lòng thử lại.');
            } finally {
                setCanvasSearchLoading(false);
            }
        }
    }, [searchQuery, canvasSearchMode, canvasSearchScope, activeNotebook, activeNotebookId]);

    // Debounced embedding generation: fires 8s after last content/title change for a node
    const updateNode = useCallback((id: string, data: Partial<MindMapNode>) => {
        if (isEffectiveReadOnly && Object.keys(data).length === 1 && data.isExpanded !== undefined) { } else if (isEffectiveReadOnly) { return; }
        if (!activeNotebookId) return;
        const nodeData = (data.content !== undefined || data.title !== undefined || data.tags !== undefined || data.imageBase64 !== undefined)
            ? { ...data, updatedAt: Date.now() }
            : data;
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;
            const shouldRelayout = data.isExpanded !== undefined;
            const updatedNodes = nb.nodes.map(node => node.id === id ? { ...node, ...nodeData } : node);
            let finalNodes = updatedNodes;
            if (shouldRelayout && mapLayoutStyle !== 'free') {
                finalNodes = calculateLayout(updatedNodes, nb.links || [], mapLayoutStyle, nodeWidth);
            }
            return { ...nb, nodes: finalNodes, updatedAt: Date.now() };
        }));
    }, [activeNotebookId, mapLayoutStyle, nodeWidth, isEffectiveReadOnly]);

    const handleUpdateLink = useCallback((linkId: string, color: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;
            return { ...nb, links: nb.links?.map(l => l.id === linkId ? { ...l, color } : l), updatedAt: Date.now() };
        }));
    }, [activeNotebookId, isEffectiveReadOnly]);

    const handleUnlinkNode = useCallback((nodeId: string, targetId: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;
            const newLinks = (nb.links || []).filter(l => !((l.fromId === nodeId && l.toId === targetId) || (l.fromId === targetId && l.toId === nodeId)));
            return { ...nb, links: newLinks, updatedAt: Date.now() };
        }));
    }, [activeNotebookId, isEffectiveReadOnly]);

    const analyzeAbortRef = useRef<Record<string, boolean>>({});

    const handleCancelAnalyze = useCallback((id: string) => {
        analyzeAbortRef.current[id] = true;
        updateNode(id, { isAnalyzing: false });
    }, []);

    const handleAnalyzeNode = useCallback(async (id: string, mode: 'simple' | 'detailed' | 'pinpoint' = 'simple') => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const node = nb?.nodes.find(n => n.id === id);
        if (!node) return;

        if (node.isAnalyzing) return;

        analyzeAbortRef.current[id] = false;
        updateNode(id, { isAnalyzing: true });

        // Pinpoint mode: find exact error location in user text
        if (mode === 'pinpoint') {
            try {
                const existingFeedback = node.analysis?.feedback || '';
                const result = await pinpointError(node.content, node.imageBase64, node.imageMimeType, existingFeedback, language);
                updateNode(id, {
                    isAnalyzing: false,
                    analysis: node.analysis ? { ...node.analysis, errorPoints: result.errorPoints } : undefined,
                });
            } catch (error: any) {
                updateNode(id, { isAnalyzing: false });
                if (error.code === 'resource-exhausted' || error.message?.includes('Hết lượt')) {
                    showToast(language === 'vietnamese' ? 'Đã hết lượt hôm nay!' : 'Daily limit reached!', 'error');
                }
            }
            return;
        }

        try {
            const result = await analyzeNodeUnderstanding(node.content, node.imageBase64, node.imageMimeType, language, node.attachments);

            if (analyzeAbortRef.current[id]) { analyzeAbortRef.current[id] = false; return; }

            posthog.capture('ai_check_used', { mode });
            updateNode(id, { analysis: result, isAnalyzing: false, lastAICheckAt: Date.now(), aiCheckVersion: 'v2', aiCheckContentHash: simpleHash(node.content || '') });

            // After AI Check, automatically score for Threads (non-blocking).
            if (nb) {
                scoreNoteForThreads({
                    notebookId: nb.id,
                    nodeId: id,
                    title: node.title || '',
                    content: node.content || '',
                    language,
                }).then(sr => {
                    if (sr?.score != null) {
                        updateNode(id, {
                            threadsScore: Math.max(0, Math.min(100, Number(sr.score))),
                            threadsAssessedAt: Date.now(),
                        });
                    }
                }).catch(() => { /* silent — postworthy score is non-critical */ });
            }

            // Auto-generate initial review questions after AI Check (silently, non-blocking).
            // Eligibility: no existing questions, score good enough.
            const autoGenEligible =
                !node.reviewQuestionCount &&
                result.score >= 60;
            if (user && autoGenEligible && nb) {
                updateNode(id, { reviewQuestionStatus: 'generating' });
                generateReviewQuestions(
                    id, nb.id, node.title, nb.title,
                    node.content, node.imageBase64 ?? null, node.imageMimeType ?? null,
                    language, 4, 'initial_ai_check',
                    node.content,    // userExplanation = the note content
                    result.feedback  // aiFeedback
                ).then(res => {
                    if (res.storedCount > 0) {
                        updateNode(id, { reviewQuestionCount: res.storedCount, reviewQuestionStatus: 'ready' });
                    } else {
                        updateNode(id, { reviewQuestionStatus: 'none' });
                    }
                }).catch(() => {
                    updateNode(id, { reviewQuestionStatus: 'none' });
                });
            }
        } catch (error: any) {
            console.error(`❌ Analysis failed - Mode: ${mode}`, error);
            updateNode(id, { isAnalyzing: false });

            // 🚨 QUOTA HANDLER (M3)
            // Only match our app's quota error (code='resource-exhausted'), NOT Gemini API rate limits
            // Gemini rate limits come as code='internal' with 'resource-exhausted' in message — that's different
            if (error.code === 'resource-exhausted' || error.message?.includes("Hết lượt")) {
                showToast(language === 'vietnamese' ? "Đã hết lượt hôm nay! Mở cài đặt để nâng cấp..." : "Daily limit reached! Opening settings to upgrade...", 'error');
                setTimeout(() => setSettingsOpen(true), 2000);
            } else if (error.message?.includes('resource-exhausted')) {
                // Gemini API rate limit — transient, just show a retry message
                showToast(language === 'vietnamese' ? "API đang bận, thử lại sau vài giây..." : "API busy, please retry in a few seconds...", 'error');
            }
        }
    }, [activeNotebookId, language, updateNode, isEffectiveReadOnly, user]);

    const handleGenerateTitle = useCallback(async (id: string): Promise<string> => {
        if (isEffectiveReadOnly || !activeNotebookId) return "";
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const node = nb?.nodes.find(n => n.id === id);
        if (!node) return "";
        return await generateTitleForNode(node.content, node.imageBase64, node.imageMimeType, language);
    }, [activeNotebookId, language, isEffectiveReadOnly]);

    const handleAutoTitleNode = useCallback(async (id: string): Promise<void> => {
        const title = await handleGenerateTitle(id);
        if (title) { updateNode(id, { title }); posthog.capture('ai_generate_title'); }
    }, [handleGenerateTitle, updateNode]);

    const handleAutoTitleBranch = useCallback(async (rootId: string): Promise<void> => {
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        if (!nb) return;
        // BFS to collect all descendants of rootId (not the root itself)
        const descendants: string[] = [];
        const queue = [rootId];
        while (queue.length > 0) {
            const current = queue.shift()!;
            nb.nodes.forEach(n => { if (n.parentId === current) { descendants.push(n.id); queue.push(n.id); } });
        }
        // Only process nodes that don't already have a title
        const targets = descendants.filter(id => {
            const n = nb.nodes.find(node => node.id === id);
            return n && !n.title?.trim();
        });
        if (targets.length === 0) {
            showToast(language === 'vietnamese' ? 'Tất cả ghi chú con đã có tiêu đề!' : 'All child notes already have titles!', 'info');
            return;
        }
        const confirmed = window.confirm(
            language === 'vietnamese'
                ? `Tạo tiêu đề cho ${targets.length} ghi chú chưa có tiêu đề? Quá trình này có thể mất vài phút.`
                : `Generate titles for ${targets.length} untitled notes? This may take a few minutes.`
        );
        if (!confirmed) return;
        autoTitleCancelRef.current = false;
        setIsAutoTitling(true);
        setAutoTitleProgress({ current: 0, total: targets.length });
        let completed = 0;
        for (const id of targets) {
            if (autoTitleCancelRef.current) break;
            try {
                const title = await handleGenerateTitle(id);
                if (title) updateNode(id, { title });
                completed++;
                setAutoTitleProgress({ current: completed, total: targets.length });
                await new Promise(r => setTimeout(r, 500));
            } catch (e) {
                console.error(`Failed to generate title for ${id}:`, e);
            }
        }
        setIsAutoTitling(false);
        const wasCancelled = autoTitleCancelRef.current;
        showToast(
            wasCancelled
                ? (language === 'vietnamese' ? `Đã hủy — đã tạo ${completed}/${targets.length} tiêu đề` : `Cancelled — generated ${completed}/${targets.length} titles`)
                : (language === 'vietnamese' ? `✅ Đã tạo ${completed} tiêu đề!` : `✅ Generated ${completed} titles!`),
            wasCancelled ? 'info' : 'success'
        );
    }, [activeNotebookId, language, handleGenerateTitle, updateNode]);

    const handleBulkGenerateTitles = useCallback(async (notebookId: string) => {
        if (isEffectiveReadOnly) return;

        const notebook = await ensureNotebookLoaded(notebookId);
        if (!notebook) {
            showToast(language === 'vietnamese' ? 'Không thể tải ghi chú' : 'Could not load notes', 'error');
            return;
        }

        // Filter untitled notes
        const untitledNotes = notebook.nodes.filter(
            n => !n.title?.trim() && (n.content || n.imageBase64)
        );

        if (untitledNotes.length === 0) {
            setToast({ message: language === 'vietnamese' ? 'Tất cả notes đã có title!' : 'All notes already have titles!', type: 'info' });
            setTimeout(() => setToast(null), 3000);
            return;
        }

        // Confirmation
        const confirmed = window.confirm(
            language === 'vietnamese'
                ? `Tạo title cho ${untitledNotes.length} notes? Quá trình này có thể mất vài phút.`
                : `Generate titles for ${untitledNotes.length} notes? This may take a few moments.`
        );
        if (!confirmed) return;

        autoTitleCancelRef.current = false;
        setIsAutoTitling(true);
        let completed = 0;
        const total = untitledNotes.length;
        setAutoTitleProgress({ current: 0, total });

        for (const node of untitledNotes) {
            if (autoTitleCancelRef.current) break;
            try {
                const newTitle = await generateTitleForNode(
                    node.content,
                    node.imageBase64,
                    node.imageMimeType,
                    language
                );
                if (newTitle) updateNode(node.id, { title: newTitle });
                completed++;
                setAutoTitleProgress({ current: completed, total });
                await new Promise(r => setTimeout(r, 500));
            } catch (error) {
                console.error(`Failed to generate title for node ${node.id}:`, error);
            }
        }

        setIsAutoTitling(false);
        const wasCancelled = autoTitleCancelRef.current;
        setToast({
            message: wasCancelled
                ? (language === 'vietnamese' ? `Đã hủy — đã tạo ${completed}/${total} tiêu đề` : `Cancelled — generated ${completed}/${total} titles`)
                : (language === 'vietnamese' ? `✅ Đã tạo ${completed} tiêu đề!` : `✅ Generated ${completed} titles!`),
            type: wasCancelled ? 'info' : 'success'
        });
        setTimeout(() => setToast(null), 3000);
    }, [isEffectiveReadOnly, language, updateNode]);

    const linksByNodeId = useMemo(() => {
        const map: Record<string, any[]> = {};
        if (!activeNotebook?.links) return map;
        activeNotebook.links.forEach(l => {
            const fromId = l.fromId; const toId = l.toId;

            if (l.toNotebookId) {
                // Cross-notebook link — one-directional, shown only on the source note
                const targetNb = notebooks.find(n => n.id === l.toNotebookId);
                const targetNode = targetNb?.nodes.find(n => n.id === toId);
                const title = targetNode?.title || l.toTitle || 'Untitled';
                const notebookTitle = targetNb?.title || l.toNotebookTitle || '';
                if (!map[fromId]) map[fromId] = [];
                map[fromId].push({ id: toId, title, linkId: l.id, linkColor: l.color, relationType: 'peer', notebookId: l.toNotebookId, notebookTitle });
            } else {
                // Same-notebook link — bidirectional
                const fromNode = activeNotebook.nodes.find(n => n.id === fromId);
                const toNode = activeNotebook.nodes.find(n => n.id === toId);
                if (!map[fromId]) map[fromId] = []; if (!map[toId]) map[toId] = [];
                const getRelationType = (sourceNode: MindMapNode | undefined, targetNode: MindMapNode | undefined) => {
                    if (!sourceNode || !targetNode) return 'peer';
                    if (targetNode.id === sourceNode.parentId) return 'parent';
                    if (targetNode.parentId === sourceNode.id) return 'child';
                    return 'peer';
                };
                if (toNode) map[fromId].push({ id: toNode.id, title: toNode.title, linkId: l.id, linkColor: l.color, relationType: getRelationType(fromNode, toNode) });
                if (fromNode) map[toId].push({ id: fromNode.id, title: fromNode.title, linkId: l.id, linkColor: l.color, relationType: getRelationType(toNode, fromNode) });
            }
        });
        return map;
    }, [activeNotebook?.links, activeNotebook?.nodes, notebooks]);

    // After hopping A → B → C through links or the outline and closing, the canvas still shows A.
    // On close: bring the last note into view, and offer a "reopen" pill until another note opens.
    const expandOriginRef = useRef<string | null>(null);
    const prevExpandedRef = useRef<string | null>(null);
    const [lastClosedNote, setLastClosedNote] = useState<{ id: string; notebookId: string; title: string } | null>(null);
    useEffect(() => {
        if (expandedNodeId && !prevExpandedRef.current) expandOriginRef.current = expandedNodeId;
        if (expandedNodeId) setLastClosedNote(null);
        prevExpandedRef.current = expandedNodeId;
    }, [expandedNodeId]);

    // Navigate to a cross-notebook link once the target notebook finishes loading
    useEffect(() => {
        if (!pendingCrossNav) return;
        if (activeNotebookId !== pendingCrossNav.notebookId) return;
        const found = activeNotebook?.nodes.find(n => n.id === pendingCrossNav.nodeId);
        if (found) {
            setExpandedNodeId(pendingCrossNav.nodeId);
            setSelectedNodeId(pendingCrossNav.nodeId);
            setPendingCrossNav(null);
        }
    }, [activeNotebook?.nodes, pendingCrossNav, activeNotebookId]);

    const tagPreviewNotes = useMemo(() => {
        if (!activePreviewTag || !activeNotebook) return [];
        const needle = activePreviewTag.replace(/^#/, '').toLowerCase();
        return activeNotebook.nodes.filter(n =>
            (n.tags || []).some(t => t.replace(/^#/, '').toLowerCase() === needle)
        );
    }, [activePreviewTag, activeNotebook]);

    const getLodLevel = useCallback((node: MindMapNode) => {
        if (layoutMode === 'stream') return 'full';
        const query = searchQuery.toLowerCase().trim();
        const isMatch = query !== "" && (
            (node.title ?? '').toLowerCase().includes(query) ||
            (node.content ?? '').toLowerCase().includes(query) ||
            (node.tags || []).some(tag => tag.toLowerCase().includes(query))
        );
        if (isMatch) return 'full';
        if (flyScaleRef.current !== null) return 'skeleton';
        if (scale >= 0.4) return 'full';
        return 'skeleton';
    }, [scale, searchQuery, layoutMode]);

    // Sync per-notebook AI settings into geminiService when active notebook changes
    useEffect(() => {
        if (!activeNotebookId) {
            setActiveNotebookAISettings(null);
            return;
        }
        const nb = notebooks.find(n => n.id === activeNotebookId);
        setActiveNotebookAISettings(nb?.aiSettings ?? null);
    }, [activeNotebookId, notebooks]);

    // Sync per-notebook patterns flag into markdownUtils when active notebook changes
    useEffect(() => {
        const nb = notebooks.find(n => n.id === activeNotebookId);
        setNotebookPatternsEnabled(nb?.patternsEnabled !== false);
    }, [activeNotebookId, notebooks]);

    const handleNotebookAISettingsChange = useCallback((notebookId: string, settings: AIBehaviorSettings | null) => {
        setNotebooks(prev => prev.map(nb => {
            if (nb.id !== notebookId) return nb;
            const updated = { ...nb };
            if (settings === null) {
                delete updated.aiSettings;
            } else {
                updated.aiSettings = settings;
            }
            return updated;
        }));
        if (user) {
            saveNotebookAISettings(user.id, notebookId, settings).catch(err => console.error('Failed to save notebook AI settings', err));
        }
        if (notebookId === activeNotebookId) {
            setActiveNotebookAISettings(settings);
        }
    }, [user, activeNotebookId]);

    const handleToggleNotebookPatterns = useCallback((notebookId: string) => {
        const nb = notebooks.find(n => n.id === notebookId);
        if (!nb) return;
        const newEnabled = nb.patternsEnabled !== false ? false : true;
        setNotebooks(prev => prev.map(n => n.id === notebookId ? { ...n, patternsEnabled: newEnabled } : n));
        if (user) {
            saveNotebookPatternsEnabled(user.id, notebookId, newEnabled).catch(console.error);
        }
        if (notebookId === activeNotebookId) {
            setNotebookPatternsEnabled(newEnabled);
        }
    }, [notebooks, user, activeNotebookId]);

    useEffect(() => {
        const handleMove = (e: MouseEvent | TouchEvent) => {
            if (!isResizingPanel && !isResizingOutline && !isResizingReviewFeed) return;
            let clientX: number;
            if ('touches' in e) { clientX = e.touches[0].clientX; } else { clientX = (e as MouseEvent).clientX; }
            if (isResizingPanel) {
                const newWidth = Math.max(300, Math.min(window.innerWidth - clientX, window.innerWidth * 0.8));
                mediaPanelWidthRef.current = newWidth;
                // Direct DOM — zero React re-renders during drag
                if (mediaPanelElRef.current) mediaPanelElRef.current.style.width = `${newWidth}px`;
                if (mediaToolbarRef.current) mediaToolbarRef.current.style.right = `${newWidth + 24}px`;
                if (mediaActionBarRef.current) mediaActionBarRef.current.style.right = `${newWidth}px`;
            }
            if (isResizingOutline) {
                const newWidth = Math.max(180, Math.min(window.innerWidth - clientX, 600));
                outlinePanelWidthRef.current = newWidth;
                if (outlinePanelElRef.current) outlinePanelElRef.current.style.width = `${newWidth}px`;
            }
            if (isResizingReviewFeed) {
                const newWidth = window.innerWidth - clientX;
                setReviewFeedWidth(Math.max(280, Math.min(newWidth, 600)));
            }
        };
        const handleEnd = () => {
            if (isResizingPanel) { setMediaPanelWidth(mediaPanelWidthRef.current); setIsResizingPanel(false); }
            if (isResizingOutline) { setOutlinePanelWidth(outlinePanelWidthRef.current); setIsResizingOutline(false); }
            if (isResizingReviewFeed) { setIsResizingReviewFeed(false); }
            if (isResizingPanel || isResizingOutline || isResizingReviewFeed) {
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };
        if (isResizingPanel || isResizingOutline || isResizingReviewFeed) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', handleEnd);
            window.addEventListener('touchmove', handleMove);
            window.addEventListener('touchend', handleEnd);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [isResizingPanel, isResizingOutline, isResizingReviewFeed]);

    // Starts a new record/export/upload flow. Screen capture is exclusive, so a second flow can only
    // start while no recording is in progress; flows that are already processing keep running in
    // the background as corner pills.
    // Parts marked while recording, being rendered as their own videos. Each becomes a background
    // upload flow (a "Ready to upload" pill) when its file is ready.
    const [pendingClips, setPendingClips] = useState<PendingClip[]>([]);
    const openUploadSession = (nodeId: string, opts?: { notebookId?: string; initialFile?: File; sourceJobId?: string; initialTitle?: string; clipOfJobId?: string; background?: boolean }) => {
        // A background flow only adds a pill, so it never interrupts a recording in progress.
        if (!opts?.background && (recordingSids.length > 0 || isRecording)) {
            showToast(language === 'vietnamese' ? 'Đang quay một video khác — dừng quay trước khi bắt đầu video mới.' : 'Another video is recording — stop it before starting a new one.', 'info');
            return;
        }
        const nbId = opts?.notebookId ?? activeNotebookId;
        const nb = notebooks.find(n => n.id === nbId);
        const usedSlots = uploadSessions.filter(x => x.nodeId === nodeId && x.notebookId === nbId).map(x => x.slot);
        let slot = 0;
        while (usedSlots.includes(slot)) slot++;
        const sid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        setUploadSessions(prev => {
            // Slots are re-read here: several clips can finish in the same tick.
            const taken = prev.filter(x => x.nodeId === nodeId && x.notebookId === nbId).map(x => x.slot);
            let free = slot;
            while (taken.includes(free)) free++;
            return [...prev, {
                sid, nodeId, notebookId: nbId || '', slot: free,
                initialFile: opts?.initialFile, sourceJobId: opts?.sourceJobId,
                initialTitle: opts?.initialTitle, clipOfJobId: opts?.clipOfJobId,
                nodeSnapshot: nb?.nodes.find(n => n.id === nodeId), notebookSnapshot: nb,
            }];
        });
        if (!opts?.background) setForegroundSid(sid);
    };
    const requestUploadForeground = (sid: string) => {
        if (recordingSids.length > 0 && !recordingSids.includes(sid)) {
            showToast(language === 'vietnamese' ? 'Đang quay một video khác — dừng quay trước khi mở phiên này.' : 'Another video is recording — stop it before opening this one.', 'info');
            return;
        }
        setForegroundSid(sid);
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    // General-purpose structural history — used by move/reorder/drag/layout/paste/import
    // call sites. Captures ONLY structural fields (§5.2), never full node payloads, so
    // undoing one of these can never revert a content edit made after the snapshot.
    const pushHistoryEntry = useCallback((entry: HistoryEntry) => {
        setHistoryPast(prev => {
            const next = [...prev, entry];
            return next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
        });
        setHistoryFuture([]);
    }, []);

    const saveHistoryImmediate = () => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const activeNodes = notebooksRef.current.find(nb => nb.id === activeNotebookId)?.nodes || [];
        pushHistoryEntry({ kind: 'structural', snapshot: toStructuralProjection(activeNodes) });
    };

    const captureSnapshot = useCallback(() => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const activeNodes = notebooksRef.current.find(nb => nb.id === activeNotebookId)?.nodes || [];
        tempSnapshotRef.current = activeNodes;
    }, [activeNotebookId, isEffectiveReadOnly]);

    const commitSnapshot = useCallback(() => {
        if (isEffectiveReadOnly || !tempSnapshotRef.current || !activeNotebookId) return;
        const currentNodes = notebooksRef.current.find(nb => nb.id === activeNotebookId)?.nodes || [];
        const before = toStructuralProjection(tempSnapshotRef.current);
        const after = toStructuralProjection(currentNodes);
        if (JSON.stringify(before) !== JSON.stringify(after)) {
            pushHistoryEntry({ kind: 'structural', snapshot: before });
        }
        tempSnapshotRef.current = null;
    }, [isEffectiveReadOnly, activeNotebookId, pushHistoryEntry]);

    // Register/unregister an explicit-deletion id with the pending-deletions tracker
    // (App.tsx's Firestore-sync layer, ~line 3266) so that restoring a node via undo
    // stops it from being deleted server-side, and re-deleting via redo re-registers it.
    const setPendingDeletion = useCallback((notebookId: string, nodeId: string, pending: boolean) => {
        const set = pendingDeletionsRef.current.get(notebookId) ?? new Set<string>();
        if (pending) set.add(nodeId); else set.delete(nodeId);
        pendingDeletionsRef.current.set(notebookId, set);
    }, []);

    const undo = () => {
        if (isEffectiveReadOnly || historyPast.length === 0 || !activeNotebookId) return;
        const entry = historyPast[historyPast.length - 1];
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        if (!nb) return;

        const result = applyUndo({ nodes: nb.nodes, links: nb.links || [] }, entry);

        if (result.status !== 'ok') {
            setHistoryPast(prev => prev.slice(0, -1)); // drop the entry either way — it can't be retried meaningfully
            if (result.status === 'has-children') {
                // §5.2.1 decision (a): refuse rather than silently reparent children the
                // user never asked to move.
                showToast(language === 'vietnamese'
                    ? "Không thể hoàn tác: note này hiện có note con mới hơn"
                    : "Can't undo creation: this note now contains newer child nodes", 'error');
            }
            return;
        }

        setHistoryPast(prev => prev.slice(0, -1));
        setHistoryFuture(prev => [result.redoEntry, ...prev]);
        setNotebooks(nbs => nbs.map(n2 => n2.id === activeNotebookId ? { ...n2, nodes: result.nodes, links: result.links } : n2));
        for (const c of result.pendingDeletionChanges) setPendingDeletion(activeNotebookId, c.id, c.pending);
        if (result.pendingDeletionChanges.length > 0) triggerSaveAfterDelete();
    };

    const redo = () => {
        if (isEffectiveReadOnly || historyFuture.length === 0 || !activeNotebookId) return;
        const entry = historyFuture[0];
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        if (!nb) return;

        const result = applyRedo({ nodes: nb.nodes, links: nb.links || [] }, entry);
        setHistoryFuture(prev => prev.slice(1));

        if (result.status !== 'ok') {
            if (result.status === 'stale') {
                showToast(language === 'vietnamese'
                    ? "Không thể làm lại: note đã được chỉnh sửa sau khi khôi phục"
                    : "Can't redo: this note changed after being restored", 'error');
            }
            return;
        }

        setHistoryPast(prev => [...prev, result.undoEntry]);
        setNotebooks(nbs => nbs.map(n2 => n2.id === activeNotebookId ? { ...n2, nodes: result.nodes, links: result.links } : n2));
        for (const c of result.pendingDeletionChanges) setPendingDeletion(activeNotebookId, c.id, c.pending);
        if (result.pendingDeletionChanges.length > 0) triggerSaveAfterDelete();
    };

    // Friendly labels for the arbiter's toast (§5.6) — best-effort; falls back to a
    // generic phrase for plain structural moves, which don't name a specific node.
    const describeHistoryEntry = (entry: HistoryEntry | undefined, forRedo: boolean): string | null => {
        if (!entry) return null;
        const vi = language === 'vietnamese';
        switch (entry.kind) {
            case 'structural':
                return vi ? (forRedo ? 'Đã làm lại di chuyển' : 'Đã hoàn tác di chuyển') : (forRedo ? 'Redid move' : 'Undid move');
            case 'create':
                return vi ? 'Đã hoàn tác tạo note' : 'Undid creating a note';
            case 'delete-single':
            case 'delete-subtree': {
                const title = entry.kind === 'delete-single' ? entry.deletedNode.title : entry.deletedNodes[0]?.title;
                const name = title?.trim() ? `"${title.trim()}"` : (vi ? 'note' : 'note');
                return vi ? `Đã hoàn tác xóa ${name}` : `Undid deleting ${name}`;
            }
        }
    };
    const describeRedoEntry = (entry: RedoEntry | undefined): string | null => {
        if (!entry) return null;
        const vi = language === 'vietnamese';
        switch (entry.kind) {
            case 'structural':
                return vi ? 'Đã làm lại di chuyển' : 'Redid move';
            case 'recreate':
                return vi ? 'Đã làm lại tạo note' : 'Redid creating a note';
            case 'redelete-single':
            case 'redelete-subtree': {
                const title = entry.kind === 'redelete-single' ? entry.deletedNode.title : entry.deletedNodes[0]?.title;
                const name = title?.trim() ? `"${title.trim()}"` : 'note';
                return vi ? `Đã làm lại xóa ${name}` : `Redid deleting ${name}`;
            }
        }
    };

    // Same wording as describeHistoryEntry/describeRedoEntry, phrased as a question for
    // the confirm dialog below instead of a past-tense toast.
    const describeUndoQuestion = (entry: HistoryEntry | undefined): string | null => {
        if (!entry) return null;
        const vi = language === 'vietnamese';
        switch (entry.kind) {
            case 'structural':
                return vi ? 'Hoàn tác lại hành động di chuyển note?' : 'Undo the last move?';
            case 'create':
                return vi ? 'Hoàn tác lại hành động tạo note?' : 'Undo creating this note?';
            case 'delete-single':
            case 'delete-subtree': {
                const title = entry.kind === 'delete-single' ? entry.deletedNode.title : entry.deletedNodes[0]?.title;
                const name = title?.trim() ? `"${title.trim()}"` : (vi ? 'note này' : 'this note');
                return vi ? `Hoàn tác lại hành động xóa ${name}?` : `Undo deleting ${name}?`;
            }
        }
    };
    const describeRedoQuestion = (entry: RedoEntry | undefined): string | null => {
        if (!entry) return null;
        const vi = language === 'vietnamese';
        switch (entry.kind) {
            case 'structural':
                return vi ? 'Làm lại hành động di chuyển note?' : 'Redo the last move?';
            case 'recreate':
                return vi ? 'Làm lại hành động tạo note?' : 'Redo creating this note?';
            case 'redelete-single':
            case 'redelete-subtree': {
                const title = entry.kind === 'redelete-single' ? entry.deletedNode.title : entry.deletedNodes[0]?.title;
                const name = title?.trim() ? `"${title.trim()}"` : 'this note';
                return vi ? `Làm lại hành động xóa ${name}?` : `Redo deleting ${name}?`;
            }
        }
    };

    // Ctrl+Z/Ctrl+Shift+Z at the canvas level (i.e. outside an expanded note's own text
    // editor, which stays confirmation-free — see ExpandedNoteModal's own undo scope)
    // always asks for confirmation before applying, naming the exact action about to be
    // reversed. Typing/drawing undo is frequent and low-stakes; canvas-level undo covers
    // create/delete/move of whole notes and is rare and high-stakes, so the two get
    // different friction on purpose.
    const [pendingUndoRedo, setPendingUndoRedo] = useState<'undo' | 'redo' | null>(null);

    const requestUndo = () => {
        if (isEffectiveReadOnly || historyPast.length === 0 || !activeNotebookId) return;
        setPendingUndoRedo('undo');
    };
    const requestRedo = () => {
        if (isEffectiveReadOnly || historyFuture.length === 0 || !activeNotebookId) return;
        setPendingUndoRedo('redo');
    };
    const confirmPendingUndoRedo = () => {
        if (pendingUndoRedo === 'undo') {
            const label = describeHistoryEntry(historyPast[historyPast.length - 1], false);
            undo();
            if (label) showToast(label, 'info');
        } else if (pendingUndoRedo === 'redo') {
            const label = describeRedoEntry(historyFuture[0]);
            redo();
            if (label) showToast(label, 'info');
        }
        setPendingUndoRedo(null);
    };

    // ── Register the notebook-level undo scope with the centralized arbiter (§5.4) ──
    // Registers requestUndo/requestRedo (which open the confirm dialog above), not the
    // real undo/redo, so canvas-level Ctrl+Z always confirms first. Plain function
    // values (recreated every render, not useCallback'd), so ref-forward through them
    // instead of registering the scope once with a stale closure. Registered once for
    // the app's lifetime; it's the always-present fallback (lowest priority — wins only
    // when nothing else, like the drawing overlay or an expanded note, currently claims
    // undo). getUndoLabel/getRedoLabel return null here — the arbiter's automatic toast
    // is skipped in favor of the confirm-dialog flow; confirmPendingUndoRedo shows its
    // own toast only once the user actually confirms.
    const undoRef = useRef(requestUndo); undoRef.current = requestUndo;
    const redoRef = useRef(requestRedo); redoRef.current = requestRedo;

    useEffect(() => {
        setUndoArbiterToast((message) => showToast(message, 'info'));
        const handle = registerUndoScope({
            id: 'notebook',
            allowNativeEditableTargets: false,
            undo: () => undoRef.current(),
            redo: () => redoRef.current(),
            getUndoLabel: () => null,
            getRedoLabel: () => null,
        });
        handle.activate();
        return () => handle.unregister();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clear undo/redo history when switching notebooks — history is per-notebook
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setHistoryPast([]);
        setHistoryFuture([]);
        tempSnapshotRef.current = null;
    }, [activeNotebookId]);


    const measureAndRelayout = useCallback((delay = 300) => {
        if (mapLayoutStyle === 'free') return;
        if (layoutRecalcTimerRef.current) clearTimeout(layoutRecalcTimerRef.current);
        layoutRecalcTimerRef.current = setTimeout(() => {
            try {
                const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
                if (!nb) return;
                // Measure actual rendered heights from DOM
                const els = document.querySelectorAll<HTMLElement>('[data-testid="node-card"][data-node-id]');
                const measuredHeights: Record<string, number> = { ...nodeActualHeightsRef.current };
                els.forEach(el => {
                    const id = el.dataset.nodeId;
                    if (id) measuredHeights[id] = el.offsetHeight;
                });
                nodeActualHeightsRef.current = measuredHeights;
                const updatedNodes = calculateLayout(nb.nodes, nb.links || [], mapLayoutStyle, nodeWidth, false, nodeExpandCyclesRef.current, measuredHeights);
                updateNotebookNodes(updatedNodes);
            } catch (e) {
                console.error('[measureAndRelayout] layout failed:', e);
            }
        }, delay);
    }, [mapLayoutStyle, nodeWidth, activeNotebookId]);

    const handleNodeExpandCycleChange = useCallback((nodeId: string, cycle: 0 | 1 | 2) => {
        nodeExpandCyclesRef.current[nodeId] = cycle;
        measureAndRelayout(200);
    }, [measureAndRelayout]);

    // Survey mode / canvas zoom-out hover preview
    const isHoverPreviewEnabled = isSurveyMode || (!isMobile && scale < 0.5);
    useEffect(() => {
        if (!isHoverPreviewEnabled) { setSurveyPreview(null); return; }
        let rafId: number | null = null;
        let clearTimer: ReturnType<typeof setTimeout> | null = null;
        let showTimer: ReturnType<typeof setTimeout> | null = null;
        let pendingNodeId: string | null = null;
        const handleMouseMove = (e: MouseEvent) => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const elements = document.elementsFromPoint(e.clientX, e.clientY);
                const overPanel = elements.some(el => (el as HTMLElement).closest?.('[data-survey-panel]'));
                if (overPanel) {
                    if (clearTimer) { clearTimeout(clearTimer); clearTimer = null; }
                    return;
                }
                const cardEl = elements.find(el => (el as HTMLElement).hasAttribute?.('data-node-id')) as HTMLElement | undefined;
                const nodeId = cardEl?.getAttribute('data-node-id');
                if (nodeId) {
                    if (clearTimer) { clearTimeout(clearTimer); clearTimer = null; }
                    if (nodeId === pendingNodeId) return; // already waiting on this node
                    pendingNodeId = nodeId;
                    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
                    showTimer = setTimeout(() => {
                        showTimer = null;
                        const node = activeNotebook?.nodes.find(n => n.id === nodeId);
                        const rect = cardEl?.getBoundingClientRect();
                        if (node && rect) setSurveyPreview({ node, rect });
                    }, 700);
                } else {
                    pendingNodeId = null;
                    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
                    if (!clearTimer) {
                        clearTimer = setTimeout(() => { clearTimer = null; setSurveyPreview(null); }, 200);
                    }
                }
            });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => { window.removeEventListener('mousemove', handleMouseMove); if (rafId) cancelAnimationFrame(rafId); if (clearTimer) clearTimeout(clearTimer); if (showTimer) clearTimeout(showTimer); };
    }, [isHoverPreviewEnabled, activeNotebook]);

    // Link midpoint drag
    useEffect(() => {
        if (!draggingLinkId) return;
        const handleMouseMove = (e: MouseEvent) => {
            if (!draggingLinkStartRef.current) return;
            const { mouseX, mouseY, dx: startDx, dy: startDy } = draggingLinkStartRef.current;
            const newDx = startDx + (e.clientX - mouseX) / scale;
            const newDy = startDy + (e.clientY - mouseY) / scale;
            setNotebooks(nbs => nbs.map(nb => nb.id !== activeNotebookId ? nb : {
                ...nb,
                links: nb.links?.map(l => l.id === draggingLinkId ? { ...l, midpoint: { dx: newDx, dy: newDy } } : l),
            }));
        };
        const handleMouseUp = () => {
            setDraggingLinkId(null);
            draggingLinkStartRef.current = null;
            setNotebooks(nbs => nbs.map(nb => nb.id !== activeNotebookId ? nb : { ...nb, updatedAt: Date.now() }));
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [draggingLinkId, scale, activeNotebookId]);

    const getCanvasStyle = (): React.CSSProperties => {
        const mobileBottomPad = isMobile && activeNotebook ? 'calc(60px + env(safe-area-inset-bottom))' : undefined;
        if (document.documentElement.classList.contains('dark')) return { paddingBottom: mobileBottomPad };
        if (isMobile) return { paddingBottom: mobileBottomPad };
        const dot = 'rgba(148,163,184,0.5)';
        const line = 'rgba(148,163,184,0.35)';
        let bgImage = 'none', bgSize = 'auto';
        switch (canvasBg.pattern) {
            case 'dots-sm': bgImage = `radial-gradient(${dot} 1.5px, transparent 1.5px)`; bgSize = '20px 20px'; break;
            case 'dots-lg': bgImage = `radial-gradient(${dot} 2px, transparent 2px)`; bgSize = '40px 40px'; break;
            case 'grid': bgImage = `linear-gradient(${line} 1px,transparent 1px),linear-gradient(90deg,${line} 1px,transparent 1px)`; bgSize = '24px 24px'; break;
            case 'lines': bgImage = `linear-gradient(${line} 1px, transparent 1px)`; bgSize = '24px 24px'; break;
        }
        return { backgroundColor: canvasBg.color, backgroundImage: bgImage, backgroundSize: bgSize, paddingBottom: mobileBottomPad };
    };

    const PRESET_CANVAS_COLORS = ['#f8fafc','#ffffff','#fefce8','#eff6ff','#f5f3ff','#f0fdf4','#fce7f3','#1e293b'];

    const handleCanvasBgSelect = (updates: Partial<{ color: string; pattern: string }>) => {
        const next = { ...canvasBg, ...updates };
        setCanvasBg(next);
        localStorage.setItem('canvasBg', JSON.stringify(next));
    };

    const saveCustomColor = (color: string) => {
        if (PRESET_CANVAS_COLORS.includes(color.toLowerCase())) return;
        setSavedCustomColors(prev => {
            const next = [color, ...prev.filter(c => c !== color)].slice(0, 3);
            localStorage.setItem('canvasBgCustomColors', JSON.stringify(next));
            return next;
        });
    };

    const removeCustomColor = (color: string) => {
        setSavedCustomColors(prev => {
            const next = prev.filter(c => c !== color);
            localStorage.setItem('canvasBgCustomColors', JSON.stringify(next));
            return next;
        });
    };

    const handleCanvasContextMenu = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('[data-node-id]')) return;
        e.preventDefault();
        setCanvasBgMenu({ x: e.clientX, y: e.clientY });
    };

    const handleSelectExpandCycle = (next: 0 | 1 | 2 | 3 | 4) => {
        setGlobalExpandCycle(next);
        localStorage.setItem('globalExpandCycle', String(next));
        setShowExpandMenu(false);
        if (!activeNotebook) return;
        if (next === 4) return;
        saveHistoryImmediate();
        const newExpandedState = next >= 1;
        const imagesExpanded = next === 2;
        let updatedNodes: MindMapNode[] = activeNotebook.nodes.map(n => ({ ...n, isExpanded: newExpandedState }));
        if (mapLayoutStyle !== 'free') {
            updatedNodes = calculateLayout(updatedNodes, activeNotebook.links || [], mapLayoutStyle, nodeWidth, imagesExpanded, nodeExpandCyclesRef.current, nodeActualHeightsRef.current);
        }
        updateNotebookNodes(updatedNodes);
        measureAndRelayout(400);
    };

    const handleToggleExpandAll = () => {
        const next = ((globalExpandCycle + 1) % 5) as 0 | 1 | 2 | 3 | 4;
        setGlobalExpandCycle(next);
        localStorage.setItem('globalExpandCycle', String(next));
        if (!activeNotebook) return;
        // State 4 = zen mode — keep current node state, only visual change
        if (next === 4) return;
        saveHistoryImmediate();
        // Text state: expanded for 1/2/3, collapsed for 0
        const newExpandedState = next >= 1;
        // Image state: full height only for state 2
        const imagesExpanded = next === 2;
        let updatedNodes: MindMapNode[] = activeNotebook.nodes.map(n => ({ ...n, isExpanded: newExpandedState }));
        if (mapLayoutStyle !== 'free') {
            updatedNodes = calculateLayout(updatedNodes, activeNotebook.links || [], mapLayoutStyle, nodeWidth, imagesExpanded, nodeExpandCyclesRef.current, nodeActualHeightsRef.current);
        }
        updateNotebookNodes(updatedNodes);
        // Re-measure after DOM updates with new content heights
        measureAndRelayout(400);
    };

    useEffect(() => {
        if (window.innerWidth < 768) { setSidebarOpen(false); }
        if (isEffectiveReadOnly) { setSidebarOpen(false); }
    }, [isEffectiveReadOnly]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const viewMode = urlParams.get('mode') === 'view';
        const sharedUid = urlParams.get('uid');
        const sharedId = urlParams.get('id');

        if (viewMode && sharedUid && sharedId) {
            setIsLocalLoading(true);
            console.log(`[DEBUG_SHARE] URL View Mode detected: uid=${sharedUid}, id=${sharedId}`);
            let resolvedNotebook: import('./types').Notebook | null = null;
            loadSharedNotebook(sharedUid, sharedId)
                .then(notebook => {
                    if (notebook && notebook !== 'private') {
                        console.log(`[DEBUG_SHARE] Setting notebooks with shared data: ID=${notebook.id}`);
                        resolvedNotebook = notebook;
                        setNotebooks([notebook]);
                        setActiveNotebookId(notebook.id);
                        setUser({ id: 'viewer', name: 'Guest Viewer', email: '' });
                        setGlobalExpandCycle(4); // Zen mode: show full note content on canvas
                        setNodeWidth('extra-wide'); // widest card for best readability
                        hasAutoFocusedRef.current = notebook.id; // prevent auto-focus from conflicting
                    } else if (notebook === 'private') {
                        showToast("This notebook is private and not available for public viewing.", 'error');
                        setUser({ id: 'viewer', name: 'Guest Viewer', email: '' });
                    } else {
                        showToast("Shared notebook not found or deleted.", 'error');
                        setUser({ id: 'viewer', name: 'Guest Viewer', email: '' });
                    }
                })
                .catch(err => {
                    console.error(err);
                    showToast("Error loading shared notebook", 'error');
                })
                .finally(() => {
                    setIsLocalLoading(false);
                    setIsInitialLoadComplete(true);
                    // Force light theme for public view — dark mode confuses first-time visitors
                    document.documentElement.classList.remove('dark');
                    // Center camera on notebook content after loading screen clears and canvas mounts.
                    // Uses resolvedNotebook directly (not React state) to avoid stale closure issues.
                    if (resolvedNotebook) {
                        const nb = resolvedNotebook;
                        setTimeout(() => {
                            const nodes = nb.nodes;
                            if (!nodes?.length) return;
                            // Center on root node at comfortable zoom — avoids the tiny fit-all scale for large notebooks
                            const root = nodes.find(n => !n.parentId) || nodes[0];
                            if (!root?.position) return;
                            const containerW = canvasRef.current?.clientWidth || window.innerWidth;
                            const containerH = canvasRef.current?.clientHeight || window.innerHeight;
                            const TARGET_SCALE = 0.6;
                            const newPan = {
                                x: containerW / 2 - root.position.x * TARGET_SCALE,
                                y: containerH / 2 - root.position.y * TARGET_SCALE,
                            };
                            panRef.current = newPan;
                            scaleRef.current = TARGET_SCALE;
                            setPan(newPan);
                            setScale(TARGET_SCALE);
                            if (innerCanvasRef.current) {
                                innerCanvasRef.current.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${TARGET_SCALE})`;
                            }
                        }, 300);
                    }
                });
            return;
        }

        if (isReadOnlyMode && !user) {
            setUser({ id: 'viewer', name: 'Viewer', email: '' });
            return;
        }

        const unsubscribe = subscribeToAuth(async (authUser) => {
            console.log(`[DEBUG_SHARE] Auth state changed: ${authUser?.id || 'null'}`);
            if (authUser) {
                hadUserRef.current = true;
                setSessionExpired(false);
                setUser(authUser);
                posthog.identify(authUser.id, {
                    email: authUser.email,
                    name: authUser.name,
                    isPro: !!authUser.isPro,
                });
                Sentry.setUser({ id: authUser.id, email: authUser.email, username: authUser.name });
                if (typeof window !== 'undefined' && window.innerWidth < 768) {
                    Sentry.setTag('platform', 'mobile');
                    Sentry.setTag('screen_width', String(window.innerWidth));
                    Sentry.captureMessage('[Mobile] session.start', {
                        level: 'info',
                        tags: { mobile: 'true', mobile_event: 'session.start' },
                        extra: { userAgent: navigator.userAgent, screenWidth: window.innerWidth },
                    });
                }
                saveUserProfile(authUser.id, { email: authUser.email, name: authUser.name, photoURL: authUser.photoURL });

                // Load cross-device prefs from Firestore
                loadUserPrefs(authUser.id).then(prefs => {
                    if (!prefs) return;
                    if (prefs.language === 'vietnamese' || prefs.language === 'english') {
                        setLanguage(prefs.language);
                        localStorage.setItem('ms_language', prefs.language);
                    }
                    if (prefs.darkMode !== undefined) {
                        document.documentElement.classList.toggle('dark', prefs.darkMode);
                        localStorage.setItem('darkMode', prefs.darkMode ? 'true' : 'false');
                    }
                });

                // Initialize math patterns Firebase sync
                const { setCurrentUser, migrateToFirebase, loadPatternsFromFirebase } = await import('./utils/mathSymbols');
                setCurrentUser(authUser.id);  // FIX: User object uses .id not .uid

                // Migrate and load patterns from Firebase
                try {
                    await migrateToFirebase(authUser.id);
                    const firebaseData = await loadPatternsFromFirebase(authUser.id);
                    if (firebaseData) {
                        // Update localStorage with Firebase data
                        localStorage.setItem('study-board-custom-math-patterns', JSON.stringify(firebaseData.customPatterns));
                        localStorage.setItem('study-board-disabled-default-patterns-v2', JSON.stringify(firebaseData.disabledDefaultPatterns));
                    }
                } catch (e) {
                    console.error('Math patterns sync failed:', e);
                }
            } else {
                console.log("[DEBUG_SHARE] Auth state is null. Checking View Mode...");
                // FIX RACE CONDITION: Only nullify user if NOT in view mode
                const currentParams = new URLSearchParams(window.location.search);
                const isViewing = currentParams.get('mode') === 'view';
                if (!isViewing) {
                    // Unexpected session expiry (not explicit logout) → try silent re-auth first
                    if (hadUserRef.current && !isExplicitLogoutRef.current) {
                        // Wait briefly — Firebase sometimes emits null transiently during token refresh
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        if (auth.currentUser) {
                            // Firebase recovered — try force-refresh
                            try {
                                await auth.currentUser.getIdToken(true);
                                hadUserRef.current = true;
                                console.log("[DEBUG_SHARE] Token refreshed silently after transient null — no dialog shown");
                                return;
                            } catch {
                                // Force-refresh failed — genuine expiry
                            }
                        }
                        console.log("[DEBUG_SHARE] Genuine session expiry — showing re-auth overlay");
                        setSessionExpired(true);
                        hadUserRef.current = false;
                        // DO NOT call setUser(null) — keep notebooks/state alive
                        return;
                    }
                    console.log("[DEBUG_SHARE] Explicit logout — calling setUser(null)");
                    hadUserRef.current = false;
                    setUser(null);
                    Sentry.setUser(null);
                } else {
                    console.log("[DEBUG_SHARE] In View Mode. BLOCKED setUser(null) to keep session.");
                }
            }
        });
        return () => unsubscribe();
    }, []);

    // Proactive token refresh — silently refresh before expiry; only show dialog if refresh fails
    useEffect(() => {
        const checkToken = async () => {
            if (!auth.currentUser || sessionExpired) return;
            try {
                const token = await auth.currentUser.getIdTokenResult(false);
                const expiresAt = new Date(token.expirationTime).getTime();
                const minsLeft = (expiresAt - Date.now()) / 60000;
                if (minsLeft < 10 && minsLeft > 0) {
                    // Token about to expire — force-refresh silently; Firebase SDK handles this
                    await auth.currentUser.getIdToken(true);
                    // Refresh succeeded, no need to show dialog
                }
            } catch {
                // Refresh failed (refresh token revoked / network) — now show dialog
                setSessionExpired(true);
            }
        };
        const interval = setInterval(checkToken, 5 * 60 * 1000); // check every 5 min
        return () => clearInterval(interval);
    }, [sessionExpired]);

    // WARNING: Do not call updateNotebookNodes directly for bulk replace, import,
    // restore, PDF upload, or any operation that removes/replaces many nodes.
    // Use handleDestructiveUpdate() so safety snapshots can run first.
    const updateNotebookNodes = (nodes: MindMapNode[]) => {
        setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, nodes, updatedAt: Date.now() } : nb));
    };

    const updateNotebookLinks = (links: NodeLink[]) => {
        if (isEffectiveReadOnly) return;
        setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, links, updatedAt: Date.now() } : nb));
    };

    // Async wrapper for destructive node writes — snapshots current state before writing.
    // Operations that already created an explicit snapshot must pass skipSnapshotGuard: true.
    const handleDestructiveUpdate = useCallback(async (
        newNodes: MindMapNode[],
        newLinks?: NodeLink[],
        options?: { skipSnapshotGuard?: boolean }
    ): Promise<boolean> => {
        if (!options?.skipSnapshotGuard && user && activeNotebookId) {
            const existing = activeNotebook?.nodes ?? [];
            const existingLinks = activeNotebook?.links ?? [];
            const incomingLinks = newLinks ?? existingLinks;
            if (isDestructiveChange(existing, newNodes, existingLinks, incomingLinks)) {
                const result = await saveSnapshot(user.id, activeNotebookId, existing, existingLinks, 'auto_guard');
                if (!result.ok) {
                    alert(language === 'vietnamese'
                        ? 'Không thể tạo backup an toàn. Thay đổi lớn đã bị chặn để bảo vệ dữ liệu.'
                        : 'Could not create a safety backup. The large change was blocked to protect your data.');
                    return false;
                }
                const now = Date.now();
                setNotebooks(nbs => nbs.map(n => n.id === activeNotebookId ? { ...n, lastSnapshotAt: now } : n));
            }
        }
        updateNotebookNodes(newNodes);
        if (newLinks !== undefined) updateNotebookLinks(newLinks);
        return true;
    }, [user, activeNotebookId, activeNotebook, language, updateNotebookNodes, updateNotebookLinks]);

    useEffect(() => {
        try {
            const configStr = localStorage.getItem('ms_drive_config');
            if (configStr) { setDriveConfig(JSON.parse(configStr)); }
        } catch (e) { console.error("Failed to restore config", e); }
    }, []);

    useEffect(() => {
        // Clear first-capture nudge when switching notebooks
        setShowFirstCaptureNudge(false);
        setHighlightCaptureButton(false);
    }, [activeNotebookId]);

    useEffect(() => {
        if (activeNotebook) {
            setNodeWidth(activeNotebook.nodeWidth || 'standard');
            setPdfUrl(activeNotebook.pdfContextUrl || null);
            if (pendingJumpNodeRef.current) {
                // Cross-notebook navigation: jump to specific node, skip viewport restore
                const nodeId = pendingJumpNodeRef.current;
                pendingJumpNodeRef.current = null;
                const node = activeNotebook.nodes.find(n => n.id === nodeId);
                if (node) {
                    const containerW = canvasRef.current?.clientWidth || window.innerWidth;
                    const containerH = canvasRef.current?.clientHeight || window.innerHeight;
                    const s = scaleRef.current;
                    setPan({ x: (containerW / 2) - (node.position.x * s), y: (containerH / 2) - (node.position.y * s) });
                    setSelectedNodeId(nodeId);
                    hasAutoFocusedRef.current = activeNotebook.id;
                }
            } else if (!isReadOnlyMode) {
                try {
                    const saved = localStorage.getItem(`sb_viewport_${activeNotebook.id}`);
                    if (saved) {
                        const { x, y, scale: s } = JSON.parse(saved);
                        setPan({ x, y });
                        setScale(s);
                        // Prevent the auto-focus effect from overriding the restored viewport
                        hasAutoFocusedRef.current = activeNotebook.id;
                    }
                } catch { /* ignore corrupt data */ }
            }
        }
    }, [activeNotebook?.id]);

    // Reopen the note that was expanded when this notebook was last active — survives a full page refresh
    useEffect(() => {
        if (!activeNotebook || expandedNodeId || isReadOnlyMode) return;
        const saved = localStorage.getItem(`sb_expanded_node_${activeNotebook.id}`);
        if (saved && activeNotebook.nodes.some(n => n.id === saved)) {
            setExpandedNodeId(saved);
        }
    }, [activeNotebook?.id]);

    // Persist viewport to localStorage (debounced 500ms) so refresh restores position
    useEffect(() => {
        if (!activeNotebookId) return;
        if (viewportSaveTimer.current) clearTimeout(viewportSaveTimer.current);
        viewportSaveTimer.current = setTimeout(() => {
            localStorage.setItem(`sb_viewport_${activeNotebookId}`, JSON.stringify({ x: pan.x, y: pan.y, scale }));
        }, 500);
        return () => {
            if (viewportSaveTimer.current) clearTimeout(viewportSaveTimer.current);
        };
    }, [pan, scale, activeNotebookId]);

    // Restore stream view scroll position when (re)opening a notebook in stream mode
    // (mirrors the pan/scale viewport restore above — same localStorage-per-notebook pattern)
    useEffect(() => {
        if (!activeNotebook || layoutMode !== 'stream' || isReadOnlyMode) return;
        const saved = localStorage.getItem(`sb_stream_scroll_${activeNotebook.id}`);
        if (!saved) return;
        const top = parseInt(saved, 10);
        if (isNaN(top)) return;
        requestAnimationFrame(() => {
            if (canvasRef.current) canvasRef.current.scrollTop = top;
        });
    }, [activeNotebook?.id, layoutMode]);

    const handleStreamScroll = useCallback(() => {
        if (layoutMode !== 'stream' || !activeNotebookId || isReadOnlyMode) return;
        if (streamScrollSaveTimer.current) clearTimeout(streamScrollSaveTimer.current);
        streamScrollSaveTimer.current = setTimeout(() => {
            if (canvasRef.current) {
                localStorage.setItem(`sb_stream_scroll_${activeNotebookId}`, String(canvasRef.current.scrollTop));
            }
        }, 400);
    }, [layoutMode, activeNotebookId, isReadOnlyMode]);

    // Also update the saved stream position when the selected node changes via cross-link/outline
    // navigation INSIDE the expanded note modal — that doesn't scroll the (hidden) stream view
    // underneath, so handleStreamScroll above never fires for it. Compute the scrollTop that
    // node's card sits at instead, so reopening the app lands on the note last navigated to.
    useEffect(() => {
        if (layoutMode !== 'stream' || !activeNotebookId || isReadOnlyMode || !selectedNodeId) return;
        const el = document.getElementById(`node-card-inner-${selectedNodeId}`);
        if (!el || !canvasRef.current) return;
        const elRect = el.getBoundingClientRect();
        const containerRect = canvasRef.current.getBoundingClientRect();
        const top = Math.max(0, canvasRef.current.scrollTop + (elRect.top - containerRect.top) - 80);
        localStorage.setItem(`sb_stream_scroll_${activeNotebookId}`, String(top));
    }, [selectedNodeId, layoutMode, activeNotebookId, isReadOnlyMode]);

    // Auto-dismiss recovery banner once cloud sync completes
    useEffect(() => {
        if (syncStatus.state === 'saved' && recoveryInfo) {
            const timer = setTimeout(() => setRecoveryInfo(null), 2500);
            return () => clearTimeout(timer);
        }
    }, [syncStatus.state, recoveryInfo]);

    // Hamburger icon: show check for 1.5s after sync completes
    useEffect(() => {
        if (prevSyncStateRef.current === 'saving' && syncStatus.state === 'saved') {
            setSyncJustSaved(true);
            const t = setTimeout(() => setSyncJustSaved(false), 1500);
            prevSyncStateRef.current = syncStatus.state;
            return () => clearTimeout(t);
        }
        prevSyncStateRef.current = syncStatus.state;
    }, [syncStatus.state]);

    // --- NOTE TEXT SYNC (phone ↔ web) ---
    // Another device (the phone app, another tab) can change a note's title/body. Those changes
    // are applied here only to notes this tab has not edited since its last save; edited notes are
    // merged by the compare-and-set save (storageService.saveNoteTextCAS). The note open in the
    // editor is never changed under the user's cursor: its update waits until the note is closed.
    const expandedNodeIdRef = useRef<string | null>(null);
    expandedNodeIdRef.current = expandedNodeId;
    const pendingRemoteTextRef = useRef(new Map<string, { notebookId: string; change: RemoteTextChange }>());

    const patchNodeLocal = useCallback((notebookId: string, nodeId: string, data: Partial<MindMapNode>, alsoSavedCopy: boolean) => {
        const patch = (nbs: Notebook[]) => nbs.map(nb => nb.id !== notebookId ? nb : {
            ...nb, nodes: nb.nodes.map(n => n.id === nodeId ? { ...n, ...data } : n),
        });
        if (alsoSavedCopy) lastSavedNotebooksRef.current = patch(lastSavedNotebooksRef.current);
        notebooksRef.current = patch(notebooksRef.current);
        setNotebooks(prev => patch(prev));
    }, []);

    const applyRemoteText = useCallback((uid: string, notebookId: string, change: RemoteTextChange) => {
        const node = notebooksRef.current.find(n => n.id === notebookId)?.nodes.find(n => n.id === change.nodeId);
        if (!node) return;
        if (!isNoteTextAtBaseline(uid, notebookId, change.nodeId, { title: node.title ?? '', content: node.content ?? '' })) return; // edited here too — the save merges
        acceptRemoteText(uid, notebookId, change);
        patchNodeLocal(notebookId, change.nodeId, change.data as Partial<MindMapNode>, true);
    }, [patchNodeLocal]);

    const activeNbReady = !!activeNotebook && !activeNotebook._isPartiallyLoaded && !activeNotebook._loadFailed;
    useEffect(() => {
        if (!user || user.id === 'viewer' || user.id === 'guest' || !activeNotebookId || !activeNbReady || !isInitialLoadComplete) return;
        const uid = user.id;
        const nbId = activeNotebookId;
        const since = notebookLoadedAt.get(nbId) ?? Date.now();
        return subscribeToNoteTextChanges(uid, nbId, since, (changes) => {
            for (const change of changes) {
                if (expandedNodeIdRef.current === change.nodeId) {
                    pendingRemoteTextRef.current.set(change.nodeId, { notebookId: nbId, change });
                    showToast(language === 'vietnamese'
                        ? 'Note đang mở vừa được sửa trên thiết bị khác. Đóng note để thấy bản mới; nếu bạn sửa tiếp, hai bản sẽ được gộp khi lưu.'
                        : 'This note was just edited on another device. Close it to see the new version; if you keep editing, both are merged on save.', 'info');
                    continue;
                }
                applyRemoteText(uid, nbId, change);
            }
        });
    }, [user?.id, activeNotebookId, activeNbReady, isInitialLoadComplete, applyRemoteText]); // eslint-disable-line react-hooks/exhaustive-deps

    // A note was closed: apply the update that arrived while it was open (if the user didn't edit it).
    useEffect(() => {
        if (!user) return;
        for (const [nodeId, p] of Array.from(pendingRemoteTextRef.current.entries())) {
            if (nodeId === expandedNodeId) continue;
            pendingRemoteTextRef.current.delete(nodeId);
            applyRemoteText(user.id, p.notebookId, p.change);
        }
    }, [expandedNodeId, user?.id, applyRemoteText]); // eslint-disable-line react-hooks/exhaustive-deps

    // A save had to merge with, or set aside, another device's text: adopt the server's result.
    useEffect(() => onTextSaveResult((r) => {
        const node = notebooksRef.current.find(n => n.id === r.notebookId)?.nodes.find(n => n.id === r.nodeId);
        if (!node) return;
        const meta: Partial<MindMapNode> = { textRev: r.textRev, ...(r.textConflicts ? { textConflicts: r.textConflicts } : {}) };
        const cur: NoteText = { title: node.title ?? '', content: node.content ?? '' };
        let next: NoteText | null = null;
        if (cur.title === r.ours.title && cur.content === r.ours.content) next = r.result;
        else {
            // The user kept typing while the save ran: fold the saved result into what they have now.
            const m = mergeNoteText(r.ours, cur, r.result);
            if (m.clean) next = m.merged;
        }
        if (next) {
            patchNodeLocal(r.notebookId, r.nodeId, { ...meta, title: next.title, content: next.content }, false);
            if (expandedNodeIdRef.current === r.nodeId && (next.title !== cur.title || next.content !== cur.content)) setFindReplaceVersion(v => v + 1);
        } else {
            // Can't fold it in without touching what they're typing: keep the server result as a copy.
            const copy: TextConflictCopy = { title: r.result.title, content: r.result.content, from: 'unknown', savedAt: Date.now(), rev: r.textRev };
            const copies = [...(r.textConflicts ?? node.textConflicts ?? []), copy];
            patchNodeLocal(r.notebookId, r.nodeId, { ...meta, textConflicts: copies }, false);
            setTextConflicts(r.userId, r.notebookId, r.nodeId, copies).catch(e => console.error('[TEXT SYNC] keep copy failed', e));
        }
        const vi = language === 'vietnamese';
        if (r.outcome === 'conflict') {
            showToast(vi ? `"${cur.title || 'Note'}" bị sửa cùng chỗ trên hai thiết bị. Bản của bạn được giữ; bản kia nằm trong mục "bản xung đột" của note.`
                         : `"${cur.title || 'Note'}" was edited in the same place on two devices. Yours is kept; the other is in the note's conflict versions.`, 'info');
        } else {
            showToast(vi ? `Đã gộp "${cur.title || 'note'}" với thay đổi từ thiết bị khác.` : `Merged "${cur.title || 'note'}" with changes from another device.`, 'success');
        }
    }), [patchNodeLocal, language]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- PDF ANNOTATION SYNC (phone ↔ web) ---
    // Highlights / pen strokes made on another device appear here live; this tab's own unsaved
    // strokes are kept (merged). PdfViewer re-reads a page's strokes when its prop changes.
    const patchAnnotationsLocal = useCallback((notebookId: string, data: Annotations, alsoSavedCopy: boolean) => {
        const patch = (nbs: Notebook[]) => nbs.map(nb => nb.id !== notebookId ? nb : { ...nb, pdfAnnotations: data as any });
        if (alsoSavedCopy) lastSavedNotebooksRef.current = patch(lastSavedNotebooksRef.current);
        notebooksRef.current = patch(notebooksRef.current);
        setNotebooks(prev => patch(prev));
    }, []);

    useEffect(() => {
        if (!user || user.id === 'viewer' || user.id === 'guest' || !activeNotebookId || !activeNbReady || !isInitialLoadComplete) return;
        const nbId = activeNotebookId;
        const local = () => (notebooksRef.current.find(n => n.id === nbId)?.pdfAnnotations ?? {}) as Annotations;
        return subscribeToAnnotations(user.id, nbId, local, (merged, server) => {
            if (sameAnnotations(merged, local())) return;
            // If nothing was pending here, the result equals the server: no save needed.
            patchAnnotationsLocal(nbId, merged, sameAnnotations(merged, server));
        });
    }, [user?.id, activeNotebookId, activeNbReady, isInitialLoadComplete, patchAnnotationsLocal]); // eslint-disable-line react-hooks/exhaustive-deps

    // A save folded in another device's strokes: adopt the result, keeping strokes drawn since.
    useEffect(() => onAnnotationsMerged((notebookId, ours, result) => {
        const current = (notebooksRef.current.find(n => n.id === notebookId)?.pdfAnnotations ?? {}) as Annotations;
        const next = mergeAnnotations(ours, current, result);
        if (!sameAnnotations(next, current)) patchAnnotationsLocal(notebookId, next, sameAnnotations(next, result));
    }), [patchAnnotationsLocal]);

    const handleResolveTextConflict = useCallback((nodeId: string, action: 'restore' | 'dismiss', index: number) => {
        if (!user || !activeNotebookId) return;
        const node = notebooksRef.current.find(n => n.id === activeNotebookId)?.nodes.find(n => n.id === nodeId);
        const copies = node?.textConflicts ?? [];
        const picked = copies[index];
        if (!node || !picked) return;
        let rest = copies.filter((_, i) => i !== index);
        if (action === 'restore') {
            // Switch texts: the current one becomes a kept copy, so nothing is lost.
            rest = [...rest, { title: node.title ?? '', content: node.content ?? '', from: 'web', savedAt: Date.now(), rev: node.textRev ?? 0 }];
        }
        patchNodeLocal(activeNotebookId, nodeId, { textConflicts: rest.length ? rest : undefined }, true);
        setTextConflicts(user.id, activeNotebookId, nodeId, rest).catch(e => console.error('[TEXT SYNC] update copies failed', e));
        if (action === 'restore') {
            updateNode(nodeId, { title: picked.title, content: picked.content });
            setFindReplaceVersion(v => v + 1);
        }
    }, [user?.id, activeNotebookId, patchNodeLocal]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- DATA SYNC & INTEGRITY SUBSCRIPTION ---
    useEffect(() => {
        console.log(`[DEBUG_SHARE] Data Sync Effect triggered: user.id = ${user?.id || 'null'}`);
        if (user && user.id !== 'viewer') {
            setIsLocalLoading(true);
            setSyncStatus({ state: 'saved' });

            console.log(`[DEBUG_SHARE] Subscribing to Firestore for user: ${user.id}`);
            const unsub = subscribeToUserData(user.id, (serverNotebooks) => {
                console.log(`\n🔄 [SNAPSHOT] Firestore callback fired - ${serverNotebooks.length} notebooks`);
                console.log('   isInitialLoadCompleteRef:', isInitialLoadCompleteRef.current);
                console.log('   isSavingCriticalOpRef:', isSavingCriticalOpRef.current);
                console.log('   Local notebooks:', notebooksRef.current.length);
                
                // --- ONE-WAY SYNC: PREVENT DATA LOSS ---
                // Only allow server updates on INITIAL LOAD
                // After that, local is the authoritative source of truth
                // This prevents race conditions where stale server snapshots overwrite local edits
                // ALWAYS block after initial load - no exceptions
                if (isInitialLoadCompleteRef.current) {
                    console.log('🛑 [ONE-WAY SYNC] BLOCKING server update');
                    console.log('   Reason: Initial load complete - local is authoritative');
                    console.log('   Server notebooks:', serverNotebooks.length);
                    console.log('   Local notebooks:', notebooksRef.current.length);
                    return;
                }
                
                if (!isInitialLoadCompleteRef.current) {
                    console.log('✅ [ONE-WAY SYNC] ALLOWING server update (initial load not complete)');
                }

                // Safe to update from server (initial load only)
                // Hash comparison is no longer used because it defeats ONE-WAY SYNC
                // We ONLY update from server on the very first load, never after
                if (!isInitialLoadCompleteRef.current) {
                    console.log(`✅ [ONE-WAY SYNC] Performing initial load from server`);
                    
                    // Migration: Initialize siblingOrder for nodes that don't have it
                    let migrationHappened = false;
                    const migratedNotebooks = serverNotebooks.map(nb => {
                        let needsMigration = false;
                        // Build O(1) siblingOrder map in a single O(n) pass
                        const siblingOrderMap = new Map<string, number>();
                        const siblingCountByParent = new Map<string | undefined, number>();
                        nb.nodes.forEach(node => {
                            if (node.siblingOrder === undefined) needsMigration = true;
                            const parentId = node.parentId;
                            const idx = siblingCountByParent.get(parentId) ?? 0;
                            siblingOrderMap.set(node.id, idx);
                            siblingCountByParent.set(parentId, idx + 1);
                        });

                        // Assign siblingOrder based on current array position
                        if (needsMigration) {
                            console.log(`🔄 [MIGRATION] Initializing siblingOrder for notebook "${nb.title}"`);
                            migrationHappened = true;

                            return {
                                ...nb,
                                nodes: nb.nodes.map(node => {
                                    if (node.siblingOrder !== undefined) return node;
                                    return { ...node, siblingOrder: siblingOrderMap.get(node.id) ?? 0 };
                                })
                            };
                        }

                        return nb;
                    });
                    
                    let deadPdfUrlFound = false;
                    let sanitizedNotebooks = migratedNotebooks.map(nb => {
                        // blob: URLs only live in the browser tab that created them — one stored
                        // in Firestore is always dead. Drop it instead of trying (and failing) to load it forever.
                        if (nb.pdfContextUrl?.startsWith('blob:')) {
                            console.warn(`[SANITIZE] Dropping dead blob: pdfContextUrl for notebook "${nb.title}"`);
                            deadPdfUrlFound = true;
                        }
                        return {
                            ...nb,
                            pdfContextUrl: nb.pdfContextUrl?.startsWith('blob:') ? undefined : nb.pdfContextUrl,
                            nodes: nb.nodes.map(n => ({
                                ...n,
                                isAnalyzing: false,
                                isGeneratingQuestions: false,
                                isExpanded: n.isExpanded !== undefined ? n.isExpanded : true
                            }))
                        };
                    });

                    // === RECOVERY: merge unsaved changes from localStorage backup ===
                    // If the user refreshed while the 5s debounce was running, the localStorage
                    // backup may have nodes (or edits) that never made it to Firestore.
                    // We merge them in here BEFORE setting notebooks, so they are never lost.
                    let recoveredNodeCount = 0;
                    let recoveredNotebookTitle = '';
                    try {
                        const backupKey = `study-board-backup-${user.id}`;
                        const backupJson = localStorage.getItem(backupKey);
                        if (backupJson) {
                            const backup = JSON.parse(backupJson);
                            if (backup.savedAt && backup.id && Array.isArray(backup.nodes)) {
                                const nbIdx = sanitizedNotebooks.findIndex(nb => nb.id === backup.id);
                                if (nbIdx !== -1) {
                                    const firestoreNb = sanitizedNotebooks[nbIdx];
                                    // Timestamp guard: skip recovery if Firestore is newer than the local backup.
                                    // Without this, a stale backup from a previous device/session can silently
                                    // overwrite newer edits that were already saved to Firestore.
                                    if (backup.savedAt < (firestoreNb.updatedAt ?? 0)) {
                                        console.log(`[RECOVERY] Skipping localStorage backup for "${firestoreNb.title}": Firestore (${firestoreNb.updatedAt}) is newer than backup (${backup.savedAt})`);
                                    } else {
                                    const firestoreNodeIds = new Set(firestoreNb.nodes.map((n: MindMapNode) => n.id));
                                    const backupNodeById = new Map<string, any>(backup.nodes.map((n: any) => [n.id, n]));

                                    // Nodes in localStorage that don't exist in Firestore = unsaved new nodes
                                    const newNodes: MindMapNode[] = backup.nodes
                                        .filter((n: any) => !firestoreNodeIds.has(n.id))
                                        .map((n: any) => ({
                                            ...n,
                                            isAnalyzing: false,
                                            isGeneratingQuestions: false,
                                            isExpanded: true,
                                        }));

                                    // Existing nodes where localStorage has different text content = unsaved edits
                                    const updatedNodes = firestoreNb.nodes.map((firestoreNode: MindMapNode) => {
                                        const localNode = backupNodeById.get(firestoreNode.id);
                                        if (!localNode) return firestoreNode;
                                        const hasEdit =
                                            localNode.title !== firestoreNode.title ||
                                            localNode.content !== firestoreNode.content;
                                        if (!hasEdit) return firestoreNode;
                                        // Merge: restore text fields from localStorage, keep Firestore media fields
                                        return {
                                            ...firestoreNode,
                                            title: localNode.title,
                                            content: localNode.content,
                                            tags: localNode.tags ?? firestoreNode.tags,
                                            position: localNode.position ?? firestoreNode.position,
                                            siblingOrder: localNode.siblingOrder ?? firestoreNode.siblingOrder,
                                        };
                                    });

                                    const editedCount = updatedNodes.filter(
                                        (n, i) => n !== firestoreNb.nodes[i]
                                    ).length;

                                    if (newNodes.length > 0 || editedCount > 0) {
                                        sanitizedNotebooks = sanitizedNotebooks.map((nb, i) =>
                                            i !== nbIdx ? nb : { ...firestoreNb, nodes: [...updatedNodes, ...newNodes] }
                                        );
                                        recoveredNodeCount = newNodes.length + editedCount;
                                        recoveredNotebookTitle = firestoreNb.title;
                                        migrationHappened = true; // trigger save so recovered data persists to Firestore
                                        console.log(`🔄 [RECOVERY] Merged ${newNodes.length} new + ${editedCount} edited nodes from localStorage`);
                                    }
                                    } // end else (backup is newer than Firestore)
                                }
                            }
                        }
                    } catch (recoveryErr) {
                        console.warn('[RECOVERY] Recovery check failed (non-critical):', recoveryErr);
                    }

                    // Build lightweight cross-search index from fully-loaded notebooks only.
                    // subscribeToUserData already marks non-active notebooks as _isPartiallyLoaded
                    // with nodes:[], so we must skip them here — otherwise we'd store empty arrays
                    // and the background-indexing effect would never re-fetch them.
                    sanitizedNotebooks.forEach(nb => {
                        if (nb.nodes.length > 0) {
                            crossSearchIndex.current.set(nb.id, nb.nodes.map(n => ({
                                id: n.id,
                                title: n.title ?? '',
                                content: n.content?.slice(0, 2000) ?? '',
                                tags: n.tags ?? [],
                            })));
                        }
                    });

                    // Determine which notebook will be active so we can keep it fully loaded.
                    // Duplicates the logic at line ~1957 (setActiveNotebookId) intentionally —
                    // we need the ID here BEFORE calling setNotebooks.
                    const _urlId = new URLSearchParams(window.location.search).get('id');
                    const _savedId = localStorage.getItem('sb_active_notebook');
                    const _activeNbs = sanitizedNotebooks.filter(nb => !nb.isDeleted);
                    const _targetNb = _urlId ? _activeNbs.find(nb => nb.id === _urlId) : _savedId ? _activeNbs.find(nb => nb.id === _savedId) : null;
                    const _initialActiveId = _targetNb?.id ?? _activeNbs[0]?.id ?? null;

                    // Evict non-active notebooks — discard full nodes array, keep metadata.
                    // crossSearchIndex preserves search data; nodes are reloaded on demand.
                    const evictedNotebooks = sanitizedNotebooks.map(nb =>
                        nb.id === _initialActiveId
                            ? nb
                            : { ...nb, nodes: [], _isPartiallyLoaded: true as const }
                    );

                    console.log('💾 [SNAPSHOT] Updating local notebooks from server');
                    console.log(`   Setting ${evictedNotebooks.length} notebooks (${evictedNotebooks.filter(nb => !nb._isPartiallyLoaded).length} fully loaded)`);
                    setNotebooks(evictedNotebooks);
                    lastSavedNotebooksRef.current = evictedNotebooks;
                    lastCloudSyncTimeRef.current = Date.now();
                    setHasUnsyncedChanges(false);
                    if (recoveredNodeCount > 0) {
                        setRecoveryInfo({ count: recoveredNodeCount, notebookTitle: recoveredNotebookTitle });
                    }

                    // Trigger save after migration/recovery to persist to Firestore
                    if (migrationHappened || deadPdfUrlFound) {
                        console.log('💾 [MIGRATION/RECOVERY] Triggering save');
                        setTimeout(() => setHasUnsyncedChanges(true), 2000);
                    }

                    if (!isInitialLoadCompleteRef.current) {
                        const urlId = new URLSearchParams(window.location.search).get('id');
                        const savedId = localStorage.getItem('sb_active_notebook');
                        const activeNbs = sanitizedNotebooks.filter(nb => !nb.isDeleted);
                        const targetNb = urlId
                            ? activeNbs.find(nb => nb.id === urlId)
                            : savedId
                            ? activeNbs.find(nb => nb.id === savedId)
                            : null;
                        setActiveNotebookId(targetNb ? targetNb.id : (activeNbs[0]?.id || null));

                        // Record node counts for fully-loaded notebooks so lazy-load guard can detect silent empty-load bugs
                        evictedNotebooks.forEach(nb => {
                            if (!nb._isPartiallyLoaded && nb.nodes.length > 0) {
                                knownNodeCountRef.current.set(nb.id, nb.nodes.length);
                            }
                        });

                        // Set initial load complete ONCE after first load
                        console.log('🎯 [INIT] Setting isInitialLoadComplete = TRUE (first time)');
                        setIsInitialLoadComplete(true);

                        // Mobile: disconnect Firestore listener immediately after initial load.
                        // The ONE-WAY SYNC guard above blocks further updates anyway, but
                        // disconnecting stops the WebSocket from running in the background
                        // (saves battery and data on mobile).
                        if (isMobile) {
                            setTimeout(() => unsub(), 200);
                        }

                        // Upgrade Getting Started notebook if outdated (non-blocking)
                        refreshOnboardingIfNeeded(user.id, sanitizedNotebooks).then(updated => {
                            if (updated !== sanitizedNotebooks) setNotebooks(updated);
                        });
                    }
                }

                setIsLocalLoading(false);
                setLastSavedTime(new Date());
            });

            return () => unsub();
        } else {
            console.log(`[DEBUG_SHARE] Data Sync branch ELSE: user is null or 'viewer'. Clear state? ${user?.id === 'viewer' ? 'CLEARING' : 'IDLE'}`);
            // LƯU Ý: Đây có thể là nơi gây lỗi nếu clearing đè lên loadSharedNotebook
            if (!isReadOnlyMode) {
                setNotebooks([]);
                setActiveNotebookId(null);
                setIsInitialLoadComplete(false);
            }
        }
    }, [user?.id]);

    // --- LOGIC WELCOME NOTEBOOK ---
    useEffect(() => {
        if (isInitialLoadComplete && notebooks.length === 0 && !isSavingRef.current && user && user.id !== 'viewer') {
            const initial = createInitialNotebook();
            setNotebooks([initial]);
            lastSavedNotebooksRef.current = [];
        }
    }, [isInitialLoadComplete, notebooks.length, user]);

    // --- LAZY LOAD: ON-DEMAND NODE LOADING ON NOTEBOOK SWITCH ---
    // When user switches to a partially-loaded notebook, fetch its full nodes from Firestore.
    // Uses a request counter to discard stale responses when user switches rapidly.
    useEffect(() => {
        if (!activeNotebookId || !user || user.id === 'guest' || !isInitialLoadComplete) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        if (!nb?._isPartiallyLoaded) return;

        // Mark as pending so the empty-notebook onboarding card doesn't flash prematurely.
        // Falls back to false after 6s even if load is stuck, so UI is never permanently blocked.
        setNotebookLoadPending(true);
        if (notebookLoadPendingTimerRef.current) clearTimeout(notebookLoadPendingTimerRef.current);
        notebookLoadPendingTimerRef.current = setTimeout(() => setNotebookLoadPending(false), 6000);

        const requestId = ++loadRequestCountRef.current;
        const targetId = activeNotebookId;
        const nbTitle = nb.title ?? targetId;

        // Hard 10s timeout — prevents getDocs from hanging forever on slow/dead networks.
        const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('load_timeout')), 10000)
        );

        console.log(`📥 [LAZY LOAD] Loading nodes for "${nbTitle}"...`);
        Promise.race([
            Promise.all([
                loadNotebookNodes(user.id, targetId),
                loadNotebookAnnotations(user.id, targetId),
            ]),
            timeoutPromise,
        ]).then(([rawNodes, extraAnnotations]) => {
            if (requestId !== loadRequestCountRef.current) return; // stale — a newer request took over
            if (activeNotebookIdRef.current !== targetId) return; // stale — user switched away before load finished

            // Guard: subcollection returned 0 nodes — always treat as suspicious for a Firestore
            // notebook, since nodeCount in Firestore may be stale (0) even when notes exist.
            // The retry button lets the user confirm: if retry also returns 0, _loadFailed is
            // cleared and the normal empty-notebook onboarding is shown.
            if (rawNodes.length === 0) {
                const nodeCountFromMeta = notebooksRef.current.find(n => n.id === targetId)?.nodeCount ?? 0;
                const nodeCountFromHistory = knownNodeCountRef.current.get(targetId) ?? 0;
                console.error(`🚨 [LAZY LOAD] "${nbTitle}" returned 0 nodes (meta=${nodeCountFromMeta}, history=${nodeCountFromHistory}) — showing retry overlay`);
                if (notebookLoadPendingTimerRef.current) clearTimeout(notebookLoadPendingTimerRef.current);
                setNotebookLoadPending(false);
                setNotebooks(prev => prev.map(n =>
                    n.id === targetId ? { ...n, nodes: [], _isPartiallyLoaded: false, _loadFailed: true } : n
                ));
                lastSavedNotebooksRef.current = lastSavedNotebooksRef.current.map(n =>
                    n.id === targetId ? { ...n, nodes: [], _isPartiallyLoaded: false, _loadFailed: true } : n
                );
                return;
            }

            const nodes = rawNodes.map(n => ({
                ...n,
                isAnalyzing: false,
                isGeneratingQuestions: false,
                isExpanded: n.isExpanded !== undefined ? n.isExpanded : true,
            }));
            const annotationsPatch = extraAnnotations && Object.keys(extraAnnotations).length > 0
                ? { pdfAnnotations: extraAnnotations } : {};
            console.log(`✅ [LAZY LOAD] "${nbTitle}" ready — ${nodes.length} nodes loaded`);
            if (nodes.length > 0) knownNodeCountRef.current.set(targetId, nodes.length);
            if (notebookLoadPendingTimerRef.current) clearTimeout(notebookLoadPendingTimerRef.current);
            setNotebookLoadPending(false);
            crossSearchIndex.current.set(targetId, nodes.map(n => ({
                id: n.id, title: n.title ?? '', content: n.content?.slice(0, 2000) ?? '', tags: n.tags ?? [],
            })));
            setNotebooks(prev => prev.map(n =>
                n.id === targetId ? { ...n, nodes, _isPartiallyLoaded: false, ...annotationsPatch } : n
            ));
            // Sync lastSavedNotebooksRef so the eviction dirty check doesn't see this notebook
            // as "dirty" just because it was loaded (lastSavedRef still had nodes: [] from eviction).
            // This reflects Firestore truth: the just-loaded nodes ARE what's saved.
            lastSavedNotebooksRef.current = lastSavedNotebooksRef.current.map(n =>
                n.id === targetId ? { ...n, nodes, _isPartiallyLoaded: false, ...annotationsPatch } : n
            );
        }).catch(err => {
            if (requestId !== loadRequestCountRef.current) return;
            const isTimeout = err?.message === 'load_timeout';
            console.error(`[LAZY LOAD] ${isTimeout ? 'Timed out' : 'Failed'} loading nodes for notebook:`, targetId, err);
            // Mark as load failed so the UI shows retry instead of the empty-notebook onboarding card
            if (notebookLoadPendingTimerRef.current) clearTimeout(notebookLoadPendingTimerRef.current);
            setNotebookLoadPending(false);
            setNotebooks(prev => prev.map(n =>
                n.id === targetId ? { ...n, _loadFailed: true, _isPartiallyLoaded: false } : n
            ));
            lastSavedNotebooksRef.current = lastSavedNotebooksRef.current.map(n =>
                n.id === targetId ? { ...n, _loadFailed: true, _isPartiallyLoaded: false } : n
            );
        });
    }, [activeNotebookId, user?.id, isInitialLoadComplete]);

    // Retry loading a notebook that previously had _loadFailed=true.
    // Directly loads from Firestore and clears the flag on success.
    const [retryingLoad, setRetryingLoad] = useState(false);
    const retryLoadFailedNotebook = useCallback(async () => {
        if (!activeNotebookId || !user || user.id === 'guest' || retryingLoad) return;
        setRetryingLoad(true);
        try {
            const [rawNodes, extraAnnotations] = await Promise.all([
                loadNotebookNodes(user.id, activeNotebookId),
                loadNotebookAnnotations(user.id, activeNotebookId),
            ]);
            const nodes = rawNodes.map(n => ({
                ...n, isAnalyzing: false, isGeneratingQuestions: false,
                isExpanded: n.isExpanded !== undefined ? n.isExpanded : true,
            }));
            const annotationsPatch = extraAnnotations && Object.keys(extraAnnotations).length > 0
                ? { pdfAnnotations: extraAnnotations } : {};
            crossSearchIndex.current.set(activeNotebookId, nodes.map(n => ({
                id: n.id, title: n.title ?? '', content: n.content?.slice(0, 2000) ?? '', tags: n.tags ?? [],
            })));
            setNotebooks(prev => prev.map(n =>
                n.id === activeNotebookId ? { ...n, nodes, _isPartiallyLoaded: false, _loadFailed: false, ...annotationsPatch } : n
            ));
            lastSavedNotebooksRef.current = lastSavedNotebooksRef.current.map(n =>
                n.id === activeNotebookId ? { ...n, nodes, _isPartiallyLoaded: false, _loadFailed: false, ...annotationsPatch } : n
            );
            console.log(`✅ [RETRY LOAD] "${activeNotebookId}" recovered — ${nodes.length} nodes`);
        } catch (err) {
            console.error('[RETRY LOAD] Failed again:', activeNotebookId, err);
        } finally {
            setRetryingLoad(false);
        }
    }, [activeNotebookId, user, retryingLoad]);

    // Load a non-active notebook's nodes for the outline panel switcher.
    // Does NOT change activeNotebookId — just hydrates the notebook in state so allNotebooksForModal has real nodes.
    const handleLoadNotebookForOutline = useCallback(async (notebookId: string) => {
        if (!user || user.id === 'guest') return;
        const nb = notebooksRef.current.find(n => n.id === notebookId);
        if (!nb || !nb._isPartiallyLoaded) return; // already loaded or not found
        try {
            const rawNodes = await loadNotebookNodes(user.id, notebookId);
            const nodes = rawNodes.map(n => ({ ...n, isAnalyzing: false, isGeneratingQuestions: false, isExpanded: n.isExpanded !== undefined ? n.isExpanded : true }));
            crossSearchIndex.current.set(notebookId, nodes.map(n => ({ id: n.id, title: n.title ?? '', content: n.content?.slice(0, 2000) ?? '', tags: n.tags ?? [] })));
            setNotebooks(prev => prev.map(n => n.id === notebookId ? { ...n, nodes, _isPartiallyLoaded: false } : n));
            lastSavedNotebooksRef.current = lastSavedNotebooksRef.current.map(n => n.id === notebookId ? { ...n, nodes, _isPartiallyLoaded: false } : n);
        } catch (err) {
            console.error('[OUTLINE LOAD] Failed to load notebook for outline view:', notebookId, err);
        }
    }, [user]);

    // Keep activeNotebookIdRef in sync so eviction timer can read the current value
    useEffect(() => { activeNotebookIdRef.current = activeNotebookId; }, [activeNotebookId]);

    // Keep crossSearchIndex fresh as nodes in the active notebook change.
    // This ensures stubs are always current before eviction flushes them.
    useEffect(() => {
        if (!activeNotebookId || !activeNotebook || activeNotebook._isPartiallyLoaded) return;
        crossSearchIndex.current.set(activeNotebookId, activeNotebook.nodes.map(n => ({
            id: n.id, title: n.title ?? '', content: n.content?.slice(0, 2000) ?? '', tags: n.tags ?? [],
        })));
    }, [activeNotebook?.nodes, activeNotebookId]);

    // --- LAZY LOAD STAGE 3: EVICT PREVIOUS NOTEBOOK AFTER SWITCH ---
    // Eviction is safe only when the notebook has no unsaved changes. We check by comparing
    // current node array reference against lastSavedNotebooksRef. If dirty, we retry every 4s
    // (up to 3 retries) to give autosave time to complete. This prevents losing async updates
    // (AI Check, auto-title, background analysis) that land after the user has switched away.
    useEffect(() => {
        if (!isInitialLoadComplete) return;
        const prevId = prevActiveNotebookIdRef.current;
        prevActiveNotebookIdRef.current = activeNotebookId;
        if (!prevId || prevId === activeNotebookId) return;

        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;

        const tryEvict = (attempt = 0) => {
            if (cancelled) return;
            if (activeNotebookIdRef.current === prevId) return; // user switched back

            const nb = notebooksRef.current.find(n => n.id === prevId);
            if (!nb || nb._isPartiallyLoaded) return;

            // P0 safety: only evict when the notebook is clean (matches last saved state).
            // If dirty, wait for autosave to complete before evicting.
            // IMPORTANT: this check relies on immutable node array updates throughout the app.
            // Never mutate nb.nodes in-place — always create a new array reference on change.
            // If any code mutates nodes directly, isDirty will be a false negative and eviction
            // will proceed even though the notebook has unsaved changes.
            // TODO: after autosave success, retry eviction for notebooks that were skipped here
            // (isDirty after max retries). Currently those notebooks stay in RAM for the session.
            const savedNb = lastSavedNotebooksRef.current.find(n => n.id === prevId);
            const isDirty = !savedNb || savedNb.nodes !== nb.nodes;

            if (isDirty && attempt < 3) {
                console.log(`⏳ [LAZY EVICT] "${nb.title}" has unsaved changes — retry ${attempt + 1}/3 in 4s`);
                retryTimer = setTimeout(() => tryEvict(attempt + 1), 4000);
                return;
            }

            if (isDirty) {
                console.warn(`⚠️ [LAZY EVICT] "${nb.title}" still dirty after max retries — skipping eviction to avoid data loss`);
                return;
            }

            // Refresh crossSearchIndex from the latest nodes before releasing them
            crossSearchIndex.current.set(prevId, nb.nodes.map(n => ({
                id: n.id, title: n.title ?? '', content: n.content?.slice(0, 2000) ?? '', tags: n.tags ?? [],
            })));

            console.log(`🗑️ [LAZY EVICT] "${nb.title}" — releasing ${nb.nodes.length} nodes from memory`);
            // Record real node count before eviction so lazy-load guard can detect silent empty-load bugs
            if (nb.nodes.length > 0) knownNodeCountRef.current.set(prevId, nb.nodes.length);
            setNotebooks(prev => prev.map(n =>
                n.id === prevId ? { ...n, nodes: [], _isPartiallyLoaded: true } : n
            ));
        };

        const evictTimer = setTimeout(() => tryEvict(), 6000);

        return () => {
            cancelled = true;
            clearTimeout(evictTimer);
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [activeNotebookId, isInitialLoadComplete]);

    // Expose memory debug helpers — assigned on every render so they're always fresh
    (window as any).__sbNotebooksRef = notebooksRef;
    (window as any).__sbMemory = () => {
        const nbs = notebooksRef.current;
        const total = nbs.reduce((s, nb) => s + nb.nodes.length, 0);
        console.group('📊 StudyBoard Memory State');
        nbs.forEach(nb => {
            const status = nb._isPartiallyLoaded ? '⚡ evicted' : `✅ loaded (${nb.nodes.length} nodes)`;
            console.log(`  ${nb.isDeleted ? '[trash] ' : ''}${nb.title}: ${status}`);
        });
        console.log(`  Total nodes in memory: ${total}`);
        if ((performance as any).memory) {
            const mb = (v: number) => (v / 1024 / 1024).toFixed(1) + ' MB';
            const m = (performance as any).memory;
            console.log(`  JS heap used: ${mb(m.usedJSHeapSize)} / ${mb(m.jsHeapSizeLimit)}`);
        }
        console.groupEnd();
    };


    // --- LAYER 1: LOCALSTORAGE PERSISTENCE (INSTANT BACKUP) ---
    // This prevents data loss when user closes tab before cloud sync completes
    useEffect(() => {
        if (!user || user.id === 'viewer' || isEffectiveReadOnly) return;
        if (!isInitialLoadComplete) return; // Don't backup until initial load done
        
        try {
            // Only backup the active notebook to stay within localStorage quota (~5MB).
            // All notebooks are persisted in Firestore — this backup is just a fast-restore
            // cache for the current session.
            const active = notebooks.find(nb => nb.id === activeNotebookId);
            if (!active) return;
            const backupKey = `study-board-backup-${user.id}`;
            const stripped = {
                id: active.id,
                title: active.title,
                savedAt: Date.now(), // track when backup was written for recovery comparison
                nodes: active.nodes.map(n => ({
                    id: n.id,
                    title: n.title,
                    content: n.content,
                    parentId: n.parentId,
                    position: n.position,
                    nodeType: n.nodeType,
                    siblingOrder: n.siblingOrder,
                    tags: n.tags,
                }))
            };
            const json = JSON.stringify(stripped);
            // First attempt: remove our own key then write
            localStorage.removeItem(backupKey);
            try {
                localStorage.setItem(backupKey, json);
            } catch {
                // Still no space — there are other large keys from old sessions.
                // Nuclear option: clear ALL study-board-backup-* keys then retry once.
                Object.keys(localStorage)
                    .filter(k => k.startsWith('study-board-backup-'))
                    .forEach(k => localStorage.removeItem(k));
                localStorage.setItem(backupKey, json); // throws if data itself is too large
            }

            if (featureFlags.debugMode) {
                console.log('💾 [LAYER 1] Saved to localStorage:', active.title);
            }
        } catch (e) {
            console.warn('localStorage backup unavailable (non-critical):', e);
        }
    }, [notebooks, activeNotebookId, user, isInitialLoadComplete, isEffectiveReadOnly, featureFlags.debugMode]);

    // Restore from localStorage on initial load (before Firestore sync)
    useEffect(() => {
        if (!user || user.id === 'viewer' || isEffectiveReadOnly) return;
        
        try {
            const backupKey = `study-board-backup-${user.id}`;
            const backup = localStorage.getItem(backupKey);
            
            if (backup && !isInitialLoadComplete) {
                const parsed = JSON.parse(backup);
                // Support both old format (array) and new format (single notebook object)
                const restoredNotebooks: Notebook[] = Array.isArray(parsed) ? parsed : [parsed];
                console.log('📂 [LAYER 1] Restored from localStorage:', restoredNotebooks.length, 'notebooks');
                setNotebooks(restoredNotebooks);
                // Don't set lastSavedNotebooksRef here - let cloud sync handle it
            }
        } catch (e) {
            console.error('Failed to restore from localStorage:', e);
        }
    }, [user, isEffectiveReadOnly, isInitialLoadComplete]);

    // --- LAYER 2: DEBOUNCED CLOUD SYNC (3 SECONDS) ---
    // This replaces the old 15s interval with immediate but debounced sync
    useEffect(() => {
        if (!user || isEffectiveReadOnly || isLocalLoading) return;
        
        const currentNotebooks = notebooks;
        // Allow auto-save if we have data, even if initial load not complete
        // This prevents data loss when isInitialLoadComplete flag has timing issues
        if (currentNotebooks.length === 0) return;

        // Check if there are actual changes to sync.
        // Use updatedAt timestamps instead of JSON.stringify (which is O(n×nodeSize) and
        // blocks the main thread — causing typing friction after every content save).
        const latestUpdatedAt = currentNotebooks.reduce((max, nb) => Math.max(max, nb.updatedAt || 0), 0);
        const savedUpdatedAt = (lastSavedNotebooksRef.current || []).reduce((max, nb) => Math.max(max, nb.updatedAt || 0), 0);
        const hasChanges = latestUpdatedAt !== savedUpdatedAt || currentNotebooks.length !== (lastSavedNotebooksRef.current || []).length;

        if (!hasChanges) {
            setHasUnsyncedChanges(false);
            return; // No changes, skip
        }

        // Mark as having unsynced changes
        setHasUnsyncedChanges(true);

        // Mobile: no auto-save — user taps the sync button manually
        if (isMobile) return;

        // Clear existing debounce timer
        if (cloudSyncDebounceTimerRef.current) {
            clearTimeout(cloudSyncDebounceTimerRef.current);
        }

        // Set new debounce timer (3 seconds)
        cloudSyncDebounceTimerRef.current = setTimeout(async () => {
            if (isSavingRef.current) return;

            isSavingRef.current = true;
            setSyncStatus({ state: 'saving', message: language === 'vietnamese' ? 'Đang đồng bộ...' : 'Syncing...' });

            try {
                // Use notebooksRef.current (latest state) instead of captured snapshot
                // to avoid race condition where async operations (e.g. AI tag extraction)
                // update local state after the debounce timer started
                const latestNotebooks = notebooksRef.current;

                // Only push notebooks that actually changed since the last successful save.
                // saveUserData() re-writes the FULL nodes subcollection (multiple batch.commit()
                // calls per notebook) for everything it's given — sending all notebooks every
                // cycle fires dozens of concurrent batch commits at Firestore's single write
                // stream and triggers "Write stream exhausted maximum allowed queued writes."
                const lastSavedMap = new Map(lastSavedNotebooksRef.current.map(nb => [nb.id, nb]));
                const changedNotebooks = latestNotebooks.filter(nb => {
                    // Never save a partially-loaded notebook — nodes: [] would wipe the subcollection
                    if (nb._isPartiallyLoaded) return false;
                    // Never save a notebook whose subcollection load failed — its nodes array is
                    // invalid (empty due to load error, not user intent) and saving it would delete data.
                    if (nb._loadFailed) {
                        console.error(`🛑 [SAVE BLOCKED] "${nb.title}": _loadFailed=true — refusing to save to prevent data loss`);
                        return false;
                    }
                    const prev = lastSavedMap.get(nb.id);
                    // Image count integrity guard: if image count drops >20% compared to last saved
                    // state without any pending explicit deletes, something went wrong during load.
                    // Block save rather than risk wiping screenshots.
                    if (prev) {
                        const prevImgCount = prev.nodes.filter(n => n.imageBase64?.startsWith('https://')).length;
                        const curImgCount = nb.nodes.filter(n => n.imageBase64?.startsWith('https://')).length;
                        const pendingImgDeletes = pendingDeletionsRef.current.get(nb.id)?.size ?? 0;
                        if (prevImgCount > 10 && curImgCount < prevImgCount * 0.8 && pendingImgDeletes < prevImgCount - curImgCount) {
                            console.error(`🛑 [SAVE BLOCKED] "${nb.title}": suspicious image drop ${prevImgCount}→${curImgCount} (pending deletes: ${pendingImgDeletes}) — refusing to save`);
                            return false;
                        }
                    }
                    if (!prev) return true;
                    try {
                        return JSON.stringify(nb) !== JSON.stringify(prev);
                    } catch {
                        return true;
                    }
                });

                if (changedNotebooks.length === 0) {
                    lastSavedNotebooksRef.current = latestNotebooks;
                    lastCloudSyncTimeRef.current = Date.now();
                    setHasUnsyncedChanges(false);
                    setSyncStatus({ state: 'saved', message: undefined });
                    setLastSavedTime(new Date());
                    return;
                }

                // Scope each notebook's write to only the nodes THIS device actually changed
                // since its own last successful save — never the whole array. A device that
                // never received another device's edits (one-way sync only reads once per load)
                // would otherwise re-push its own stale copy of untouched notes and clobber
                // newer content saved elsewhere in the meantime.
                const nodeDiffByNotebookId = new Map<string, { dirtyIds: Set<string>; deletedIds: string[] }>();
                changedNotebooks.forEach(nb => {
                    const prev = lastSavedMap.get(nb.id);
                    const prevNodeMap = new Map((prev?.nodes ?? []).map(n => [n.id, n]));
                    const dirtyIds = new Set<string>();
                    nb.nodes.forEach(n => {
                        const prevNode = prevNodeMap.get(n.id);
                        if (!prevNode) { dirtyIds.add(n.id); return; }
                        try {
                            if (JSON.stringify(n) !== JSON.stringify(prevNode)) dirtyIds.add(n.id);
                        } catch {
                            dirtyIds.add(n.id);
                        }
                    });
                    // Use explicit pending deletions only — never infer deletions from missing IDs.
                    // Diff-based deletion (prev has X, current doesn't → delete X) is unsafe with
                    // lazy loading: a failed load looks identical to "user deleted all nodes".
                    const deletedIds = Array.from(pendingDeletionsRef.current.get(nb.id) ?? []);
                    nodeDiffByNotebookId.set(nb.id, { dirtyIds, deletedIds });
                });

                const result = await saveUserData(
                    user.id,
                    changedNotebooks,
                    (msg) => setSyncStatus({ state: 'saving', message: msg }),
                    nodeDiffByNotebookId
                );

                if (result && result.hasChanges) {
                    // saveUserData converted base64 images → Firebase Storage URLs.
                    // Compute the updated state explicitly so we can set both setNotebooks
                    // and lastSavedNotebooksRef to the SAME object — preventing the useEffect
                    // from detecting a "change" and triggering another save cycle.
                    const savedNodeMap = new Map<string, MindMapNode>();
                    result.data.forEach(nb => nb.nodes.forEach(n => savedNodeMap.set(n.id, n)));
                    const latestNodeMap = new Map<string, MindMapNode>();
                    latestNotebooks.forEach(nb => nb.nodes.forEach(n => latestNodeMap.set(n.id, n)));
                    const savedNbMap = new Map(result.data.map(nb => [nb.id, nb]));

                    // Warn if PDF upload failed for any notebook (blob: → undefined means Storage upload failed)
                    changedNotebooks.forEach(sent => {
                        const saved = savedNbMap.get(sent.id);
                        if (sent.pdfContextUrl?.startsWith('blob:') && saved && !saved.pdfContextUrl) {
                            const title = sent.title || 'notebook';
                            showToast(
                                language === 'vietnamese'
                                    ? `Không thể lưu PDF của "${title}" lên cloud. Vui lòng tải lại file PDF để không bị mất khi đổi thiết bị.`
                                    : `Could not save PDF for "${title}" to cloud. Please re-upload the PDF file to avoid losing it.`,
                                'error'
                            );
                        }
                    });

                    const updatedNotebooks = notebooksRef.current.map(nb => {
                        const savedNb = savedNbMap.get(nb.id);
                        if (!savedNb) return nb;
                        return {
                            ...nb,
                            pdfContextUrl: savedNb.pdfContextUrl ?? nb.pdfContextUrl,
                            nodes: nb.nodes.map(n => {
                                const saved = savedNodeMap.get(n.id);
                                const atSaveTime = latestNodeMap.get(n.id);
                                if (!saved || !atSaveTime) return n;
                                const updates: Partial<MindMapNode> = {};
                                if (saved.imageBase64 !== atSaveTime.imageBase64 && n.imageBase64 === atSaveTime.imageBase64)
                                    updates.imageBase64 = saved.imageBase64;
                                if (saved.drawingBase64 !== atSaveTime.drawingBase64 && n.drawingBase64 === atSaveTime.drawingBase64)
                                    updates.drawingBase64 = saved.drawingBase64;
                                if (saved.attachments && JSON.stringify(saved.attachments) !== JSON.stringify(atSaveTime.attachments) && JSON.stringify(n.attachments) === JSON.stringify(atSaveTime.attachments))
                                    updates.attachments = saved.attachments;
                                return Object.keys(updates).length > 0 ? { ...n, ...updates } : n;
                            })
                        };
                    });
                    // Set both to same reference — useEffect hash comparison = equal, no re-trigger
                    lastSavedNotebooksRef.current = updatedNotebooks;
                    setNotebooks(updatedNotebooks);
                } else {
                    lastSavedNotebooksRef.current = latestNotebooks;
                }

                // Clear pending deletions for notebooks that were successfully saved
                changedNotebooks.forEach(nb => pendingDeletionsRef.current.delete(nb.id));

                lastCloudSyncTimeRef.current = Date.now();
                setHasUnsyncedChanges(false);
                setSyncStatus({ state: 'saved', message: undefined });
                setLastSavedTime(new Date());

                if (featureFlags.debugMode) {
                    console.log('☁️ [LAYER 2] Cloud sync completed');
                }
            } catch (e: any) {
                console.error("Cloud sync failed", e);
                Sentry.captureException(e, {
                    tags: { feature: 'cloud_sync' },
                    extra: {
                        userId: user.id,
                        notebookCount: notebooksRef.current.length,
                        totalNodes: notebooksRef.current.reduce((sum, nb) => sum + nb.nodes.length, 0),
                        errorMessage: e?.message,
                    }
                });
                if (e.message === "ERROR_NOTEBOOK_TOO_LARGE") {
                    setSyncStatus({
                        state: 'error',
                        message: language === 'vietnamese' ? '⚠️ Quá dung lượng đồng bộ' : '⚠️ Sync Limit Exceeded'
                    });
                    showToast(language === 'vietnamese' ? 'Sổ tay quá lớn (>1MB), vui lòng chia nhỏ nội dung để tiếp tục đồng bộ Cloud.' : 'Notebook too large (>1MB), please split content to continue Cloud sync.', 'error');
                } else if (
                    e?.code === 'permission-denied' ||
                    e?.code === 'unauthenticated' ||
                    e?.message?.includes('permission-denied') ||
                    e?.message?.includes('Missing or insufficient permissions') ||
                    e?.message?.includes('unauthenticated')
                ) {
                    setAuthSessionExpired(true);
                    setSyncStatus({ state: 'error', message: language === 'vietnamese' ? '⚠️ Phiên hết hạn' : '⚠️ Session expired' });
                } else {
                    setSyncStatus({ state: 'error', message: language === 'vietnamese' ? 'Lỗi lưu' : 'Save failed' });
                }
            } finally {
                isSavingRef.current = false;
            }
        }, 5000); // 5 second debounce - allows large saves (1000+ nodes) to complete before next save

        // Cleanup on unmount
        return () => {
            if (cloudSyncDebounceTimerRef.current) {
                clearTimeout(cloudSyncDebounceTimerRef.current);
            }
        };
    }, [notebooks, user, isLocalLoading, isEffectiveReadOnly, language, featureFlags.debugMode]);

    useEffect(() => {
        if (!isGuest && !isEffectiveReadOnly) {
            initGoogleDrive((isSignedIn) => setDriveConnected(isSignedIn));
            initYouTube(() => {});
        }
    }, [isGuest, isEffectiveReadOnly]);

    const handleDriveBackup = async (notebookId: string): Promise<string> => {
        const notebook = notebooks.find(n => n.id === notebookId);
        if (!notebook) throw new Error('Notebook not found');
        const { blob, filename } = await backupService.generateZipBlob(notebook, undefined);
        const record = await saveZipToDrive(blob, filename);
        setDriveBackupRecords(prev => {
            const next = [record, ...prev.filter(r => r.name !== record.name)].slice(0, 50);
            localStorage.setItem('sb_drive_backups', JSON.stringify(next));
            return next;
        });
        return `https://drive.google.com/file/d/${record.id}/view`;
    };

    const handleDriveRestore = async (fileId: string) => {
        const blob = await downloadDriveBackup(fileId);
        const file = new File([blob], 'drive-backup.zip', { type: 'application/zip' });
        await handleImportBackup(file);
    };

    const applyLayout = (type: LayoutType, width: NodeWidth) => {
        if (!activeNotebook) return;
        if (type === 'free') return;
        if (activeNotebook.nodes.length > 1) { saveHistoryImmediate(); }
        const laidOutNodes = calculateLayout(activeNotebook.nodes, activeNotebook.links || [], type, width, false, nodeExpandCyclesRef.current, nodeActualHeightsRef.current);
        updateNotebookNodes(laidOutNodes);
        measureAndRelayout(400);
    };

    useEffect(() => {
        if (mapLayoutStyle !== 'free' && activeNotebook) { applyLayout(mapLayoutStyle, nodeWidth); }
    }, [mapLayoutStyle, nodeWidth]);

    // Re-measure actual node heights after switching notebooks or initial load — fixes stale positions from Firestore
    useEffect(() => {
        if (!activeNotebookId || mapLayoutStyle === 'free') return;
        nodeActualHeightsRef.current = {}; // reset heights for new notebook
        // Nodes load from Firestore asynchronously — wait for them to render
        measureAndRelayout(1200);
    }, [activeNotebookId]);

    // Background auto-title queue: runs when notebook opens, text-only, throttled
    useEffect(() => {
        if (!autoTitleEnabled || !activeNotebookId || !user || isEffectiveReadOnly) return;
        if (bgAutoTitleRunningRef.current) return;

        const today = new Date().toISOString().split('T')[0];
        const dailyKey = `autoTitle_daily_${today}`;
        const dailyCount = parseInt(localStorage.getItem(dailyKey) || '0');
        const MAX_PER_DAY = 30;
        const MAX_PER_SESSION = 10;
        if (dailyCount >= MAX_PER_DAY) return;

        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        if (!nb) return;

        const isUntitled = (n: MindMapNode) => {
            if (n.titleSource === 'user' || n.titleSource === 'ai') return false;
            if ((n.autoTitleAttempts ?? 0) >= 2) return false;
            const t = n.title?.trim() ?? '';
            return t === '' || t === 'Tiêu đề...' || t.toLowerCase() === 'untitled';
        };
        // Text-first priority, skip image-only (they need vision — not handled here)
        const targets = nb.nodes
            .filter(n => isUntitled(n) && (n.content?.trim().length ?? 0) >= 20)
            .slice(0, Math.min(MAX_PER_SESSION, MAX_PER_DAY - dailyCount));

        if (targets.length === 0) return;

        bgAutoTitleRunningRef.current = true;
        setBgAutoTitleStatus(language === 'vietnamese' ? `Đang tạo tiêu đề cho ${targets.length} ghi chú...` : `Generating titles for ${targets.length} notes...`);

        (async () => {
            let processed = 0;
            for (const node of targets) {
                if (!bgAutoTitleRunningRef.current) break;
                try {
                    const title = await autoGenerateTitleFromText(node.content, language);
                    if (title) {
                        updateNode(node.id, { title, titleSource: 'ai' });
                        processed++;
                        localStorage.setItem(dailyKey, String(dailyCount + processed));
                    } else {
                        updateNode(node.id, { autoTitleAttempts: (node.autoTitleAttempts ?? 0) + 1 });
                    }
                } catch {
                    updateNode(node.id, { autoTitleAttempts: (node.autoTitleAttempts ?? 0) + 1 });
                }
                await new Promise(r => setTimeout(r, 300));
            }
            bgAutoTitleRunningRef.current = false;
            setBgAutoTitleStatus(null);
        })();

        return () => { bgAutoTitleRunningRef.current = false; };
    }, [activeNotebookId, autoTitleEnabled]);

    // Also re-measure when nodes are added/removed (e.g. after AI Check adds analysis)
    const activeNodeCount = activeNotebook?.nodes.length ?? 0;
    useEffect(() => {
        if (!activeNotebookId || mapLayoutStyle === 'free' || activeNodeCount === 0) return;
        measureAndRelayout(600);
    }, [activeNodeCount]);

    // Re-measure when zooming from skeleton → full LOD (scale crosses 0.4 threshold)
    // Skeleton cards are much shorter than full cards, so positions need recalculating after zoom-in
    const LOD_THRESHOLD = 0.4;
    useEffect(() => {
        const isSkeletonNow = scale < LOD_THRESHOLD;
        if (wasSkeletonModeRef.current && !isSkeletonNow) {
            // Transitioned from skeleton → full: cards just got much taller, recalculate
            measureAndRelayout(300);
        }
        wasSkeletonModeRef.current = isSkeletonNow;
    }, [scale]);

    const handleZoom = (delta: number, cursorPos?: { x: number; y: number }) => {
        // Read from refs so rapid scroll events always see the latest values, not stale closure state
        const currentScale = scaleRef.current;
        const currentPan = panRef.current;
        const newScale = Math.min(Math.max(0.1, currentScale + delta), 4);
        if (newScale === currentScale) return;
        const containerW = canvasRef.current?.clientWidth || window.innerWidth;
        const containerH = canvasRef.current?.clientHeight || window.innerHeight;

        const zoomCenterX = cursorPos?.x ?? containerW / 2;
        const zoomCenterY = cursorPos?.y ?? containerH / 2;

        const worldX = (zoomCenterX - currentPan.x) / currentScale;
        const worldY = (zoomCenterY - currentPan.y) / currentScale;

        const newPan = {
            x: zoomCenterX - (worldX * newScale),
            y: zoomCenterY - (worldY * newScale),
        };

        // Update refs + DOM immediately so the next rapid event sees correct values
        panRef.current = newPan;
        scaleRef.current = newScale;
        if (innerCanvasRef.current) {
            innerCanvasRef.current.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${newScale})`;
        }

        setPan(newPan);
        setScale(newScale);
    };

    const requestDeleteNode = useCallback((id: string) => {
        if (isEffectiveReadOnly) return;
        const activeNb = notebooks.find(nb => nb.id === activeNotebookId);
        const hasChildren = !!activeNb?.nodes.some(n => n.parentId === id);
        setDeleteNodeHasChildren(hasChildren);
        setNodeToDeleteId(id);
    }, [isEffectiveReadOnly, notebooks, activeNotebookId]);

    const triggerSaveAfterDelete = useCallback(() => {
        if (cloudSyncDebounceTimerRef.current) {
            clearTimeout(cloudSyncDebounceTimerRef.current);
        }
        setHasUnsyncedChanges(true);
    }, []);

    const executeDeleteNode = useCallback((mode: 'single' | 'subtree') => {
        if (!nodeToDeleteId || isEffectiveReadOnly) return;

        // Compute idsToRemove before setNotebooks so we can clean up searchIndex
        const preDeleteNotebook = notebooksRef.current.find(nb => nb.id === activeNotebookId);
        let idsToRemove: Set<string>;
        if (mode === 'subtree' && preDeleteNotebook) {
            idsToRemove = new Set<string>();
            const queue = [nodeToDeleteId];
            while (queue.length > 0) {
                const current = queue.shift()!;
                idsToRemove.add(current);
                preDeleteNotebook.nodes.forEach(n => { if (n.parentId === current) queue.push(n.id); });
            }
        } else {
            idsToRemove = new Set([nodeToDeleteId]);
        }

        // §5.2.1: capture the FULL affected set — the deleted node(s), any children being
        // re-parented (single mode), and every link being removed — with live payload,
        // before anything is mutated, so undo/redo is lossless for the whole operation,
        // not just the one node named by nodeToDeleteId.
        if (activeNotebookId && preDeleteNotebook) {
            const removedLinks = (preDeleteNotebook.links || []).filter(
                l => idsToRemove.has(l.fromId) || idsToRemove.has(l.toId)
            );
            if (mode === 'subtree') {
                const deletedNodes = preDeleteNotebook.nodes.filter(n => idsToRemove.has(n.id));
                pushHistoryEntry({ kind: 'delete-subtree', deletedNodes, removedLinks });
            } else {
                const deletedNode = preDeleteNotebook.nodes.find(n => n.id === nodeToDeleteId);
                if (deletedNode) {
                    const reparentedChildren: ReparentedChild[] = preDeleteNotebook.nodes
                        .filter(n => n.parentId === nodeToDeleteId)
                        .map(n => ({ id: n.id, fromParentId: nodeToDeleteId, toParentId: deletedNode.parentId }));
                    pushHistoryEntry({ kind: 'delete-single', deletedNode, reparentedChildren, removedLinks });
                }
            }
        }

        // A deleted note takes the only link to its videos with it. Keep those videos on record so
        // YouTube Manager can list them and attach them to another note.
        if (activeNotebookId && preDeleteNotebook) {
            const gone = preDeleteNotebook.nodes.filter(n => idsToRemove.has(n.id));
            const ownerId = auth.currentUser?.uid;
            if (ownerId && countVideos(gone) > 0) {
                void saveOrphanVideos(ownerId, { id: preDeleteNotebook.id, title: preDeleteNotebook.title }, gone);
            }
        }

        // Register explicit deletions so autosave can send them to Firestore.
        // This replaces diff-based deletion (which can't distinguish "user deleted" from "failed to load").
        if (activeNotebookId) {
            const set = pendingDeletionsRef.current.get(activeNotebookId) ?? new Set<string>();
            for (const id of idsToRemove) set.add(id);
            pendingDeletionsRef.current.set(activeNotebookId, set);
        }

        setNotebooks(currentNotebooks => {
            const targetNbIndex = currentNotebooks.findIndex(nb => nb.id === activeNotebookId);
            if (targetNbIndex === -1) return currentNotebooks;
            const targetNotebook = currentNotebooks[targetNbIndex];

            const deletedNode = targetNotebook.nodes.find(n => n.id === nodeToDeleteId);
            const grandparentId = deletedNode?.parentId;

            const updatedNodes = targetNotebook.nodes
                .filter(n => !idsToRemove.has(n.id))
                .map(n => {
                    if (mode === 'single' && n.parentId === nodeToDeleteId) {
                        return { ...n, parentId: grandparentId, updatedAt: Date.now() };
                    }
                    return n;
                });
            const updatedLinks = (targetNotebook.links || []).filter(
                l => !idsToRemove.has(l.fromId) && !idsToRemove.has(l.toId)
            );
            const newNotebook = { ...targetNotebook, nodes: updatedNodes, links: updatedLinks, updatedAt: Date.now() };
            const newNotebooks = [...currentNotebooks];
            newNotebooks[targetNbIndex] = newNotebook;
            return newNotebooks;
        });

        triggerSaveAfterDelete();

        // Clean up searchIndex for deleted nodes
        if (user && activeNotebookId) {
            for (const id of idsToRemove) {
                deleteDoc(doc(db, `users/${user.id}/searchIndex/${activeNotebookId}_${id}`)).catch(() => {});
            }
        }

        if (selectedNodeId === nodeToDeleteId) { setSelectedNodeId(null); }
        showToast(language === 'vietnamese' ? 'Đã xóa ghi chú' : 'Note deleted', 'success');
        posthog.capture('node_deleted', { mode });
        setNodeToDeleteId(null);
        setDeleteNodeHasChildren(false);
    }, [activeNotebookId, selectedNodeId, language, nodeToDeleteId, isEffectiveReadOnly, triggerSaveAfterDelete, pushHistoryEntry]);

    const executeDeleteNotebook = useCallback(async () => {
        if (!notebookToDeleteId || isEffectiveReadOnly) return;
        saveHistoryImmediate();
        if (user) { try { await deleteNotebookData(user.id, notebookToDeleteId); } catch (error) { console.error("Failed to delete notebook from storage", error); } }
        // Optimistic update: mark as deleted locally
        setNotebooks(nbs => nbs.map(nb => nb.id === notebookToDeleteId ? { ...nb, isDeleted: true, deletedAt: Date.now() } : nb));
        if (activeNotebookId === notebookToDeleteId) {
            const remaining = notebooks.filter(nb => nb.id !== notebookToDeleteId && !nb.isDeleted);
            setActiveNotebookId(remaining.length > 0 ? remaining[0].id : null);
        }
        setNotebookToDeleteId(null);
        showToast(language === 'vietnamese' ? 'Đã chuyển vào thùng rác' : 'Moved to trash', 'success');
    }, [notebooks, activeNotebookId, notebookToDeleteId, language, isEffectiveReadOnly, user]);

    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

            // Cancel pick-parent mode
            if (e.key === 'Escape' && pickParentForNodeId) {
                setPickParentForNodeId(null);
                return;
            }


            // Undo/redo (Ctrl+Z, Ctrl+Shift+Z, Ctrl+Y) is no longer handled here — it goes
            // through the centralized undo arbiter (hooks/useUndoArbiter.ts, §5.4), which
            // is the single window keydown listener deciding which scope (drawing overlay,
            // note editor, or this notebook canvas) owns any given Ctrl+Z. Handling it here
            // too, alongside the arbiter, would reproduce this incident's original bug
            // class — two listeners racing for the same keypress (§5.4.2).

            if (!activeNotebook) return;

            // Delete shortcuts - Restricted in RO
            // Also guard against text selection: if the user has text highlighted anywhere,
            // Delete/Backspace should erase that text, not delete the selected node. And guard
            // against the note detail modal being open: its read-mode content isn't an
            // input/textarea, so without this check a Backspace pressed there (e.g. out of "go
            // back" muscle memory) would fall through to this canvas-level shortcut and delete the
            // note the user is simply looking at, not editing.
            const hasTextSelection = (window.getSelection()?.toString().length ?? 0) > 0;
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedNodeId && !isInput && !hasTextSelection && !isEffectiveReadOnly && !expandedNodeId) {
                e.preventDefault();
                requestDeleteNode(selectedNodeId);
                return;
            }

            // Find & Replace — Ctrl+F (scoped to the open note, or whole notebook if none is open),
            // Ctrl+Shift+F (always whole notebook), or Ctrl+H (whole notebook, override browser default)
            if ((e.key === 'f' || e.key === 'F') && (e.ctrlKey || e.metaKey) && !isEffectiveReadOnly && activeNotebook) {
                // One shortcut, one search. While a note is expanded, plain Ctrl+F belongs to that
                // note's own find bar (ExpandedNoteModal) — opening this panel as well gave two
                // search boxes at once. Ctrl+Shift+F still opens the notebook-wide search here.
                if (expandedNodeId && !e.shiftKey) return;
                e.preventDefault();
                const wantScope: 'notebook' | 'note' = (e.shiftKey || !expandedNodeId) ? 'notebook' : 'note';
                // Seed the search box with the currently selected text, if any — covers both
                // textarea/input selections (own selection model) and contentEditable (DOM Selection API).
                const activeEl = document.activeElement as HTMLTextAreaElement | HTMLInputElement | null;
                let selectedText = '';
                if (activeEl && (activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'INPUT') && activeEl.selectionStart != null && activeEl.selectionEnd != null && activeEl.selectionStart !== activeEl.selectionEnd) {
                    selectedText = activeEl.value.substring(activeEl.selectionStart, activeEl.selectionEnd);
                } else {
                    selectedText = window.getSelection()?.toString() ?? '';
                }
                selectedText = selectedText.trim();
                if (selectedText) setFindReplaceQuery(selectedText);
                if (selectedText || !(findReplaceOpen && findReplaceScope === wantScope)) {
                    setFindReplaceOpen(true);
                } else {
                    setFindReplaceOpen(false);
                }
                setFindReplaceScope(wantScope);
                return;
            }
            if ((e.key === 'h' || e.key === 'H') && (e.ctrlKey || e.metaKey) && !isEffectiveReadOnly && activeNotebook) {
                e.preventDefault();
                setFindReplaceScope('notebook');
                setFindReplaceOpen(v => !v);
                return;
            }

            if (isInput) return;

            // Navigation shortcuts - Allowed in RO
            if (e.key === 'Tab') {
                e.preventDefault();
                const nodes = activeNotebook.nodes;
                if (nodes.length === 0) return;
                let nextIndex = 0;
                if (selectedNodeId) {
                    const currentIndex = nodes.findIndex(n => n.id === selectedNodeId);
                    if (e.shiftKey) { nextIndex = (currentIndex - 1 + nodes.length) % nodes.length; } else { nextIndex = (currentIndex + 1) % nodes.length; }
                }
                const nextNode = nodes[nextIndex];
                handleJumpToNode(nextNode.id);
                return;
            }

            if (selectedNodeId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                const current = activeNotebook.nodes.find(n => n.id === selectedNodeId);
                if (!current) return;
                let bestMatch: MindMapNode | null = null;
                let minDistance = Infinity;
                activeNotebook.nodes.forEach(n => {
                    if (n.id === selectedNodeId) return;
                    const dx = n.position.x - current.position.x;
                    const dy = n.position.y - current.position.y;
                    let isValidDirection = false;
                    if (e.key === 'ArrowRight' && dx > 0 && Math.abs(dy) < Math.abs(dx) * 2) isValidDirection = true;
                    if (e.key === 'ArrowLeft' && dx < 0 && Math.abs(dy) < Math.abs(dx) * 2) isValidDirection = true;
                    if (e.key === 'ArrowDown' && dy > 0 && Math.abs(dx) < Math.abs(dy) * 2) isValidDirection = true;
                    if (e.key === 'ArrowUp' && dy < 0 && Math.abs(dx) < Math.abs(dy) * 2) isValidDirection = true;
                    if (isValidDirection) {
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist < minDistance) { minDistance = dist; bestMatch = n; }
                    }
                });
                if (bestMatch) { handleJumpToNode(bestMatch.id, { instant: true }); }
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [activeNotebook, selectedNodeId, scale, language, requestDeleteNode, isEffectiveReadOnly, undo, redo, handleJumpToNode, pickParentForNodeId, expandedNodeId]);

    const handleLogin = (loggedInUser: User) => { setSessionExpired(false); setUser(loggedInUser); };
    const handleLogout = async () => {
        isExplicitLogoutRef.current = true;
        setSimulateFreeUser(false);
        setGeminiSimulateFreeUser(false);
        if (isGuest) { setUser(null); } else { await logout(); setUser(null); }
        hadUserRef.current = false;
        isExplicitLogoutRef.current = false;
        setDriveConnected(false);
        setIsInitialLoadComplete(false);
    };

    const handleCheckout = async (plan: 'monthly' | 'yearly') => {
        Sentry.addBreadcrumb({ category: 'payment', message: `checkout.initiated: ${plan}`, level: 'info' });
        try {
            const url = await createCheckoutSession(plan);
            if (url) {
                Sentry.addBreadcrumb({ category: 'payment', message: `checkout.redirecting: ${plan}`, level: 'info' });
                window.location.href = url;
            } else {
                Sentry.captureMessage('[Payment] createCheckoutSession returned no URL', { level: 'error', tags: { payment_event: 'no_url' }, extra: { plan } });
                alert(language === 'vietnamese' ? 'Không thể tạo phiên thanh toán. Vui lòng thử lại.' : 'Could not start checkout. Please try again.');
            }
        } catch (err: any) {
            Sentry.captureException(err, { tags: { payment_event: 'checkout_client_error' }, extra: { plan } });
            alert(language === 'vietnamese' ? 'Không thể tạo phiên thanh toán. Vui lòng thử lại.' : 'Could not start checkout. Please try again.');
        }
    };

    const handleManageSubscription = async () => {
        try {
            const url = await createPortalSession();
            if (url) window.location.href = url;
        } catch (err: any) {
            Sentry.captureException(err, { tags: { payment_event: 'portal_error' } });
            alert(language === 'vietnamese' ? 'Không thể mở cổng quản lý. Vui lòng thử lại.' : 'Could not open subscription portal. Please try again.');
        }
    };

    const logMobileEvent = useCallback((event: string, context?: Record<string, unknown>) => {
        Sentry.addBreadcrumb({ category: 'mobile.ui', message: event, data: context, level: 'info' });
        Sentry.captureMessage(`[Mobile] ${event}`, {
            level: 'info',
            tags: { mobile: 'true', mobile_event: event },
            extra: { layoutMode, activeNotebookId, ...context },
        });
    }, [layoutMode, activeNotebookId]);

    const handleMobileSync = async () => {
        if (isSavingRef.current || !user || user.id === 'guest') return;
        if (cloudSyncDebounceTimerRef.current) clearTimeout(cloudSyncDebounceTimerRef.current);
        isSavingRef.current = true;
        setSyncStatus({ state: 'saving', message: language === 'vietnamese' ? 'Đang đồng bộ...' : 'Syncing...' });
        logMobileEvent('sync.start', { notebookCount: notebooksRef.current.length });
        try {
            // Same integrity guards as the desktop debounced sync: never push a notebook that
            // didn't fully load, and never push one whose image count dropped suspiciously
            // (silent partial load that didn't set _loadFailed) — a bad push here would
            // overwrite good cloud data with incomplete local data.
            const lastSavedMap = new Map(lastSavedNotebooksRef.current.map(nb => [nb.id, nb]));
            const skipped: string[] = [];
            const toSave = notebooksRef.current.filter(nb => {
                if (nb._isPartiallyLoaded || nb._loadFailed) {
                    skipped.push(nb.title);
                    return false;
                }
                const prev = lastSavedMap.get(nb.id);
                if (prev) {
                    const prevImgCount = prev.nodes.filter(n => n.imageBase64?.startsWith('https://')).length;
                    const curImgCount = nb.nodes.filter(n => n.imageBase64?.startsWith('https://')).length;
                    const pendingImgDeletes = pendingDeletionsRef.current.get(nb.id)?.size ?? 0;
                    if (prevImgCount > 10 && curImgCount < prevImgCount * 0.8 && pendingImgDeletes < prevImgCount - curImgCount) {
                        console.error(`🛑 [MOBILE SYNC BLOCKED] "${nb.title}": suspicious image drop ${prevImgCount}→${curImgCount} — refusing to sync`);
                        skipped.push(nb.title);
                        return false;
                    }
                }
                return true;
            });
            const result = await saveUserData(user.id, toSave, (msg) => setSyncStatus({ state: 'saving', message: msg }));
            if (result?.hasChanges) lastSavedNotebooksRef.current = notebooksRef.current;
            setSyncStatus({ state: 'saved' });
            setLastSavedTime(new Date());
            setHasUnsyncedChanges(skipped.length > 0);
            if (skipped.length > 0) {
                showToast(
                    language === 'vietnamese'
                        ? `Bỏ qua ${skipped.length} notebook chưa tải xong hoặc nghi thiếu dữ liệu: ${skipped.join(', ')}`
                        : `Skipped ${skipped.length} notebook(s) that didn't fully load or looked incomplete: ${skipped.join(', ')}`,
                    'error'
                );
            }
            logMobileEvent('sync.success', { hasChanges: result?.hasChanges, skipped: skipped.length });
        } catch (err) {
            setSyncStatus({ state: 'error' });
            showToast(language === 'vietnamese' ? 'Đồng bộ thất bại, thử lại sau' : 'Sync failed, please try again', 'error');
            Sentry.captureException(err, { tags: { mobile: 'true', mobile_event: 'sync.fail' } });
        } finally {
            isSavingRef.current = false;
        }
    };

    const addNotebook = () => {
        if (isEffectiveReadOnly) return;
        saveHistoryImmediate();
        const newNotebook = createInitialNotebook();
        newNotebook.title = "New Notebook";
        posthog.capture('notebook_created');
        setNotebooks([...notebooks, newNotebook]);
        setActiveNotebookId(newNotebook.id);
        setRenamingNotebookId(newNotebook.id);
        setRenamingTitle(newNotebook.title);
        if (window.innerWidth < 768) setSidebarOpen(false);
    };

    const deleteNotebook = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEffectiveReadOnly) return;
        setNotebookToDeleteId(id);
    };

    const handleStartRename = (id: string, currentTitle: string) => {
        if (isEffectiveReadOnly) return;
        setRenamingNotebookId(id);
        setRenamingTitle(currentTitle);
    };

    const handleFinishRename = (id: string) => {
        if (!renamingTitle.trim()) { setRenamingNotebookId(null); return; }
        saveHistoryImmediate();
        const notebook = notebooks.find(nb => nb.id === id);
        setNotebooks(nbs => nbs.map(nb => nb.id === id ? { ...nb, title: renamingTitle, updatedAt: Date.now() } : nb));
        setRenamingNotebookId(null);
        if (notebook?.isPublic) {
            updatePublicNotebookTitle(id, renamingTitle.trim()).catch(err => console.error('Failed to sync public notebook title:', err));
        }
    };

    const handleWheel = (e: React.WheelEvent) => {
        if (isSurveyMode) return;
        if (!isQuizConfigMode && layoutMode === 'map') {
            // Allow normal scroll inside text inputs/textareas
            const target = e.target as HTMLElement;
            if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable) {
                return; // Let browser handle scroll inside text fields
            }

            // Shift+scroll = Pan
            if (e.shiftKey) {
                setPan(p => ({ x: p.x - e.deltaX - e.deltaY, y: p.y }));
            } else {
                // Normal scroll = Zoom (slower for mouse, faster for trackpad pinch)
                e.preventDefault();
                const isTrackpadPinch = e.ctrlKey || e.metaKey;
                const sensitivity = isTrackpadPinch ? -0.002 : -0.001;
                const delta = e.deltaY * sensitivity;
                // Cursor position relative to the canvas container (not viewport),
                // so the zoom center is correct regardless of sidebar width
                const rect = canvasRef.current?.getBoundingClientRect();
                handleZoom(delta, {
                    x: e.clientX - (rect?.left ?? 0),
                    y: e.clientY - (rect?.top ?? 0),
                });
            }
        }
    };

    const handleNodeStartDrag = useCallback((id: string, e: React.PointerEvent) => {
        if (isEffectiveReadOnly) return;
        if (layoutMode === 'stream') return;
        if (mapLayoutStyle !== 'free') return;
        captureSnapshot();
        draggingNodeId.current = id;
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
    }, [layoutMode, mapLayoutStyle, isEffectiveReadOnly, captureSnapshot]);

    const handleCanvasPointerDown = (e: React.PointerEvent) => {
        if (isSurveyMode) return;
        if (layoutMode !== 'map' || isQuizConfigMode) return;

        // Safety Check: Don't disable selection if user clicked an input or interactive element
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
        if (target.closest('button, a, [role="button"]')) return;

        // Deselect when clicking canvas background
        if (!target.closest('[data-node-id]')) setSelectedNodeId(null);
        if (!target.closest('[data-link-popup]')) setSelectedLinkId(null);


        // Prevent text selection during pan
        document.body.style.userSelect = 'none';

        activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY, type: e.pointerType });
        canvasRef.current?.setPointerCapture(e.pointerId);
        if (activePointers.current.size === 1) {
            isDraggingCanvas.current = true;
            lastMousePos.current = { x: e.clientX, y: e.clientY };
            if (innerCanvasRef.current) innerCanvasRef.current.style.transition = 'none';
        } else if (activePointers.current.size === 2) {
            isDraggingCanvas.current = false;
            prevPinchDist.current = null;
        }
    };

    const handleCanvasPointerMove = (e: React.PointerEvent) => {
        if (layoutMode !== 'map' || isQuizConfigMode) return;
        // Only pointers that were pressed on the canvas are tracked. Updating on every move used to add
        // hovering pointers too (a pen or tablet hovering, or any stray pointer id), so two "pointers"
        // appeared while just moving the mouse and the canvas pinch-zoomed in and out.
        if (!activePointers.current.has(e.pointerId)) return;
        activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY, type: e.pointerType });
        const tracked = Array.from(activePointers.current.values()) as { x: number; y: number; type?: string }[];
        // Pinch-zoom is two fingers on a touch screen — never a mouse or pen paired with something else.
        if (tracked.length === 2 && tracked.every(p => p.type === 'touch')) {
            const points = tracked;
            const p1 = points[0]; const p2 = points[1];
            const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
            const rect = canvasRef.current?.getBoundingClientRect();
            const containerX = rect ? rect.left : 0; const containerY = rect ? rect.top : 0;
            const cx = (p1.x + p2.x) / 2 - containerX; const cy = (p1.y + p2.y) / 2 - containerY;
            if (prevPinchDist.current) {
                const delta = dist - prevPinchDist.current;
                const zoomSensitivity = 0.005; const zoomFactor = delta * zoomSensitivity;
                const newScale = Math.min(Math.max(0.1, scale + zoomFactor), 4);
                if (newScale !== scale) {
                    const scaleRatio = newScale / scale;
                    const newPanX = cx - (cx - pan.x) * scaleRatio;
                    const newPanY = cy - (cy - pan.y) * scaleRatio;
                    setPan({ x: newPanX, y: newPanY });
                    setScale(newScale);
                }
            }
            prevPinchDist.current = dist;
            return;
        }
        if (isDraggingCanvas.current && activePointers.current.size === 1) {
            const dx = e.clientX - lastMousePos.current.x;
            const dy = e.clientY - lastMousePos.current.y;
            panRef.current = { x: panRef.current.x + dx, y: panRef.current.y + dy };
            if (innerCanvasRef.current) {
                innerCanvasRef.current.style.transform = `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${scaleRef.current})`;
            }
            lastMousePos.current = { x: e.clientX, y: e.clientY };
            return;
        }
        if (draggingNodeId.current && activeNotebook && !isEffectiveReadOnly) {
            const dx = (e.clientX - lastMousePos.current.x) / scale;
            const dy = (e.clientY - lastMousePos.current.y) / scale;
            const node = activeNotebook.nodes.find(n => n.id === draggingNodeId.current);
            if (node) {
                const newPos = { x: node.position.x + dx, y: node.position.y + dy };
                updateNode(draggingNodeId.current, { position: newPos });
                if (mapLayoutStyle === 'free') {
                    let bestCandidateId: string | null = null;
                    let minDistance = Infinity;
                    const threshold = 150;
                    activeNotebook.nodes.forEach(candidate => {
                        if (candidate.id === node.id) return;
                        if (candidate.id === node.parentId) return;
                        const dist = Math.hypot(newPos.x - candidate.position.x, newPos.y - candidate.position.y);
                        if (dist < threshold && dist < minDistance) {
                            let isCycle = false; let curr = candidate;
                            while (curr.parentId) {
                                if (curr.parentId === node.id) { isCycle = true; break; }
                                const parent = activeNotebook.nodes.find(n => n.id === curr.parentId);
                                if (!parent) break; curr = parent;
                            }
                            if (!isCycle) { minDistance = dist; bestCandidateId = candidate.id; }
                        }
                    });
                    if (dropCandidateId !== bestCandidateId) { setDropCandidateId(bestCandidateId); }
                }
            }
            lastMousePos.current = { x: e.clientX, y: e.clientY };
        }
    };

    // The browser can drop pointer capture without a pointerup (window loses focus, a modal opens
    // mid-drag…). Forget that pointer so it can never linger as a phantom second finger.
    const handleCanvasLostPointerCapture = (e: React.PointerEvent) => {
        if (!activePointers.current.delete(e.pointerId)) return;
        if (activePointers.current.size < 2) prevPinchDist.current = null;
        if (activePointers.current.size === 0 && isDraggingCanvas.current) {
            isDraggingCanvas.current = false;
            document.body.style.userSelect = '';
            setPan({ ...panRef.current });
            requestAnimationFrame(() => { if (innerCanvasRef.current) innerCanvasRef.current.style.transition = ''; });
        }
    };

    const handleCanvasPointerUp = (e: React.PointerEvent) => {
        activePointers.current.delete(e.pointerId);
        canvasRef.current?.releasePointerCapture(e.pointerId);
        if (activePointers.current.size < 2) { prevPinchDist.current = null; }
        if (activePointers.current.size === 0) {
            const wasDraggingCanvas = isDraggingCanvas.current;
            isDraggingCanvas.current = false;
            // Restore text selection
            document.body.style.userSelect = '';
            if (wasDraggingCanvas) {
                setPan({ ...panRef.current });
                requestAnimationFrame(() => {
                    if (innerCanvasRef.current) innerCanvasRef.current.style.transition = '';
                });
            }
        }
        if (draggingNodeId.current && dropCandidateId && activeNotebook && mapLayoutStyle === 'free' && !isEffectiveReadOnly) {
            const childId = draggingNodeId.current; const parentId = dropCandidateId;
            const otherLinks = (activeNotebook.links || []).filter(l => l.toId !== childId);
            const newLink: NodeLink = { id: generateId(), fromId: parentId, toId: childId, createdAt: Date.now() };
            const updatedNodes = activeNotebook.nodes.map(n => n.id === childId ? { ...n, parentId: parentId, updatedAt: Date.now() } : n);
            const finalLinks = [...otherLinks, newLink];
            setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, nodes: updatedNodes, links: finalLinks, updatedAt: Date.now() } : nb));
            setDropCandidateId(null);
            showToast(language === 'vietnamese' ? 'Đã kết nối ghi chú' : 'Notes Connected', 'success');
        } else { setDropCandidateId(null); }
        if (mapLayoutStyle !== 'free' && draggingNodeId.current && !isEffectiveReadOnly) { applyLayout(mapLayoutStyle, nodeWidth); }
        if (draggingNodeId.current) { commitSnapshot(); }
        draggingNodeId.current = null;
    };

    const addNode = (x: number, y: number) => {
        if (isEffectiveReadOnly) return;
        if (!activeNotebook) return;
        saveHistoryImmediate();
        const newNode: MindMapNode = {
            id: generateId(),
            position: { x: (x - pan.x) / scale, y: (y - pan.y) / scale },
            title: '', content: '', imageBase64: null, imageMimeType: null, isAnalyzing: false, analysis: null, isExpanded: true,
            createdAt: Date.now(), updatedAt: Date.now()
        };
        posthog.capture('note_created');
        updateNotebookNodes([...activeNotebook.nodes, newNode]);
        setSelectedNodeId(newNode.id);
    };

    const handleCanvasDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (isEffectiveReadOnly || !activeNotebookId) return;

        // If dropped onto an existing node card, let NodeCard's own onDrop handle it
        if ((e.target as HTMLElement).closest('[data-node-id]')) return;

        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        if (files.length === 0) return;
        const file = files[0];
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            const rect = canvasRef.current?.getBoundingClientRect();
            const dropX = rect ? e.clientX - rect.left : e.clientX;
            const dropY = rect ? e.clientY - rect.top : e.clientY;
            const newNodeId = generateId();
            const newNode: MindMapNode = {
                id: newNodeId,
                position: { x: (dropX - pan.x) / scale, y: (dropY - pan.y) / scale },
                title: '', content: '',
                imageBase64: base64, imageMimeType: file.type,
                isAnalyzing: false, analysis: null, isExpanded: true,
                createdAt: Date.now(), updatedAt: Date.now()
            };
            saveHistoryImmediate();
            setNotebooks(nbs => nbs.map(nb => nb.id !== activeNotebookId ? nb : { ...nb, nodes: [...nb.nodes, newNode], updatedAt: Date.now() }));
            setSelectedNodeId(newNodeId);
            setPickParentForNodeId(newNodeId);
            posthog.capture('note_created_by_image_drop');

            // Auto-extract AI tags + title + writing hint from the dropped image (fire-and-forget, non-blocking)
            extractImageHashtags([{ base64, mimeType: file.type }], language).then(({ tags: newTags, title, hint }) => {
                const updates: Record<string, unknown> = {};
                if (newTags.length > 0) updates.tags = newTags;
                if (title) { updates.title = title; updates.titleSource = 'ai'; }
                if (hint) updates.writingHint = hint;
                if (Object.keys(updates).length > 0) updateNode(newNodeId, updates);
            }).catch(() => {});
        };
        reader.readAsDataURL(file);
    }, [isEffectiveReadOnly, activeNotebookId, pan, scale, language, updateNode]);

    const handleStartReview = useCallback((nodeId: string) => {
        posthog.capture('memory_gym_started');
        setActiveReviewSessionNodeId(nodeId);
    }, []);

    const handleGenerateMissingQuestions = useCallback(() => {
        if (!activeNotebook) return;
        setBackfillModalOpen(true);
    }, [activeNotebook]);

    const handleAddToReviewFeed = useCallback(async (nodeId: string, origin: string = 'manual_generation') => {
        if (!user || !activeNotebook) return;
        const node = activeNotebook.nodes.find(n => n.id === nodeId);
        if (!node) return;
        showToast(language === 'vietnamese' ? 'Đang tạo câu hỏi...' : 'Generating review questions...', 'info');
        try {
            // For checked_note, pull the latest review session explanation + AI feedback
            const lastSession = origin === 'checked_note' && node.reviewSessions?.length
                ? node.reviewSessions[node.reviewSessions.length - 1]
                : undefined;
            const result = await generateReviewQuestions(
                nodeId, activeNotebook.id, node.title, activeNotebook.title,
                node.content, node.imageBase64 ?? null, node.imageMimeType ?? null,
                language, 4, origin,
                lastSession?.userExplanation,
                lastSession?.feedback
            );
            // Persist question count on the node so badge + suggestion strip update immediately
            updateNode(nodeId, { reviewQuestionCount: result.storedCount });
            showToast(
                language === 'vietnamese'
                    ? `Đã thêm ${result.storedCount} câu hỏi vào Review Feed!`
                    : `Added ${result.storedCount} questions to Review Feed!`,
                'success'
            );
        } catch (e) {
            showToast(language === 'vietnamese' ? 'Không thể tạo câu hỏi' : 'Failed to generate questions', 'error');
        }
    }, [user, activeNotebook, language]);

    const addChildNode = useCallback((parentId: string): string | null => {
        if (isEffectiveReadOnly || !activeNotebookId) return null;
        const pendingChildId = generateId();
        // §5.2.1: CREATE_NODE is its own command kind (not a generic structural
        // snapshot) so its undo captures the node's live payload at undo-time, not a
        // stale snapshot from now.
        pushHistoryEntry({ kind: 'create', nodeId: pendingChildId });
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;
            const parentNode = nb.nodes.find(n => n.id === parentId);
            if (!parentNode) return nb;

            // Calculate base position based on layout style
            let baseX = parentNode.position.x + 300;
            let baseY = parentNode.position.y;
            if (mapLayoutStyle === 'vertical') {
                baseX = parentNode.position.x;
                baseY = parentNode.position.y + 200;
            }

            // Find all existing children of this parent
            const existingChildren = nb.nodes.filter(n => n.parentId === parentId);
            
            // Smart collision detection and positioning
            const NODE_WIDTH = 320; // Approximate node width with padding
            const NODE_HEIGHT = 180; // Approximate node height with padding
            const VERTICAL_SPACING = 120; // Space between siblings vertically
            
            // Check if a position collides with existing nodes
            const hasCollision = (x: number, y: number, excludeId?: string) => {
                return nb.nodes.some(node => {
                    if (node.id === excludeId || node.id === parentId) return false;
                    const dx = Math.abs(node.position.x - x);
                    const dy = Math.abs(node.position.y - y);
                    return dx < NODE_WIDTH && dy < NODE_HEIGHT;
                });
            };

            // Try to find a good position
            let newX = baseX;
            let newY = baseY;

            // If this parent already has children, stack them vertically
            if (existingChildren.length > 0) {
                // Sort existing children by Y position
                const sortedChildren = [...existingChildren].sort((a, b) => a.position.y - b.position.y);
                
                if (mapLayoutStyle === 'vertical') {
                    // For vertical layout, place below the last child
                    const lastChild = sortedChildren[sortedChildren.length - 1];
                    newX = lastChild.position.x;
                    newY = lastChild.position.y + VERTICAL_SPACING + 80;
                } else {
                    // For horizontal layout, offset vertically from siblings
                    // Find the average Y and offset below
                    newY = baseY + (existingChildren.length * VERTICAL_SPACING);
                }
            } else {
                // First child, use base position but add small random offset
                newX += (Math.random() * 40 - 20);
                newY += (Math.random() * 40 - 20);
            }

            // If there's still a collision, try to find nearby free space
            let attempts = 0;
            const MAX_ATTEMPTS = 20;
            while (hasCollision(newX, newY) && attempts < MAX_ATTEMPTS) {
                // Try moving down first
                newY += VERTICAL_SPACING;
                attempts++;
                
                // If still colliding after many attempts, try moving horizontally too
                if (attempts > 10) {
                    newX += 50;
                }
            }

            const newNode: MindMapNode = {
                id: pendingChildId,
                parentId: parentId,
                position: { x: newX, y: newY },
                title: '',
                content: '',
                imageBase64: null,
                imageMimeType: null,
                isAnalyzing: false,
                analysis: null,
                isExpanded: true,
                createdAt: Date.now(),
                updatedAt: Date.now()
            };
            const newLink: NodeLink = { id: generateId(), fromId: parentId, toId: newNode.id, createdAt: Date.now() };
            
            // Create new nodes and links arrays
            const updatedNodes = [...nb.nodes, newNode];
            const updatedLinks = [...(nb.links || []), newLink];
            
            // If using a layout algorithm (not free mode), apply it immediately
            let finalNodes = updatedNodes;
            if (mapLayoutStyle !== 'free') {
                finalNodes = calculateLayout(updatedNodes, updatedLinks, mapLayoutStyle, nodeWidth);
            }
            
            setSelectedNodeId(newNode.id);
            
            // IMMEDIATE SAVE: Critical operation - prevent data loss
            setTimeout(() => {
                if (cloudSyncDebounceTimerRef.current) {
                    clearTimeout(cloudSyncDebounceTimerRef.current);
                }
                setHasUnsyncedChanges(true); // Trigger immediate save via useEffect
            }, 0);
            
            return { ...nb, nodes: finalNodes, links: updatedLinks, updatedAt: Date.now() };
        }));
        return pendingChildId;
    }, [activeNotebookId, mapLayoutStyle, isEffectiveReadOnly, nodeWidth, pushHistoryEntry]);

    const moveNode = useCallback((nodeId: string, direction: 'up' | 'down') => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        saveHistoryImmediate();
        
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;

            const node = nb.nodes.find(n => n.id === nodeId);
            if (!node || !node.parentId) return nb; // Can't reorder root nodes

            // Find all siblings (same parent)
            const allSiblings = nb.nodes.filter(n => n.parentId === node.parentId);
            if (allSiblings.length < 2) return nb; // No siblings to swap with

            // Sort siblings by siblingOrder (or array position if not set)
            const sortedSiblings = allSiblings.sort((a, b) => {
                const orderA = a.siblingOrder ?? nb.nodes.indexOf(a);
                const orderB = b.siblingOrder ?? nb.nodes.indexOf(b);
                return orderA - orderB;
            });

            // Find current node's index among sorted siblings
            const siblingIndex = sortedSiblings.findIndex(n => n.id === nodeId);
            
            // Determine target position
            let targetIndex = -1;
            if (direction === 'up' && siblingIndex > 0) {
                targetIndex = siblingIndex - 1;
            } else if (direction === 'down' && siblingIndex < sortedSiblings.length - 1) {
                targetIndex = siblingIndex + 1;
            }

            if (targetIndex === -1) return nb; // Already at boundary

            // Get the actual nodes to swap
            const currentNode = sortedSiblings[siblingIndex];
            const targetNode = sortedSiblings[targetIndex];
            
            // Find their indices in main array
            const currentNodeIndex = nb.nodes.findIndex(n => n.id === currentNode.id);
            const targetNodeIndex = nb.nodes.findIndex(n => n.id === targetNode.id);

            // Create new array and swap positions
            const newNodes = [...nb.nodes];
            newNodes[currentNodeIndex] = { ...targetNode, siblingOrder: siblingIndex };
            newNodes[targetNodeIndex] = { ...currentNode, siblingOrder: targetIndex };

            // Re-apply layout if not in free mode
            let finalNodes = newNodes;
            if (mapLayoutStyle !== 'free') {
                finalNodes = calculateLayout(newNodes, nb.links || [], mapLayoutStyle, nodeWidth);
            }

            return { ...nb, nodes: finalNodes, updatedAt: Date.now() };
        }));
        
        // DEBOUNCED SAVE: Reorder with longer wait to prevent write queue exhaustion
        // When saving to subcollections (1025+ nodes in 3 batches), immediate save
        // can exhaust Firestore write queue if user clicks reorder multiple times.
        // 10s debounce batches multiple rapid clicks into a single save.
        if (cloudSyncDebounceTimerRef.current) {
            clearTimeout(cloudSyncDebounceTimerRef.current);
        }
        
        cloudSyncDebounceTimerRef.current = setTimeout(() => {
            setHasUnsyncedChanges(true);
        }, 10000); // 10s debounce for reorder to batch rapid clicks
    }, [activeNotebookId, isEffectiveReadOnly, mapLayoutStyle, nodeWidth]);

    const focusNode = useCallback((nodeId: string) => {
        const node = activeNotebook?.nodes.find(n => n.id === nodeId);
        if (!node) return;
        flyToCanvasNode(node.position.x, node.position.y);
    }, [activeNotebook]); // eslint-disable-line react-hooks/exhaustive-deps

    const flyCancelRef = useRef<(() => void) | null>(null);

    const noteExpandedClosing = (id: string | null) => {
        if (!id || !activeNotebookId) return;
        const node = activeNotebook?.nodes.find(n => n.id === id);
        if (!node) return;
        setLastClosedNote({ id, notebookId: activeNotebookId, title: node.title || '' });
        // Moved away from the note first opened? Then the view still shows that one — follow along.
        if (id !== expandOriginRef.current) {
            setSelectedNodeId(id);
            setTimeout(() => {
                if (layoutMode === 'stream') handleJumpToNode(id);
                else if (layoutMode === 'flow') flowViewRef.current?.scrollToNode(id);
                else focusNode(id);
            }, 50);
        }
    };
    const reopenLastNote = () => {
        const last = lastClosedNote;
        if (!last) return;
        if (last.notebookId === activeNotebookId) { setSelectedNodeId(last.id); setExpandedNodeId(last.id); }
        else { setActiveNotebookId(last.notebookId); setPendingCrossNav({ notebookId: last.notebookId, nodeId: last.id }); }
        setLastClosedNote(null);
    };

    // Instant pan to node — no zoom animation, used for arrow key / adjacent navigation
    const snapToCanvasNode = useCallback((targetX: number, targetY: number) => {
        const innerCanvas = innerCanvasRef.current;
        const container = canvasRef.current;
        if (!innerCanvas || !container) return;
        if (flyCancelRef.current) { flyCancelRef.current(); flyCancelRef.current = null; }
        const s = scaleRef.current;
        const cx = container.clientWidth / 2;
        const cy = container.clientHeight / 2;
        const newPan = { x: cx - targetX * s, y: cy - targetY * s };
        panRef.current = newPan;
        innerCanvas.style.transform = `translate(${newPan.x}px, ${newPan.y}px) scale(${s})`;
        setPan(newPan);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const flyToCanvasNode = useCallback((targetX: number, targetY: number) => {
        const innerCanvas = innerCanvasRef.current;
        const container = canvasRef.current;
        if (!innerCanvas || !container) return;

        // Cancel any in-progress fly animation
        if (flyCancelRef.current) { flyCancelRef.current(); flyCancelRef.current = null; }

        const containerW = container.clientWidth;
        const containerH = container.clientHeight;
        const centerX = containerW / 2;
        const centerY = containerH / 2;

        const s0 = scaleRef.current;
        const p0 = { ...panRef.current };

        // World coords of current viewport center
        const worldCX = (centerX - p0.x) / s0;
        const worldCY = (centerY - p0.y) / s0;

        // Fly lower — zoom out to ~60% of current scale (less extreme = fewer corridor nodes)
        const sMid = Math.max(0.3, Math.min(s0 * 0.6, 0.5));
        const s1 = s0; // return to same scale

        // Pan positions for each phase
        const pOut  = { x: centerX - worldCX * sMid,  y: centerY - worldCY * sMid  };
        const pFly  = { x: centerX - targetX * sMid,  y: centerY - targetY * sMid  };
        const pIn   = { x: centerX - targetX * s1,    y: centerY - targetY * s1    };

        // Compute corridor bounds at sMid — nodes rendered as mini (title-only) bars
        const halfW = containerW / sMid / 2;
        const halfH = containerH / sMid / 2;
        flyCorridorBoundsRef.current = {
            minX: Math.min(worldCX, targetX) - halfW,
            maxX: Math.max(worldCX, targetX) + halfW,
            minY: Math.min(worldCY, targetY) - halfH,
            maxY: Math.max(worldCY, targetY) + halfH,
        };
        flyScaleRef.current = sMid;

        // Force synchronous React render BEFORE animation starts.
        // Corridor nodes mount as lightweight 'mini'/'dot' bars (no markdown, no images).
        // flushSync blocks ~5ms; after it returns the DOM is ready and RAF runs clean.
        flushSync(() => setIsFlying(true));

        const D_OUT = 420, D_PAN = 560, D_IN = 380;
        const TOTAL = D_OUT + D_PAN + D_IN;

        // Cubic easing — smoother, more cinematic than quadratic
        const easeOut  = (t: number) => 1 - (1 - t) ** 3;
        const easeIn   = (t: number) => t ** 3;
        const easeIO   = (t: number) => t < 0.5
            ? 4 * t ** 3
            : 1 - (-2 * t + 2) ** 3 / 2;

        let startTime = -1;
        let rafId: number;

        const apply = (pan: { x: number; y: number }, scale: number) => {
            innerCanvas.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${scale})`;
        };

        const lerp = (a: { x: number; y: number }, b: { x: number; y: number }, t: number) => ({
            x: a.x + (b.x - a.x) * t,
            y: a.y + (b.y - a.y) * t,
        });

        const tick = (now: number) => {
            if (startTime < 0) startTime = now;
            const e = now - startTime;

            if (e < D_OUT) {
                const t = easeOut(e / D_OUT);
                apply(lerp(p0, pOut, t), s0 + (sMid - s0) * t);
            } else if (e < D_OUT + D_PAN) {
                const t = easeIO((e - D_OUT) / D_PAN);
                apply(lerp(pOut, pFly, t), sMid);
            } else if (e < TOTAL) {
                const t = easeIn((e - D_OUT - D_PAN) / D_IN);
                apply(lerp(pFly, pIn, t), sMid + (s1 - sMid) * t);
            } else {
                panRef.current = pIn;
                scaleRef.current = s1;
                apply(pIn, s1);
                flyScaleRef.current = null;
                flyCorridorBoundsRef.current = null;
                setIsFlying(false);
                setPan(pIn);
                setScale(s1);
                flyCancelRef.current = null;
                return;
            }
            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
        flyCancelRef.current = () => {
            cancelAnimationFrame(rafId);
            flyScaleRef.current = null;
            flyCorridorBoundsRef.current = null;
            setIsFlying(false);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const applyOutlineOrder = useCallback((orderedNodes: MindMapNode[]) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        saveHistoryImmediate();

        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;

            const oldNodeById = new Map(nb.nodes.map(n => [n.id, n]));

            // Detect reparented nodes (parentId changed)
            const reparentedNodes = orderedNodes.filter(n => {
                const old = oldNodeById.get(n.id);
                return old && old.parentId !== n.parentId;
            });

            // Update links[]: canvas draws connections from links[], not from parentId alone
            // Remove old parent→child links, add new parent→child links for reparented nodes
            const newNodeById = new Map(orderedNodes.map(n => [n.id, n]));
            let updatedLinks = nb.links || [];
            if (reparentedNodes.length > 0) {
                const reparentedIds = new Set(reparentedNodes.map(n => n.id));
                updatedLinks = updatedLinks.filter(l => {
                    if (!reparentedIds.has(l.toId)) return true; // unrelated — keep
                    const oldN = oldNodeById.get(l.toId);
                    return !oldN || l.fromId !== oldN.parentId; // keep cross-links, remove old parent-child
                });
                const newLinks: NodeLink[] = reparentedNodes
                    .filter(n => n.parentId)
                    .map(n => ({ id: generateId(), fromId: n.parentId!, toId: n.id, createdAt: Date.now() }));
                updatedLinks = [...updatedLinks, ...newLinks];
            }
            // Ensure all correct parent-child links exist (fixes inconsistent Firestore state
            // where parentId was already updated by a previous op but links[] was not synced)
            const coveredToIds = new Set(
                updatedLinks.filter(l => {
                    const n = newNodeById.get(l.toId);
                    return n && n.parentId === l.fromId;
                }).map(l => l.toId)
            );
            const missingLinks: NodeLink[] = orderedNodes
                .filter(n => n.parentId && !coveredToIds.has(n.id))
                .map(n => ({ id: generateId(), fromId: n.parentId!, toId: n.id, createdAt: Date.now() }));
            if (missingLinks.length > 0) {
                updatedLinks = [...updatedLinks, ...missingLinks];
            }

            if (mapLayoutStyle !== 'free') {
                return { ...nb, nodes: calculateLayout(orderedNodes, updatedLinks, mapLayoutStyle, nodeWidth), links: updatedLinks, updatedAt: Date.now() };
            }

            // Free layout: each sibling group in new order inherits positions from old order
            const oldOrderByParent = new Map<string, string[]>();
            nb.nodes.forEach(n => {
                const pid = n.parentId ?? '__root__';
                if (!oldOrderByParent.has(pid)) oldOrderByParent.set(pid, []);
                oldOrderByParent.get(pid)!.push(n.id);
            });
            const newOrderByParent = new Map<string, string[]>();
            orderedNodes.forEach(n => {
                const pid = n.parentId ?? '__root__';
                if (!newOrderByParent.has(pid)) newOrderByParent.set(pid, []);
                newOrderByParent.get(pid)!.push(n.id);
            });
            // new node at index i gets old node's position at index i
            const positionMap = new Map<string, { x: number; y: number }>();
            oldOrderByParent.forEach((oldOrder, pid) => {
                const newOrder = newOrderByParent.get(pid) || [];
                oldOrder.forEach((oldId, i) => {
                    const newId = newOrder[i];
                    const oldPos = oldNodeById.get(oldId)?.position;
                    if (newId && oldPos) positionMap.set(newId, oldPos);
                });
            });

            // Reparented nodes: keep same relative offset from old parent, re-anchored to new parent
            orderedNodes.forEach(n => {
                const oldN = oldNodeById.get(n.id);
                if (!oldN || oldN.parentId === n.parentId) return;
                if (positionMap.has(n.id)) return;
                const oldParentPos = oldN.parentId ? oldNodeById.get(oldN.parentId)?.position : undefined;
                const newParentPos = n.parentId ? oldNodeById.get(n.parentId)?.position ?? { x: 0, y: 0 } : { x: 0, y: 0 };
                const relX = oldParentPos != null ? n.position.x - oldParentPos.x : 300;
                const relY = oldParentPos != null ? n.position.y - oldParentPos.y : 0;
                positionMap.set(n.id, { x: newParentPos.x + relX, y: newParentPos.y + relY });
            });

            const finalNodes = orderedNodes.map(n =>
                positionMap.has(n.id) ? { ...n, position: positionMap.get(n.id)! } : n
            );
            return { ...nb, nodes: finalNodes, links: updatedLinks, updatedAt: Date.now() };
        }));
    }, [activeNotebookId, isEffectiveReadOnly, mapLayoutStyle, nodeWidth]);

    const handleStartLink = useCallback((id: string) => {
        if (isEffectiveReadOnly) return;
        if (linkingSourceId === id) {
            setLinkingSourceId(null);
            setIsLinkSelectorOpen(false);
        } else if (linkingSourceId) {
        } else {
            setLinkingSourceId(id);
            if (layoutMode === 'stream') {
                setIsLinkSelectorOpen(true);
            }
        }
    }, [layoutMode, linkingSourceId, isEffectiveReadOnly]);

    const executeRelationshipLink = (type: 'cross' | 'child') => {
        if (!linkDecision || !activeNotebook) return;
        const { sourceId, targetId } = linkDecision;
        saveHistoryImmediate();

        if (type === 'child') {
            let isCycle = false;
            let curr = activeNotebook.nodes.find(n => n.id === targetId);
            while (curr && curr.parentId) {
                if (curr.parentId === sourceId) { isCycle = true; break; }
                curr = activeNotebook.nodes.find(n => n.id === curr!.parentId);
            }
            if (isCycle) {
                showToast(language === 'vietnamese' ? 'Không thể đặt làm con: Gây ra vòng lặp' : 'Cannot set as child: Cycle detected', 'error');
                setLinkDecision(null);
                return;
            }
            const otherLinks = (activeNotebook.links || []).filter(l => l.toId !== sourceId);
            const newLink: NodeLink = { id: generateId(), fromId: targetId, toId: sourceId, createdAt: Date.now() };
            const updatedNodes = activeNotebook.nodes.map(n => n.id === sourceId ? { ...n, parentId: targetId } : n);
            let finalNodes = updatedNodes;
            if (mapLayoutStyle !== 'free') { finalNodes = calculateLayout(updatedNodes, [...otherLinks, newLink], mapLayoutStyle, nodeWidth); }
            setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, nodes: finalNodes, links: [...otherLinks, newLink], updatedAt: Date.now() } : nb));
            
            // IMMEDIATE SAVE: Critical operation - prevent data loss
            if (cloudSyncDebounceTimerRef.current) {
                clearTimeout(cloudSyncDebounceTimerRef.current);
            }
            setHasUnsyncedChanges(true); // Trigger immediate save via useEffect
            
            showToast(language === 'vietnamese' ? 'Đã thiết lập quan hệ cha con' : 'Child relationship established', 'success');
        } else {
            const exists = activeNotebook.links?.some(l => (l.fromId === sourceId && l.toId === targetId) || (l.fromId === targetId && l.toId === sourceId));
            if (!exists) {
                const newLink: NodeLink = { id: generateId(), fromId: sourceId, toId: targetId, createdAt: Date.now() };
                updateNotebookLinks([...(activeNotebook.links || []), newLink]);
                
                // IMMEDIATE SAVE: Critical operation - prevent data loss
                if (cloudSyncDebounceTimerRef.current) {
                    clearTimeout(cloudSyncDebounceTimerRef.current);
                }
                setHasUnsyncedChanges(true); // Trigger immediate save via useEffect
                
                showToast(language === 'vietnamese' ? 'Đã tạo liên kết' : 'Link created', 'success');
            }
        }
        setLinkDecision(null);
    };

    const executeMergeNotes = () => {
        if (!linkDecision || !activeNotebook) return;
        const { sourceId, targetId } = linkDecision;
        
        // Find source and target nodes
        const sourceNode = activeNotebook.nodes.find(n => n.id === sourceId);
        const targetNode = activeNotebook.nodes.find(n => n.id === targetId);
        
        if (!sourceNode || !targetNode) {
            showToast(language === 'vietnamese' ? 'Không tìm thấy ghi chú' : 'Notes not found', 'error');
            setLinkDecision(null);
            return;
        }
        
        saveHistoryImmediate();
        
        // 1. Merge content (append source content to target with separator)
        const separator = targetNode.content && sourceNode.content ? '\n\n---\n\n' : '';
        const mergedContent = targetNode.content + separator + sourceNode.content;
        
        // 2. Merge images and attachments
        const mergedAttachments: NodeAttachment[] = [...(targetNode.attachments || [])];
        
        // If source has a main image, convert it to an attachment
        if (sourceNode.imageBase64) {
            mergedAttachments.push({
                id: generateId(),
                type: 'image',
                content: sourceNode.imageBase64,
                mimeType: sourceNode.imageMimeType || 'image/png',
                createdAt: Date.now()
            });
        }
        
        // Add all source attachments
        if (sourceNode.attachments && sourceNode.attachments.length > 0) {
            mergedAttachments.push(...sourceNode.attachments);
        }
        
        // 3. Redirect all cross-links pointing to/from source to target
        const updatedLinks = (activeNotebook.links || []).map(link => {
            // Skip links that will be removed (both from and to are sourceId)
            if (link.fromId === sourceId && link.toId === sourceId) {
                return null; // Will be filtered out
            }
            
            // Redirect links FROM source to FROM target
            if (link.fromId === sourceId) {
                // Don't create duplicate link if target already has this link
                const isDuplicate = (activeNotebook.links || []).some(l => 
                    l.fromId === targetId && l.toId === link.toId && l.id !== link.id
                );
                if (isDuplicate || link.toId === targetId) return null;
                return { ...link, fromId: targetId };
            }
            
            // Redirect links TO source to TO target
            if (link.toId === sourceId) {
                // Don't create duplicate link if target already has this link
                const isDuplicate = (activeNotebook.links || []).some(l => 
                    l.toId === targetId && l.fromId === link.fromId && l.id !== link.id
                );
                if (isDuplicate || link.fromId === targetId) return null;
                return { ...link, toId: targetId };
            }
            
            return link;
        }).filter(link => link !== null) as NodeLink[];
        
        // 4. Update target node with merged data; re-parent children of source to target
        const updatedNodes = activeNotebook.nodes.map(n => {
            if (n.id === targetId) {
                return { ...n, content: mergedContent, attachments: mergedAttachments };
            }
            if (n.parentId === sourceId) {
                return { ...n, parentId: targetId };
            }
            return n;
        }).filter(n => n.id !== sourceId); // Remove source node
        
        // 5. Update notebook
        setNotebooks(nbs => nbs.map(nb => 
            nb.id === activeNotebookId 
                ? { ...nb, nodes: updatedNodes, links: updatedLinks, updatedAt: Date.now() } 
                : nb
        ));
        
        // IMMEDIATE SAVE: Critical operation - prevent data loss
        if (cloudSyncDebounceTimerRef.current) {
            clearTimeout(cloudSyncDebounceTimerRef.current);
        }
        setHasUnsyncedChanges(true); // Trigger immediate save via useEffect
        
        showToast(
            language === 'vietnamese' 
                ? `Đã gộp "${sourceNode.title}" vào "${targetNode.title}"` 
                : `Merged "${sourceNode.title}" into "${targetNode.title}"`, 
            'success'
        );
        setLinkDecision(null);
    };

    const handleCreateCrossLink = useCallback((sourceId: string, targetId: string, targetNotebookId?: string, targetTitle?: string, targetNotebookTitle?: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;

        const nb = notebooks.find(n => n.id === activeNotebookId);
        if (!nb) return;

        // Check if link already exists
        const exists = nb.links?.some(l => {
            if (l.toNotebookId && targetNotebookId) {
                return l.toNotebookId === targetNotebookId && l.fromId === sourceId && l.toId === targetId;
            }
            if (!l.toNotebookId && !targetNotebookId) {
                return (l.fromId === sourceId && l.toId === targetId) || (l.fromId === targetId && l.toId === sourceId);
            }
            return false;
        });

        if (exists) {
            showToast(language === 'vietnamese' ? 'Liên kết đã tồn tại!' : 'Link already exists!', 'info');
            return;
        }

        saveHistoryImmediate();
        const newLink: NodeLink = {
            id: generateId(),
            fromId: sourceId,
            toId: targetId,
            createdAt: Date.now(),
            ...(targetNotebookId ? { toNotebookId: targetNotebookId, toTitle: targetTitle, toNotebookTitle: targetNotebookTitle } : {}),
        };

        updateNotebookLinks([...(nb.links || []), newLink]);

        // Create reverse link in target notebook (bidirectional cross-notebook link)
        if (targetNotebookId) {
            const targetNb = notebooks.find(n => n.id === targetNotebookId);
            if (targetNb) {
                const reverseExists = targetNb.links?.some(l =>
                    l.toNotebookId === activeNotebookId && l.fromId === targetId && l.toId === sourceId
                );
                if (!reverseExists) {
                    const sourceNodeTitle = nb.nodes.find(n => n.id === sourceId)?.title || '';
                    const reverseLink: NodeLink = {
                        id: generateId(),
                        fromId: targetId,
                        toId: sourceId,
                        createdAt: Date.now(),
                        toNotebookId: activeNotebookId,
                        toTitle: sourceNodeTitle,
                        toNotebookTitle: nb.title,
                    };
                    const updatedTargetLinks = [...(targetNb.links || []), reverseLink];
                    setNotebooks(nbs => nbs.map(n => n.id === targetNotebookId ? { ...n, links: updatedTargetLinks, updatedAt: Date.now() } : n));
                    if (user) {
                        const targetNbRef = doc(db, 'users', user.id, 'notebooks', targetNotebookId);
                        updateDoc(targetNbRef, { links: updatedTargetLinks, updatedAt: Date.now() }).catch(err => console.error('reverse cross-link save failed:', err));
                    }
                }
            }
        }

        showToast(language === 'vietnamese' ? 'Đã tạo liên kết!' : 'Link created!', 'success');
        posthog.capture('cross_link_created', { cross_notebook: !!targetNotebookId });
    }, [activeNotebookId, notebooks, isEffectiveReadOnly, language]);

    // A link started from the outline ("Start a link from this note") waits here until another
    // note is right-clicked → "Link here". Kept above the panels so closing one doesn't lose it;
    // dropped when the notebook changes or the starting note is gone.
    const [pendingOutlineLink, setPendingOutlineLink] = useState<{ id: string; title: string; notebookId: string } | null>(null);
    useEffect(() => {
        if (!pendingOutlineLink) return;
        const nb = notebooks.find(n => n.id === pendingOutlineLink.notebookId);
        if (pendingOutlineLink.notebookId !== activeNotebookId || (nb && !nb.nodes.some(n => n.id === pendingOutlineLink.id))) setPendingOutlineLink(null);
    }, [pendingOutlineLink, activeNotebookId, notebooks]);
    const onPendingOutlineLinkChange = useCallback((p: { id: string; title: string } | null) => {
        setPendingOutlineLink(p && activeNotebookId ? { ...p, notebookId: activeNotebookId } : null);
    }, [activeNotebookId]);
    const outlineLinkProps = isEffectiveReadOnly ? {} : {
        pendingLink: pendingOutlineLink,
        onPendingLinkChange: onPendingOutlineLinkChange,
        onLinkNotes: handleCreateCrossLink,
    };

    // Attaches a reference to a video that lives on a DIFFERENT node (possibly a different
    // notebook) — e.g. "I already explained this in an earlier video". Stored on the source node
    // itself (not notebook-level links[]), since it points at a specific video, not just a node.
    const handleAddRelatedVideo = useCallback((sourceNodeId: string, ref: Omit<RelatedVideoRef, 'id' | 'createdAt'>) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const sourceNode = nb?.nodes.find(n => n.id === sourceNodeId);
        if (!sourceNode) return;
        const exists = sourceNode.relatedVideoRefs?.some(r => r.nodeId === ref.nodeId && r.videoId === ref.videoId);
        if (exists) {
            showToast(language === 'vietnamese' ? 'Video này đã được gắn rồi!' : 'This video is already attached!', 'info');
            return;
        }
        saveHistoryImmediate();
        const newRef: RelatedVideoRef = { ...ref, id: generateId(), createdAt: Date.now() };
        updateNode(sourceNodeId, { relatedVideoRefs: [...(sourceNode.relatedVideoRefs || []), newRef] });
        showToast(language === 'vietnamese' ? 'Đã gắn video liên quan!' : 'Related video attached!', 'success');
    }, [activeNotebookId, isEffectiveReadOnly, language]);

    const handleRemoveRelatedVideo = useCallback((sourceNodeId: string, refId: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const sourceNode = nb?.nodes.find(n => n.id === sourceNodeId);
        if (!sourceNode) return;
        updateNode(sourceNodeId, { relatedVideoRefs: (sourceNode.relatedVideoRefs || []).filter(r => r.id !== refId) });
    }, [activeNotebookId, isEffectiveReadOnly]);

    const handleDuplicateNote = useCallback((nodeId: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const node = nb?.nodes.find(n => n.id === nodeId);
        if (!node) return;
        saveHistoryImmediate();
        const now = Date.now();
        const newNode: MindMapNode = {
            id: generateId(),
            parentId: node.parentId,
            position: { x: node.position.x + 340, y: node.position.y + 60 },
            title: node.title ? `${node.title} (bản sao)` : '',
            content: node.content,
            imageBase64: node.imageBase64,
            imageMimeType: node.imageMimeType,
            imageHeight: node.imageHeight,
            drawingBase64: node.drawingBase64,
            attachments: node.attachments ? node.attachments.map(a => ({ ...a, id: generateId() })) : undefined,
            analysis: null,
            isAnalyzing: false,
            isExpanded: node.isExpanded,
            tags: node.tags ? [...node.tags] : undefined,
            nodeType: node.nodeType,
            titleSource: node.titleSource,
            createdAt: now,
            updatedAt: now,
        };
        setNotebooks(nbs => nbs.map(nb =>
            nb.id === activeNotebookId ? { ...nb, nodes: [...nb.nodes, newNode], updatedAt: Date.now() } : nb
        ));
        showToast(language === 'vietnamese' ? 'Đã nhân đôi note!' : 'Note duplicated!', 'success');
        posthog.capture('note_duplicated');
    }, [activeNotebookId, isEffectiveReadOnly, language]);

    const handleDuplicateBranch = useCallback((rootNodeId: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        if (!nb) return;
        saveHistoryImmediate();
        // Collect all descendant IDs via BFS
        const subtreeIds: string[] = [];
        const queue = [rootNodeId];
        while (queue.length) {
            const id = queue.shift()!;
            subtreeIds.push(id);
            nb.nodes.filter(n => n.parentId === id).forEach(child => queue.push(child.id));
        }
        const idMap = new Map<string, string>();
        subtreeIds.forEach(id => idMap.set(id, generateId()));
        const rootNode = nb.nodes.find(n => n.id === rootNodeId)!;
        const branchNow = Date.now();
        const newNodes: MindMapNode[] = subtreeIds.flatMap(id => {
            const node = nb.nodes.find(n => n.id === id);
            if (!node) return [];
            const isRoot = id === rootNodeId;
            return {
                id: idMap.get(id)!,
                parentId: isRoot ? node.parentId : idMap.get(node.parentId!),
                position: { x: node.position.x + 340, y: node.position.y + 60 },
                title: isRoot ? (node.title ? `${node.title} (bản sao)` : '') : node.title,
                content: node.content,
                imageBase64: node.imageBase64,
                imageMimeType: node.imageMimeType,
                imageHeight: node.imageHeight,
                drawingBase64: node.drawingBase64,
                attachments: node.attachments ? node.attachments.map(a => ({ ...a, id: generateId() })) : undefined,
                analysis: null,
                isAnalyzing: false,
                isExpanded: node.isExpanded,
                tags: node.tags ? [...node.tags] : undefined,
                nodeType: node.nodeType,
                titleSource: node.titleSource,
                createdAt: branchNow,
                updatedAt: branchNow,
            };
        });
        // Internal links (parent-child and cross-links within subtree)
        const existingLinks = nb.links || [];
        const internalNewLinks: NodeLink[] = existingLinks
            .filter(l => subtreeIds.includes(l.fromId) && subtreeIds.includes(l.toId))
            .map(l => ({ id: generateId(), fromId: idMap.get(l.fromId)!, toId: idMap.get(l.toId)!, createdAt: Date.now() }));
        // Link from original parent to new root (if root has a parent)
        const parentLink: NodeLink[] = rootNode.parentId
            ? [{ id: generateId(), fromId: rootNode.parentId, toId: idMap.get(rootNodeId)!, createdAt: Date.now() }]
            : [];
        setNotebooks(nbs => nbs.map(nb =>
            nb.id === activeNotebookId
                ? { ...nb, nodes: [...nb.nodes, ...newNodes], links: [...existingLinks, ...parentLink, ...internalNewLinks], updatedAt: Date.now() }
                : nb
        ));
        showToast(
            language === 'vietnamese'
                ? `Đã nhân đôi nhánh (${subtreeIds.length} notes)!`
                : `Branch duplicated (${subtreeIds.length} notes)!`,
            'success'
        );
        posthog.capture('branch_duplicated', { node_count: subtreeIds.length });
    }, [activeNotebookId, isEffectiveReadOnly, language]);

    const handleGetFramework = useCallback(async (id: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const node = nb?.nodes.find(n => n.id === id);
        if (!node) return;
        updateNode(id, { isGeneratingQuestions: true });
        try {
            const questions = await generateStudyQuestions(node.imageBase64, node.imageMimeType, language, node.attachments);
            updateNode(id, { suggestedQuestions: questions, isGeneratingQuestions: false });
        } catch (error) { updateNode(id, { isGeneratingQuestions: false }); }
    }, [activeNotebookId, language, updateNode, isEffectiveReadOnly]);

    const magicExpand = useCallback(async (id: string) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
        const node = nb?.nodes.find(n => n.id === id);
        if (!node) return;
        setMagicExpandingNodeId(id);
        posthog.capture('ai_magic_expand');
        try {
            const subTopics = await generateSubTopics(node.title, node.content, language);
            const newNodes: MindMapNode[] = []; const newLinks: NodeLink[] = [];
            const angleStep = (Math.PI / 2) / (subTopics.length + 1);
            subTopics.forEach((topic, idx) => {
                const angle = (Math.PI / 4) + ((idx + 1) * angleStep); const distance = 350;
                const newNode: MindMapNode = {
                    id: generateId(), parentId: id, position: { x: node.position.x + (Math.cos(angle) * distance), y: node.position.y + (Math.sin(angle) * distance) }, title: topic, content: '', imageBase64: null, imageMimeType: null, isAnalyzing: false, analysis: null, isExpanded: true, createdAt: Date.now(), updatedAt: Date.now()
                };
                newNodes.push(newNode);
                newLinks.push({ id: generateId(), fromId: id, toId: newNode.id, createdAt: Date.now() });
            });
            setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, nodes: [...nb.nodes, ...newNodes], links: [...(nb.links || []), ...newLinks], updatedAt: Date.now() } : nb));
        } catch (error) { console.error("Magic Expand Failed", error); } finally { setMagicExpandingNodeId(null); }
    }, [activeNotebookId, language, isEffectiveReadOnly]);

    const toggleQuizMode = () => {
        if (isQuizConfigMode) {
            setIsQuizConfigMode(false);
            setQuizSelectedNodeIds(new Set());
        } else {
            setIsQuizConfigMode(true);
        }
    };

    const handleQuizSelectNode = useCallback((id: string) => {
        setQuizSelectedNodeIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleGuideSelectNode = useCallback((id: string) => {
        setGuideSelectedNodeIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const enterGuideSelectMode = useCallback(() => {
        setGuideSelectedNodeIds(new Set());
        setIsGuideSelectMode(true);
    }, []);

    const confirmGuideSelection = useCallback(() => {
        const ids = Array.from(guideSelectedNodeIds);
        setGuideInitialIds(ids);
        setIsGuideSelectMode(false);
        setShowGuideBuilder(true);
    }, [guideSelectedNodeIds]);

    const handleGenerateQuiz = async (selectedNodeIds?: string[], questionCount?: number) => {
        if (!activeNotebook) return;
        const targetIds = selectedNodeIds || Array.from(quizSelectedNodeIds);
        const targetCount = questionCount || quizQuestionCount;

        const selectedNodes = activeNotebook.nodes.filter(n => targetIds.includes(n.id));
        if (selectedNodes.length === 0) { alert(language === 'vietnamese' ? "Vui lòng chọn ít nhất một ghi chú." : "Please select at least one note."); return; }

        setQuizQuestionCount(targetCount);
        setIsGeneratingQuiz(true);
        try {
            const generatedQuiz = await generateQuizFromNodes(selectedNodes, targetCount, language);
            setQuiz(generatedQuiz);
            posthog.capture('quiz_generated', { question_count: targetCount, node_count: selectedNodes.length });
            setIsQuizConfigMode(false);
        } catch (error) {
            alert(language === 'vietnamese' ? 'Không thể tạo câu hỏi. Thử thêm nội dung vào ghi chú.' : 'Failed to generate quiz. Try adding more content to your notes.');
        } finally { setIsGeneratingQuiz(false); }
    };

    const handleSetNodeWidth = (width: NodeWidth) => {
        setNodeWidth(width); setWidthDropdownOpen(false);
        if (activeNotebookId && !isEffectiveReadOnly) {
            saveHistoryImmediate();
            setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, nodeWidth: width, updatedAt: Date.now() } : nb));
        }
    };

    const handleUploadPdf = useCallback(async (file: File) => {
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        const existingNodes = activeNotebook?.nodes || [];
        const existingLinks = activeNotebook?.links || [];
        const notebookWasEmpty = existingNodes.length === 0;
        if (activeNotebookId) {
            setNotebooks(nbs => nbs.map(nb => nb.id === activeNotebookId ? { ...nb, pdfContextUrl: url, updatedAt: Date.now() } : nb));
        }
        // Create root note immediately with filename as placeholder title
        const filename = file.name.replace(/\.pdf$/i, '');
        const rootNoteId = generateId();
        const rootNote: MindMapNode = {
            id: rootNoteId,
            position: { x: 0, y: 0 },
            title: filename,
            content: language === 'vietnamese' ? 'Đang phân tích tài liệu...' : 'Analyzing document...',
            imageBase64: null,
            imageMimeType: null,
            isAnalyzing: false,
            analysis: null,
            isExpanded: true,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };
        const newNodes = notebookWasEmpty ? [rootNote] : [...existingNodes, rootNote];
        // Explicit safety snapshot before any node write when notebook has content
        if (!notebookWasEmpty && user && activeNotebookId) {
            await saveSnapshot(user.id, activeNotebookId, existingNodes, existingLinks, 'before_pdf_upload');
        }
        // skipSnapshotGuard: explicit snapshot already created above
        updateNotebookNodes(newNodes);
        pendingRootNoteIdRef.current = rootNoteId;
        pendingPdfFilenameRef.current = filename;
        if (notebookWasEmpty) setShowFirstCaptureNudge(true);
    }, [activeNotebookId, activeNotebook?.nodes, activeNotebook?.links, language, user, updateNotebookNodes]);

    const handleDocumentReady = useCallback(async (utils: import('./components/PdfViewer').PdfDocumentUtils) => {
        pdfUtilsRef.current = utils;
        const rootNoteId = pendingRootNoteIdRef.current;
        if (!rootNoteId) return;
        pendingRootNoteIdRef.current = null; // consume — prevent double-fire
        try {
            const [pageText, thumbnail] = await Promise.all([
                utils.getPageText(1),
                utils.getPageThumbnail(1),
            ]);
            // Apply thumbnail immediately
            updateNode(rootNoteId, { imageBase64: thumbnail, imageMimeType: 'image/jpeg' });
            // Call AI for title
            const pdfFilename = pendingPdfFilenameRef.current;
            const result = await callIdentifyDocument(pageText, pdfFilename, language);
            const guidanceContent = language === 'vietnamese'
                ? 'Bắt đầu bằng cách tạo nhánh đầu tiên cho chương bạn đang đọc. Rồi dùng "Gợi ý đọc" hoặc Capture để tạo note con — viết lại bằng lời của bạn để hiểu thật sự.'
                : 'Start by creating your first branch for the chapter you\'re reading. Then use "Guided Reading" or Capture to add child notes — write in your own words to truly understand.';
            updateNode(rootNoteId, {
                title: result.title,
                content: guidanceContent,
            });
        } catch (e) {
            // Graceful: keep filename title, show guidance
            const guidanceContent = language === 'vietnamese'
                ? 'Bắt đầu bằng cách tạo nhánh đầu tiên cho chương bạn đang đọc. Rồi dùng "Gợi ý đọc" hoặc Capture để tạo note con — viết lại bằng lời của bạn để hiểu thật sự.'
                : 'Start by creating your first branch for the chapter you\'re reading. Then use "Guided Reading" or Capture to add child notes — write in your own words to truly understand.';
            updateNode(rootNoteId, { content: guidanceContent });
        }
    }, [language, activeNotebook?.pdfContextUrl, updateNode]);

    const handleSavePdfAnnotations = (pageNumber: number, strokes: Stroke[]) => {
        if (isEffectiveReadOnly || !activeNotebookId) return;
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== activeNotebookId) return nb;
            const newAnnotations = { ...(nb.pdfAnnotations || {}) }; newAnnotations[pageNumber] = strokes;
            return { ...nb, pdfAnnotations: newAnnotations, updatedAt: Date.now() };
        }));
    };

    const startRecording = async () => {
        // Capture which note the user is viewing when recording starts
        setRecordingTargetNodeId(expandedNodeId || selectedNodeId || null);
        try {
            const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30, width: { ideal: 1920 }, height: { ideal: 1080 } } as any, audio: true });

            // Mix tab audio + mic into one audio track
            let micStream: MediaStream | null = null;
            try { micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false }); } catch { /* mic optional */ }

            const audioCtx = new AudioContext();
            const dest = audioCtx.createMediaStreamDestination();
            const displayAudio = displayStream.getAudioTracks();
            if (displayAudio.length > 0) {
                audioCtx.createMediaStreamSource(new MediaStream(displayAudio)).connect(dest);
            }
            if (micStream) {
                audioCtx.createMediaStreamSource(micStream).connect(dest);
            }

            const combined = new MediaStream([...displayStream.getVideoTracks(), ...dest.stream.getTracks()]);
            recordedChunksRef.current = [];
            const mimeType = MediaRecorder.isTypeSupported('video/webm; codecs=vp9') ? 'video/webm; codecs=vp9' : 'video/webm';
            const mr = new MediaRecorder(combined, { mimeType });
            mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
            mr.onstop = () => {
                displayStream.getTracks().forEach(t => t.stop());
                micStream?.getTracks().forEach(t => t.stop());
                audioCtx.close();
                const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
                const file = new File([blob], `studyboard-${new Date().toISOString().slice(0, 10)}.webm`, { type: 'video/webm' });
                setPendingRecordingFile(file);
                setIsRecording(false);
                setIsRecordingPaused(false);
                setRecordingSeconds(0);
                if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
            };
            mr.start(1000);
            mediaRecorderRef.current = mr;
            setIsRecording(true);
            setIsRecordingPaused(false);
            setRecordingSeconds(0);
            recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
            displayStream.getVideoTracks()[0].onended = () => stopRecording();
        } catch (e) {
            console.error('Screen recording failed', e);
        }
    };

    const pauseRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.pause();
            setIsRecordingPaused(true);
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
        }
    };

    const resumeRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
            mediaRecorderRef.current.resume();
            setIsRecordingPaused(false);
            recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
    };

    const handleSnapshotAction = (mode: 'add' | 'create', base64Override?: string) => {
        const snapshot = base64Override ?? pendingSnapshot;
        if (!snapshot || !activeNotebookId) { setPendingSnapshot(null); setPendingSnapshotText(''); return; }
        const snapshotText = pendingSnapshotText;
        saveHistoryImmediate();
        let resolvedNodeId: string | null = null;
        if (mode === 'add' && selectedNodeId) {
            resolvedNodeId = selectedNodeId;
            const nb = notebooksRef.current.find(n => n.id === activeNotebookId);
            const node = nb?.nodes.find(n => n.id === selectedNodeId);
            if (node && !node.imageBase64) { updateNode(selectedNodeId, { imageBase64: snapshot, imageMimeType: 'image/png' }); } else if (node) {
                const newAtt = { id: generateId(), type: 'image' as const, content: snapshot, mimeType: 'image/png', createdAt: Date.now() };
                updateNode(selectedNodeId, { attachments: [...(node.attachments || []), newAtt] });
            }
            if (snapshotText) {
                updateNode(selectedNodeId, { sourceExtractedText: snapshotText });
            }
            extractImageHashtags([{ base64: snapshot, mimeType: 'image/png' }], language).then(({ tags: newTags, title, hint }) => {
                const cur = notebooksRef.current.find(n => n.id === activeNotebookId)?.nodes.find(n => n.id === selectedNodeId);
                const updates: Record<string, unknown> = {};
                if (newTags.length > 0) updates.tags = [...(cur?.tags || []), ...newTags];
                const curTitle = cur?.title?.trim() ?? '';
                const isUntitled = curTitle === '' || curTitle === 'Tiêu đề...' || curTitle.toLowerCase() === 'untitled';
                if (title && isUntitled && cur?.titleSource !== 'user') {
                    updates.title = title;
                    updates.titleSource = 'ai';
                }
                if (hint && !cur?.writingHint) updates.writingHint = hint;
                if (Object.keys(updates).length > 0) updateNode(selectedNodeId, updates);
            }).catch(() => {});
        } else {
            // Always create as child: prefer selected node, fallback to root note
            const nodes = notebooksRef.current.find(n => n.id === activeNotebookId)?.nodes || [];
            const parentId = selectedNodeId || nodes.find(n => !n.parentId)?.id || null;
            let newNodeId: string | null = null;
            if (parentId) {
                newNodeId = addChildNode(parentId);
                if (newNodeId) updateNode(newNodeId, { imageBase64: snapshot, imageMimeType: 'image/png', title: 'Snapshot', ...(snapshotText ? { sourceExtractedText: snapshotText } : {}) });
            } else {
                // Empty canvas fallback — standalone
                const newNode: MindMapNode = { id: generateId(), position: { x: (-pan.x + window.innerWidth / 2) / scale, y: (-pan.y + window.innerHeight / 2) / scale }, title: 'Snapshot', content: '', sourceExtractedText: snapshotText || undefined, imageBase64: snapshot, imageMimeType: 'image/png', isAnalyzing: false, analysis: null, isExpanded: true, createdAt: Date.now(), updatedAt: Date.now() };
                updateNotebookNodes([...nodes, newNode]);
                newNodeId = newNode.id;
            }
            if (newNodeId) {
                resolvedNodeId = newNodeId;
                setSelectedNodeId(newNodeId);
                const id = newNodeId;
                extractImageHashtags([{ base64: snapshot, mimeType: 'image/png' }], language).then(({ tags: newTags, title, hint }) => {
                    const updates: Record<string, unknown> = {};
                    if (newTags.length > 0) updates.tags = newTags;
                    if (title) { updates.title = title; updates.titleSource = 'ai'; }
                    if (hint) updates.writingHint = hint;
                    if (Object.keys(updates).length > 0) updateNode(id, updates);
                }).catch(() => {});
            }
        }
        setPendingSnapshot(null);
        setPendingSnapshotText('');

        // Mobile: close PDF, open note in expanded modal, return to PDF on close
        if (isMobile && resolvedNodeId) {
            setMediaPanelOpen(false);
            setPdfReturnPending(true);
            setExpandedNodeIdStable(resolvedNodeId);
        }
    };

    const handleVoiceCapture = (imageBase64: string, transcript: string) => {
        if (!activeNotebookId) return;
        saveHistoryImmediate();
        const nodes = notebooksRef.current.find(n => n.id === activeNotebookId)?.nodes || [];
        const parentId = selectedNodeId || nodes.find(n => !n.parentId)?.id || null;
        const title = language === 'vietnamese' ? 'Ghi chú giọng nói' : 'Voice Note';
        let newNodeId: string | null = null;

        if (parentId) {
            newNodeId = addChildNode(parentId);
            if (newNodeId) updateNode(newNodeId, { imageBase64, imageMimeType: 'image/png', title, content: transcript });
        } else {
            const newNode: MindMapNode = {
                id: generateId(), position: { x: 0, y: 0 },
                title, content: transcript, imageBase64, imageMimeType: 'image/png',
                isAnalyzing: false, analysis: null, isExpanded: true,
                createdAt: Date.now(), updatedAt: Date.now(),
            };
            updateNotebookNodes([...nodes, newNode]);
            newNodeId = newNode.id;
        }

        showToast(language === 'vietnamese' ? 'Đã tạo ghi chú' : 'Note created', 'success');

        if (newNodeId) {
            const id = newNodeId;
            setSelectedNodeId(id);
            extractImageHashtags([{ base64: imageBase64, mimeType: 'image/png' }], language).then(({ tags, title: aiTitle, hint }) => {
                const updates: Record<string, unknown> = {};
                if (tags.length > 0) updates.tags = tags;
                if (aiTitle) { updates.title = aiTitle; updates.titleSource = 'ai'; }
                if (hint) updates.writingHint = hint;
                if (Object.keys(updates).length > 0) updateNode(id, updates);
            }).catch(() => {});
        }
    };

    const handleOpenAskAI = () => {
        if (!pendingSnapshot) return;
        // Build learning context to enrich the AI request
        const pdfUrl = activeNotebook?.pdfContextUrl || '';
        let pdfName: string | undefined;
        try {
            const match = pdfUrl.match(/\/o\/(.+?)\?/);
            if (match) pdfName = decodeURIComponent(match[1]).split('/').pop();
            else if (pdfUrl && !pdfUrl.startsWith('blob:')) pdfName = pdfUrl.split('/').pop()?.split('?')[0];
        } catch {}
        const selectedNode = activeNotebook?.nodes.find(n => n.id === selectedNodeId);
        const recentNotes = activeNotebook?.nodes
            .filter(n => n.id !== selectedNodeId && n.title && n.title !== 'Snapshot' && n.content)
            .slice(-4)
            .map(n => n.title)
            .reverse();
        setAskAIContext({
            boardTitle: activeNotebook?.title,
            pdfName,
            pageNumber: pdfCurrentPage,
            selectedNote: selectedNode ? { title: selectedNode.title ?? '', content: (selectedNode.content ?? '').slice(0, 300) } : undefined,
            recentNotesTitles: recentNotes,
        });
        setAskAISnapshot(pendingSnapshot);
        setPendingSnapshot(null);
        setAskAIOpen(true);
    };

    const handleGuidedReading = async (pageText: string, pageNumber: number) => {
        if (guidedReadingLoading) return;
        setGuidedReadingLoading(true);
        setGuidedReadingData(null);
        setGuidedReadingHighlights([]);
        try {
            const selectedNode = activeNotebook?.nodes.find(n => n.id === selectedNodeId);
            const result = await callGuidedReading(
                pageText,
                pageNumber,
                language,
                activeNotebook?.title,
                activeNotebook?.title,
                selectedNode?.title,
            );
            setGuidedReadingData(result);
            if (result.readingSegments.length > 0) {
                // Init highlights: all segments' keyHighlights with per-segment color
                const items = result.readingSegments.flatMap(seg =>
                    seg.keyHighlights.map(text => ({ text, colorIndex: (seg.order - 1) % 4 }))
                );
                setGuidedReadingHighlights(items);
                setGuidedReadingHighlightsVisible(true);
            }
        } catch (e: any) {
            const code = e?.code || e?.message || '';
            if (code.includes('resource-exhausted')) {
                alert(language === 'vietnamese' ? 'Đã đạt giới hạn AI hôm nay.' : 'Daily AI limit reached.');
            }
        } finally {
            setGuidedReadingLoading(false);
        }
    };

    const handleCreateNoteFromGuided = async (title: string, _prompt: string, segment?: ReadingSegment) => {
        const nodes = notebooksRef.current.find(n => n.id === activeNotebookId)?.nodes || [];
        const parentId = selectedNodeId || nodes.find(n => !n.parentId)?.id || null;
        const isVi = language === 'vietnamese';

        // Capture segment crop (falls back to full page if text match fails)
        let imageBase64: string | null = null;
        let imageMimeType: string | null = null;
        try {
            if (pdfUtilsRef.current && segment) {
                imageBase64 = await pdfUtilsRef.current.captureSegment(
                    pdfCurrentPage,
                    segment.startText,
                    segment.endText,
                    segment.keyHighlights,
                    (segment.order - 1) % 4,
                );
                imageMimeType = 'image/jpeg';
            } else if (pdfUtilsRef.current) {
                imageBase64 = await pdfUtilsRef.current.captureCurrentPage(pdfCurrentPage);
                imageMimeType = 'image/jpeg';
            }
        } catch {}

        // Build note content — guidance card (blockquote) + writing area
        const pageRef = segment
            ? `📄 Trang ${pdfCurrentPage} — ${segment.segmentLabel}`
            : `📄 Trang ${pdfCurrentPage}`;

        let content: string;
        if (segment) {
            const cardHeader = isVi ? '💡 **Gợi ý viết note**' : '💡 **Writing guide**';
            const ifStuck = isVi ? '**Nếu bí, hãy trả lời:**' : '**If stuck, answer these:**';
            const qLines = segment.guidingQuestions.map((q, i) => `> ${i + 1}. ${q}`).join('\n');
            const footer = isVi ? 'Viết giải thích của bạn ở đây...' : 'Write your explanation here...';
            content = [
                pageRef,
                '',
                `> ${cardHeader}`,
                `> ${segment.writingTask}`,
                `> ${ifStuck}`,
                qLines,
                '',
                '---',
                footer,
            ].join('\n');
        } else {
            const footer = isVi ? '---\nViết giải thích của bạn ở đây...' : '---\nWrite your explanation here...';
            content = `${pageRef}\n\n${footer}`;
        }

        let newId: string | null = null;
        if (parentId) {
            newId = addChildNode(parentId);
        } else {
            const newNode = { id: generateId(), position: { x: (-pan.x + window.innerWidth / 2) / scale, y: (-pan.y + window.innerHeight / 2) / scale }, title, content, imageBase64, imageMimeType, isAnalyzing: false, analysis: null, isExpanded: true };
            updateNotebookNodes([...nodes, newNode]);
            newId = newNode.id;
        }
        if (newId) {
            updateNode(newId, { title, content, imageBase64, imageMimeType });
            setSelectedNodeId(newId);
        }
    };

    const handleExportBackup = async () => {
        if (notebooks.length === 0) return;
        setSettingsOpen(false); // Close settings modal
        setIsExportModalOpen(true); // Open export modal
    };

    const executeExportBackup = async (selectedNotebooks: Notebook[]) => {
        if (selectedNotebooks.length === 0) return;
        const controller = new AbortController();
        exportAbortControllerRef.current = controller;
        setExportTask({ isActive: true, progress: 0, status: 'processing' });
        try {
            for (let i = 0; i < selectedNotebooks.length; i++) {
                if (controller.signal.aborted) break;
                let nb = selectedNotebooks[i];
                // If this notebook was evicted, reload its nodes before exporting
                if (nb._isPartiallyLoaded && user && user.id !== 'guest') {
                    try {
                        const nodes = await loadNotebookNodes(user.id, nb.id);
                        nb = { ...nb, nodes, _isPartiallyLoaded: false };
                    } catch (loadErr) {
                        console.error(`[BACKUP] Failed to load nodes for "${nb.title}":`, loadErr);
                        showToast(language === 'vietnamese' ? `Không thể tải "${nb.title}"` : `Could not load "${nb.title}"`, 'error');
                        continue;
                    }
                }
                const baseProgress = (i / selectedNotebooks.length) * 100;
                await backupService.exportSingleZip(nb, (p) => {
                    const overallProgress = baseProgress + (p / selectedNotebooks.length);
                    setExportTask(prev => ({ ...prev, progress: Math.round(overallProgress) }));
                }, controller.signal);
                if (i < selectedNotebooks.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
            if (controller.signal.aborted) {
                setExportTask(prev => ({ ...prev, status: 'cancelled' }));
                showToast(language === 'vietnamese' ? 'Đã hủy xuất file' : 'Export cancelled', 'error');
                setTimeout(() => setExportTask(prev => ({ ...prev, isActive: false })), 2000);
            } else {
                setExportTask(prev => ({ ...prev, status: 'success', progress: 100 }));
                showToast(language === 'vietnamese' ? 'Đã xuất file backup' : 'Backup exported', 'success');
                setTimeout(() => setExportTask(prev => ({ ...prev, isActive: false })), 3000);
            }
        } catch (err: any) {
            if (err?.name === 'AbortError') {
                setExportTask(prev => ({ ...prev, status: 'cancelled' }));
                showToast(language === 'vietnamese' ? 'Đã hủy xuất file' : 'Export cancelled', 'error');
                setTimeout(() => setExportTask(prev => ({ ...prev, isActive: false })), 2000);
            } else {
                console.error(err);
                setExportTask(prev => ({ ...prev, status: 'error' }));
                showToast(language === 'vietnamese' ? 'Lỗi xuất file' : 'Export failed', 'error');
                setTimeout(() => setExportTask(prev => ({ ...prev, isActive: false })), 5000);
            }
        } finally {
            exportAbortControllerRef.current = null;
        }
    };

    const handleImportBackup = async (file: File) => {
        if (!file) return;
        setSettingsOpen(false);
        setImportTask({ isActive: true, progress: 0, fileName: file.name, status: 'processing' });
        try {
            const importedNotebooks = await backupService.importFromZip(file, (p) => {
                setImportTask(prev => ({ ...prev, progress: p }));
            });
            if (importedNotebooks && importedNotebooks.length > 0) {
                saveHistoryImmediate();
                setNotebooks(prev => [...prev, ...importedNotebooks]);
                setImportTask(prev => ({ ...prev, status: 'success' }));
                posthog.capture('backup_imported', { notebook_count: importedNotebooks.length });
                showToast(language === 'vietnamese' ? `Đã nhập ${importedNotebooks.length} sổ tay` : `Imported ${importedNotebooks.length} notebooks`, 'success');
                setTimeout(() => setImportTask(prev => ({ ...prev, isActive: false })), 3000);
            } else {
                setImportTask(prev => ({ ...prev, isActive: false }));
            }
        } catch (err) {
            console.error(err);
            setImportTask(prev => ({ ...prev, status: 'error' }));
            showToast(language === 'vietnamese' ? 'File không hợp lệ' : 'Invalid backup file', 'error');
            setTimeout(() => setImportTask(prev => ({ ...prev, isActive: false })), 5000);
        }
    };

    const handleExportSmmx = async () => {
        const nb = activeNotebook;
        if (!nb) return;
        try {
            await backupService.exportToSmmx(nb);
            showToast(language === 'vietnamese' ? 'Đã xuất sang SimpleMind' : 'Exported to SimpleMind', 'success');
        } catch (err) {
            console.error(err);
            showToast(language === 'vietnamese' ? 'Lỗi xuất SimpleMind' : 'Export failed', 'error');
        }
    };

    const handleImportSmmx = async (file: File) => {
        if (!file) return;
        setSettingsOpen(false);
        setImportTask({ isActive: true, progress: 0, fileName: file.name, status: 'processing' });
        try {
            const importedNotebook = await backupService.importFromSmmx(file, (p) => {
                setImportTask(prev => ({ ...prev, progress: p }));
            });
            setImportTask(prev => ({ ...prev, isActive: false }));
            setPendingImportNotebook(importedNotebook);
        } catch (err) {
            console.error(err);
            setImportTask(prev => ({ ...prev, status: 'error' }));
            showToast(language === 'vietnamese' ? 'Lỗi nhập file SimpleMind' : 'Invalid SimpleMind file', 'error');
            setTimeout(() => setImportTask(prev => ({ ...prev, isActive: false })), 5000);
        }
    };

    const handleConnectGitHub = useCallback(() => {
        window.location.href = getGitHubInstallUrl();
    }, []);

    const handleUpdateNotebookDescription = useCallback((notebookId: string, description: string) => {
        if (!user) return;
        setNotebooks(nbs => nbs.map(n => n.id === notebookId ? { ...n, description } : n));
        saveNotebookDescription(user.id, notebookId, description).catch(console.error);
    }, [user]);

    const handleUpdateNotebookGroup = useCallback((notebookId: string, group: string) => {
        if (!user) return;
        setNotebooks(nbs => nbs.map(n => n.id === notebookId ? { ...n, githubGroup: group } : n));
        saveNotebookGroup(user.id, notebookId, group).catch(console.error);
    }, [user]);

    const handleUpdateGitHubRepo = useCallback(async (repo: string, branch: string) => {
        try {
            await updateGitHubRepoConfig(repo, branch);
        } catch (e) {
            console.error('Failed to update GitHub repo config:', e);
        }
    }, []);

    const handleClearGithubConfig = useCallback(async () => {
        try {
            await clearGitHubConfigFn();
        } catch (e) {
            console.error('Failed to clear GitHub config:', e);
        }
    }, []);

    // Loads a notebook's nodes locally for read-only operations (backup, restore pre-snapshot).
    // Does NOT attach nodes to global React state — avoids permanently loading a non-active
    // notebook into RAM where it would never be evicted by the active↔inactive eviction timer.
    const getNotebookNodes = useCallback(async (notebookId: string): Promise<import('./types').MindMapNode[] | null> => {
        const nb = notebooksRef.current.find(n => n.id === notebookId);
        if (!nb) return null;
        if (!nb._isPartiallyLoaded) return nb.nodes;
        if (!user || user.id === 'guest') return nb.nodes;
        try {
            return await loadNotebookNodes(user.id, notebookId);
        } catch (err) {
            console.error('[getNotebookNodes] Failed:', notebookId, err);
            return null;
        }
    }, [user?.id]);

    // Ensures a notebook has its full nodes in React state — use only when the UI truly needs
    // the notebook's nodes attached to global state (e.g. import merge, bulk title generation).
    // For read-only operations, prefer getNotebookNodes() to avoid permanent RAM growth.
    const ensureNotebookLoaded = useCallback(async (notebookId: string): Promise<import('./types').Notebook | null> => {
        const nb = notebooksRef.current.find(n => n.id === notebookId);
        if (!nb) return null;
        if (!nb._isPartiallyLoaded) return nb;
        if (!user || user.id === 'guest') return nb;
        try {
            const nodes = await loadNotebookNodes(user.id, notebookId);
            const loaded = { ...nb, nodes, _isPartiallyLoaded: false as const };
            setNotebooks(prev => prev.map(n => n.id === notebookId ? loaded : n));
            return loaded;
        } catch (err) {
            console.error('[ensureNotebookLoaded] Failed:', notebookId, err);
            return null;
        }
    }, [user?.id]);

    const handleManualBackup = useCallback(async (notebookId: string): Promise<boolean> => {
        if (!user) return false;
        const nbMeta = notebooksRef.current.find(n => n.id === notebookId);
        if (!nbMeta) return false;
        const nodes = await getNotebookNodes(notebookId); // local only — avoids permanent RAM growth
        if (!nodes) {
            showToast(language === 'vietnamese' ? 'Không thể tải ghi chú để backup' : 'Could not load notes for backup', 'error');
            return false;
        }
        const result = await saveSnapshot(user.id, notebookId, nodes, nbMeta.links ?? [], 'manual');
        if (result.ok) {
            const now = Date.now();
            setNotebooks(nbs => nbs.map(n => n.id === notebookId ? { ...n, lastSnapshotAt: now } : n));
            showToast(language === 'vietnamese' ? 'Đã lưu backup thủ công' : 'Manual backup saved', 'success');
        } else {
            showToast(language === 'vietnamese' ? 'Lỗi tạo backup' : 'Backup failed', 'error');
        }
        return result.ok;
    }, [user, getNotebookNodes, language]);

    const handleRestoreSnapshot = useCallback(async (notebookId: string, snapshotId: string): Promise<void> => {
        if (!user) return;
        const nbMeta = notebooksRef.current.find(n => n.id === notebookId);
        // Load nodes locally for pre-restore backup — no need to permanently attach to state
        const currentNodes = await getNotebookNodes(notebookId) ?? [];
        if (!nbMeta) {
            showToast(language === 'vietnamese' ? 'Không thể tải ghi chú để restore' : 'Could not load notes for restore', 'error');
            return;
        }
        // Snapshot current state before restoring (safety net)
        const currentLinks = nbMeta?.links ?? [];
        const preResult = await saveSnapshot(user.id, notebookId, currentNodes, currentLinks, 'before_restore');
        if (!preResult.ok) {
            showToast(
                language === 'vietnamese' ? 'Không thể tạo backup trước khi khôi phục' : 'Could not backup before restore',
                'error'
            );
            return;
        }
        try {
            const { nodes, links } = await restoreSnapshot(user.id, notebookId, snapshotId);
            // Apply directly — explicit snapshot already created above
            setNotebooks(nbs => nbs.map(n => n.id === notebookId
                ? { ...n, nodes, links, updatedAt: Date.now() }
                : n
            ));
            showToast(
                language === 'vietnamese' ? 'Đã khôi phục. Trạng thái trước được lưu làm backup.' : 'Restored. Previous state saved as backup.',
                'success'
            );
        } catch (err) {
            console.error('[Restore] Failed:', err);
            showToast(language === 'vietnamese' ? 'Lỗi khôi phục dữ liệu' : 'Restore failed', 'error');
        }
    }, [user, getNotebookNodes, language]);

    const handleRestoreMissingScreenshots = useCallback(async (notebookId: string, snapshotId: string): Promise<{ restored: number }> => {
        if (!user) throw new Error('Not logged in');
        // Load full current nodes — handles evicted notebooks
        const currentNodes = await getNotebookNodes(notebookId);
        if (!currentNodes) throw new Error('Could not load current notes');
        const nbMeta = notebooksRef.current.find(n => n.id === notebookId);
        if (!nbMeta) throw new Error('Notebook not found');

        // Safety snapshot from fully loaded nodes before any write
        const preResult = await saveSnapshot(user.id, notebookId, currentNodes, nbMeta.links ?? [], 'before_restore');
        if (!preResult.ok) throw new Error('Could not create safety snapshot before restore');

        const { nodes: snapNodes } = await restoreSnapshot(user.id, notebookId, snapshotId);
        const snapMap = new Map(snapNodes.map(n => [n.id, n]));

        const now = Date.now();
        let restored = 0;
        const updatedNodes = currentNodes.map(curNode => {
            const snapNode = snapMap.get(curNode.id);
            if (!snapNode) return curNode;
            const curHasImg = curNode.imageBase64?.startsWith('https://');
            const snapHasImg = snapNode.imageBase64?.startsWith('https://');
            // Only copy image if current is missing it and backup has it — never overwrite existing
            if (!curHasImg && snapHasImg) {
                restored++;
                return { ...curNode, imageBase64: snapNode.imageBase64, updatedAt: now };
            }
            return curNode;
        });

        setNotebooks(nbs => nbs.map(n => n.id === notebookId ? { ...n, nodes: updatedNodes, updatedAt: now } : n));
        showToast(
            language === 'vietnamese'
                ? `Đã khôi phục ${restored} ảnh. Đang tự động lưu...`
                : `Restored ${restored} screenshot${restored !== 1 ? 's' : ''}. Auto-saving...`,
            'success'
        );
        return { restored };
    }, [user, getNotebookNodes, language]);

    const handleRestoreMissingNotes = useCallback(async (notebookId: string, snapshotId: string, nodeIds?: string[]): Promise<{ restored: number }> => {
        if (!user) throw new Error('Not logged in');
        // Load full current nodes — handles evicted notebooks
        const currentNodes = await getNotebookNodes(notebookId);
        if (!currentNodes) throw new Error('Could not load current notes');
        const nbMeta = notebooksRef.current.find(n => n.id === notebookId);
        if (!nbMeta) throw new Error('Notebook not found');

        // Safety snapshot from fully loaded nodes before any write
        const preResult = await saveSnapshot(user.id, notebookId, currentNodes, nbMeta.links ?? [], 'before_restore');
        if (!preResult.ok) throw new Error('Could not create safety snapshot before restore');

        const { nodes: snapNodes, links: snapLinks } = await restoreSnapshot(user.id, notebookId, snapshotId);
        const curSet = new Set(currentNodes.map(n => n.id));
        const nodeIdFilter = nodeIds ? new Set(nodeIds) : null;
        const missingNodes = snapNodes.filter(n =>
            !curSet.has(n.id) && (!nodeIdFilter || nodeIdFilter.has(n.id))
        );

        if (missingNodes.length === 0) return { restored: 0 };

        const mergedNodes = [...currentNodes, ...missingNodes];

        // Restore links where both endpoints exist in the merged set — no orphan links
        const mergedSet = new Set(mergedNodes.map(n => n.id));
        const existingLinks = nbMeta.links ?? [];
        const existingLinkKeys = new Set(existingLinks.map(l => `${l.fromId}:${l.toId}`));
        const restoredLinks = (snapLinks ?? []).filter(l =>
            mergedSet.has(l.fromId) && mergedSet.has(l.toId) && !existingLinkKeys.has(`${l.fromId}:${l.toId}`)
        );
        const mergedLinks = [...existingLinks, ...restoredLinks];

        setNotebooks(nbs => nbs.map(n => n.id === notebookId
            ? { ...n, nodes: mergedNodes, links: mergedLinks, updatedAt: Date.now() }
            : n
        ));
        showToast(
            language === 'vietnamese'
                ? `Đã thêm lại ${missingNodes.length} ghi chú${restoredLinks.length > 0 ? ` và ${restoredLinks.length} link` : ''}. Đang tự động lưu...`
                : `Re-added ${missingNodes.length} note${missingNodes.length !== 1 ? 's' : ''}${restoredLinks.length > 0 ? ` and ${restoredLinks.length} link${restoredLinks.length !== 1 ? 's' : ''}` : ''}. Auto-saving...`,
            'success'
        );
        return { restored: missingNodes.length };
    }, [user, getNotebookNodes, language]);

    const handleRevertNodeChanges = useCallback(async (notebookId: string, nodeId: string, changes: { title?: string; content?: string; imageBase64?: string }): Promise<void> => {
        if (!user) throw new Error('Not logged in');
        const updatedAt = Date.now();
        setNotebooks(nbs => nbs.map(nb => {
            if (nb.id !== notebookId) return nb;
            return { ...nb, nodes: nb.nodes.map(n => n.id !== nodeId ? n : { ...n, ...changes, updatedAt }) };
        }));
        const nodeRef = doc(db, 'users', user.id, 'notebooks', notebookId, 'nodes', nodeId);
        await updateDoc(nodeRef, { ...changes, updatedAt });
    }, [user]);

    const handleListSnapshots = useCallback(async (notebookId: string): Promise<SnapshotMeta[]> => {
        if (!user) return [];
        const snaps = await listSnapshots(user.id, notebookId);
        // Update lastSnapshotAt from Firestore so sidebar subtext is accurate across sessions
        const latestManual = snaps.find(s => s.reason === 'manual');
        const latestAny = snaps[0];
        const latest = latestManual ?? latestAny;
        if (latest) {
            setNotebooks(nbs => nbs.map(n =>
                n.id === notebookId && (!n.lastSnapshotAt || n.lastSnapshotAt < latest.createdAt)
                    ? { ...n, lastSnapshotAt: latest.createdAt }
                    : n
            ));
        }
        return snaps;
    }, [user]);

    const handleImportChooseNotebook = useCallback((targetNotebookId: string | 'new') => {
        if (!pendingImportNotebook) return;
        setExpandedNodeId(null);
        if (targetNotebookId === 'new') {
            saveHistoryImmediate();
            setNotebooks(prev => [...prev, pendingImportNotebook]);
            setActiveNotebookId(pendingImportNotebook.id);
            showToast(language === 'vietnamese' ? `Đã nhập "${pendingImportNotebook.title}"` : `Imported "${pendingImportNotebook.title}"`, 'success');
            setPendingImportNotebook(null);
        } else {
            setActiveNotebookId(targetNotebookId);
            setImportPickMode({ notebookId: targetNotebookId, importedNotebook: pendingImportNotebook });
            setPendingImportNotebook(null);
        }
    }, [pendingImportNotebook, language, saveHistoryImmediate]);

    const handleImportMerge = useCallback(async (targetNodeId: string | null) => {
        if (!importPickMode) return;
        const { notebookId, importedNotebook } = importPickMode;
        const targetNotebook = await ensureNotebookLoaded(notebookId);
        if (!targetNotebook) {
            showToast(language === 'vietnamese' ? 'Không thể tải notebook để ghép' : 'Could not load notebook for merge', 'error');
            return;
        }

        saveHistoryImmediate();

        const rootNode = importedNotebook.nodes.find(n => !n.parentId) || importedNotebook.nodes[0];

        let offsetX = 0;
        let offsetY = 0;

        if (targetNodeId) {
            const targetNode = targetNotebook.nodes.find(n => n.id === targetNodeId);
            if (targetNode && rootNode) {
                offsetX = targetNode.position.x + 500 - rootNode.position.x;
                offsetY = targetNode.position.y - rootNode.position.y;
            }
        } else {
            const maxX = targetNotebook.nodes.length > 0
                ? Math.max(...targetNotebook.nodes.map(n => n.position.x)) + 500
                : 0;
            offsetX = maxX - (rootNode?.position.x || 0);
        }

        const offsetNodes = importedNotebook.nodes.map(n => ({
            ...n,
            position: { x: n.position.x + offsetX, y: n.position.y + offsetY },
            ...(n.id === rootNode?.id && targetNodeId ? { parentId: targetNodeId } : {}),
        }));

        setNotebooks(prev => prev.map(nb => nb.id === notebookId ? {
            ...nb,
            nodes: [...nb.nodes, ...offsetNodes],
            links: [...(nb.links || []), ...(importedNotebook.links || [])],
            updatedAt: Date.now(),
        } : nb));

        setImportPickMode(null);
        showToast(language === 'vietnamese' ? `Đã ghép ${importedNotebook.nodes.length} ghi chú` : `Merged ${importedNotebook.nodes.length} notes`, 'success');
    }, [importPickMode, language, saveHistoryImmediate]);

    const handleNodeSelectForImport = useCallback((id: string, e: React.MouseEvent | React.PointerEvent) => {
        if (!importPickMode) return;
        e.stopPropagation();
        handleImportMerge(id);
    }, [importPickMode, handleImportMerge]);

    // --- FOCUS BUTTON LOGIC IMPROVEMENTS ---
    const handleTargetView = () => {
        if (!activeNotebook || activeNotebook.nodes.length === 0) return;

        const nodes = activeNotebook.nodes;
        const containerW = canvasRef.current?.clientWidth || window.innerWidth;
        const containerH = canvasRef.current?.clientHeight || window.innerHeight;

        // Calculate bounding box of all nodes
        const NODE_WIDTH = 300; // Approximate node width
        const NODE_HEIGHT = 200; // Approximate node height
        const PADDING = 100; // Padding around nodes

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        nodes.forEach(node => {
            const x = node.position.x;
            const y = node.position.y;
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x + NODE_WIDTH);
            maxY = Math.max(maxY, y + NODE_HEIGHT);
        });

        // Add padding
        minX -= PADDING;
        minY -= PADDING;
        maxX += PADDING;
        maxY += PADDING;

        // Calculate required scale to fit all nodes
        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;
        const scaleX = containerW / contentWidth;
        const scaleY = containerH / contentHeight;
        const MIN_SCALE = 0.05; // Allow full fit for large notebooks; user can zoom in after
        const MAX_SCALE = 1.2; // Maximum zoom to prevent over-zooming
        const newScale = Math.max(MIN_SCALE, Math.min(scaleX, scaleY, MAX_SCALE));

        // Calculate pan to center the bounding box
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        setScale(newScale);
        setPan({
            x: (containerW / 2) - (centerX * newScale),
            y: (containerH / 2) - (centerY * newScale)
        });

        // Select root or first node for context
        const root = nodes.find(n => !n.parentId) || nodes[0];
        if (root) setSelectedNodeId(root.id);
    };

    // Auto-focus when notebook loads to prevent empty viewport
    useEffect(() => {
        // Only auto-focus in map mode, when notebook has nodes, and we haven't focused this notebook yet
        // isLocalLoading is included so shared-notebook views re-trigger after the loading screen clears
        if (
            layoutMode === 'map' &&
            activeNotebookId &&
            activeNotebook?.nodes.length > 0 &&
            !isLocalLoading &&
            hasAutoFocusedRef.current !== activeNotebookId
        ) {
            // Small delay to allow layout to settle and avoid race conditions with user interactions
            const timer = setTimeout(() => {
                handleTargetView();
                hasAutoFocusedRef.current = activeNotebookId;
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [activeNotebookId, layoutMode, activeNotebook?.nodes.length, isLocalLoading]);


    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let lastOverNode: boolean | null = null;
        const handleDragOver = (e: DragEvent) => {
            if (!e.dataTransfer?.types.includes('Files')) return;
            const el = document.elementFromPoint(e.clientX, e.clientY);
            const overNode = !!el?.closest('[data-node-id]');
            if (overNode !== lastOverNode) {
                lastOverNode = overNode;
                setIsDragOverCanvas(!overNode);
            }
        };
        canvas.addEventListener('dragover', handleDragOver, true); // capture phase
        return () => canvas.removeEventListener('dragover', handleDragOver, true);
    }, []);

    const setExpandedNodeIdStable = useCallback((id: string | null) => {
        setExpandedNodeId(id);
        if (id) {
            posthog.capture('note_opened');
            if (isMobile) logMobileEvent('note.open', { nodeId: id });
        } else {
            if (isMobile) logMobileEvent('note.close');
        }
    }, [isMobile, logMobileEvent]);

    if (isEffectiveReadOnly && !user) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-900">
                <Loader2 size={40} className="animate-spin text-blue-500" />
                <p className="ml-3 text-gray-500">Loading Shared Map...</p>
            </div>
        );
    }

    // Assume closed until the status check proves otherwise (maintenanceMode === false) — a
    // fraction-of-a-second flash of the real sign-up screen before a slow/cold-started status
    // check resolves is exactly the bad-first-impression risk this exists to avoid.
    if (!user && !isEffectiveReadOnly && maintenanceMode !== false && !showLoginDuringMaintenance) {
        return (
            <WaitlistScreen
                language={language}
                onToggleLanguage={() => setLanguage(l => l === 'english' ? 'vietnamese' : 'english')}
                onShowLogin={() => setShowLoginDuringMaintenance(true)}
                autoJoinedEmail={waitlistBlockedEmail}
            />
        );
    }

    if (!user && !isEffectiveReadOnly) {
        return (
            <LoginScreen
                onLogin={handleLogin}
                clientId={driveConfig.clientId}
                onOpenSettings={() => setSettingsOpen(true)}
                language={language}
                onToggleLanguage={() => setLanguage(l => l === 'english' ? 'vietnamese' : 'english')}
                appVersion={VERSION_STRING}
                sessionExpired={sessionExpired}
            />
        );
    }

    if (user && !isInitialLoadComplete) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-slate-900">
                <div className="flex flex-col items-center gap-6">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">StudyBoard</div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 tracking-widest uppercase">Think · Explain · Master</div>
                    </div>
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            {language === 'vietnamese' ? 'Đang tải notebook...' : 'Loading notebooks...'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Blog view now renders in canvas area (line 1884), not early return
    if (false && layoutMode === 'blog' && activeNotebook) {
        const blogExpandedNode = expandedNodeId ? activeNotebook.nodes.find(n => n.id === expandedNodeId) : null;
        return (
            <>
                <BlogView
                    notebook={activeNotebook}
                    user={user}
                    onClose={() => setLayoutMode('map')}
                    onUpdateNode={updateNode}
                    onExpandNote={(id) => setExpandedNodeId(id)}
                />
                {blogExpandedNode && (
                    <ExpandedNoteModal
                        node={blogExpandedNode}
                        isOpen={true}
                        onClose={() => setExpandedNodeId(null)}
                        onUpdate={updateNode}
                        onGetFramework={handleGetFramework}
                        onAnalyze={handleAnalyzeNode}
                        onCancelAnalyze={handleCancelAnalyze}
                        onGenerateTitle={handleGenerateTitle}
                        onOpenSettings={() => {
                            setSettingsInitialTab('math');
                            setSettingsOpen(true);
                        }}
                        linkedNodes={linksByNodeId[expandedNodeId!] || []}
                        onNavigateToNote={(noteId) => { setExpandedNodeId(noteId); setSelectedNodeId(noteId); }}
                        language={language}
                        readOnly={isEffectiveReadOnly}
                        allNotebooks={allNotebooksForModal}
                        onNavigateCrossNotebook={(notebookId, nodeId) => { setActiveNotebookId(notebookId); setPendingCrossNav({ notebookId, nodeId }); }}
                        currentNotebookId={activeNotebookId ?? undefined}
                        onTagSearch={handleTagSearch}
                        userApiKeys={userApiKeys}
                    />
                )}
                {settingsOpen && (
                    <SettingsModal
                        isOpen={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                        onSave={(key, client) => {
                            const config = { apiKey: key, clientId: client };
                            setDriveConfig(config);
                            localStorage.setItem('ms_drive_config', JSON.stringify(config));
                            setSettingsOpen(false);
                            window.location.reload();
                        }}
                        initialApiKey={driveConfig.apiKey}
                        initialClientId={driveConfig.clientId}
                        language={language}
                        onExportBackup={handleExportBackup}
                        onImportBackup={handleImportBackup}
                        onExportSmmx={handleExportSmmx}
                        onImportSmmx={handleImportSmmx}
                        isPremium={effectiveIsPremium}
                        isAdmin={isAdmin}
                        simulateFreeUser={simulateFreeUser}
                        onSimulateFreeUserChange={handleSimulateFreeUserChange}
                        initialTab={settingsInitialTab}
                        notebooks={notebooks.map(n => ({ id: n.id, title: n.title, nodeCount: n.nodeCount ?? n.nodes.length, imageCount: n._isPartiallyLoaded ? undefined : n.nodes.filter(nd => nd.imageBase64?.startsWith('https://')).length }))}
                        onGetCurrentNodes={getNotebookNodes}
                        activeNotebookId={activeNotebookId}
                        userId={user?.id}
                        onManualBackup={handleManualBackup}
                        onListSnapshots={handleListSnapshots}
                        onRestoreSnapshot={handleRestoreSnapshot}
                        onRestoreMissingScreenshots={handleRestoreMissingScreenshots}
                        onRestoreMissingNotes={handleRestoreMissingNotes}
                        dataTabInitialNotebookId={settingsDataTabNotebookId}
                        autoTitleEnabled={autoTitleEnabled}
                        onAutoTitleEnabledChange={v => { setAutoTitleEnabled(v); localStorage.setItem('autoTitleEnabled', String(v)); }}
                        bgAutoTitleStatus={bgAutoTitleStatus}
                        autoTitlePendingCount={autoTitlePendingCount}
                        autoTitleStuckCount={autoTitleStuckCount}
                        onResetStuckAutoTitle={() => {
                            if (!activeNotebookId) return;
                            setNotebooks(nbs => nbs.map(nb => nb.id !== activeNotebookId ? nb : {
                                ...nb,
                                nodes: nb.nodes.map(n => isUntitledNode(n) && (n.autoTitleAttempts ?? 0) >= 2
                                    ? { ...n, autoTitleAttempts: 0 }
                                    : n)
                            }));
                        }}
                        driveConnected={driveConnected}
                        onDriveConnect={connectDrive}
                        onDriveDisconnect={disconnectDrive}
                        onDriveBackup={handleDriveBackup}
                        onDriveRestore={handleDriveRestore}
                        driveBackupRecords={driveBackupRecords}
                        onIndexAllNotes={async () => {
                            const allNotebooks = notebooksRef.current.filter(nb => !nb.isDeleted);
                            for (const nb of allNotebooks) {
                                const nodes = nb._isPartiallyLoaded
                                    ? (crossSearchIndex.current.get(nb.id) ?? [])
                                    : nb.nodes;
                                for (const node of nodes) {
                                    if (!(node as any).content?.trim() && !(node as any).title?.trim()) continue;
                                    if ((node as any).nodeType === 'label') continue;
                                    await generateNodeEmbedding(nb.id, node.id).catch(() => {});
                                }
                            }
                        }}
                        onRevertNodeChanges={handleRevertNodeChanges}
                        onOpenNote={(notebookId, nodeId) => {
                            if (notebookId !== activeNotebookId) setActiveNotebookId(notebookId);
                            setExpandedNodeId(nodeId);
                            setSettingsOpen(false);
                        }}
                        onUpgrade={() => { setSettingsOpen(false); setUpgradeModalOpen(true); }}
                        onManageSubscription={handleManageSubscription}
                        subscriptionStatus={subscriptionStatus}
                    />
                )}

                {isExportModalOpen && (
                    <ExportModal
                        isOpen={isExportModalOpen}
                        onClose={() => setIsExportModalOpen(false)}
                        notebooks={notebooks}
                        onExport={executeExportBackup}
                        language={language}
                    />
                )}
            </>
        );
    }

    const lodStatus = scale >= 0.4 ? 'Full' : 'Skeleton';

    // Payment success page
    if (window.location.pathname === '/payment-success') {
        return <PaymentSuccessPage language={language} />;
    }

    return (
        <div className="flex h-[100dvh] w-screen bg-gray-50 dark:bg-slate-900 overflow-hidden font-sans relative print:overflow-visible print:h-auto">
            <NewVersionNotice language={language} />
            {lastClosedNote && !expandedNodeId && (
                <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-white/95 dark:bg-slate-800/95 border border-gray-200 dark:border-slate-700 shadow-lg backdrop-blur text-sm max-w-[90vw]">
                    <button onClick={reopenLastNote} className="flex items-center gap-1.5 min-w-0 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400" title={language === 'vietnamese' ? 'Mở lại note vừa đóng' : 'Reopen the note you just closed'}>
                        <Undo size={14} className="shrink-0 text-blue-500" />
                        <span className="shrink-0">{language === 'vietnamese' ? 'Mở lại' : 'Reopen'}</span>
                        <span className="truncate max-w-[40vw] font-semibold">“{lastClosedNote.title || (language === 'vietnamese' ? 'Không tên' : 'Untitled')}”</span>
                    </button>
                    <button onClick={() => setLastClosedNote(null)} className="shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700" title={language === 'vietnamese' ? 'Ẩn' : 'Hide'}><X size={13} /></button>
                </div>
            )}
            <QuickDrawLayer
                open={quickDrawOpen}
                onClose={() => setQuickDrawOpen(false)}
                target={drawingTargetNote}
                noteDrawings={(liveExpandedNote?.attachments || []).filter(a => a.type === 'drawing')}
                onAttachmentsSaved={(attachments, notebookId, nodeId) => setNotebooks(nbs => nbs.map(nb => nb.id !== notebookId ? nb : { ...nb, nodes: nb.nodes.map(n => n.id === nodeId ? { ...n, attachments } : n) }))}
                vn={language === 'vietnamese'}
            />

            {authSessionExpired && (
                <div className="fixed top-0 left-0 w-full bg-red-600 text-white z-[300] px-4 py-3 flex items-center justify-between text-sm font-medium shadow-lg animate-in slide-in-from-top print:hidden">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <AlertTriangle size={16} className="text-red-200 flex-shrink-0" />
                        <span className="truncate">
                            {language === 'vietnamese'
                                ? '⚠️ Phiên đăng nhập hết hạn — dữ liệu mới chưa được lưu lên Cloud. Xác thực lại để tiếp tục, dữ liệu sẽ không bị mất.'
                                : '⚠️ Session expired — new notes are NOT syncing to Cloud. Re-authenticate to resume — no data will be lost.'}
                        </span>
                    </div>
                    <button
                        onClick={() => setShowReAuthModal(true)}
                        className="ml-4 px-3 py-1.5 bg-white text-red-600 rounded-md text-xs font-bold hover:bg-red-50 transition-colors shrink-0"
                    >
                        {language === 'vietnamese' ? 'Xác thực lại' : 'Re-authenticate'}
                    </button>
                </div>
            )}

            {recoveryInfo && (
                <div className="fixed top-0 left-0 w-full bg-amber-500 text-white z-[299] px-4 py-3 flex items-center justify-between text-sm font-medium shadow-lg animate-in slide-in-from-top print:hidden"
                     style={{ top: authSessionExpired ? '52px' : '0' }}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <AlertTriangle size={16} className="text-amber-100 flex-shrink-0" />
                        <span className="truncate">
                            {language === 'vietnamese'
                                ? `✅ Đã khôi phục ${recoveryInfo.count} thay đổi chưa lưu trong "${recoveryInfo.notebookTitle}" — đang lưu lên Cloud...`
                                : `✅ Recovered ${recoveryInfo.count} unsaved change(s) in "${recoveryInfo.notebookTitle}" — saving to Cloud...`}
                        </span>
                    </div>
                    <button
                        onClick={() => setRecoveryInfo(null)}
                        className="ml-4 p-1.5 hover:bg-amber-400 rounded transition-colors shrink-0"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}



            {showTrashModal && (
                <TrashModal
                    isOpen={showTrashModal}
                    onClose={() => setShowTrashModal(false)}
                    trashedNotebooks={trashedNotebooks}
                    language={language}
                    onRestored={(notebookId) => {
                        setNotebooks(nbs => nbs.map(nb => nb.id === notebookId ? { ...nb, isDeleted: false, deletedAt: undefined } : nb));
                        showToast(language === 'vietnamese' ? 'Đã khôi phục sổ tay' : 'Notebook restored', 'success');
                    }}
                    onPermanentlyDeleted={(notebookId) => {
                        setNotebooks(nbs => nbs.filter(nb => nb.id !== notebookId));
                        showToast(language === 'vietnamese' ? 'Đã xóa vĩnh viễn' : 'Permanently deleted', 'success');
                    }}
                />
            )}

            {showReAuthModal && user && (
                <ReAuthModal
                    userEmail={user.email}
                    authProvider={
                        auth.currentUser?.providerData?.[0]?.providerId === 'google.com' ? 'google' :
                        auth.currentUser?.providerData?.[0]?.providerId === 'password' ? 'email' : 'unknown'
                    }
                    language={language}
                    onSuccess={() => {
                        setAuthSessionExpired(false);
                        setShowReAuthModal(false);
                        // Bump notebooks updatedAt to trigger the next sync cycle immediately
                        setNotebooks(nbs => nbs.map(nb => ({ ...nb, updatedAt: Date.now() })));
                    }}
                />
            )}

            {showDeployHint && (
                <div className="fixed top-0 left-0 w-full bg-amber-600 text-white z-[200] px-4 py-2 flex items-center justify-between text-xs font-medium shadow-md animate-in slide-in-from-top print:hidden">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <AlertTriangle size={14} className="text-amber-200 flex-shrink-0" />
                        <span className="truncate">
                            {language === 'vietnamese'
                                ? <span>Đây là bản xem trước (staging). Để cập nhật <strong>studyboard.app</strong>, hãy chạy lệnh này trong Terminal:</span>
                                : <span>Staging Environment. To update <strong>studyboard.app</strong>, run this in Terminal:</span>}
                        </span>
                        <code className="bg-black/20 px-2 py-0.5 rounded font-mono select-all hidden md:inline">npm run build && firebase deploy --only hosting</code>
                    </div>
                    <div className="flex items-center gap-2">
                        <Tip text="Copy deploy command" side="top"><button onClick={() => navigator.clipboard.writeText('npm run build && firebase deploy --only hosting')} className="hover:bg-white/20 p-1.5 rounded transition-colors flex items-center gap-1">
                            <Copy size={12} /> <span className="hidden sm:inline">Copy</span>
                        </button></Tip>
                        <button onClick={() => setShowDeployHint(false)} className="hover:bg-white/20 p-1.5 rounded transition-colors"><X size={14} /></button>
                    </div>
                </div>
            )}

            {isEffectiveReadOnly && (
                <div className="absolute top-0 left-0 w-full z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b dark:border-slate-800 p-3 flex justify-between items-center shadow-sm">
                    <div className="flex items-center gap-2">
                        <img src="/logo-mark.png" alt="StudyBoard" className="w-8 h-8 object-contain" />
                        <div>
                            <h1 className="font-bold text-sm text-gray-900 dark:text-white leading-none">StudyBoard</h1>
                            <p className="text-[10px] text-gray-500">Public View Mode {testViewMode ? '(Preview)' : ''}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {testViewMode && (
                            <button onClick={() => setTestViewMode(false)} className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold rounded-full transition-colors">
                                Exit Preview
                            </button>
                        )}
                        <a href="/" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-full transition-colors shadow-lg shadow-blue-500/30">
                            Create your own Map
                        </a>
                    </div>
                </div>
            )}

            {(isResizingPanel || isResizingOutline || isResizingReviewFeed) && (
                <div className="fixed inset-0 z-[9998] cursor-col-resize" />
            )}

            {toast && (
                <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 border ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-200' : toast.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-800 text-white border-gray-700'
                    }`}>
                    {toast.type === 'error' && <AlertCircle size={20} />} {toast.type === 'success' && <CheckCircle2 size={20} />} {toast.type === 'info' && <Info size={20} />}
                    <span className="font-semibold">{toast.message}</span>
                </div>
            )}

            {pendingUndoRedo && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-6 max-w-sm w-full animate-in zoom-in-95">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">
                            {pendingUndoRedo === 'undo'
                                ? describeUndoQuestion(historyPast[historyPast.length - 1])
                                : describeRedoQuestion(historyFuture[0])}
                        </h3>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setPendingUndoRedo(null)} className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">{language === 'vietnamese' ? 'Hủy' : 'Cancel'}</button>
                            <button onClick={confirmPendingUndoRedo} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-lg shadow-blue-500/30 transition-colors">{language === 'vietnamese' ? 'Đồng ý' : 'Confirm'}</button>
                        </div>
                    </div>
                </div>
            )}

            {nodeToDeleteId && !isEffectiveReadOnly && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-6 max-w-sm w-full animate-in zoom-in-95">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {language === 'vietnamese' ? 'Xóa ghi chú?' : 'Delete Note?'}
                        </h3>
                        {(() => {
                            // Videos attached to the note (and, for a subtree delete, to its descendants).
                            const nb = notebooks.find(n => n.id === activeNotebookId);
                            if (!nb) return null;
                            const ids = new Set<string>([nodeToDeleteId]);
                            for (let grew = true; grew;) {
                                grew = false;
                                for (const n of nb.nodes) if (n.parentId && ids.has(n.parentId) && !ids.has(n.id)) { ids.add(n.id); grew = true; }
                            }
                            const own = countVideos(nb.nodes.filter(n => n.id === nodeToDeleteId));
                            const all = countVideos(nb.nodes.filter(n => ids.has(n.id)));
                            if (all === 0) return null;
                            const vn = language === 'vietnamese';
                            return (
                                <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2 mb-3">
                                    {vn
                                        ? `Ghi chú này có ${own} video${all > own ? ` (cả nhánh: ${all} video)` : ''}. Video vẫn còn trên YouTube; sau khi xóa, chúng sẽ nằm trong mục "Video không còn note" của YouTube Manager để bạn gắn vào ghi chú khác.`
                                        : `This note has ${own} video${own === 1 ? '' : 's'}${all > own ? ` (whole subtree: ${all})` : ''}. They stay on YouTube; after deleting, they are listed under "Videos without a note" in YouTube Manager so you can attach them to another note.`}
                                </p>
                            );
                        })()}
                        {deleteNodeHasChildren ? (
                            <>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                                    {language === 'vietnamese'
                                        ? 'Ghi chú này có các ghi chú con. Bạn muốn làm gì?'
                                        : 'This note has child notes. What would you like to do?'}
                                </p>
                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={() => executeDeleteNode('single')}
                                        className="w-full px-4 py-2.5 text-sm font-semibold text-left text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 rounded-lg border border-amber-200 dark:border-amber-800 transition-colors"
                                    >
                                        {language === 'vietnamese' ? 'Xóa ghi chú này, giữ các ghi chú con' : 'Delete this note, keep children'}
                                        <span className="block text-xs font-normal opacity-70 mt-0.5">
                                            {language === 'vietnamese' ? 'Các ghi chú con sẽ được gắn vào ghi chú cha.' : 'Child notes will be re-parented.'}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => executeDeleteNode('subtree')}
                                        className="w-full px-4 py-2.5 text-sm font-semibold text-left text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg border border-red-200 dark:border-red-800 transition-colors"
                                    >
                                        {language === 'vietnamese' ? 'Xóa toàn bộ nhánh' : 'Delete entire subtree'}
                                        <span className="block text-xs font-normal opacity-70 mt-0.5">
                                            {language === 'vietnamese' ? 'Ghi chú này và tất cả ghi chú con sẽ bị xóa.' : 'This note and all descendants will be removed.'}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => { setNodeToDeleteId(null); setDeleteNodeHasChildren(false); }}
                                        className="w-full px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        {language === 'vietnamese' ? 'Hủy' : 'Cancel'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                    {language === 'vietnamese' ? 'Hành động này không thể hoàn tác. Bạn có chắc chắn muốn xóa?' : 'This action cannot be undone. Are you sure you want to delete this note?'}
                                </p>
                                <div className="flex justify-end gap-3">
                                    <button onClick={() => setNodeToDeleteId(null)} className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">{language === 'vietnamese' ? 'Hủy' : 'Cancel'}</button>
                                    <button onClick={() => executeDeleteNode('single')} className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-lg shadow-red-500/30 transition-colors">{language === 'vietnamese' ? 'Xóa' : 'Delete'}</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {notebookToDeleteId && !isEffectiveReadOnly && (
                <div data-testid="delete-confirm-modal" className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-6 max-w-sm w-full animate-in zoom-in-95">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{language === 'vietnamese' ? 'Xóa sổ tay?' : 'Delete Notebook?'}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{language === 'vietnamese' ? 'Tất cả ghi chú trong sổ tay này sẽ bị xóa vĩnh viễn. Bạn có chắc chắn không?' : 'All notes in this notebook will be permanently deleted. Are you sure?'}</p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setNotebookToDeleteId(null)} className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">{language === 'vietnamese' ? 'Hủy' : 'Cancel'}</button>
                            <button data-testid="delete-confirm-btn" onClick={executeDeleteNotebook} className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-lg shadow-red-500/30 transition-colors">{language === 'vietnamese' ? 'Xóa' : 'Delete'}</button>
                        </div>
                    </div>
                </div>
            )}

            {sidebarOpen && (<div className="fixed inset-0 bg-black/50 z-[130] md:hidden backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />)}

            {!isEffectiveReadOnly && (
                <div data-testid="sidebar-container" className={`${sidebarOpen ? 'translate-x-0 w-72 border-r pointer-events-auto visible delay-0 z-[150]' : '-translate-x-full w-0 border-none pointer-events-none invisible delay-300 z-[-1]'} bg-white dark:bg-slate-900 dark:border-slate-800 transition-all duration-300 flex flex-col print:hidden shadow-2xl md:shadow-xl fixed h-full md:relative overflow-hidden ${showDeployHint ? 'mt-[32px] md:mt-0' : ''}`}>
                    <div className="p-4 border-b dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-950 shrink-0">
                        <div className="flex flex-col min-w-0">
                            <a href="/landing.html" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 font-bold text-xl text-blue-600 dark:text-violet-400 hover:opacity-80 transition-opacity"><img src="/logo-mark.png" alt="StudyBoard" className="w-8 h-8 object-contain shrink-0 dark:invert dark:brightness-150" />StudyBoard</a>
                            <p className="text-[11px] font-bold mt-1 ml-[38px] tracking-widest uppercase bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent">Think · Explain · Master</p>
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 ml-[38px] leading-snug">An AI system that turns notes into understanding.</p>
                        </div>
                        <button onClick={() => setSidebarOpen(false)} className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-lg transition-colors"><AlignLeft size={20} /></button>
                    </div>
                    {isMobile ? (
                        <button
                            onClick={handleMobileSync}
                            disabled={syncStatus.state === 'saving'}
                            className={`w-full px-4 py-3 text-xs font-semibold flex flex-col gap-1 border-b dark:border-slate-800 transition-colors shrink-0 text-left active:opacity-75 disabled:opacity-60 ${
                                hasUnsyncedChanges && syncStatus.state !== 'saving'
                                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20'
                                    : syncStatus.state === 'error' ? 'bg-red-50 text-red-600 dark:bg-red-900/20'
                                    : syncStatus.state === 'saving' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20'
                                    : 'bg-green-50 text-green-600 dark:bg-green-900/20'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Cloud size={14} />
                                <div className="flex flex-col flex-1 min-w-0">
                                    <span>{
                                        syncStatus.state === 'saving'
                                            ? (syncStatus.message || (language === 'vietnamese' ? 'Đang lưu...' : 'Syncing...'))
                                            : syncStatus.state === 'error'
                                            ? (syncStatus.message || (language === 'vietnamese' ? 'Lỗi đồng bộ!' : 'Sync Error!'))
                                            : hasUnsyncedChanges
                                            ? (language === 'vietnamese' ? 'Nhấn để đồng bộ' : 'Tap to sync')
                                            : (language === 'vietnamese' ? 'Đã đồng bộ' : 'Cloud Synced')
                                    }</span>
                                    {lastSavedTime && <span className="text-[9px] font-normal opacity-60">Last saved: {lastSavedTime.toLocaleTimeString()}</span>}
                                </div>
                                {syncStatus.state === 'saving' && <Loader2 size={12} className="animate-spin ml-auto" />}
                                {syncStatus.state === 'saved' && !hasUnsyncedChanges && <CheckCircle2 size={12} className="ml-auto" />}
                                {syncStatus.state === 'error' && <AlertCircle size={12} className="ml-auto" />}
                                {hasUnsyncedChanges && syncStatus.state !== 'saving' && <RefreshCw size={12} className="ml-auto" />}
                            </div>
                        </button>
                    ) : (
                        <div className={`px-4 py-3 text-xs font-semibold flex flex-col gap-1 border-b dark:border-slate-800 transition-colors shrink-0 ${syncStatus.state === 'error' ? 'bg-red-50 text-red-600 dark:bg-red-900/20' : syncStatus.state === 'saving' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : 'bg-green-50 text-green-600 dark:bg-green-900/20'}`}>
                            <div className="flex items-center gap-2">
                                <Cloud size={14} />
                                <div className="flex flex-col">
                                    <span>{syncStatus.state === 'saving' ? (syncStatus.message || (language === 'vietnamese' ? 'Đang lưu...' : 'Syncing...')) : syncStatus.state === 'error' ? (syncStatus.message || (language === 'vietnamese' ? 'Lỗi đồng bộ!' : 'Sync Error!')) : (language === 'vietnamese' ? 'Đã đồng bộ' : 'Cloud Synced')}</span>
                                    {lastSavedTime && <span className="text-[9px] font-normal opacity-60">Last saved: {lastSavedTime.toLocaleTimeString()}</span>}
                                </div>
                                {syncStatus.state === 'saving' && <Loader2 size={12} className="animate-spin ml-auto" />}
                                {syncStatus.state === 'saved' && <CheckCircle2 size={12} className="ml-auto" />}
                                {syncStatus.state === 'error' && <AlertCircle size={12} className="ml-auto" />}
                            </div>
                        </div>
                    )}
                    <div className="flex-1 flex flex-col min-h-0">
                        <div className="p-4 space-y-2 shrink-0">
                            <div className="grid grid-cols-2 gap-2">
                                <button data-testid="sidebar-new-notebook-btn" onClick={addNotebook} className="w-full py-2.5 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-slate-800 dark:text-blue-400 dark:hover:bg-slate-700 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors border border-blue-100 dark:border-slate-700"><Plus size={16} />{language === 'vietnamese' ? 'Mới' : 'New'}</button>
                                <button onClick={() => { posthog.capture('marketplace_opened'); setMarketplaceOpen(true); }} className="w-full py-2.5 px-3 bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-slate-800 dark:text-purple-400 dark:hover:bg-slate-700 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors border border-purple-100 dark:border-slate-700"><BookOpen size={16} />{language === 'vietnamese' ? 'Thư viện' : 'Explore'}</button>
                            </div>
                        </div>
                        <div className="px-3 pb-1 shrink-0">
                            <span className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-widest">{activeNotebooks.length} {language === 'vietnamese' ? 'sổ tay' : 'notebooks'}</span>
                        </div>
                        <div className="flex-1 overflow-y-auto hide-scrollbar px-2 pb-2 space-y-1">
                            {activeNotebooks.map((nb, nbIndex) => {
                                const isDragging = draggingNbId === nb.id;
                                const isDragOver = !!draggingNbId && dragOverNbId === nb.id && draggingNbId !== nb.id;
                                return (
                                    <div
                                        key={nb.id}
                                        data-testid="notebook-list-item"
                                        draggable
                                        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDraggingNbId(nb.id); }}
                                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverNbId(nb.id); }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (!draggingNbId || draggingNbId === nb.id) return;
                                            const ids = activeNotebooks.map(n => n.id);
                                            const from = ids.indexOf(draggingNbId);
                                            const to = ids.indexOf(nb.id);
                                            if (from < 0 || to < 0) return;
                                            const newOrder = [...ids];
                                            newOrder.splice(from, 1);
                                            newOrder.splice(to, 0, draggingNbId);
                                            setNotebookOrder(newOrder);
                                            localStorage.setItem('notebookOrder', JSON.stringify(newOrder));
                                            setDraggingNbId(null);
                                            setDragOverNbId(null);
                                        }}
                                        onDragEnd={() => { setDraggingNbId(null); setDragOverNbId(null); }}
                                        onClick={() => { setActiveNotebookId(nb.id); if (window.innerWidth < 768) setSidebarOpen(false); }}
                                        onContextMenu={(e) => {
                                            if (renamingNotebookId !== nb.id) {
                                                e.preventDefault();
                                                setContextMenu({ x: e.clientX, y: e.clientY, notebookId: nb.id });
                                            }
                                        }}
                                        className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-200 border ${
                                            isDragging ? 'opacity-40' :
                                            isDragOver ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20' :
                                            activeNotebookId === nb.id ? 'bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-900 shadow-md ring-1 ring-blue-100 dark:ring-blue-900' : 'border-transparent hover:bg-gray-100 dark:hover:bg-slate-800/50 text-gray-600 dark:text-gray-400'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 overflow-hidden min-w-0">
                                            <GripVertical size={14} className="shrink-0 text-gray-300 dark:text-slate-600 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity" />
                                            <FolderOpen size={18} className={`shrink-0 ${activeNotebookId === nb.id ? 'text-blue-500 fill-blue-100' : 'text-gray-400'}`} />
                                            {renamingNotebookId === nb.id ? (
                                                <input data-testid="notebook-rename-input" autoFocus className="w-full bg-transparent outline-none border-b border-blue-500 text-sm font-medium" value={renamingTitle} onChange={(e) => setRenamingTitle(e.target.value)} onBlur={() => handleFinishRename(nb.id)} onKeyDown={(e) => { if (e.key === 'Enter') handleFinishRename(nb.id); if (e.key === 'Escape') setRenamingNotebookId(null); }} onClick={(e) => e.stopPropagation()} />
                                            ) : (
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span title={nb.title} className={`text-sm font-medium truncate ${activeNotebookId === nb.id ? 'text-gray-900 dark:text-white' : ''}`}>{nb.title}</span>
                                                    {/* Size Warning Indicator */}
                                                    {featureFlags.enableSizeWarnings && (() => {
                                                        const warning = getSizeLevelWarning(nb);
                                                        if (warning.level === 'safe') return null;
                                                        const colors = {
                                                            info: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
                                                            warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                                                            danger: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                                        };
                                                        return (
                                                            <span
                                                                onClick={(e) => { e.stopPropagation(); setSizeModalNotebookId(nb.id); }}
                                                                className={`px-1.5 py-0.5 text-[10px] font-bold rounded flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity ${colors[warning.level]}`}
                                                                title={`${warning.percentage.toFixed(0)}% full - ${warning.message}\nClick for details`}
                                                            >
                                                                <AlertTriangle size={10} />
                                                                {warning.percentage.toFixed(0)}%
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        <Tip text={language === 'vietnamese' ? 'Tùy chọn thêm' : 'More options'} side={nbIndex < 2 ? 'bottom' : 'top'} align="right">
                                        <button
                                            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                                            onClick={(e) => { e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, notebookId: nb.id }); }}
                                        >
                                            <MoreHorizontal size={14} />
                                        </button>
                                        </Tip>
                                    </div>
                                );
                            })}

                            {/* Trash */}
                            {!isGuest && (
                                <div className="mt-1 pt-1 border-t border-gray-100 dark:border-slate-800 space-y-0.5">
                                    <button
                                        onClick={() => setShowTrashModal(true)}
                                        className="w-full group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-200 border border-transparent hover:bg-gray-100 dark:hover:bg-slate-800/50 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Trash2 size={18} className="shrink-0" />
                                            <span className="text-sm font-medium">{language === 'vietnamese' ? 'Thùng rác' : 'Trash'}</span>
                                        </div>
                                        {trashedNotebooks.length > 0 && (
                                            <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 text-[10px] font-bold rounded">
                                                {trashedNotebooks.length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* --- TASK CENTER (IMPORT PROGRESS) --- */}
                    {importTask.isActive && (
                        <div className="mx-4 mb-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-lg animate-in slide-in-from-bottom-2">
                            <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                    {importTask.status === 'processing' ? (
                                        <Loader2 size={14} className="animate-spin text-blue-500" />
                                    ) : importTask.status === 'success' ? (
                                        <Check size={14} className="text-green-500" />
                                    ) : (
                                        <AlertCircle size={14} className="text-red-500" />
                                    )}
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                        {importTask.status === 'processing' ? (language === 'vietnamese' ? 'Đang nhập...' : 'Importing...') : (language === 'vietnamese' ? 'Hoàn tất' : 'Complete')}
                                    </span>
                                </div>
                                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{importTask.progress}%</span>
                            </div>
                            <p className="text-[10px] text-gray-400 truncate mb-2">{importTask.fileName}</p>
                            <div className="h-2 w-full bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-300 ${importTask.status === 'error' ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-indigo-600'}`}
                                    style={{ width: `${importTask.progress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* --- TASK CENTER (EXPORT PROGRESS) --- */}
                    {exportTask.isActive && (
                        <div className="mx-4 mb-4 p-4 bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-lg animate-in slide-in-from-bottom-2">
                            <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                    {exportTask.status === 'processing' ? (
                                        <Loader2 size={14} className="animate-spin text-indigo-500" />
                                    ) : exportTask.status === 'success' ? (
                                        <Check size={14} className="text-green-500" />
                                    ) : exportTask.status === 'cancelled' ? (
                                        <X size={14} className="text-orange-500" />
                                    ) : (
                                        <AlertCircle size={14} className="text-red-500" />
                                    )}
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                        {exportTask.status === 'processing'
                                            ? (language === 'vietnamese' ? 'Đang xuất...' : 'Exporting...')
                                            : exportTask.status === 'success'
                                            ? (language === 'vietnamese' ? 'Hoàn tất' : 'Complete')
                                            : exportTask.status === 'cancelled'
                                            ? (language === 'vietnamese' ? 'Đã hủy' : 'Cancelled')
                                            : (language === 'vietnamese' ? 'Lỗi' : 'Error')}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">{exportTask.progress}%</span>
                                    {exportTask.status === 'processing' && (
                                        <button
                                            onClick={() => exportAbortControllerRef.current?.abort()}
                                            className="text-[10px] font-bold text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-0.5 rounded-full border border-red-200 dark:border-red-800 transition-colors"
                                        >
                                            {language === 'vietnamese' ? 'Hủy' : 'Cancel'}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400 truncate mb-2">Preparing Assets...</p>
                            <div className="h-2 w-full bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-300 ${exportTask.status === 'error' ? 'bg-red-500' : exportTask.status === 'cancelled' ? 'bg-orange-400' : 'bg-gradient-to-r from-indigo-500 to-blue-600'}`}
                                    style={{ width: `${exportTask.progress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Explore — featured notebooks by the author */}
                    {!isEffectiveReadOnly && (
                        <div className="px-2 pb-2 pt-1 border-t dark:border-slate-800">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500 px-1 py-2">
                                ✦ {language === 'vietnamese' ? 'Khám phá' : 'Explore'}
                            </p>
                            {FEATURED_NOTEBOOKS.map(nb => (
                                <a
                                    key={nb.id}
                                    href={`/?mode=view&uid=${nb.uid}&id=${nb.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-2 rounded-xl text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-xs group"
                                >
                                    <BookOpen size={13} className="text-purple-400 shrink-0" />
                                    <span className="truncate flex-1">{nb.title}</span>
                                    <ExternalLink size={10} className="opacity-0 group-hover:opacity-60 shrink-0 transition-opacity" />
                                </a>
                            ))}
                        </div>
                    )}

                    <div className="mt-auto">
                        <div className="p-4 border-t dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50 flex items-center gap-3 shrink-0">{user?.photoURL ? (<img src={user.photoURL} alt={user.name} className="w-9 h-9 rounded-full border border-white shadow-sm" />) : (<div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs shadow-sm">{user?.name.charAt(0).toUpperCase()}</div>)}<div className="flex-1 min-w-0"><p className="font-bold text-sm truncate dark:text-white">{user?.name}</p><div className="flex items-center gap-1">{user?.isPro ? (<span className="text-[10px] font-bold text-amber-500 flex items-center gap-0.5"><Crown size={10} fill="currentColor" /> PRO</span>) : (<span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Free</span>)}</div></div>{!isGuest && (<Tip text={language === 'vietnamese' ? 'Đăng xuất' : 'Sign Out'} side="top" align="right"><button data-testid="sidebar-logout-btn" onClick={handleLogout} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"><LogOut size={16} /></button></Tip>)}</div>
                        <div className="border-t dark:border-slate-800 bg-gray-50 dark:bg-slate-950 text-gray-500 shrink-0">
                            <div className="px-2 py-2 flex items-center gap-0.5">
                                <Tip text={VERSION_STRING} side="top" align="left">
                                    <button onClick={() => { const next = language === 'english' ? 'vietnamese' : 'english'; posthog.capture('language_changed', { language: next }); setLanguage(next); }} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors font-bold text-xs min-w-[32px] text-center">{language === 'english' ? 'EN' : 'VN'}</button>
                                </Tip>
                                <div className="flex-1" />
                                {!isGuest && (<Tip text={language === 'vietnamese' ? 'Bộ sưu tập' : 'Collections'} side="top" align="center"><button onClick={() => setShowCollectionsHub(true)} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-gray-400 hover:text-indigo-500"><Bookmark size={15} /></button></Tip>)}
                                <Tip text="Podcast Hub" side="top" align="center"><button onClick={() => setPodcastHubOpen(true)} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-gray-400 hover:text-purple-500"><Radio size={15} /></button></Tip>
                                <Tip text="GitHub Hub" side="top" align="center"><button onClick={() => setShowGitHubHub(true)} className={`p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors ${githubStatus?.configured ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>{githubStatus?.publishing ? <Loader2 size={15} className="animate-spin text-blue-500" /> : <Github size={15} />}</button></Tip>
                                {isAdmin && activeNotebook && (<Tip text="Threads Advisor" side="top" align="center"><button onClick={() => { setSidebarOpen(false); setShowThreadsAdvisor(true); }} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-indigo-500 hover:text-indigo-700"><Share2 size={15} /></button></Tip>)}
                                {isAdmin && (<Tip text="YouTube Manager" side="top" align="center"><button onClick={() => { setSidebarOpen(false); setShowYouTubeManager(true); }} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-red-500 hover:text-red-700"><Youtube size={15} /></button></Tip>)}
                                {isAdmin && activeNotebook && (<Tip text="Guide Builder" side="top" align="center"><button onClick={() => { setSidebarOpen(false); enterGuideSelectMode(); }} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors text-violet-500 hover:text-violet-700"><FileText size={15} /></button></Tip>)}
                                <Tip text="Settings" side="top" align="right"><button onClick={() => setSettingsOpen(true)} className="p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"><Settings size={15} /></button></Tip>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-1 relative flex flex-row h-full overflow-hidden print:overflow-visible print:h-auto print:block">
                {/* Phase 1: Workspace Wrapper */}
                <div className="relative flex-1 h-full overflow-hidden flex flex-col print:overflow-visible print:h-auto print:block" id="workspace-wrapper">
                    <div ref={mediaToolbarRef} className={`absolute left-4 z-[90] ${mediaPanelOpen ? 'hidden md:flex' : 'flex'} items-start pointer-events-none print:hidden ${isEffectiveReadOnly ? 'top-16' : (showDeployHint ? 'top-14 md:top-10' : 'top-4')}`} style={{ right: mediaPanelOpen ? (mediaPanelWidth + 24) : 16 }}>
                        <div className="flex items-center w-full">
                            {!isSearchFocused && !sidebarOpen && !isEffectiveReadOnly && (
                                <div className="pointer-events-auto bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 flex-shrink-0">
                                    <button onClick={() => setSidebarOpen(true)} className="p-3 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors">{syncStatus.state === 'saving' ? <RefreshCw size={20} className="animate-spin text-blue-500" /> : syncJustSaved ? <Check size={20} className="text-green-500" /> : <Menu size={20} className="text-gray-700 dark:text-gray-200" />}</button>
                                </div>
                            )}
                            <div className={`relative ml-auto pointer-events-auto bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-300 flex items-center ${isSearchFocused ? 'w-full max-w-xl shadow-xl ring-2 ring-blue-500/20' : 'shadow-sm'}`}>
                                {!isSearchFocused && !isEffectiveReadOnly && (
                                    <div className="relative flex-shrink-0">
                                        <button onClick={() => setLayoutDropdownOpen(v => !v)} className="w-10 h-10 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-l-xl transition-colors border-r border-gray-200 dark:border-slate-700">
                                            {layoutMode === 'blog' ? <BookOpen size={16} /> : layoutMode === 'stream' ? <GalleryVertical size={16} /> : layoutMode === 'flow' ? <Columns size={16} /> : <Network size={16} />}
                                        </button>
                                        {layoutDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-[89]" onClick={() => setLayoutDropdownOpen(false)} />
                                                <div className="absolute top-full right-0 mt-2 w-44 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden z-[110]">
                                                    {isMobile && <button onClick={() => { setLayoutMode('stream'); setLayoutDropdownOpen(false); }} className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${layoutMode === 'stream' ? 'bg-blue-50 text-blue-600 font-bold dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><GalleryVertical size={15} /> Stream{layoutMode === 'stream' && <Check size={14} className="ml-auto" />}</button>}
                                                    <button onClick={() => { setLayoutMode('blog'); setLayoutDropdownOpen(false); }} className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${layoutMode === 'blog' ? 'bg-blue-50 text-blue-600 font-bold dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><BookOpen size={15} /> Blog{layoutMode === 'blog' && <Check size={14} className="ml-auto" />}</button>
                                                    {!isMobile && <>
                                                        <button onClick={() => { setLayoutMode('map'); setMapLayoutStyle('horizontal'); setLayoutDropdownOpen(false); }} className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${layoutMode === 'map' && mapLayoutStyle === 'horizontal' ? 'bg-blue-50 text-blue-600 font-bold dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><GitGraph size={15} className="rotate-90" /> H. Tree{layoutMode === 'map' && mapLayoutStyle === 'horizontal' && <Check size={14} className="ml-auto" />}</button>
                                                        <button onClick={() => { setLayoutMode('map'); setMapLayoutStyle('vertical'); setLayoutDropdownOpen(false); }} className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${layoutMode === 'map' && mapLayoutStyle === 'vertical' ? 'bg-blue-50 text-blue-600 font-bold dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><GitGraph size={15} /> V. Tree{layoutMode === 'map' && mapLayoutStyle === 'vertical' && <Check size={14} className="ml-auto" />}</button>
                                                        <button onClick={() => { setLayoutMode('flow'); setLayoutDropdownOpen(false); }} className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm transition-colors ${layoutMode === 'flow' ? 'bg-blue-50 text-blue-600 font-bold dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700'}`}><Columns size={15} /> Flow{layoutMode === 'flow' && <Check size={14} className="ml-auto" />}</button>
                                                    </>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            <div className={`relative group/search transition-all duration-300 ${isSearchFocused ? 'flex-1' : 'w-10 h-10'}`}>
                                {isSearchFocused ? (<button onClick={() => { setIsSearchFocused(false); setShowHistoryDropdown(false); }} className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors z-20"><ArrowLeft size={16} /></button>) : (<Search size={16} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-400 transition-colors pointer-events-none" />)}
                                <input type="text" value={searchQuery} onFocus={() => setIsSearchFocused(true)} onBlur={() => { setTimeout(() => { if (!searchQuery && searchChips.length === 0) { setIsSearchFocused(false); setShowHistoryDropdown(false); } }, 150); }} onChange={(e) => { setSearchQuery(e.target.value); setCanvasSearchError(null); if (e.target.value) setShowHistoryDropdown(false); }} onKeyDown={(e) => { if (e.key === 'Enter' && searchQuery.trim()) { saveSearchHistory(searchQuery); const chip = searchQuery.trim(); if (canvasSearchScope === 'current' && canvasSearchMode === 'keyword') { const newChips = searchChips.includes(chip) ? searchChips : [...searchChips, chip]; setSearchChips(newChips); setSearchQuery(''); handleCanvasSearch(undefined, undefined, newChips); } else { handleCanvasSearch(); } } else if (e.key === 'Backspace' && !searchQuery && searchChips.length > 0) { const newChips = searchChips.slice(0, -1); setSearchChips(newChips); if (newChips.length === 0) { setCanvasSearchResults([]); setCanvasSearchedQuery(''); } else { handleCanvasSearch(undefined, undefined, newChips); } } }} placeholder={isSearchFocused ? (searchChips.length > 0 ? (language === 'vietnamese' ? 'Thêm từ khóa...' : 'Add keyword...') : (language === 'vietnamese' ? 'Nhập từ khóa để tìm kiếm...' : 'Type to search nodes...')) : ''} className={`py-2 w-full bg-transparent focus:bg-white dark:focus:bg-slate-950 border border-transparent focus:border-blue-200 dark:focus:border-slate-600 rounded-xl text-sm outline-none transition-all dark:text-white placeholder-gray-400 ${isSearchFocused ? 'pl-10 pr-8 shadow-inner' : 'opacity-0 cursor-pointer h-10 absolute inset-0'}`} />
                                {searchQuery
                                    ? (<button onClick={() => { setSearchQuery(""); setShowHistoryDropdown(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full"><X size={14} /></button>)
                                    : (searchChips.length > 0 && isSearchFocused)
                                    ? (<button onClick={() => { setSearchChips([]); setCanvasSearchResults([]); setCanvasSearchedQuery(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"><X size={14} /></button>)
                                    : isSearchFocused && (<button onMouseDown={(e) => { e.preventDefault(); setShowHistoryDropdown(v => !v); }} className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors ${showHistoryDropdown ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><Clock size={14} /></button>)
                                }
                                {/* Search history dropdown */}
                                {isSearchFocused && !searchQuery && showHistoryDropdown && searchHistory.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 pointer-events-auto overflow-hidden">
                                        <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
                                            <span className="flex items-center gap-1.5"><Clock size={10} />{language === 'vietnamese' ? 'Tìm kiếm gần đây' : 'Recent searches'}</span>
                                            <button onMouseDown={(e) => { e.preventDefault(); setSearchHistory([]); localStorage.removeItem('sb_search_history'); }} className="text-gray-300 hover:text-red-400 transition-colors text-[10px] normal-case tracking-normal font-normal">{language === 'vietnamese' ? 'Xoá tất cả' : 'Clear all'}</button>
                                        </div>
                                        {searchHistory.map(h => (
                                            <div key={h} className="flex items-center border-b border-gray-50 dark:border-slate-700/50 last:border-0">
                                                <button onMouseDown={(e) => { e.preventDefault(); setSearchQuery(h); setShowHistoryDropdown(false); }} className="flex-1 flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors text-left">
                                                    <Clock size={13} className="shrink-0 text-gray-300 dark:text-gray-600" />
                                                    {h}
                                                </button>
                                                <button onMouseDown={(e) => { e.preventDefault(); setSearchHistory(prev => { const next = prev.filter(x => x !== h); localStorage.setItem('sb_search_history', JSON.stringify(next)); return next; }); }} className="px-3 py-2 text-gray-300 hover:text-red-400 dark:hover:text-red-400 transition-colors">
                                                    <X size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Scope toggle — visible as soon as search is focused, only for keyword mode */}
                                {isSearchFocused && !showHistoryDropdown && activeNotebookId && canvasSearchMode === 'keyword' && (
                                    <div className="absolute top-full left-0 right-0 mt-1 z-50 pointer-events-auto">
                                        <div className={`flex items-center gap-1 px-2 py-1.5 ${searchQuery.trim() ? 'bg-white dark:bg-slate-800 border-b-0 rounded-t-xl border border-b-0 border-gray-200 dark:border-slate-700' : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg'}`}>
                                            <span className="text-[10px] text-gray-400 dark:text-gray-500 mr-1">{language === 'vietnamese' ? 'Tìm trong:' : 'Search in:'}</span>
                                            <button
                                                onMouseDown={e => { e.preventDefault(); if (canvasSearchScope !== 'current') { const newScope = 'current'; setCanvasSearchScope(newScope); if (canvasSearchedQuery) handleCanvasSearch('keyword', newScope); } }}
                                                className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full transition-colors ${canvasSearchScope === 'current' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                            >
                                                {language === 'vietnamese' ? 'Notebook này' : 'This notebook'}
                                            </button>
                                            <button
                                                onMouseDown={e => { e.preventDefault(); if (canvasSearchScope !== 'all') { const newScope = 'all'; setCanvasSearchScope(newScope); if (canvasSearchedQuery) handleCanvasSearch('keyword', newScope); } }}
                                                className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full transition-colors ${canvasSearchScope === 'all' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                            >
                                                {language === 'vietnamese' ? 'Tất cả sách' : 'All notebooks'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {/* Canvas search dropdown */}
                                {isSearchFocused && (searchQuery.trim() || searchChips.length > 0) && !showHistoryDropdown && (
                                    <div className={`absolute left-0 right-0 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden pointer-events-auto ${activeNotebookId && canvasSearchMode === 'keyword' ? 'top-[calc(100%+2.25rem)]' : 'top-full mt-1'}`}>
                                        {/* Mode tabs */}
                                        <div className="flex border-b border-gray-100 dark:border-slate-700">
                                            <button
                                                onMouseDown={(e) => { e.preventDefault(); setCanvasSearchMode('keyword'); setCanvasSemanticResults([]); setCanvasSearchError(null); if (canvasSearchedQuery && canvasSearchResults.length === 0) handleCanvasSearch('keyword'); }}
                                                className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${canvasSearchMode === 'keyword' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                            >
                                                {language === 'vietnamese' ? 'Từ khóa' : 'Keyword'}
                                            </button>
                                            <button
                                                onMouseDown={(e) => { e.preventDefault(); setCanvasSearchMode('semantic'); setCanvasSearchResults([]); setCanvasSearchError(null); if (canvasSearchedQuery && canvasSemanticResults.length === 0) handleCanvasSearch('semantic'); }}
                                                className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${canvasSearchMode === 'semantic' ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                            >
                                                <BrainCircuit size={11} /> AI Semantic
                                            </button>
                                        </div>
                                        {/* Keyword chips */}
                                        {searchChips.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-gray-100 dark:border-slate-700 bg-blue-50/40 dark:bg-blue-900/10">
                                                {searchChips.map(chip => (
                                                    <span key={chip} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium">
                                                        {chip}
                                                        <button onMouseDown={(e) => { e.preventDefault(); const newChips = searchChips.filter(c => c !== chip); setSearchChips(newChips); if (newChips.length === 0) { setCanvasSearchResults([]); setCanvasSearchedQuery(''); } else { handleCanvasSearch(undefined, undefined, newChips); } }} className="hover:text-red-500 transition-colors ml-0.5"><X size={10} /></button>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {/* Results */}
                                        <div className="max-h-[65vh] overflow-y-auto">
                                            {canvasSearchLoading ? (
                                                <div className="flex items-center justify-center py-8 gap-2 text-sm text-gray-400">
                                                    <Loader2 size={15} className="animate-spin" />
                                                    {language === 'vietnamese' ? 'Đang tìm kiếm...' : 'Searching...'}
                                                </div>
                                            ) : canvasSearchMode === 'keyword' ? (
                                                canvasSearchError ? (
                                                    <div className="py-6 px-4 text-center text-sm text-red-400">{canvasSearchError}</div>
                                                ) : canvasSearchResults.length === 0 ? (
                                                    canvasSearchedQuery ? (
                                                        <div className="py-6 text-center text-sm text-gray-400">{language === 'vietnamese' ? 'Không có kết quả' : 'No results found'}</div>
                                                    ) : (
                                                        <div className="py-6 text-center text-sm text-gray-400">{language === 'vietnamese' ? 'Nhấn Enter để tìm kiếm' : 'Press Enter to search'}</div>
                                                    )
                                                ) : (() => {
                                                    const visibleResults = canvasSearchScope === 'current' && activeNotebookId
                                                        ? canvasSearchResults.filter(r => r.notebookId === activeNotebookId)
                                                        : canvasSearchResults;
                                                    if (visibleResults.length === 0) return <div className="py-6 text-center text-sm text-gray-400">{language === 'vietnamese' ? 'Không có kết quả' : 'No results found'}</div>;
                                                    const groups: { notebookId: string; notebookTitle: string; items: typeof canvasSearchResults }[] = [];
                                                    for (const r of visibleResults) {
                                                        const g = groups.find(g => g.notebookId === r.notebookId);
                                                        if (g) g.items.push(r); else groups.push({ notebookId: r.notebookId, notebookTitle: r.notebookTitle, items: [r] });
                                                    }
                                                    return groups.map(group => (
                                                        <div key={group.notebookId}>
                                                            <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 flex items-center gap-1.5">
                                                                <BookOpen size={10} />{group.notebookTitle}
                                                            </div>
                                                            {group.items.map(r => (
                                                                <div key={r.nodeId} className="border-b border-gray-50 dark:border-slate-700/50 last:border-0 flex items-stretch">
                                                                    <button
                                                                        onMouseEnter={(e) => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); const rect = e.currentTarget.getBoundingClientRect(); hoverTimerRef.current = setTimeout(() => { const nb = notebooks.find(nb => nb.id === r.notebookId); const node = nb?.nodes.find(n => n.id === r.nodeId) ?? (activeNotebook?.nodes.find(n => n.id === r.nodeId)); if (node) { setHoverPreview({ node, notebookId: r.notebookId, top: rect.top, left: rect.right + 8 }); } }, 200); }}
                                                                        onMouseLeave={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); hoverTimerRef.current = setTimeout(() => { setHoverPreview(null); setSearchHoverExpanded(false); }, 150); }}
                                                                        onMouseDown={(e) => { e.preventDefault(); const nb = notebooks.find(nb => nb.id === r.notebookId); const node = nb?.nodes.find(n => n.id === r.nodeId) ?? (activeNotebook?.nodes.find(n => n.id === r.nodeId)); if (node) setHoverPreview({ node, notebookId: r.notebookId, top: 0, left: 0 }); }}
                                                                        className="shrink-0 flex items-center px-2.5 text-gray-300 dark:text-gray-600 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-r border-gray-100 dark:border-slate-700"
                                                                    >
                                                                        <Eye size={14} />
                                                                    </button>
                                                                    <button
                                                                        onMouseDown={() => handleGlobalSearchSelect(r.notebookId, r.nodeId)}
                                                                        className="flex-1 text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                                                    >
                                                                        <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{r.nodeTitle || (language === 'vietnamese' ? '(Không có tiêu đề)' : '(Untitled)')}</div>
                                                                        {r.excerpt && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1">{r.excerpt}</div>}
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ));
                                                })()
                                            ) : (
                                                canvasSearchError ? (
                                                    <div className="py-6 px-4 text-center">
                                                        <p className="text-sm text-red-400 mb-3">{canvasSearchError}</p>
                                                        <button
                                                            onMouseDown={async (e) => { e.preventDefault(); setCanvasSearchLoading(true); try { await buildSearchIndex(); } finally { setCanvasSearchLoading(false); } }}
                                                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                                                        >
                                                            {language === 'vietnamese' ? 'Xây dựng chỉ mục' : 'Build Index'}
                                                        </button>
                                                    </div>
                                                ) : canvasSemanticResults.length === 0 ? (
                                                    canvasSearchedQuery ? (
                                                        <div className="py-6 text-center text-sm text-gray-400">{language === 'vietnamese' ? 'Không có kết quả' : 'No results found'}</div>
                                                    ) : (
                                                        <div className="py-6 px-4 text-center">
                                                            <BrainCircuit size={24} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{language === 'vietnamese' ? 'Nhấn Enter để tìm kiếm ngữ nghĩa' : 'Press Enter to search semantically'}</p>
                                                            <button
                                                                onMouseDown={async (e) => { e.preventDefault(); setCanvasSearchLoading(true); try { await buildSearchIndex(); } finally { setCanvasSearchLoading(false); } }}
                                                                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                                                            >
                                                                {language === 'vietnamese' ? 'Xây dựng chỉ mục' : 'Build Index'}
                                                            </button>
                                                        </div>
                                                    )
                                                ) : (
                                                    canvasSemanticResults.map(r => (
                                                        <div key={r.nodeId} className="border-b border-gray-50 dark:border-slate-700/50 last:border-0">
                                                            <div className="px-3 pt-2 text-[10px] text-gray-400 dark:text-gray-500 font-medium flex items-center gap-1">
                                                                <BookOpen size={9} />{r.notebookTitle}
                                                            </div>
                                                            <button
                                                                onMouseDown={() => handleGlobalSearchSelect(r.notebookId, r.nodeId)}
                                                                className="w-full text-left px-3 pb-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                                            >
                                                                <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{r.nodeTitle || (language === 'vietnamese' ? '(Không có tiêu đề)' : '(Untitled)')}</div>
                                                                {r.excerpt && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2">{r.excerpt}</div>}
                                                                <div className="mt-0.5 text-[10px] text-blue-400">{Math.round(r.similarity * 100)}% match</div>
                                                            </button>
                                                        </div>
                                                    ))
                                                )
                                            )}
                                        </div>
                                        {/* Footer count + scope toggle */}
                                        {!canvasSearchLoading && (canvasSearchMode === 'keyword' ? canvasSearchResults.length : canvasSemanticResults.length) > 0 && (
                                            <div className="px-3 py-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-700">
                                                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                    {canvasSearchMode === 'keyword'
                                                        ? (canvasSearchScope === 'current' && activeNotebookId
                                                            ? canvasSearchResults.filter(r => r.notebookId === activeNotebookId).length
                                                            : canvasSearchResults.length)
                                                        : canvasSemanticResults.length}
                                                    {' '}{language === 'vietnamese'
                                                        ? (canvasSearchScope === 'current' ? 'kết quả trong notebook này' : 'kết quả trong tất cả sách')
                                                        : (canvasSearchScope === 'current' ? 'results in this notebook' : 'results across all notebooks')}
                                                </span>
                                                {activeNotebookId && canvasSearchMode !== 'semantic' && (
                                                    <button
                                                        onMouseDown={e => {
                                                            e.preventDefault();
                                                            const newScope = canvasSearchScope === 'all' ? 'current' : 'all';
                                                            setCanvasSearchScope(newScope);
                                                            if (canvasSearchedQuery) handleCanvasSearch('keyword', newScope);
                                                        }}
                                                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${canvasSearchScope === 'current' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-blue-300 dark:border-blue-700' : 'text-gray-400 border-gray-200 dark:border-slate-600 hover:border-blue-300 hover:text-blue-500'}`}
                                                    >
                                                        {language === 'vietnamese' ? (canvasSearchScope === 'current' ? 'Notebook này' : 'Tất cả sách') : (canvasSearchScope === 'current' ? 'This notebook' : 'All notebooks')}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Screen record button */}
                        <button
                            onClick={isRecording ? stopRecording : startRecording}
                            title={isRecording ? (language === 'vietnamese' ? 'Dừng ghi hình' : 'Stop recording') : (language === 'vietnamese' ? 'Quay màn hình' : 'Record screen')}
                            className={`pointer-events-auto ml-2 w-10 h-10 flex items-center justify-center rounded-xl border shadow-sm backdrop-blur-sm transition-colors flex-shrink-0 ${
                                isRecording
                                    ? 'bg-red-50/90 dark:bg-red-900/30 border-red-400 dark:border-red-500'
                                    : 'bg-white/90 dark:bg-slate-800/90 border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            <Circle size={14} className={isRecording ? 'fill-red-500 text-red-500 animate-pulse' : 'text-gray-500 dark:text-gray-400'} />
                        </button>
                        </div>
                        {pickParentForNodeId && (
                            <div className="pointer-events-auto bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3 mx-2 animate-in fade-in slide-in-from-top-2">
                                <ImagePlus size={15} className="flex-shrink-0" />
                                <span className="text-sm font-bold">{language === 'vietnamese' ? 'Chọn note cha cho ảnh vừa thả' : 'Click a note to set as parent'}</span>
                                <button onClick={() => setPickParentForNodeId(null)} className="text-xs bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded-full transition-colors">{language === 'vietnamese' ? 'Bỏ qua' : 'Skip'}</button>
                            </div>
                        )}
                        {linkingSourceId && (<div className="pointer-events-auto bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3 animate-bounce mx-2"><span className="text-sm font-bold truncate">Select target to link</span><button onClick={() => { setLinkingSourceId(null); setIsLinkSelectorOpen(false); }} className="p-1 hover:bg-indigo-700 rounded-full"><X size={14} /></button></div>)}
                        {importPickMode && (
                            <div className="pointer-events-auto bg-emerald-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3 mx-2">
                                <span className="text-sm font-bold">{language === 'vietnamese' ? '📌 Bấm vào note để gắn import vào' : '📌 Click a note to attach import'}</span>
                                <button onClick={() => handleImportMerge(null)} className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-colors">{language === 'vietnamese' ? 'Đặt tự do' : 'Free place'}</button>
                                <button onClick={() => setImportPickMode(null)} className="p-1 hover:bg-emerald-700 rounded-full"><X size={14} /></button>
                            </div>
                        )}
                        {isAutoTitling && (
                            <div className="pointer-events-auto bg-violet-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3 mx-2 animate-in fade-in slide-in-from-top-3">
                                <Loader2 size={14} className="animate-spin flex-shrink-0" />
                                <span className="text-sm font-bold">{language === 'vietnamese' ? `Đang tạo tiêu đề ${autoTitleProgress.current}/${autoTitleProgress.total}` : `Generating titles ${autoTitleProgress.current}/${autoTitleProgress.total}`}</span>
                                <button onClick={() => { autoTitleCancelRef.current = true; }} className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-colors">{language === 'vietnamese' ? 'Hủy' : 'Cancel'}</button>
                            </div>
                        )}
                        {isRecording && (
                            <div className="pointer-events-auto bg-red-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3 mx-2 animate-in fade-in slide-in-from-top-2">
                                <span className={`w-2 h-2 rounded-full bg-white flex-shrink-0 ${isRecordingPaused ? 'opacity-40' : 'animate-pulse'}`} />
                                <span className="text-sm font-bold font-mono">
                                    {isRecordingPaused ? 'PAUSED' : 'REC'} {String(Math.floor(recordingSeconds / 60)).padStart(2, '0')}:{String(recordingSeconds % 60).padStart(2, '0')}
                                </span>
                                <button
                                    onClick={isRecordingPaused ? resumeRecording : pauseRecording}
                                    className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-colors"
                                >
                                    {isRecordingPaused ? (language === 'vietnamese' ? '▶ Tiếp tục' : '▶ Resume') : (language === 'vietnamese' ? '⏸ Tạm dừng' : '⏸ Pause')}
                                </button>
                                <button onClick={stopRecording} className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full transition-colors">
                                    {language === 'vietnamese' ? '⏹ Dừng & Lưu' : '⏹ Stop & Save'}
                                </button>
                            </div>
                        )}
                        {bgAutoTitleStatus && !isAutoTitling && (
                            <div className="pointer-events-none bg-gray-700/80 text-white px-3 py-1.5 rounded-full shadow text-xs flex items-center gap-2 mx-2">
                                <Loader2 size={11} className="animate-spin flex-shrink-0" />
                                <span>{bgAutoTitleStatus}</span>
                            </div>
                        )}
                    </div>


                    <div ref={canvasRef} data-testid="mindmap-canvas" className={`w-full h-full bg-gray-50 dark:bg-slate-900 cursor-grab active:cursor-grabbing relative ${layoutMode === 'stream' ? 'overflow-y-auto overflow-x-hidden cursor-auto' : layoutMode === 'flow' ? 'overflow-hidden cursor-auto' : 'overflow-hidden touch-none'} print:overflow-visible print:h-auto print:block`} style={getCanvasStyle()} onContextMenu={handleCanvasContextMenu} onPointerDown={handleCanvasPointerDown} onPointerMove={handleCanvasPointerMove} onPointerUp={handleCanvasPointerUp} onPointerLeave={handleCanvasPointerUp} onPointerCancel={handleCanvasPointerUp} onLostPointerCapture={handleCanvasLostPointerCapture} onWheel={handleWheel} onDragOver={(e) => {
                        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
                    }} onDragLeave={(e) => { if (!canvasRef.current?.contains(e.relatedTarget as Node)) setIsDragOverCanvas(false); }} onDrop={(e) => { setIsDragOverCanvas(false); handleCanvasDrop(e); }} onScroll={handleStreamScroll}>{isDragOverCanvas && !isEffectiveReadOnly && (<div className="absolute inset-0 z-[500] pointer-events-none border-4 border-dashed border-blue-400 dark:border-blue-500 bg-blue-50/30 dark:bg-blue-900/20 flex items-center justify-center"><div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl px-8 py-6 flex flex-col items-center gap-3"><ImagePlus size={32} className="text-blue-500" /><p className="text-base font-bold text-blue-600 dark:text-blue-400">{language === 'vietnamese' ? 'Thả ảnh để tạo note mới' : 'Drop image to create a note'}</p>{(selectedNodeId ?? lastActiveNodeIdRef.current) && activeNotebook && (() => { const pid = selectedNodeId ?? lastActiveNodeIdRef.current; const pnode = activeNotebook.nodes.find(n => n.id === pid); return pnode ? <p className="text-xs text-blue-400 dark:text-blue-500">{language === 'vietnamese' ? `Gắn vào: ${pnode.title || 'Untitled'}` : `Attach to: ${pnode.title || 'Untitled'}`}</p> : null; })()}</div></div>)}
                        {layoutMode === 'flow' && activeNotebook ? (
                            <FlowView
                                ref={flowViewRef}
                                nodes={activeNotebook.nodes}
                                selectedNodeId={selectedNodeId}
                                onSelectNode={handleNodeSelect}
                                onExpandNote={setExpandedNodeIdStable}
                                linksByNodeId={linksByNodeId}
                                searchQuery={searchQuery}
                            />
                        ) : layoutMode === 'blog' && activeNotebook ? (
                            <BlogView
                                notebook={activeNotebook}
                                user={user}
                                onClose={() => setLayoutMode(isMobile ? 'stream' : 'map')}
                                onUpdateNode={updateNode}
                                onExpandNote={(id) => setExpandedNodeId(id)}
                            />
                        ) : layoutMode === 'stream' && activeNotebook ? (
                            <div className="w-full max-w-2xl mx-auto pb-20 px-4 pt-24 space-y-5">
                                {activeNotebook.nodes.map(node => {
                                    const parentNode = activeNotebook.nodes.find(p => p.id === node.parentId);
                                    return (
                                        <div key={node.id} className="relative group">
                                            <NodeCard scoreMap={showScoreMap && videoScoreDisplay.canvas} node={node} isSelected={selectedNodeId === node.id} isLinkSource={linkingSourceId === node.id || node.id === pickParentForNodeId} isLinkingMode={!!linkingSourceId || !!pickParentForNodeId} isDropCandidate={dropCandidateId === node.id} isMagicExpanding={magicExpandingNodeId === node.id} isQuizMode={isQuizConfigMode} isQuizSelected={quizSelectedNodeIds.has(node.id)} onQuizSelect={handleQuizSelectNode} isGuideMode={isGuideSelectMode} isGuideSelected={guideSelectedNodeIds.has(node.id)} onGuideSelect={handleGuideSelectNode} scale={1} language={language} layoutMode={layoutMode} lodLevel="full" nodeWidth={nodeWidth} searchQuery={searchQuery} readOnly={isEffectiveReadOnly} parentTitle={parentNode?.title} onSelect={handleNodeSelect} onStartDrag={handleNodeStartDrag} onUpdate={updateNode} onDelete={requestDeleteNode} onAnalyze={handleAnalyzeNode} onGenerateTitle={handleGenerateTitle} onAddChild={addChildNode} onMagicExpand={magicExpand} onStartLink={handleStartLink} onUnlink={(targetId) => handleUnlinkNode(node.id, targetId)} onUpdateLink={handleUpdateLink} onJumpToNode={handleJumpToNodeInstant} onGetFramework={handleGetFramework} onExpandNote={setExpandedNodeIdStable} linkedNodes={linksByNodeId[node.id] || []} onStartEditing={captureSnapshot} onEndEditing={commitSnapshot} onNodeTypeChange={(id, type) => updateNode(id, { nodeType: type })} onStartReview={handleStartReview} onMoveNode={moveNode} onTagSearch={handleTagSearch} onGenerateReviewQuestions={handleAddToReviewFeed} onViewNoteQuestions={setNoteQuestionsNodeId} lastSavedTime={lastSavedTime} syncState={syncStatus.state} globalExpandCycle={effectiveExpandCycle} onExpandCycleChange={handleNodeExpandCycleChange} isSourceRoot={!node.parentId && !!node.imageBase64 && !!activeNotebook?.pdfContextUrl} hasChildNodes={activeNotebook!.nodes.some(n => n.parentId === node.id)} isMobile={isMobile} onMobileImageOpen={isMobile ? setMobileImageSrc : undefined} isZenMode={effectiveExpandCycle === 4} isSurveyMode={isGuideSelectMode ? false : isSurveyMode} onSurveyHover={isGuideSelectMode ? undefined : (n, r) => setSurveyPreview({ node: n, rect: r })} onSurveyLeave={isGuideSelectMode ? undefined : () => setSurveyPreview(null)} onNodeContextMenu={!isEffectiveReadOnly && !isGuideSelectMode ? (e, id) => setNodeContextMenu({ x: e.clientX, y: e.clientY, nodeId: id }) : undefined} isIndexed={false} />
                                        </div>
                                    );
                                })}
                                {activeNotebook.nodes.length === 0 && !activeNotebook._isPartiallyLoaded && (<div className="text-center text-gray-400 py-10">Empty Notebook. Add a note to start the feed.</div>)}
                                <div className="h-20"></div>
                            </div>
                        ) : (
                            <div ref={innerCanvasRef} className="absolute inset-0 transition-transform duration-75 ease-out origin-top-left will-change-transform pointer-events-none" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`, }}>
                                <svg className="absolute top-0 left-0 w-full h-full overflow-visible" style={{ pointerEvents: 'none' }}>

                                    <defs><marker id="arrowhead" markerWidth="10" markerHeight="7" refX="28" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" /></marker></defs>
                                    {visibleLinks.map(link => {
                                        const fromNode = activeNotebook?.nodes.find(n => n.id === link.fromId);
                                        const toNode = activeNotebook?.nodes.find(n => n.id === link.toId);
                                        if (!fromNode || !toNode) return null;
                                        const fx = fromNode.position.x, fy = fromNode.position.y;
                                        const tx = toNode.position.x, ty = toNode.position.y;
                                        const isParentChild = toNode.parentId === fromNode.id;
                                        const color = link.color || "#94a3b8";
                                        let d: string;
                                        let handleX = 0, handleY = 0;
                                        if (isParentChild) {
                                            const dx = tx - fx, dy = ty - fy, dist = Math.sqrt(dx*dx+dy*dy);
                                            const cp1x = fx + dx*0.4, cp2x = tx - dx*0.4;
                                            d = dist < 100 ? `M ${fx} ${fy} L ${tx} ${ty}` : `M ${fx} ${fy} C ${cp1x} ${fy}, ${cp2x} ${ty}, ${tx} ${ty}`;
                                        } else {
                                            const naturalMidX = (fx + tx) / 2, naturalMidY = (fy + ty) / 2;
                                            const dist = Math.sqrt((tx-fx)**2+(ty-fy)**2);
                                            if (link.midpoint) {
                                                // control point = naturalMid + 2*offset → curve passes through naturalMid + offset at t=0.5
                                                const cx = naturalMidX + 2 * link.midpoint.dx;
                                                const cy = naturalMidY + 2 * link.midpoint.dy;
                                                handleX = naturalMidX + link.midpoint.dx;
                                                handleY = naturalMidY + link.midpoint.dy;
                                                d = dist < 100 ? `M ${fx} ${fy} L ${tx} ${ty}` : `M ${fx} ${fy} Q ${cx} ${cy} ${tx} ${ty}`;
                                            } else {
                                                // Default: cubic S-curve (original appearance), handle at t=0.5 = natural midpoint
                                                const ddx = tx - fx;
                                                const cp1x = fx + ddx*0.4, cp2x = tx - ddx*0.4;
                                                d = dist < 100 ? `M ${fx} ${fy} L ${tx} ${ty}` : `M ${fx} ${fy} C ${cp1x} ${fy}, ${cp2x} ${ty}, ${tx} ${ty}`;
                                                handleX = naturalMidX; handleY = naturalMidY;
                                            }
                                        }
                                        const handleR = 6 / scale;
                                        const isSelected = selectedLinkId?.id === link.id;
                                        return (
                                            <g key={link.id}>
                                                <path d={d} stroke={color} strokeWidth={isParentChild ? "5" : "7"} strokeDasharray={isParentChild ? "none" : "12, 12"} strokeLinecap="round" fill="none" markerEnd="url(#arrowhead)" style={{ pointerEvents: 'none' }} />
                                                {/* Invisible wide hit area for clicking the link */}
                                                <path d={d} stroke="transparent" strokeWidth={20 / scale} fill="none" style={{ pointerEvents: 'all', cursor: 'pointer' }}
                                                    onPointerDown={(e) => { e.stopPropagation(); }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isSelected) { setSelectedLinkId(null); return; }
                                                        const rect = canvasRef.current?.getBoundingClientRect();
                                                        if (!rect) return;
                                                        setSelectedLinkId({ id: link.id, sx: e.clientX - rect.left, sy: e.clientY - rect.top });
                                                    }}
                                                />
                                                {!isParentChild && !isEffectiveReadOnly && (
                                                    <circle
                                                        cx={handleX} cy={handleY} r={handleR}
                                                        fill={draggingLinkId === link.id ? color : "white"}
                                                        stroke={color} strokeWidth={2 / scale}
                                                        style={{ pointerEvents: 'all', cursor: draggingLinkId === link.id ? 'grabbing' : 'grab' }}
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            setDraggingLinkId(link.id);
                                                            draggingLinkStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, dx: link.midpoint?.dx ?? 0, dy: link.midpoint?.dy ?? 0 };
                                                        }}
                                                        onDoubleClick={(e) => {
                                                            e.stopPropagation();
                                                            setNotebooks(nbs => nbs.map(nb => nb.id !== activeNotebookId ? nb : { ...nb, links: nb.links?.map(l => l.id === link.id ? { ...l, midpoint: undefined } : l), updatedAt: Date.now() }));
                                                        }}
                                                    />
                                                )}
                                            </g>
                                        );
                                    })}
                                </svg>
                                {visibleNodes.map((node) => (
                                    <NodeCard key={node.id} scoreMap={showScoreMap && videoScoreDisplay.canvas} node={node} isSelected={selectedNodeId === node.id} isLinkSource={linkingSourceId === node.id || node.id === pickParentForNodeId} isLinkingMode={!!linkingSourceId || !!importPickMode || !!pickParentForNodeId} isDropCandidate={dropCandidateId === node.id} isMagicExpanding={magicExpandingNodeId === node.id} isQuizMode={isQuizConfigMode} isQuizSelected={quizSelectedNodeIds.has(node.id)} onQuizSelect={handleQuizSelectNode} isGuideMode={isGuideSelectMode} isGuideSelected={guideSelectedNodeIds.has(node.id)} onGuideSelect={handleGuideSelectNode} scale={scale} language={language} layoutMode={layoutMode} lodLevel={getLodLevel(node)} nodeWidth={nodeWidth} searchQuery={searchQuery} readOnly={isEffectiveReadOnly} onSelect={importPickMode ? handleNodeSelectForImport : handleNodeSelect} onClick={importPickMode ? undefined : handleNodeClick} onStartDrag={handleNodeStartDrag} onUpdate={updateNode} onDelete={requestDeleteNode} onAnalyze={handleAnalyzeNode} onGenerateTitle={handleGenerateTitle} onAddChild={addChildNode} onMagicExpand={magicExpand} onStartLink={handleStartLink} onUnlink={(targetId) => handleUnlinkNode(node.id, targetId)} onUpdateLink={handleUpdateLink} onJumpToNode={handleJumpToNodeInstant} onGetFramework={handleGetFramework} onExpandNote={setExpandedNodeIdStable} linkedNodes={linksByNodeId[node.id] || []} onStartEditing={captureSnapshot} onEndEditing={commitSnapshot} onNodeTypeChange={(id, type) => updateNode(id, { nodeType: type })} onStartReview={handleStartReview} onMoveNode={moveNode} onTagSearch={handleTagSearch} onGenerateReviewQuestions={handleAddToReviewFeed} onViewNoteQuestions={setNoteQuestionsNodeId} lastSavedTime={lastSavedTime} syncState={syncStatus.state} globalExpandCycle={effectiveExpandCycle} onExpandCycleChange={handleNodeExpandCycleChange} isSourceRoot={!node.parentId && !!node.imageBase64 && !!activeNotebook?.pdfContextUrl} hasChildNodes={activeNotebook!.nodes.some(n => n.parentId === node.id)} isMobile={isMobile} onMobileImageOpen={isMobile ? setMobileImageSrc : undefined} isZenMode={effectiveExpandCycle === 4} isSurveyMode={isSurveyMode} isLinkHovered={linkHoverNodeId === node.id} onSurveyHover={(n, r) => setSurveyPreview({ node: n, rect: r })} onSurveyLeave={() => setSurveyPreview(null)} onNodeContextMenu={!isEffectiveReadOnly ? (e, id) => setNodeContextMenu({ x: e.clientX, y: e.clientY, nodeId: id }) : undefined} isIndexed={false} />
                                ))}
                            </div>
                        )}

                        {/* Loading overlay while lazy-loading nodes for a newly-switched notebook */}
                        {activeNotebook?._isPartiallyLoaded && !activeNotebook._loadFailed && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 z-10 pointer-events-none">
                                <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-slate-500">
                                    <Loader2 size={32} className="animate-spin text-blue-500" />
                                    <span className="text-sm">{language === 'vietnamese' ? 'Đang tải ghi chú...' : 'Loading notes...'}</span>
                                </div>
                            </div>
                        )}


                        {/* Empty notebook onboarding card */}
                        {!isEffectiveReadOnly && isInitialLoadComplete && activeNotebook && activeNotebook.nodes.length === 0 && !activeNotebook._isPartiallyLoaded && !activeNotebook._loadFailed && !notebookLoadPending && !pdfUrl && (
                            <EmptyNotebookOnboarding
                                onUploadPdf={() => { setMediaPanelOpenedForPdf(true); setMediaPanelOpen(true); }}
                                onCreateBlankNote={() => addNode(window.innerWidth / 2, window.innerHeight / 2)}
                                language={language}
                            />
                        )}

                        {/* Cross-link navigation popup — rendered outside SVG so it's above all NodeCards */}
                        {selectedLinkId && activeNotebook && (() => {
                            const link = activeNotebook.links?.find(l => l.id === selectedLinkId.id);
                            if (!link) return null;
                            const fromNode = activeNotebook.nodes.find(n => n.id === link.fromId);
                            const toNode = activeNotebook.nodes.find(n => n.id === link.toId);
                            if (!fromNode || !toNode) return null;
                            const popW = 220;
                            return (
                                <div
                                    data-link-popup
                                    onPointerDown={(e) => e.stopPropagation()}
                                    style={{
                                        position: 'absolute',
                                        left: Math.max(4, Math.min(selectedLinkId.sx - popW / 2, (canvasRef.current?.clientWidth ?? 800) - popW - 4)),
                                        top: selectedLinkId.sy,
                                        width: popW,
                                        zIndex: 9999,
                                        background: 'white',
                                        borderRadius: 12,
                                        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                                        border: '1px solid #e5e7eb',
                                        overflow: 'hidden',
                                        fontFamily: 'inherit',
                                    }}
                                >
                                    <button
                                        onClick={(e) => { e.stopPropagation(); flyToCanvasNode(fromNode.position.x, fromNode.position.y); setSelectedNodeId(fromNode.id); setSelectedLinkId(null); setLinkHoverNodeId(null); }}
                                        onMouseEnter={() => setLinkHoverNodeId(fromNode.id)}
                                        onMouseLeave={() => setLinkHoverNodeId(null)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left' }}
                                    >
                                        <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', minWidth: 24, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Từ</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{fromNode.title || '(Untitled)'}</span>
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); flyToCanvasNode(toNode.position.x, toNode.position.y); setSelectedNodeId(toNode.id); setSelectedLinkId(null); setLinkHoverNodeId(null); }}
                                        onMouseEnter={() => setLinkHoverNodeId(toNode.id)}
                                        onMouseLeave={() => setLinkHoverNodeId(null)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                                    >
                                        <span style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', minWidth: 24, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Đến</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{toNode.title || '(Untitled)'}</span>
                                    </button>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Load failure overlay — at workspace level so it's always visible above canvas stacking contexts */}
                    {activeNotebook?._loadFailed && (
                        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/95 dark:bg-slate-900/95">
                            <div className="flex flex-col items-center gap-4 max-w-sm text-center px-6">
                                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                    <AlertTriangle size={24} className="text-red-500" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">
                                        {language === 'vietnamese' ? 'Không thể tải notebook này' : 'Could not load this notebook'}
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                        {language === 'vietnamese'
                                            ? 'Để bảo vệ dữ liệu, StudyBoard đã tạm dừng autosave. Ghi chú của bạn vẫn an toàn trên server.'
                                            : 'To protect your data, autosave is paused. Your notes are safe on the server.'}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2 w-full">
                                    <button
                                        onClick={retryLoadFailedNotebook}
                                        disabled={retryingLoad}
                                        className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-xl font-medium text-sm transition-colors"
                                    >
                                        {retryingLoad
                                            ? <><Loader2 size={15} className="animate-spin" />{language === 'vietnamese' ? 'Đang thử lại...' : 'Retrying...'}</>
                                            : <><RefreshCw size={15} />{language === 'vietnamese' ? 'Thử tải lại' : 'Retry loading'}</>
                                        }
                                    </button>
                                    <button
                                        onClick={() => { setSettingsInitialTab('data'); setSettingsOpen(true); }}
                                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                                    >
                                        {language === 'vietnamese' ? 'Mở backup' : 'Open backups'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Post-PDF upload first-capture nudge */}
                    {showFirstCaptureNudge && !isEffectiveReadOnly && (
                        <FirstCaptureNudge
                            language={language}
                            onShowCapture={() => {
                                setShowFirstCaptureNudge(false);
                                setMediaPanelOpen(true);
                                setHighlightCaptureButton(true);
                            }}
                            onDismiss={() => setShowFirstCaptureNudge(false)}
                        />
                    )}


                    {expandedNodeId && activeNotebook && activeNotebook.nodes.some(n => n.id === expandedNodeId) && (<ExpandedNoteModal node={activeNotebook.nodes.find(n => n.id === expandedNodeId)!} isOpen={true} onClose={() => { noteExpandedClosing(expandedNodeId); setExpandedNodeId(null); setTagNavPrevNodeId(null); if (pdfReturnPending) { setPdfReturnPending(false); setMediaPanelOpen(true); setMediaPanelOpenedForPdf(true); } }} defaultFullscreen={isMobile} onUpdate={updateNode} onGetFramework={handleGetFramework} onAnalyze={handleAnalyzeNode} onCancelAnalyze={handleCancelAnalyze} onGenerateTitle={handleGenerateTitle} onOpenSettings={() => { setSettingsInitialTab('math'); setSettingsOpen(true); }} linkedNodes={linksByNodeId[expandedNodeId] || []} onNavigateToNote={(noteId) => { setExpandedNodeId(noteId); setSelectedNodeId(noteId); }} language={language} readOnly={isEffectiveReadOnly} allNodes={activeNotebook.nodes} onCreateLink={(targetId, targetNotebookId, targetTitle, targetNotebookTitle) => handleCreateCrossLink(expandedNodeId, targetId, targetNotebookId, targetTitle, targetNotebookTitle)} onUnlink={(targetId) => handleUnlinkNode(expandedNodeId, targetId)} onAddRelatedVideo={!isEffectiveReadOnly ? (ref) => handleAddRelatedVideo(expandedNodeId, ref) : undefined} onRemoveRelatedVideo={!isEffectiveReadOnly ? (refId) => handleRemoveRelatedVideo(expandedNodeId, refId) : undefined} allNotebooks={allNotebooksForModal} onNavigateCrossNotebook={(notebookId, nodeId) => { setActiveNotebookId(notebookId); setPendingCrossNav({ notebookId, nodeId }); }} currentNotebookId={activeNotebookId ?? undefined} outlinePanelOpen={outlinePanelOpen} onToggleOutline={() => setOutlinePanelOpen(o => !o)} onCreateChildNote={!isEffectiveReadOnly ? () => { const newId = addChildNode(expandedNodeId); if (newId) setExpandedNodeId(newId); } : undefined} mediaPanelOpen={mediaPanelOpen} onToggleMediaPanel={() => setMediaPanelOpen(o => !o)} lastSavedTime={lastSavedTime} syncState={syncStatus.state} globalExpandCycle={effectiveExpandCycle} onExpandCycleChange={handleNodeExpandCycleChange} onTagSearch={handleTagSearch} onBack={tagNavPrevNodeId ? () => { const prev = tagNavPrevNodeId; setTagNavPrevNodeId(null); setExpandedNodeIdStable(prev); setSelectedNodeId(prev); } : undefined} backLabel={tagNavPrevNodeId ? (activeNotebook.nodes.find(n => n.id === tagNavPrevNodeId)?.title || undefined) : undefined} onEnterOutlineLinkMode={!isEffectiveReadOnly ? () => { setOutlineLinkMode(true); setOutlinePanelOpen(true); } : undefined} onApplyOrder={applyOutlineOrder} onAutoTitle={handleAutoTitleNode} onAutoTitleBranch={handleAutoTitleBranch} onRenameNode={!isEffectiveReadOnly ? (id, newTitle) => updateNode(id, { title: newTitle }) : undefined} linkMode={outlineLinkMode && !!expandedNodeId} onLinkSelect={(targetId) => { if (expandedNodeId) handleCreateCrossLink(expandedNodeId, targetId); setOutlineLinkMode(false); }} onCancelLinkMode={() => setOutlineLinkMode(false)} onCreateChildNoteAt={!isEffectiveReadOnly ? (parentId) => { const newId = addChildNode(parentId); if (newId) setExpandedNodeId(newId); } : undefined} userApiKeys={userApiKeys} findReplaceVersion={findReplaceVersion} onResolveTextConflict={!isEffectiveReadOnly ? (action, i) => handleResolveTextConflict(expandedNodeId, action, i) : undefined} externalFindQuery={findReplaceOpen ? findReplaceQuery : ''} onSharePost={!isEffectiveReadOnly && expandedNodeId ? () => setShareNodeId(expandedNodeId) : undefined} onUploadYouTube={!isEffectiveReadOnly && expandedNodeId ? () => openUploadSession(expandedNodeId) : undefined} onDrawOnScreen={!isEffectiveReadOnly && uploadSessions.length === 0 ? () => setQuickDrawOpen(true) : undefined} {...outlineLinkProps} onLoadNotebook={handleLoadNotebookForOutline} elevateAboveOverlay={showThreadsAdvisor || showYouTubeManager} />)}

                    {/* Canvas background context menu */}
                    {canvasBgMenu && (
                        <>
                            <div className="fixed inset-0 z-[99]" onClick={() => setCanvasBgMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCanvasBgMenu(null); }} />
                            <div className="fixed z-[100] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-4 w-72"
                                style={{ left: Math.min(canvasBgMenu.x, window.innerWidth - 296), top: Math.min(canvasBgMenu.y, window.innerHeight - 520) }}>
                                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">{language === 'vietnamese' ? 'Nền canvas' : 'Canvas Background'}</p>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {([
                                        { color: '#f8fafc', label: 'Gray' },
                                        { color: '#ffffff', label: 'White' },
                                        { color: '#fefce8', label: 'Cream' },
                                        { color: '#eff6ff', label: 'Blue' },
                                        { color: '#f5f3ff', label: 'Purple' },
                                        { color: '#f0fdf4', label: 'Green' },
                                        { color: '#fce7f3', label: 'Pink' },
                                        { color: '#1e293b', label: 'Dark' },
                                    ] as { color: string; label: string }[]).map(({ color, label }) => (
                                        <Tip key={color} text={label} side="top">
                                        <button onClick={() => handleCanvasBgSelect({ color })}
                                            className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${canvasBg.color === color ? 'border-blue-500 scale-110 shadow-md' : 'border-gray-200 dark:border-slate-600'}`}
                                            style={{ backgroundColor: color }} />
                                        </Tip>
                                    ))}
                                    {savedCustomColors.map(color => (
                                        <Tip key={color} text={color} side="top">
                                            <button onClick={() => handleCanvasBgSelect({ color })}
                                                className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 relative group ${canvasBg.color === color ? 'border-blue-500 scale-110 shadow-md' : 'border-gray-200 dark:border-slate-600'}`}
                                                style={{ backgroundColor: color }}>
                                                <span onClick={(e) => { e.stopPropagation(); removeCustomColor(color); }}
                                                    className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-gray-500 hover:bg-red-500 text-white rounded-full text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity leading-none cursor-pointer">×</span>
                                            </button>
                                        </Tip>
                                    ))}
                                    <label title="Custom color" className={`w-8 h-8 rounded-full border-2 cursor-pointer transition-all hover:scale-110 overflow-hidden flex items-center justify-center ${!PRESET_CANVAS_COLORS.includes(canvasBg.color) && !savedCustomColors.includes(canvasBg.color) ? 'border-blue-500 scale-110 shadow-md' : 'border-dashed border-gray-300 dark:border-slate-500'}`}>
                                        <input type="color" value={canvasBg.color} onChange={(e) => handleCanvasBgSelect({ color: e.target.value })} onBlur={(e) => saveCustomColor(e.target.value)} className="opacity-0 absolute w-8 h-8 cursor-pointer" />
                                        <span className="text-gray-400 text-xs pointer-events-none">+</span>
                                    </label>
                                </div>
                                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">{language === 'vietnamese' ? 'Pattern' : 'Pattern'}</p>
                                <div className="grid grid-cols-5 gap-1.5">
                                    {([
                                        { id: 'none', label: language === 'vietnamese' ? 'Trơn' : 'None' },
                                        { id: 'dots-sm', label: language === 'vietnamese' ? 'Chấm S' : 'Dots S' },
                                        { id: 'dots-lg', label: language === 'vietnamese' ? 'Chấm L' : 'Dots L' },
                                        { id: 'grid', label: 'Grid' },
                                        { id: 'lines', label: language === 'vietnamese' ? 'Kẻ' : 'Lines' },
                                    ] as { id: string; label: string }[]).map(({ id, label }) => (
                                        <button key={id} onClick={() => handleCanvasBgSelect({ pattern: id })}
                                            className={`py-1.5 text-[10px] font-semibold rounded-lg border transition-all ${canvasBg.pattern === id ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                                    <button onClick={() => {
                                        const isDark = document.documentElement.classList.contains('dark');
                                        document.documentElement.classList.toggle('dark', !isDark);
                                        localStorage.setItem('darkMode', !isDark ? 'true' : 'false');
                                        posthog.capture('dark_mode_toggled', { enabled: !isDark });
                                        if (user && user.id !== 'guest') saveUserPrefs(user.id, { darkMode: !isDark });
                                    }} className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                            {document.documentElement.classList.contains('dark')
                                                ? (language === 'vietnamese' ? 'Chế độ sáng' : 'Light Mode')
                                                : (language === 'vietnamese' ? 'Chế độ tối' : 'Dark Mode')}
                                        </span>
                                        {document.documentElement.classList.contains('dark')
                                            ? <Sun size={14} className="text-yellow-500" />
                                            : <Moon size={14} className="text-purple-500" />}
                                    </button>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">{language === 'vietnamese' ? 'Bố cục' : 'Layout'}</p>
                                    <div className="grid grid-cols-3 gap-1">
                                        {isMobile && <button onClick={() => { setLayoutMode('stream'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${layoutMode === 'stream' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><GalleryVertical size={14} />{language === 'vietnamese' ? 'Dòng' : 'Stream'}</button>}
                                        <button onClick={() => { setLayoutMode('blog'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${layoutMode === 'blog' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><BookOpen size={14} />Blog</button>
                                        {!isMobile && <>
                                            <button onClick={() => { setLayoutMode('map'); setMapLayoutStyle('horizontal'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${layoutMode === 'map' && mapLayoutStyle === 'horizontal' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><GitGraph size={14} className="rotate-90" />{language === 'vietnamese' ? 'Cây ngang' : 'H. Tree'}</button>
                                            <button onClick={() => { setLayoutMode('map'); setMapLayoutStyle('vertical'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${layoutMode === 'map' && mapLayoutStyle === 'vertical' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><GitGraph size={14} />{language === 'vietnamese' ? 'Cây dọc' : 'V. Tree'}</button>
                                            <button onClick={() => { setLayoutMode('flow'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${layoutMode === 'flow' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><Columns size={14} />Flow</button>
                                        </>}
                                    </div>
                                </div>
                                {layoutMode === 'map' && (
                                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">{language === 'vietnamese' ? 'Độ rộng thẻ' : 'Card Width'}</p>
                                        <div className="grid grid-cols-3 gap-1">
                                            <button onClick={() => { handleSetNodeWidth('standard'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${nodeWidth === 'standard' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><Minimize size={14} />{language === 'vietnamese' ? 'Chuẩn' : 'Standard'}</button>
                                            <button onClick={() => { handleSetNodeWidth('wide'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${nodeWidth === 'wide' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><Maximize size={14} />{language === 'vietnamese' ? 'Rộng' : 'Wide'}</button>
                                            <button onClick={() => { handleSetNodeWidth('extra-wide'); setCanvasBgMenu(null); }} className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg text-[10px] font-semibold border transition-all ${nodeWidth === 'extra-wide' ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}><MoveHorizontal size={14} />{language === 'vietnamese' ? 'Rất rộng' : 'X-Wide'}</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Survey mode floating preview */}
                    {isHoverPreviewEnabled && surveyPreview && !expandedNodeId && (() => {
                        const PANEL_W = 520;
                        const rect = surveyPreview.rect;
                        const spaceRight = window.innerWidth - rect.right - 16;
                        const spaceLeft = rect.left - 16;
                        const left = spaceRight >= PANEL_W ? rect.right + 12 : spaceLeft >= PANEL_W ? rect.left - PANEL_W - 12 : Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_W - 8));
                        const PANEL_MAX_H = window.innerHeight * 0.8;
                        const top = Math.max(8, Math.min(rect.top, window.innerHeight - PANEL_MAX_H - 8));
                        const node = surveyPreview.node;
                        const allImages = [
                            ...(node.imageBase64 ? [{ id: 'main', content: node.imageBase64 }] : []),
                            ...(node.drawingBase64 ? [{ id: 'drawing', content: node.drawingBase64 }] : []),
                            ...(node.attachments || []).filter(a => a.type === 'image').map(a => ({ id: a.id, content: a.content })),
                        ];
                        return (
                            <div
                                data-survey-panel="true"
                                className="fixed z-[200] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-y-auto cursor-pointer flex flex-col"
                                style={{ left, top, width: PANEL_W, maxHeight: PANEL_MAX_H }}
                                onMouseEnter={() => setSurveyPreview(p => p)}
                                onClick={() => { setExpandedNodeIdStable(node.id); setSurveyPreview(null); }}
                            >
                                {allImages.map(img => (
                                    <img key={img.id} src={img.content} alt="" className="w-full h-auto block" draggable={false} />
                                ))}
                                <div className="p-5 flex flex-col gap-3">
                                    {node.title && <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 leading-snug">{node.title}</h3>}
                                    {node.tags && node.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {node.tags.map((tag, i) => (
                                                <span key={i} className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-[11px] font-semibold rounded-full border border-indigo-200 dark:border-indigo-700/50">{tag}</span>
                                            ))}
                                        </div>
                                    )}
                                    {node.content && (
                                        <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed prose dark:prose-invert max-w-none [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4" dangerouslySetInnerHTML={{ __html: parseMarkdown(node.content) }} />
                                    )}
                                    <div className="flex items-center gap-1.5 mt-1 text-[11px] text-teal-500 font-semibold">
                                        <Pin size={12} />
                                        {language === 'vietnamese' ? 'Nhấn để mở' : 'Click to open'}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Search peek modal — read-only, closes without losing search state */}
                    {searchPeekNode && (<ExpandedNoteModal node={searchPeekNode.node} isOpen={true} onClose={() => setSearchPeekNode(null)} onUpdate={() => {}} onGetFramework={() => {}} onAnalyze={() => {}} language={language} readOnly={true} allNodes={notebooks.find(nb => nb.id === searchPeekNode.notebookId)?.nodes || []} allNotebooks={allNotebooksForModal} onNavigateCrossNotebook={(notebookId, nodeId) => { setActiveNotebookId(notebookId); setExpandedNodeId(nodeId); setSearchPeekNode(null); setSearchQuery(''); setIsSearchFocused(false); }} currentNotebookId={searchPeekNode.notebookId} lastSavedTime={lastSavedTime} syncState={syncStatus.state} />)}

                    {/* Hover preview card — small floating card, appears on hovering search result */}
                    {hoverPreview && (() => {
                        const isExpanded = searchHoverExpanded;
                        const cardW = isExpanded ? 680 : 320;
                        const cardH = isExpanded ? 880 : 380;
                        const vpW = window.innerWidth; const vpH = window.innerHeight;
                        let left = hoverPreview.left + cardW > vpW - 8 ? hoverPreview.left - cardW - 34 : hoverPreview.left;
                        if (left < 8) left = 8;
                        let top = hoverPreview.top;
                        if (top + cardH > vpH - 8) top = vpH - cardH - 8;
                        if (top < 8) top = 8;
                        const hoverPreviewNodes = notebooks.find(nb => nb.id === hoverPreview.notebookId)?.nodes ?? activeNotebook?.nodes ?? [];
                        const { prevId: hoverPrevId, nextId: hoverNextId } = getAdjacentNoteIds(hoverPreviewNodes, hoverPreview.node.id);
                        const goToAdjacentHover = (id: string | null) => {
                            if (!id) return;
                            if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                            const target = hoverPreviewNodes.find(n => n.id === id);
                            if (target) setHoverPreview(prev => prev ? { ...prev, node: target } : prev);
                        };
                        return (
                            <div
                                style={{ position: 'fixed', top, left, zIndex: 9999, width: cardW, maxHeight: cardH, transition: 'width 0.2s ease, max-height 0.2s ease' }}
                                className={`bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-150 relative ${isExpanded ? 'overflow-y-auto' : 'overflow-hidden flex flex-col'}`}
                                onMouseEnter={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); }}
                                onMouseLeave={() => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); hoverTimerRef.current = setTimeout(() => { setHoverPreview(null); setSearchHoverExpanded(false); }, 120); }}
                            >
                                <Tip text={isExpanded ? (language === 'vietnamese' ? 'Thu nhỏ' : 'Collapse') : (language === 'vietnamese' ? 'Phóng to' : 'Expand')} side="bottom">
                                <button
                                    onClick={() => setSearchHoverExpanded(v => !v)}
                                    className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-white/90 dark:bg-slate-700/90 text-gray-600 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
                                >
                                    {isExpanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
                                </button>
                                </Tip>
                                <NodeImageSlider node={hoverPreview.node} expanded={isExpanded} />
                                <div className={isExpanded ? 'p-3.5' : 'p-3.5 overflow-y-auto flex-1 min-h-0'} onWheel={(e) => e.stopPropagation()}>
                                    {hoverPreview.node.title && (
                                        <div className={`font-semibold text-sm text-gray-900 dark:text-white mb-2 ${isExpanded ? 'pr-8' : 'pr-6'}`}>{hoverPreview.node.title}</div>
                                    )}
                                    {hoverPreview.node.content ? (
                                        <div className={`${isExpanded ? 'text-sm' : 'text-xs'} text-gray-500 dark:text-gray-400 leading-relaxed [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>ul]:pl-3 [&>ol]:pl-3 [&>li]:my-0.5 [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-semibold`} dangerouslySetInnerHTML={{ __html: parseMarkdown(hoverPreview.node.content) }} />
                                    ) : (
                                        <p className="text-xs text-gray-300 dark:text-gray-600 italic">{language === 'vietnamese' ? 'Không có nội dung' : 'No content'}</p>
                                    )}
                                    {!isExpanded && (
                                        <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-slate-700 flex justify-end">
                                            <button
                                                onMouseDown={(e) => { e.preventDefault(); setSearchPeekNode({ node: hoverPreview.node, notebookId: hoverPreview.notebookId }); setHoverPreview(null); setSearchHoverExpanded(false); }}
                                                className="text-xs text-blue-500 hover:text-blue-600 font-medium flex items-center gap-1 transition-colors"
                                            >
                                                <Maximize2 size={11} />{language === 'vietnamese' ? 'Mở rộng' : 'Full view'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {(hoverPrevId || hoverNextId) && (
                                    <div className="shrink-0 flex items-center justify-between border-t border-gray-100 dark:border-slate-700 px-2 py-1.5 bg-gray-50/80 dark:bg-slate-900/40">
                                        <button
                                            onClick={() => goToAdjacentHover(hoverPrevId)}
                                            disabled={!hoverPrevId}
                                            title={language === 'vietnamese' ? 'Note trước đó' : 'Previous note'}
                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                                        >
                                            <ChevronLeft size={13} /> {language === 'vietnamese' ? 'Lùi' : 'Back'}
                                        </button>
                                        <button
                                            onClick={() => goToAdjacentHover(hoverNextId)}
                                            disabled={!hoverNextId}
                                            title={language === 'vietnamese' ? 'Note tiếp theo' : 'Next note'}
                                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                                        >
                                            {language === 'vietnamese' ? 'Tiến' : 'Next'} <ChevronRight size={13} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })()}


                </div>
                {/* End Workspace Wrapper */}

                {/* Review Feed Panel — full-screen on mobile, 320px sidebar on desktop */}
                {user && !isGuest && reviewFeedPanelOpen && (
                    <div className="sm:hidden fixed inset-0 z-[150] bg-white dark:bg-slate-900">
                        <ReviewFeedPanel
                            userId={user.id}
                            language={language}
                            activeNotebookId={activeNotebookId ?? undefined}
                            notebooks={notebooks.map(nb => ({ id: nb.id, nodes: nb._isPartiallyLoaded ? (crossSearchIndex.current.get(nb.id) ?? []).map(s => ({ id: s.id, title: s.title })) : nb.nodes.map(n => ({ id: n.id, title: n.title })) }))}
                            onClose={() => setReviewFeedPanelOpen(false)}
                            onOpenNote={(nodeId, notebookId) => {
                                const nb = notebooks.find(n => n.id === notebookId);
                                if (nb) setActiveNotebookId(notebookId);
                                setReviewFeedPanelOpen(false);
                                setExpandedNodeId(nodeId);
                            }}
                            onGenerateMissing={handleGenerateMissingQuestions}
                            isGeneratingMissing={isGeneratingMissing}
                        />
                    </div>
                )}
                {user && !isGuest && (
                    <div
                        className={`hidden sm:block relative h-full shrink-0 border-l border-gray-100 dark:border-slate-800/60 ${isResizingReviewFeed ? 'transition-none' : 'transition-[width] duration-300'} overflow-hidden`}
                        style={{ width: reviewFeedPanelOpen ? reviewFeedWidth : 0 }}
                    >
                        {reviewFeedPanelOpen && (
                            <>
                                <div
                                    className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-blue-400/40 transition-colors"
                                    onMouseDown={() => setIsResizingReviewFeed(true)}
                                    onTouchStart={(e) => { e.preventDefault(); setIsResizingReviewFeed(true); }}
                                />
                                <ReviewFeedPanel
                                    userId={user.id}
                                    language={language}
                                    activeNotebookId={activeNotebookId ?? undefined}
                                    notebooks={notebooks.map(nb => ({ id: nb.id, nodes: nb._isPartiallyLoaded ? (crossSearchIndex.current.get(nb.id) ?? []).map(s => ({ id: s.id, title: s.title })) : nb.nodes.map(n => ({ id: n.id, title: n.title })) }))}
                                    onClose={() => setReviewFeedPanelOpen(false)}
                                    onOpenNote={(nodeId, notebookId) => {
                                        const nb = notebooks.find(n => n.id === notebookId);
                                        if (nb) setActiveNotebookId(notebookId);
                                        setExpandedNodeId(nodeId);
                                    }}
                                    onGenerateMissing={handleGenerateMissingQuestions}
                                    isGeneratingMissing={isGeneratingMissing}
                                />
                            </>
                        )}
                    </div>
                )}

                {/* Outline Panel — full-screen drawer on mobile, flex sibling on desktop */}
                {isMobile && outlinePanelOpen && activeNotebook && (
                    <div className="fixed inset-0 z-[250] flex flex-col bg-white dark:bg-slate-900">
                        <OutlinePanel
                            nodes={activeNotebook.nodes}
                            onApplyOrder={applyOutlineOrder}
                            onFocusNode={(nodeId) => {
                                if (outlineLinkMode) return;
                                setOutlinePanelOpen(false);
                                if (expandedNodeId) setExpandedNodeId(nodeId);
                                else if (layoutMode === 'stream') handleJumpToNode(nodeId);
                                else if (layoutMode === 'flow') { setSelectedNodeId(nodeId); flowViewRef.current?.scrollToNode(nodeId); }
                                else focusNode(nodeId);
                            }}
                            onExpandNode={(nodeId) => { setExpandedNodeId(nodeId); setSelectedNodeId(nodeId); }}
                            onClose={() => { setOutlinePanelOpen(false); setOutlineLinkMode(false); }}
                            onAutoTitle={handleAutoTitleNode}
                            onAutoTitleBranch={handleAutoTitleBranch}
                            onCreateChildNote={!isEffectiveReadOnly ? (parentId) => {
                                const newId = addChildNode(parentId);
                                if (newId) { setOutlinePanelOpen(false); setExpandedNodeId(newId); }
                            } : undefined}
                            onRenameNode={!isEffectiveReadOnly ? (id, newTitle) => updateNode(id, { title: newTitle }) : undefined}
                            activeNodeId={expandedNodeId || selectedNodeId}
                            language={language}
                            linkMode={outlineLinkMode && !!expandedNodeId}
                            onLinkSelect={(targetId, notebookId, nodeTitle, notebookTitle) => { if (expandedNodeId) handleCreateCrossLink(expandedNodeId, targetId, notebookId, nodeTitle, notebookTitle); setOutlineLinkMode(false); }}
                            onCancelLinkMode={() => setOutlineLinkMode(false)}
                            initialCollapsedIds={outlineCollapsedIds}
                            onCollapsedIdsChange={setOutlineCollapsedIds}
                            onCreateLink={expandedNodeId ? (targetId, notebookId, nodeTitle, notebookTitle) => handleCreateCrossLink(expandedNodeId, targetId, notebookId, nodeTitle, notebookTitle) : undefined}
                            {...outlineLinkProps}
                            allNotebooks={allNotebooksForModal}
                            currentNotebookId={activeNotebookId ?? undefined}
                            onLoadNotebook={handleLoadNotebookForOutline}
                        />
                    </div>
                )}
                <div
                    ref={outlinePanelElRef}
                    className={`relative h-full shrink-0 border-l border-gray-100 dark:border-slate-800/60 ${isResizingOutline ? 'transition-none' : 'transition-[width] duration-300'} overflow-hidden ${isMobile ? 'hidden' : ''}`}
                    style={{ width: !isMobile && outlinePanelOpen ? outlinePanelWidth : 0 }}
                >
                    {!isMobile && outlinePanelOpen && (
                        <>
                            <div
                                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 hover:bg-blue-400/40 transition-colors"
                                onMouseDown={() => setIsResizingOutline(true)}
                            />
                            {activeNotebook && (
                                <OutlinePanel
                                    nodes={activeNotebook.nodes}
                                    onApplyOrder={applyOutlineOrder}
                                    onFocusNode={(nodeId) => { if (!outlineLinkMode) { expandedNodeId ? setExpandedNodeId(nodeId) : layoutMode === 'stream' ? handleJumpToNode(nodeId) : layoutMode === 'flow' ? (setSelectedNodeId(nodeId), flowViewRef.current?.scrollToNode(nodeId)) : focusNode(nodeId); } }}
                                    onExpandNode={(nodeId) => { setExpandedNodeId(nodeId); setSelectedNodeId(nodeId); }}
                                    onClose={() => { setOutlinePanelOpen(false); setOutlineLinkMode(false); }}
                                    onAutoTitle={handleAutoTitleNode}
                                    onAutoTitleBranch={handleAutoTitleBranch}
                                    onCreateChildNote={!isEffectiveReadOnly ? (parentId) => {
                                        const newId = addChildNode(parentId);
                                        if (newId) { setExpandedNodeId(newId); focusNode(newId); }
                                    } : undefined}
                                    onRenameNode={!isEffectiveReadOnly ? (id, newTitle) => updateNode(id, { title: newTitle }) : undefined}
                                    activeNodeId={expandedNodeId || selectedNodeId}
                                    language={language}
                                    linkMode={outlineLinkMode && !!expandedNodeId}
                                    onLinkSelect={(targetId, notebookId, nodeTitle, notebookTitle) => { if (expandedNodeId) handleCreateCrossLink(expandedNodeId, targetId, notebookId, nodeTitle, notebookTitle); setOutlineLinkMode(false); }}
                                    onCancelLinkMode={() => setOutlineLinkMode(false)}
                                    initialCollapsedIds={outlineCollapsedIds}
                                    onCollapsedIdsChange={setOutlineCollapsedIds}
                                    onCreateLink={expandedNodeId ? (targetId, notebookId, nodeTitle, notebookTitle) => handleCreateCrossLink(expandedNodeId, targetId, notebookId, nodeTitle, notebookTitle) : undefined}
                            {...outlineLinkProps}
                                    allNotebooks={allNotebooksForModal}
                                    currentNotebookId={activeNotebookId ?? undefined}
                                    onLoadNotebook={handleLoadNotebookForOutline}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Find & Replace — flex sibling so ExpandedNoteModal respects it */}
                <div
                    className={`relative h-full shrink-0 border-l border-gray-100 dark:border-slate-800/60 transition-[width] duration-300 overflow-hidden ${isMobile ? 'hidden' : ''}`}
                    style={{ width: findReplaceOpen && activeNotebook && !isEffectiveReadOnly ? 360 : 0 }}
                >
                    {findReplaceOpen && activeNotebook && !isEffectiveReadOnly && (
                        <FindReplacePanel
                            nodes={findReplaceScope === 'note' && expandedNodeId ? activeNotebook.nodes.filter(n => n.id === expandedNodeId) : activeNotebook.nodes}
                            language={language}
                            onClose={() => setFindReplaceOpen(false)}
                            onJumpToNode={handleJumpToNode}
                            onUpdateNode={(id, data) => {
                                updateNode(id, data);
                                if (expandedNodeId === id) setFindReplaceVersion(v => v + 1);
                            }}
                            expandedNodeId={expandedNodeId}
                            onOpenExpanded={(id) => { setExpandedNodeId(id); setSelectedNodeId(id); }}
                            onQueryChange={setFindReplaceQuery}
                            scope={findReplaceScope}
                            initialQuery={findReplaceQuery}
                        />
                    )}
                </div>

                {/* Phase 2: MediaPanel as Flex Sibling */}
                <div
                    ref={mediaPanelElRef}
                    className={`relative h-full flex flex-row ${isResizingPanel ? 'transition-none' : 'transition-[width] duration-300'} shrink-0 ${mediaPanelOpen ? 'border-l border-gray-100 dark:border-slate-800/60' : 'border-none overflow-hidden'}`}
                    style={{ width: mediaPanelOpen ? (isMobile ? '100%' : mediaPanelWidth) : 0 }}
                >
                    <div className="hidden md:flex w-2 h-full bg-transparent hover:bg-blue-500 cursor-col-resize z-50 absolute left-0 top-0 bottom-0 items-center justify-center group/resize transition-colors" onMouseDown={() => setIsResizingPanel(true)} onTouchStart={(e) => { e.preventDefault(); setIsResizingPanel(true); }}><div className="h-8 w-1 bg-gray-300 group-hover/resize:bg-white rounded-full"></div></div>
                    {/* Inner row: MediaPanel + docked Guided Reading Rail */}
                    <div className="flex-1 min-w-0 h-full flex flex-row overflow-hidden">
                        <div className="flex-1 min-w-0 h-full overflow-hidden">
                            <MediaPanel
                                isOpen={mediaPanelOpen}
                                onClose={() => { setMediaPanelOpen(false); setMediaPanelOpenedForPdf(false); }}
                                onSnap={(base64, extractedText) => {
                                    setPendingSnapshot(base64);
                                    setPendingSnapshotText(extractedText || '');
                                    setShowFirstCaptureNudge(false);
                                    setHighlightCaptureButton(false);
                                }}
                                onPageChange={(page) => setPdfCurrentPage(page)}
                                pdfUrl={pdfUrl}
                                onUploadPdf={handleUploadPdf}
                                language={language}
                                annotations={activeNotebook?.pdfAnnotations}
                                onSaveAnnotations={handleSavePdfAnnotations}
                                onDocumentReady={handleDocumentReady}
                                onGuidedReading={handleGuidedReading}
                                guidedReadingHighlights={guidedReadingHighlightsVisible ? guidedReadingHighlights : []}
                                isGuidedReadingLoading={guidedReadingLoading}
                                userEmail={user?.email}
                                documentTitle={activeNotebook?.pdfContextUrl ? activeNotebook.title : undefined}
                                boardTitle={activeNotebook?.title}
                                autoSwitchToPdf={mediaPanelOpenedForPdf}
                                highlightCaptureButton={highlightCaptureButton}
                                isMobile={isMobile}
                                onVoiceCapture={undefined}
                                selectedNoteName={activeNotebook?.nodes.find(n => n.id === selectedNodeId)?.title}
                            />
                        </div>
                        {/* Guided Reading Rail — docked right side, does not overlay PDF */}
                        {guidedReadingData && (
                            <GuidedReadingPanel
                                result={guidedReadingData}
                                language={language}
                                pageNumber={pdfCurrentPage}
                                highlightsVisible={guidedReadingHighlightsVisible}
                                onToggleHighlights={() => setGuidedReadingHighlightsVisible(v => !v)}
                                onCreateNote={handleCreateNoteFromGuided}
                                onSetHighlights={setGuidedReadingHighlights}
                                onRefresh={() => {
                                    setGuidedReadingData(null);
                                    setGuidedReadingHighlights([]);
                                }}
                                onClose={() => {
                                    setGuidedReadingData(null);
                                    setGuidedReadingHighlights([]);
                                    setGuidedReadingHighlightsVisible(true);
                                }}
                            />
                        )}
                    </div>
                </div>

                {linkDecision && (
                    <>
                        <div className="fixed inset-0 z-[299]" onClick={() => setLinkDecision(null)} onContextMenu={(e) => { e.preventDefault(); setLinkDecision(null); }} />
                        <div className="fixed z-[300] bg-white dark:bg-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-slate-700 p-2 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-200 overflow-y-auto max-h-[80vh]" style={{ left: Math.min(linkDecision.position.x, window.innerWidth - 220), top: Math.min(linkDecision.position.y, window.innerHeight - 230) }}>
                            <button onClick={() => executeRelationshipLink('cross')} className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"><div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg group-hover:scale-110 transition-transform"><LinkIcon size={18} /></div><span>{language === 'vietnamese' ? 'Liên kết ngang hàng' : 'Cross-link'}</span></button>
                            <button onClick={() => executeRelationshipLink('child')} className="flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"><div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-lg group-hover:scale-110 transition-transform"><GitPullRequest size={18} /></div><span>{language === 'vietnamese' ? 'Đặt làm con' : 'Set as Child'}</span></button>
                            <button onClick={() => executeMergeNotes()} className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"><div className="p-2 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-lg group-hover:scale-110 transition-transform"><GitMerge size={18} /></div><span>{language === 'vietnamese' ? 'Nhập vào' : 'Merge Into'}</span></button>
                            <div className="h-px bg-gray-100 dark:bg-slate-700 my-1 mx-2"></div>
                            <button onClick={() => setLinkDecision(null)} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors text-xs font-bold text-gray-400 dark:text-gray-500"><X size={14} className="ml-2" /><span>{language === 'vietnamese' ? 'Hủy bỏ' : 'Cancel'}</span></button>
                        </div>
                    </>
                )}

                {/* Node right-click context menu */}
                {nodeContextMenu && (() => {
                    const ncm = nodeContextMenu;
                    const ncmNode = activeNotebook?.nodes.find(n => n.id === ncm.nodeId);
                    const hasChildren = !!ncmNode && activeNotebook!.nodes.some(n => n.parentId === ncm.nodeId);
                    return (
                        <>
                            <div className="fixed inset-0 z-[299]" onClick={() => setNodeContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setNodeContextMenu(null); }} />
                            <div
                                className="fixed z-[300] bg-white dark:bg-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-slate-700 p-2 flex flex-col gap-1 animate-in fade-in zoom-in-95 duration-200 min-w-[180px]"
                                style={{ left: Math.min(ncm.x, window.innerWidth - 210), top: Math.min(ncm.y, window.innerHeight - 340) }}
                            >
                                <button
                                    onClick={() => { handleDuplicateNote(ncm.nodeId); setNodeContextMenu(null); }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                >
                                    <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg group-hover:scale-110 transition-transform"><Copy size={15} /></div>
                                    <span>{language === 'vietnamese' ? 'Nhân đôi note' : 'Duplicate note'}</span>
                                </button>
                                {hasChildren && (
                                    <button
                                        onClick={() => { handleDuplicateBranch(ncm.nodeId); setNodeContextMenu(null); }}
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                    >
                                        <div className="p-1.5 bg-teal-100 dark:bg-teal-900/30 text-teal-600 rounded-lg group-hover:scale-110 transition-transform"><GitBranchPlus size={15} /></div>
                                        <span>{language === 'vietnamese' ? 'Nhân đôi cả nhánh' : 'Duplicate branch'}</span>
                                    </button>
                                )}
                                <div className="h-px bg-gray-100 dark:bg-slate-700 my-1 mx-2" />
                                <button
                                    onClick={() => { handleStartLink(ncm.nodeId); setNodeContextMenu(null); }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                >
                                    <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-lg group-hover:scale-110 transition-transform"><Link2 size={15} /></div>
                                    <span>{language === 'vietnamese' ? 'Tạo cross link' : 'Create cross link'}</span>
                                </button>
                                <button
                                    onClick={() => { addChildNode(ncm.nodeId); setNodeContextMenu(null); }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                >
                                    <div className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-lg group-hover:scale-110 transition-transform"><PlusCircle size={15} /></div>
                                    <span>{language === 'vietnamese' ? 'Thêm note con' : 'Add child note'}</span>
                                </button>
                                <button
                                    onClick={() => { setShareNodeId(ncm.nodeId); setNodeContextMenu(null); }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                >
                                    <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-lg group-hover:scale-110 transition-transform"><Share2 size={15} /></div>
                                    <span>{language === 'vietnamese' ? 'Chia sẻ note' : 'Share as card'}</span>
                                </button>
                                <button
                                    onClick={() => {
                                        const vNode = activeNotebook?.nodes.find(n => n.id === ncm.nodeId);
                                        setVideoEditDraft({ url: vNode?.videoUrl || '', title: vNode?.videoTitle || '', duration: vNode?.videoDuration || '' });
                                        setVideoEditNodeId(ncm.nodeId);
                                        setNodeContextMenu(null);
                                    }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                >
                                    <div className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-lg group-hover:scale-110 transition-transform"><span className="text-sm">🎥</span></div>
                                    <span>{ncmNode?.videoUrl ? (language === 'vietnamese' ? 'Sửa video link' : 'Edit video link') : (language === 'vietnamese' ? 'Thêm video link' : 'Add video link')}</span>
                                </button>
                                <button
                                    onClick={() => { openUploadSession(ncm.nodeId); setNodeContextMenu(null); }}
                                    className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors text-sm font-bold text-gray-700 dark:text-gray-200 group"
                                >
                                    <div className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-lg group-hover:scale-110 transition-transform"><Upload size={15} className="text-red-600" /></div>
                                    <span>{language === 'vietnamese' ? 'Upload video → YouTube' : 'Upload video → YouTube'}</span>
                                </button>
                                <div className="h-px bg-gray-100 dark:bg-slate-700 my-1 mx-2" />
                                <button
                                    onClick={() => { setNodeContextMenu(null); }}
                                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors text-xs font-bold text-gray-400 dark:text-gray-500"
                                >
                                    <X size={14} className="ml-2" /><span>{language === 'vietnamese' ? 'Hủy bỏ' : 'Cancel'}</span>
                                </button>
                            </div>
                        </>
                    );
                })()}

                {videoEditNodeId && activeNotebook && (() => {
                    const vNode = activeNotebook.nodes.find(n => n.id === videoEditNodeId);
                    if (!vNode) return null;
                    return (
                        <>
                            <div className="fixed inset-0 z-[399] bg-black/40 backdrop-blur-sm" onClick={() => setVideoEditNodeId(null)} />
                            <div className="fixed z-[400] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.4)] border border-gray-100 dark:border-slate-700 p-6 w-[360px] flex flex-col gap-4 animate-in fade-in zoom-in-95 duration-200">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">🎥</span>
                                    <div>
                                        <h3 className="font-bold text-gray-900 dark:text-white text-base">{language === 'vietnamese' ? 'Gắn video YouTube' : 'Attach YouTube Video'}</h3>
                                        <p className="text-xs text-gray-400 mt-0.5">{language === 'vietnamese' ? 'Dán link YouTube vào đây' : 'Paste a YouTube URL'}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 block">URL *</label>
                                        <input
                                            type="url"
                                            autoFocus
                                            placeholder="https://youtube.com/watch?v=..."
                                            value={videoEditDraft.url}
                                            onChange={e => setVideoEditDraft(d => ({ ...d, url: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 block">{language === 'vietnamese' ? 'Tiêu đề (tuỳ chọn)' : 'Title (optional)'}</label>
                                        <input
                                            type="text"
                                            placeholder={language === 'vietnamese' ? 'Tên video...' : 'Video title...'}
                                            value={videoEditDraft.title}
                                            onChange={e => setVideoEditDraft(d => ({ ...d, title: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 block">{language === 'vietnamese' ? 'Thời lượng (tuỳ chọn)' : 'Duration (optional)'}</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. 12:34"
                                            value={videoEditDraft.duration}
                                            onChange={e => setVideoEditDraft(d => ({ ...d, duration: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-700"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-1">
                                    {vNode.videoUrl && (
                                        <button
                                            onClick={() => { updateNode(videoEditNodeId, { videoUrl: undefined, videoTitle: undefined, videoDuration: undefined }); setVideoEditNodeId(null); }}
                                            className="flex-1 px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-bold hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            {language === 'vietnamese' ? 'Xóa video' : 'Remove'}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setVideoEditNodeId(null)}
                                        className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        {language === 'vietnamese' ? 'Hủy' : 'Cancel'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            const url = videoEditDraft.url.trim();
                                            if (!url) return;
                                            updateNode(videoEditNodeId, {
                                                videoUrl: url,
                                                videoTitle: videoEditDraft.title.trim() || undefined,
                                                videoDuration: videoEditDraft.duration.trim() || undefined,
                                            });
                                            setVideoEditNodeId(null);
                                        }}
                                        disabled={!videoEditDraft.url.trim()}
                                        className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors"
                                    >
                                        {language === 'vietnamese' ? 'Lưu' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </>
                    );
                })()}

                {uploadSessions.map((sess, idx) => {
                    const nb = notebooks.find(n => n.id === sess.notebookId) ?? sess.notebookSnapshot;
                    const ytNode = nb?.nodes?.find(n => n.id === sess.nodeId) ?? sess.nodeSnapshot;
                    const ytNotebook = (nb && nb.nodes?.length) ? nb : (sess.notebookSnapshot ?? nb);
                    if (!ytNode || !ytNotebook) return null;
                    const removeSession = () => {
                        setUploadSessions(prev => prev.filter(x => x.sid !== sess.sid));
                        setForegroundSid(cur => cur === sess.sid ? null : cur);
                        setRecordingSids(prev => prev.filter(x => x !== sess.sid));
                    };
                    return (
                        <YouTubeUploadModal
                            key={sess.sid}
                            sessionId={sess.sid}
                            slot={sess.slot}
                            isForeground={foregroundSid === sess.sid}
                            pillIndex={idx}
                            onRequestForeground={() => requestUploadForeground(sess.sid)}
                            onRecordingChange={rec => setRecordingSids(prev => rec ? (prev.includes(sess.sid) ? prev : [...prev, sess.sid]) : prev.filter(x => x !== sess.sid))}
                            node={ytNode}
                            notebook={ytNotebook}
                            language={language}
                            uid={user?.id}
                            initialFile={sess.initialFile}
                            sourceJobId={sess.sourceJobId}
                            initialTitle={sess.initialTitle}
                            clipOfJobId={sess.clipOfJobId}
                            onClipsStarted={clips => setPendingClips(prev => [...prev, ...clips.map(c => ({ ...c, nodeId: sess.nodeId, notebookId: sess.notebookId }))])}
                            onClose={removeSession}
                            onAttachmentsSaved={(attachments, notebookId, nodeId) => setNotebooks(nbs => nbs.map(nb => nb.id !== notebookId ? nb : {
                                ...nb,
                                nodes: nb.nodes.map(n => n.id === nodeId ? { ...n, attachments } : n),
                            }))}
                            drawingTarget={drawingTargetNote}
                            liveNote={liveExpandedNote}
                            onDone={async (videoUrl, videoTitle, videoId, videoDescription, segments, links) => {
                                if (videoId) {
                                    // The modal already linked the video on the server. Read that back
                                    // instead of patching from ytNode, which can predate another upload
                                    // to the same note and would overwrite that video's link on save.
                                    let fresh: any = null;
                                    try {
                                        const ownerId = auth.currentUser?.uid || user?.id;
                                        const s = await getDoc(doc(db, `users/${ownerId}/notebooks/${sess.notebookId}/nodes/${sess.nodeId}`));
                                        fresh = s.exists() ? s.data() : null;
                                    } catch { /* fall back to the local copy */ }
                                    const linked = fresh && (fresh.youtubeVideoId === videoId || (fresh.youtubeVideos || []).some((v: any) => v.youtubeVideoId === videoId));
                                    if (linked) {
                                        updateNode(sess.nodeId, Object.fromEntries(Object.entries(fresh).filter(([k]) => /^(youtube|video)/.test(k))) as Partial<MindMapNode>);
                                    } else {
                                        // The dialog's own write did not land. Link the video on the server
                                        // here (autosave does not carry video fields), then mirror it locally.
                                        const appendPatch = buildVideoAppendPatch(fresh ? { youtubeVideoId: fresh.youtubeVideoId, youtubeVideos: fresh.youtubeVideos } : ytNode, {
                                        youtubeVideoId: videoId,
                                        youtubeStatus: 'private' as const,
                                        youtubeTitle: videoTitle,
                                        youtubeUploadedAt: Date.now(),
                                        videoUrl, videoTitle,
                                        ...(videoDescription ? { youtubeDescription: videoDescription } : {}),
                                        // The saved file and export job: without them the video cannot be combined or reused later.
                                        ...(links?.videoStoragePath ? { videoStoragePath: links.videoStoragePath } : {}),
                                        ...(links?.exportJobId ? { exportJobId: links.exportJobId } : {}),
                                        ...(links?.clipOfJobId ? { clipOfJobId: links.clipOfJobId } : {}),
                                        ...(segments && segments.length > 0 ? { videoSegments: segments.map(s => ({
                                            index: s.index, startSecs: s.startSecs, endSecs: s.endSecs,
                                            status: s.status, score: s.score, clarityScore: s.clarityScore,
                                            riskLevel: s.riskLevel, transcript: s.transcript || '',
                                            summary: s.summary, mainIssue: s.mainIssue,
                                            suggestedCorrection: s.suggestedCorrection, overridden: s.overridden,
                                        })) } : {}),
                                    });
                                        const ownerId = auth.currentUser?.uid || user?.id;
                                        if (ownerId) safeUpdateDoc(doc(db, `users/${ownerId}/notebooks/${sess.notebookId}/nodes/${sess.nodeId}`), appendPatch).catch(e => console.error('[upload] could not link the video to its note', e));
                                        updateNode(sess.nodeId, appendPatch);
                                    }
                                }
                                removeSession();
                                if (sess.notebookId) setGithubPublishNotebookId(sess.notebookId);
                            }}
                        />
                    );
                })}

                <ClipJobsWatcher
                    clips={pendingClips}
                    uid={user?.id}
                    vn={language === 'vietnamese'}
                    onReady={(clip, file) => openUploadSession(clip.nodeId, { notebookId: clip.notebookId, initialFile: file, sourceJobId: clip.jobId, initialTitle: clip.title, clipOfJobId: clip.mainJobId, background: true })}
                    onGone={jobId => setPendingClips(prev => prev.filter(c => c.jobId !== jobId))}
                />

                {/* Post-recording dialog */}
                {pendingRecordingFile && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 w-80 flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Circle size={18} className="fill-red-500 text-red-500" />
                                </div>
                                <div>
                                    <p className="font-bold text-gray-900 dark:text-white text-sm">{language === 'vietnamese' ? 'Ghi hình xong!' : 'Recording done!'}</p>
                                    <p className="text-xs text-gray-400">{pendingRecordingFile.name}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    const url = URL.createObjectURL(pendingRecordingFile);
                                    const a = document.createElement('a');
                                    a.href = url; a.download = pendingRecordingFile.name; a.click();
                                    URL.revokeObjectURL(url);
                                    setPendingRecordingFile(null);
                                    setRecordingTargetNodeId(null);
                                }}
                                className="w-full py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                            >
                                <Download size={15} /> {language === 'vietnamese' ? 'Download về máy' : 'Download'}
                            </button>
                            <button
                                onClick={() => {
                                    const nodeId = recordingTargetNodeId || expandedNodeId || selectedNodeId || activeNotebook?.nodes[0]?.id;
                                    if (nodeId) {
                                        openUploadSession(nodeId, { initialFile: pendingRecordingFile });
                                        setPendingRecordingFile(null);
                                        setRecordingTargetNodeId(null);
                                    }
                                }}
                                className="w-full py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex flex-col items-center gap-0.5"
                            >
                                <span className="flex items-center gap-2">
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                                    {language === 'vietnamese' ? 'Upload lên YouTube' : 'Upload to YouTube'}
                                </span>
                                {(() => {
                                    const nodeId = recordingTargetNodeId || expandedNodeId || selectedNodeId || activeNotebook?.nodes[0]?.id;
                                    const nodeTitle = activeNotebook?.nodes.find(n => n.id === nodeId)?.title;
                                    return nodeTitle ? <span className="text-[11px] text-red-200 font-normal">→ {nodeTitle}</span> : null;
                                })()}
                            </button>
                            <button onClick={() => { setPendingRecordingFile(null); setRecordingTargetNodeId(null); }} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-center transition-colors">
                                {language === 'vietnamese' ? 'Bỏ qua' : 'Discard'}
                            </button>
                        </div>
                    </div>
                )}

                {shareNodeId && activeNotebook && (() => {
                    const shareNode = activeNotebook.nodes.find(n => n.id === shareNodeId);
                    if (!shareNode) return null;
                    return <SocialPostModal node={shareNode} notebook={activeNotebook} language={language} onClose={() => setShareNodeId(null)} />;
                })()}

                {/* Right-edge vertical action bar — desktop only */}
                {!isQuizConfigMode && !isMobile && (
                    <div
                        ref={mediaActionBarRef}
                        className="absolute top-1/2 -translate-y-1/2 z-20 flex flex-col bg-white dark:bg-slate-800 rounded-l-xl shadow-lg border-l border-y border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-700 transition-[right] duration-300 [&>*:first-child]:rounded-tl-xl [&>*:last-child]:rounded-bl-xl"
                        style={{ right: mediaPanelOpen ? `${mediaPanelWidth}px` : 0 }}
                    >
                        {!isEffectiveReadOnly && user && !isGuest && (
                            <Tip text={language === 'vietnamese' ? 'Review Feed' : 'Review Feed'} side="left">
                            <button
                                onClick={() => setReviewFeedPanelOpen(o => !o)}
                                className={`p-3 transition-colors active:bg-amber-100 dark:active:bg-amber-900/40 active:text-amber-600 ${reviewFeedPanelOpen ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-500' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-amber-500'}`}
                            >
                                <Zap size={20} />
                            </button>
                            </Tip>
                        )}
                        <Tip text={language === 'vietnamese' ? 'Cấu trúc' : 'Outline'} side="left">
                        <button
                            onClick={() => setOutlinePanelOpen(o => !o)}
                            className={`p-3 transition-colors active:bg-blue-100 dark:active:bg-blue-900/40 active:text-blue-600 ${outlinePanelOpen ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500' : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-blue-500'}`}
                        >
                            <ListTree size={20} />
                        </button>
                        </Tip>
                        {!isEffectiveReadOnly && !mediaPanelOpen && (
                            <Tip text={language === 'vietnamese' ? 'PDF / Ảnh' : 'PDF / Media'} side="left">
                            <button
                                onClick={() => setMediaPanelOpen(true)}
                                className="p-3 text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-blue-500 active:bg-blue-100 dark:active:bg-blue-900/40 active:text-blue-600 transition-colors"
                            >
                                <Monitor size={20} />
                            </button>
                            </Tip>
                        )}
                        {!isEffectiveReadOnly && activeNotebook && (
                            <Tip text={language === 'vietnamese' ? 'Tạo Quiz' : 'Generate Quiz'} side="left">
                            <button
                                onClick={toggleQuizMode}
                                className="p-3 text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-violet-500 active:bg-violet-100 dark:active:bg-violet-900/40 active:text-violet-600 transition-colors"
                            >
                                {isGeneratingQuiz ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
                            </button>
                            </Tip>
                        )}
                    </div>
                )}

                {/* Mobile bottom tab bar — replaces right-side vertical bar */}
                {isMobile && !isQuizConfigMode && activeNotebook && (
                    <div
                        className="fixed bottom-0 left-0 right-0 z-[60] flex flex-row bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-700 print:hidden"
                        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                    >
                        {/* Cards tab — active when neither PDF nor Review feed is open */}
                        <button
                            onClick={() => { setMediaPanelOpen(false); setReviewFeedPanelOpen(false); logMobileEvent('tab.cards', { from: mediaPanelOpen ? 'pdf' : reviewFeedPanelOpen ? 'review' : 'cards' }); }}
                            className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors active:bg-blue-50 dark:active:bg-blue-900/20 ${!mediaPanelOpen && !reviewFeedPanelOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}
                        >
                            <LayoutGrid size={22} />
                            {language === 'vietnamese' ? 'Thẻ' : 'Cards'}
                        </button>

                        {!isEffectiveReadOnly && (
                            <button
                                onClick={() => { const next = !mediaPanelOpen; setMediaPanelOpen(next); logMobileEvent(next ? 'tab.pdf.open' : 'tab.pdf.close'); }}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors active:bg-blue-50 dark:active:bg-blue-900/20 ${mediaPanelOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}`}
                            >
                                <Monitor size={22} />
                                PDF
                            </button>
                        )}

                        {!isEffectiveReadOnly && user && !isGuest && (
                            <button
                                onClick={() => setReviewFeedPanelOpen(o => !o)}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors active:bg-amber-50 dark:active:bg-amber-900/20 ${reviewFeedPanelOpen ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500'}`}
                            >
                                <Zap size={22} />
                                {language === 'vietnamese' ? 'Ôn tập' : 'Review'}
                            </button>
                        )}

                        {!isEffectiveReadOnly && (
                            <button
                                onClick={toggleQuizMode}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors active:bg-violet-50 dark:active:bg-violet-900/20 ${isQuizConfigMode ? 'text-violet-600 dark:text-violet-400' : 'text-gray-400 dark:text-slate-500'}`}
                            >
                                {isGeneratingQuiz ? <Loader2 size={22} className="animate-spin" /> : <Sparkles size={22} />}
                                Quiz
                            </button>
                        )}

                        {!isEffectiveReadOnly && user && !isGuest && (
                            <button
                                onClick={() => { setPodcastHubOpen(true); logMobileEvent('tab.podcast'); }}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors active:bg-purple-50 dark:active:bg-purple-900/20 ${podcastHubOpen ? 'text-purple-500 dark:text-purple-400' : 'text-gray-400 dark:text-slate-500'}`}
                            >
                                <Radio size={22} />
                                Podcast
                            </button>
                        )}

                        <button
                            onClick={() => { setSettingsOpen(true); logMobileEvent('tab.settings'); }}
                            className="flex-1 flex flex-col items-center justify-center gap-1 py-3 text-[11px] font-semibold transition-colors active:bg-gray-50 dark:active:bg-slate-800 text-gray-400 dark:text-slate-500"
                        >
                            <Settings size={22} />
                            {language === 'vietnamese' ? 'Cài đặt' : 'Settings'}
                        </button>
                    </div>
                )}

                {/* Outline FAB — floats above tab bar, toggles full-screen outline panel */}
                {isMobile && !isQuizConfigMode && activeNotebook && !mediaPanelOpen && !expandedNodeId && (
                    <button
                        onClick={() => { const next = !outlinePanelOpen; setOutlinePanelOpen(next); logMobileEvent(next ? 'outline.open' : 'outline.close'); }}
                        className={`fixed z-[270] flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all active:scale-95 print:hidden ${outlinePanelOpen ? 'bg-blue-600 text-white shadow-blue-500/40' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-700'}`}
                        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 72px)', right: '16px' }}
                    >
                        {outlinePanelOpen ? <X size={20} /> : <ListTree size={20} />}
                    </button>
                )}

                {!isMobile && !isQuizConfigMode && (layoutMode === 'map' || layoutMode === 'stream') && (
                    <div className={`${mediaPanelOpen ? 'hidden' : ''} ${layoutMode === 'stream' ? 'fixed bottom-6 left-6 z-50' : 'absolute top-28 left-4 md:top-auto md:bottom-6 md:left-6 z-20'} flex flex-col gap-2 pointer-events-auto print:hidden`}>
                        {/* Expand mode context menu */}
                        {showExpandMenu && (
                            <>
                                <div className="fixed inset-0 z-[49]" onClick={() => setShowExpandMenu(false)} />
                                <div className="absolute bottom-full mb-2 left-0 z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden w-52">
                                    {([
                                        { state: 0 as const, icon: <Maximize2 size={15} />, label: language === 'vietnamese' ? 'Thu gọn' : 'Compact', desc: language === 'vietnamese' ? 'Ẩn text, chỉ hiện tiêu đề' : 'Hide text, show title only' },
                                        { state: 1 as const, icon: <GalleryVertical size={15} />, label: language === 'vietnamese' ? 'Mở rộng text' : 'Expand Text', desc: language === 'vietnamese' ? 'Hiện đầy đủ nội dung text' : 'Show full text content' },
                                        { state: 2 as const, icon: <Minimize2 size={15} />, label: language === 'vietnamese' ? 'Mở rộng ảnh' : 'Expand Images', desc: language === 'vietnamese' ? 'Hiện đầy đủ ảnh, không cắt' : 'Show full images, no crop' },
                                        { state: 3 as const, icon: <Minimize size={15} />, label: language === 'vietnamese' ? 'Thu gọn text' : 'Collapse Text', desc: language === 'vietnamese' ? 'Thu gọn text, giữ ảnh' : 'Collapse text, keep images' },
                                        { state: 4 as const, icon: <ScanLine size={15} />, label: 'Zen', desc: language === 'vietnamese' ? 'Chỉ ảnh + text, chọn để xem thêm' : 'Image + text only, select to reveal' },
                                    ] as { state: 0|1|2|3|4, icon: React.ReactNode, label: string, desc: string }[]).map(({ state, icon, label, desc }) => (
                                        <button key={state} onClick={() => handleSelectExpandCycle(state)} className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${globalExpandCycle === state ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300' : 'hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200'}`}>
                                            <span className={`flex-shrink-0 ${globalExpandCycle === state ? 'text-violet-600 dark:text-violet-400' : 'text-gray-400'}`}>{icon}</span>
                                            <div className="min-w-0">
                                                <div className="text-xs font-semibold leading-tight">{label}</div>
                                                <div className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight mt-0.5">{desc}</div>
                                            </div>
                                            {globalExpandCycle === state && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0" />}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                        <Tip text={language === 'vietnamese' ? 'Chế độ hiển thị thẻ' : 'Card display mode'} side="top">
                        <button onClick={() => setShowExpandMenu(v => !v)} className={`p-2 rounded-lg shadow-lg border active:scale-95 transition-all mb-2 ${
                            globalExpandCycle === 0 ? 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                            : globalExpandCycle === 1 ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                            : globalExpandCycle === 2 ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                            : globalExpandCycle === 3 ? 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                            : 'bg-violet-50 dark:bg-violet-900/30 border-violet-300 dark:border-violet-700 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/50'
                        }`}>
                            {globalExpandCycle === 0 ? <Maximize2 size={20} /> : globalExpandCycle === 1 ? <GalleryVertical size={20} /> : globalExpandCycle === 2 ? <Minimize2 size={20} /> : globalExpandCycle === 3 ? <Minimize size={20} /> : <ScanLine size={20} />}
                        </button>
                        </Tip>
                        {layoutMode === 'map' && (
                            <>
                                <Tip text={language === 'vietnamese' ? 'Về trung tâm' : 'Center View'} side="top">
                                <button onClick={handleTargetView} className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:scale-95 transition-all text-blue-600"><Target size={20} /></button>
                                </Tip>
                                <Tip text={language === 'vietnamese' ? 'Phóng to' : 'Zoom In'} side="top">
                                <button onClick={() => handleZoom(0.1)} className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:scale-95 transition-all"><ZoomIn size={20} /></button>
                                </Tip>
                                <Tip text={language === 'vietnamese' ? 'Thu nhỏ' : 'Zoom Out'} side="top">
                                <button onClick={() => handleZoom(-0.1)} className="p-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:scale-95 transition-all"><ZoomOut size={20} /></button>
                                </Tip>
                                {videoScoreDisplay.canvas && (<>
                                <Tip text={language === 'vietnamese' ? 'Bản đồ điểm: hiện điểm tiềm năng video trên từng note (✓ = đã có video)' : 'Score map: show each note\'s video-potential score (✓ = has a video)'} side="top">
                                <button onClick={() => setShowScoreMap(v => !v)} className={`p-2 rounded-lg shadow-lg border active:scale-95 transition-all text-xs font-black tabular-nums ${showScoreMap ? 'bg-green-500 border-green-600 text-white' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`} style={{ width: 38, height: 38 }}>88</button>
                                </Tip>
                                </>)}
                                <Tip text={language === 'vietnamese' ? 'Chế độ duyệt nhanh' : 'Survey Mode'} side="top">
                                <button onClick={() => { setIsSurveyMode(v => { posthog.capture('survey_mode_toggled', { enabled: !v }); return !v; }); setSurveyPreview(null); }} className={`p-2 rounded-lg shadow-lg border active:scale-95 transition-all ${isSurveyMode ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700 text-teal-600 dark:text-teal-400' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}><Pin size={20} /></button>
                                </Tip>
                            </>
                        )}
                    </div>
                )}

                <ChatWidget
                    language={language}
                    rightOffset={isSurveyMode ? 80 : 24}
                    onNavigateToNote={(notebookId, nodeId) => {
                        setActiveNotebookId(notebookId);
                        setTimeout(() => setExpandedNodeIdStable(nodeId), 300);
                    }}
                />


                {quiz && (<QuizModal quiz={quiz} onClose={() => setQuiz(null)} />)}

                {isGuideSelectMode && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-3 px-5 py-3 bg-white dark:bg-slate-900 border border-violet-200 dark:border-violet-700 rounded-2xl shadow-2xl shadow-violet-500/20 animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                                <FileText size={14} className="text-violet-600 dark:text-violet-400" />
                            </div>
                            <span className="text-sm font-bold text-gray-800 dark:text-white">
                                {language === 'vietnamese' ? 'Guide Builder' : 'Guide Builder'}
                            </span>
                        </div>
                        <span className="text-xs text-gray-400 hidden sm:inline">
                            {language === 'vietnamese' ? 'Click vào note để chọn' : 'Click notes to select'}
                        </span>
                        <div className="h-5 w-px bg-gray-200 dark:bg-slate-700" />
                        <span className="text-sm font-bold text-violet-600 dark:text-violet-400 min-w-[2.5rem] text-center">
                            {guideSelectedNodeIds.size} {language === 'vietnamese' ? 'note' : 'notes'}
                        </span>
                        <button
                            onClick={confirmGuideSelection}
                            disabled={guideSelectedNodeIds.size === 0}
                            className="px-4 py-2 bg-violet-500 hover:bg-violet-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl transition-colors">
                            {language === 'vietnamese' ? 'Build Guide →' : 'Build Guide →'}
                        </button>
                        <button
                            onClick={() => setIsGuideSelectMode(false)}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-gray-400">
                            <X size={15} />
                        </button>
                    </div>
                )}

                {isQuizConfigMode && activeNotebook && (
                    <QuizSelectionModal
                        isOpen={isQuizConfigMode}
                        onClose={toggleQuizMode}
                        nodes={activeNotebook.nodes}
                        onConfirm={handleGenerateQuiz}
                        selectedIds={quizSelectedNodeIds}
                        onToggle={handleQuizSelectNode}
                        isLoading={isGeneratingQuiz}
                    />
                )}

                {isLinkSelectorOpen && activeNotebook && linkingSourceId && (
                    <LinkSelectorModal
                        isOpen={isLinkSelectorOpen}
                        onClose={() => { setIsLinkSelectorOpen(false); setLinkingSourceId(null); }}
                        nodes={activeNotebook.nodes}
                        sourceId={linkingSourceId}
                        language={language}
                        onSelect={(targetId) => {
                            setLinkDecision({
                                sourceId: linkingSourceId!,
                                targetId: targetId,
                                position: { x: window.innerWidth / 2, y: window.innerHeight / 2 }
                            });
                            setLinkingSourceId(null);
                            setIsLinkSelectorOpen(false);
                        }}
                    />
                )}

                <SnapshotDecisionModal isOpen={!!pendingSnapshot} onClose={() => setPendingSnapshot(null)} image={pendingSnapshot} hasSelectedNode={!!selectedNodeId} selectedNodeTitle={activeNotebook?.nodes.find(n => n.id === selectedNodeId)?.title} isSelectedNodeRoot={!!selectedNodeId && !activeNotebook?.nodes.find(n => n.id === selectedNodeId)?.parentId} onAddToSelected={() => handleSnapshotAction('add')} onCreateNew={() => handleSnapshotAction('create')} onAskAI={handleOpenAskAI} language={language} />
                <AskAIModal
                    isOpen={askAIOpen}
                    onClose={() => { setAskAIOpen(false); setAskAISnapshot(null); setAskAIContext(undefined); }}
                    imageBase64={askAISnapshot}
                    language={language}
                    context={askAIContext}
                    onCreateNote={(base64) => {
                        handleSnapshotAction('create', base64);
                        setAskAIOpen(false);
                        setAskAISnapshot(null);
                        setAskAIContext(undefined);
                    }}
                />

                <SettingsModal
                    isOpen={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    onSave={(key, client) => {
                        const config = { apiKey: key, clientId: client };
                        setDriveConfig(config);
                        localStorage.setItem('ms_drive_config', JSON.stringify(config));
                        setSettingsOpen(false);
                        window.location.reload();
                    }}
                    initialApiKey={driveConfig.apiKey}
                    initialClientId={driveConfig.clientId}
                    language={language}
                    onExportBackup={handleExportBackup}
                    onImportBackup={handleImportBackup}
                    onExportSmmx={handleExportSmmx}
                    onImportSmmx={handleImportSmmx}
                    isPremium={effectiveIsPremium}
                    isAdmin={isAdmin}
                    simulateFreeUser={simulateFreeUser}
                    onSimulateFreeUserChange={setSimulateFreeUser}
                    initialTab={settingsInitialTab}
                    notebooks={notebooks.map(n => ({ id: n.id, title: n.title, nodeCount: n.nodeCount ?? n.nodes.length, imageCount: n._isPartiallyLoaded ? undefined : n.nodes.filter(nd => nd.imageBase64?.startsWith('https://')).length }))}
                    onGetCurrentNodes={getNotebookNodes}
                    activeNotebookId={activeNotebookId}
                    userId={user?.id}
                    onManualBackup={handleManualBackup}
                    onListSnapshots={handleListSnapshots}
                    onRestoreSnapshot={handleRestoreSnapshot}
                    onRestoreMissingScreenshots={handleRestoreMissingScreenshots}
                    onRestoreMissingNotes={handleRestoreMissingNotes}
                    dataTabInitialNotebookId={settingsDataTabNotebookId}
                    autoTitleEnabled={autoTitleEnabled}
                    onAutoTitleEnabledChange={v => { setAutoTitleEnabled(v); localStorage.setItem('autoTitleEnabled', String(v)); }}
                    driveConnected={driveConnected}
                    onDriveConnect={connectDrive}
                    onDriveDisconnect={disconnectDrive}
                    onDriveBackup={handleDriveBackup}
                    onDriveRestore={handleDriveRestore}
                    driveBackupRecords={driveBackupRecords}
                    onIndexAllNotes={async () => {
                        const allNotebooks = notebooksRef.current.filter(nb => !nb.isDeleted);
                        for (const nb of allNotebooks) {
                            for (const node of nb.nodes) {
                                if (!node.content?.trim() && !node.title?.trim()) continue;
                                if (node.nodeType === 'label') continue;
                                await generateNodeEmbedding(nb.id, node.id).catch(() => {});
                            }
                        }
                    }}
                    onRevertNodeChanges={handleRevertNodeChanges}
                    onOpenNote={(notebookId, nodeId) => {
                        if (notebookId !== activeNotebookId) setActiveNotebookId(notebookId);
                        setExpandedNodeId(nodeId);
                        setSettingsOpen(false);
                    }}
                    githubStatus={githubStatus}
                    onConnectGitHub={handleConnectGitHub}
                    onUpdateGitHubRepo={handleUpdateGitHubRepo}
                    onClearGitHubConfig={handleClearGithubConfig}
                    onUpgrade={() => { setSettingsOpen(false); setUpgradeModalOpen(true); }}
                    onManageSubscription={handleManageSubscription}
                    subscriptionStatus={subscriptionStatus}
                    isMobile={isMobile}
                    onMobileSync={handleMobileSync}
                    hasUnsyncedChanges={hasUnsyncedChanges}
                    syncState={syncStatus.state}
                />


                {/* GitHub Hub Modal */}
                {showGitHubHub && (
                    <GitHubHubModal
                        githubStatus={githubStatus}
                        notebooks={notebooks.filter(nb => !nb.isDeleted).map(nb => {
                            const lastPub = nb.lastPublishedAt ?? 0;
                            const latestNodeUpdatedAt = nb._isPartiallyLoaded
                                ? null
                                : (nb.nodes?.length
                                    ? Math.max(...nb.nodes.map(n => (n as any).updatedAt || (n as any).createdAt || 0))
                                    : 0);
                            const changedNodeCount = nb._isPartiallyLoaded
                                ? null
                                : (lastPub > 0
                                    ? (nb.nodes?.filter(n => ((n as any).updatedAt || (n as any).createdAt || 0) > lastPub).length ?? 0)
                                    : (nb.nodes?.length ?? 0));
                            return {
                                id: nb.id,
                                title: nb.title,
                                nodeCount: nb.nodeCount ?? nb.nodes?.length,
                                description: nb.description,
                                githubGroup: nb.githubGroup,
                                lastPublishUrl: nb.lastPublishUrl ?? null,
                                lastPublishedAt: nb.lastPublishedAt ?? null,
                                lastPublishedNodeCount: nb.lastPublishedNodeCount ?? null,
                                latestNodeUpdatedAt: latestNodeUpdatedAt || null,
                                notebookUpdatedAt: nb.updatedAt ?? null,
                                changedNodeCount,
                            };
                        })}
                        language={language}
                        onClose={() => setShowGitHubHub(false)}
                        onUpdateGitHubRepo={handleUpdateGitHubRepo}
                        onClearGitHubConfig={handleClearGithubConfig}
                        onPublishNotebook={(notebookId) => { setShowGitHubHub(false); setGithubPublishNotebookId(notebookId); }}
                        onBulkPublish={(ids, mode, commitMessage) => {
                            setShowGitHubHub(false);
                            showToast(language === 'vietnamese' ? `Bắt đầu publish ${ids.length} notebooks… Bạn có thể làm việc khác.` : `Publishing ${ids.length} notebooks… You can do something else.`, 'info');
                            setBulkPublishQueue({ ids, mode, commitMessage, idx: 0 });
                        }}
                        onUpdateNotebookDescription={handleUpdateNotebookDescription}
                        onUpdateNotebookGroup={handleUpdateNotebookGroup}
                        onUnpublishNotebook={(notebookId) => {
                            setNotebooks(nbs => nbs.map(n =>
                                n.id === notebookId
                                    ? { ...n, lastPublishedAt: undefined, lastPublishedNodeCount: undefined, publishedFolderSlug: undefined, lastPublishUrl: undefined }
                                    : n
                            ));
                        }}
                        userId={user?.id}
                    />
                )}

                {/* GitHub Publish Modal */}
                {githubPublishNotebookId && (() => {
                    const nb = notebooks.find(n => n.id === githubPublishNotebookId);
                    if (!nb) return null;
                    return (
                        <GitHubPublishModal
                            notebookId={nb.id}
                            notebookTitle={nb.title}
                            nodes={nb.nodes}
                            links={nb.links}
                            lastPublishedAt={nb.lastPublishedAt}
                            status={githubStatus}
                            onClose={() => setGithubPublishNotebookId(null)}
                            onOpenSettings={() => { setGithubPublishNotebookId(null); setShowGitHubHub(true); }}
                            language={language}
                            onPublishSuccess={() => {
                                const nbId = nb.id;
                                setNotebooks(nbs => nbs.map(n =>
                                    n.id === nbId ? { ...n, lastPublishedNodeCount: n.nodeCount ?? n.nodes.length, lastPublishedAt: Date.now() } : n
                                ));
                            }}
                        />
                    );
                })()}

                {isExportModalOpen && (
                    <ExportModal
                        isOpen={isExportModalOpen}
                        onClose={() => setIsExportModalOpen(false)}
                        notebooks={notebooks}
                        onExport={executeExportBackup}
                        language={language}
                    />
                )}


                <UpgradeModal isOpen={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} onCheckout={handleCheckout} language={language} />


                {user && <MarketplaceModal
                    isOpen={marketplaceOpen}
                    onClose={() => setMarketplaceOpen(false)}
                    currentUserId={user.id}
                    language={language}
                    onCloneSuccess={(newNotebookId) => {
                        posthog.capture('notebook_cloned');
                        showToast(language === 'vietnamese' ? 'Đã clone notebook thành công!' : 'Notebook cloned!', 'success');
                        setActiveNotebookId(newNotebookId);
                    }}
                />}

                {shareModalOpen && activeNotebook && user && (
                    <ShareModal
                        isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} notebookId={activeNotebook.id} notebookTitle={activeNotebook.title} isPublic={!!activeNotebook.isPublic} onTogglePublic={(newStatus) => { const updated = { ...activeNotebook, isPublic: newStatus }; setNotebooks(nbs => nbs.map(n => n.id === activeNotebook.id ? updated : n)); setNotebookPublic(user.id, activeNotebook.id, newStatus, { title: activeNotebook.title, nodeCount: activeNotebook.nodes.length, ownerName: user.name }).catch(err => { console.error(err); showToast("Failed to update share settings", 'error'); setNotebooks(nbs => nbs.map(n => n.id === activeNotebook.id ? activeNotebook : n)); }); }} language={language} onPreview={() => { setTestViewMode(true); setShareModalOpen(false); }} uid={user.id}
                    />
                )}

                {/* Context Menu */}
                {contextMenu && (
                    <>
                        <div
                            className="fixed inset-0 z-[9998]"
                            onClick={() => setContextMenu(null)}
                        />
                        <div
                            className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 py-1 min-w-[220px] overflow-y-auto"
                            style={{
                                left: Math.min(contextMenu.x, window.innerWidth - 240),
                                top: Math.min(contextMenu.y, window.innerHeight - 420),
                                maxHeight: window.innerHeight - 24,
                            }}
                        >
                            {/* Rename */}
                            <button
                                onClick={() => {
                                    handleStartRename(contextMenu.notebookId, notebooks.find(n => n.id === contextMenu.notebookId)?.title || '');
                                    setContextMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-2 transition-colors"
                            >
                                <Edit3 size={14} className="text-gray-500" />
                                <span className="text-gray-700 dark:text-gray-200">{language === 'vietnamese' ? 'Đổi tên' : 'Rename'}</span>
                            </button>
                            {/* Share */}
                            {!notebooks.find(n => n.id === contextMenu.notebookId)?.title.startsWith('👋') && (
                                <button
                                    onClick={() => {
                                        setActiveNotebookId(contextMenu.notebookId);
                                        setShareModalOpen(true);
                                        setContextMenu(null);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2 transition-colors"
                                >
                                    <Share2 size={14} className="text-green-500" />
                                    <span className="text-gray-700 dark:text-gray-200">{language === 'vietnamese' ? 'Chia sẻ' : 'Share'}</span>
                                </button>
                            )}
                            <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                            {/* Generate titles */}
                            <button
                                onClick={() => {
                                    handleBulkGenerateTitles(contextMenu.notebookId);
                                    setContextMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-violet-50 dark:hover:bg-violet-900/20 flex items-center gap-2 transition-colors"
                            >
                                <Sparkles size={14} className="text-violet-500" />
                                {(() => {
                                    const stats = notebookStats.find(s => s.id === contextMenu.notebookId);
                                    const count = stats?.untitledCount || 0;
                                    return (
                                        <span className="text-gray-700 dark:text-gray-200">
                                            {language === 'vietnamese'
                                                ? `Tạo title tự động (${count} note)`
                                                : `Generate titles (${count} notes)`}
                                        </span>
                                    );
                                })()}
                            </button>
                            {/* AI Settings */}
                            <button
                                onClick={() => {
                                    setSettingsInitialTab('ai');
                                    setSettingsInitialAIScope(contextMenu.notebookId);
                                    setSettingsOpen(true);
                                    setContextMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center gap-2 transition-colors"
                            >
                                <BrainCircuit size={14} className="text-indigo-500" />
                                <span className="text-gray-700 dark:text-gray-200">{language === 'vietnamese' ? 'Cài đặt AI' : 'AI Settings'}</span>
                            </button>
                            {/* Math Patterns toggle */}
                            <button
                                onClick={() => {
                                    handleToggleNotebookPatterns(contextMenu.notebookId);
                                    setContextMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-2 transition-colors"
                            >
                                <Sigma size={14} className="text-purple-500" />
                                <span className="text-gray-700 dark:text-gray-200">
                                    {(() => {
                                        const nb = notebooks.find(n => n.id === contextMenu.notebookId);
                                        const isOn = nb?.patternsEnabled !== false;
                                        return language === 'vietnamese'
                                            ? `Math Patterns: ${isOn ? 'Bật ✓' : 'Tắt ✗'}`
                                            : `Math Patterns: ${isOn ? 'On ✓' : 'Off ✗'}`;
                                    })()}
                                </span>
                            </button>
                            <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                            {/* Backup now */}
                            <button
                                onClick={async () => {
                                    const nbId = contextMenu.notebookId;
                                    setContextMenu(null);
                                    await handleManualBackup(nbId);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2 transition-colors"
                            >
                                <Shield size={14} className="text-emerald-500 shrink-0" />
                                <span className="flex flex-col min-w-0">
                                    <span className="text-gray-700 dark:text-gray-200">{language === 'vietnamese' ? 'Backup ngay' : 'Backup now'}</span>
                                    {(() => {
                                        const last = notebooks.find(n => n.id === contextMenu.notebookId)?.lastSnapshotAt;
                                        if (!last) return <span className="text-[11px] text-gray-400 dark:text-slate-500">{language === 'vietnamese' ? 'Tạo snapshot thủ công ngay bây giờ' : 'Create a manual snapshot now'}</span>;
                                        const d = new Date(last);
                                        const isToday = d.toDateString() === new Date().toDateString();
                                        const timeStr = d.toLocaleTimeString(language === 'vietnamese' ? 'vi-VN' : 'en-US', { hour: '2-digit', minute: '2-digit' });
                                        const label = isToday
                                            ? (language === 'vietnamese' ? `Lần cuối: Hôm nay, ${timeStr}` : `Last: Today, ${timeStr}`)
                                            : (language === 'vietnamese' ? `Lần cuối: ${d.toLocaleDateString('vi-VN', { day: 'numeric', month: 'short' })}, ${timeStr}` : `Last: ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${timeStr}`);
                                        return <span className="text-[11px] text-gray-400 dark:text-slate-500">{label}</span>;
                                    })()}
                                </span>
                            </button>
                            {/* View backups */}
                            <button
                                onClick={() => {
                                    const nbId = contextMenu.notebookId;
                                    setContextMenu(null);
                                    setSettingsDataTabNotebookId(nbId);
                                    setSettingsInitialTab('data');
                                    setSettingsOpen(true);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex items-center gap-2 transition-colors"
                            >
                                <RotateCcw size={14} className="text-emerald-600 shrink-0" />
                                <span className="flex flex-col min-w-0">
                                    <span className="text-gray-700 dark:text-gray-200">{language === 'vietnamese' ? 'Xem bản backup' : 'View backups'}</span>
                                    <span className="text-[11px] text-gray-400 dark:text-slate-500">{language === 'vietnamese' ? 'Khôi phục hoặc quản lý snapshot' : 'Restore or manage snapshots'}</span>
                                </span>
                            </button>
                            {/* AI search index coverage for this notebook */}
                            <NotebookIndexMenuItem notebookId={contextMenu.notebookId} vn={language === 'vietnamese'} />
                            {/* Export single notebook */}
                            <button
                                onClick={() => {
                                    const nb = notebooks.find(n => n.id === contextMenu.notebookId);
                                    setContextMenu(null);
                                    if (!nb) return;
                                    executeExportBackup([nb]);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center gap-2 transition-colors"
                            >
                                <Download size={14} className="text-indigo-500" />
                                <span className="text-gray-700 dark:text-gray-200">{language === 'vietnamese' ? 'Xuất notebook (.zip)' : 'Export notebook (.zip)'}</span>
                            </button>
                            <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                            {/* Delete */}
                            <button
                                onClick={(e) => {
                                    deleteNotebook(contextMenu.notebookId, e as any);
                                    setContextMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                            >
                                <Trash2 size={14} className="text-red-500" />
                                <span className="text-red-600 dark:text-red-400">{language === 'vietnamese' ? 'Xóa notebook' : 'Delete notebook'}</span>
                            </button>
                        </div>
                    </>
                )}
                {/* DEBUG OVERLAY */}
            </div>
            {/* DEBUG OVERLAY - Commented out for production, uncomment for debugging */}
            {/* <DebugOverlay 
                notebookId={activeNotebook?.id || null}
                nodeCount={activeNotebook?.nodes?.length || 0}
                userId={user?.id}
                logs={debugLogs}
            /> */}
            {/* NEW: App-wide Review Session Modal */}
            {activeReviewSessionNodeId && activeNotebook && (() => {
                const node = activeNotebook.nodes.find(n => n.id === activeReviewSessionNodeId);
                if (!node) return null;

                return (
                    <ReviewSessionModal
                        node={node}
                        language={language}
                        onClose={() => setActiveReviewSessionNodeId(null)}
                        onSaveSession={async (session) => {
                            try {
                                // Add session to node's reviewSessions array
                                const updatedSessions = [...(node.reviewSessions || []), session];

                                // Update in Firestore
                                if (activeNotebook.id && user?.uid) {
                                    const docRef = doc(db, `users/${user.uid}/notebooks/${activeNotebook.id}/nodes/${activeReviewSessionNodeId}`);
                                    await updateDoc(docRef, {
                                        reviewSessions: updatedSessions,
                                        lastActiveReviewAt: Date.now(),
                                    });
                                }

                                // Update local state
                                updateNode(activeReviewSessionNodeId, {
                                    reviewSessions: updatedSessions,
                                    lastActiveReviewAt: Date.now(),
                                });

                                // If session score < 80 and has incorrect/partial points,
                                // generate 1-2 targeted questions (append, non-blocking)
                                const hasWeaknesses = (session.analysisPoints || [])
                                    .some((p: any) => p.verdict === 'incorrect' || p.verdict === 'partial');
                                if (user && session.score < 80 && hasWeaknesses) {
                                    generateReviewQuestions(
                                        activeReviewSessionNodeId, activeNotebook.id,
                                        node.title, activeNotebook.title,
                                        node.content, node.imageBase64 ?? null, node.imageMimeType ?? null,
                                        language, 2, 'active_review',
                                        session.userExplanation, session.feedback,
                                        session.analysisPoints,
                                        true  // append=true: keep existing questions
                                    ).then(res => {
                                        if (res.storedCount > 0) {
                                            const newCount = (node.reviewQuestionCount || 0) + res.storedCount;
                                            updateNode(activeReviewSessionNodeId, { reviewQuestionCount: newCount });
                                        }
                                    }).catch(() => {});
                                }

                                console.log('Review session saved successfully!');
                                // Keep modal open to show results - user will close manually
                            } catch (err) {
                                console.error('Failed to save review session:', err);
                                alert('Failed to save review session');
                            }
                        }}
                        onDeleteSession={async (timestamp) => {
                            try {
                                // Filter out the session with matching timestamp
                                const updatedSessions = (node.reviewSessions || []).filter(s => s.timestamp !== timestamp);

                                // Update in Firestore
                                if (activeNotebook.id && user?.uid) {
                                    const docRef = doc(db, `users/${user.uid}/notebooks/${activeNotebook.id}/nodes/${activeReviewSessionNodeId}`);
                                    await updateDoc(docRef, {
                                        reviewSessions: updatedSessions
                                    });
                                }

                                // Update local state
                                updateNode(activeReviewSessionNodeId, {
                                    reviewSessions: updatedSessions
                                });
                            } catch (err) {
                                console.error('Failed to delete review session:', err);
                                alert('Failed to delete review session');
                            }
                        }}
                        onPromoteSession={async (content) => {
                            try {
                                // Append content to node
                                const newContent = (node.content || '') + '\n\n' + content;

                                // Update in Firestore
                                if (activeNotebook.id && user?.uid) {
                                    const docRef = doc(db, `users/${user.uid}/notebooks/${activeNotebook.id}/nodes/${activeReviewSessionNodeId}`);
                                    await updateDoc(docRef, {
                                        content: newContent
                                    });
                                }

                                // Update local state
                                updateNode(activeReviewSessionNodeId, {
                                    content: newContent
                                });

                                alert('Explanation promoted to note content!');
                            } catch (err) {
                                console.error('Failed to promote content:', err);
                                alert('Failed to promote content');
                            }
                        }}
                    />
                );
            })()}

            {noteQuestionsNodeId && user && activeNotebook && (() => {
                const node = activeNotebook.nodes.find(n => n.id === noteQuestionsNodeId);
                if (!node) return null;
                return (
                    <NoteQuestionsModal
                        node={node}
                        userId={user.id}
                        language={language}
                        onClose={() => setNoteQuestionsNodeId(null)}
                        onGenerateMore={(nodeId) => {
                            setNoteQuestionsNodeId(null);
                            handleAddToReviewFeed(nodeId, 'manual_generation');
                        }}
                        onOpenReviewFeed={() => setReviewFeedPanelOpen(true)}
                    />
                );
            })()}

            {/* Backfill Questions Modal */}
            {backfillModalOpen && user && activeNotebook && (
                <BackfillQuestionsModal
                    notebook={activeNotebook}
                    language={language}
                    onClose={() => setBackfillModalOpen(false)}
                    onNodeUpdated={(nodeId, count) => updateNode(nodeId, { reviewQuestionCount: count })}
                />
            )}

            {/* Size Analysis Modal */}
            {pendingImportNotebook && (
                <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6">
                        <h2 className="text-base font-bold mb-1 dark:text-white">{language === 'vietnamese' ? 'Nhập vào đâu?' : 'Import destination'}</h2>
                        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">"{pendingImportNotebook.title}" — {pendingImportNotebook.nodes.length} {language === 'vietnamese' ? 'ghi chú' : 'notes'}</p>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                            <button onClick={() => handleImportChooseNotebook('new')} className="w-full text-left px-4 py-3 rounded-xl border-2 border-dashed border-blue-400 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium text-sm transition-colors">
                                + {language === 'vietnamese' ? 'Tạo notebook mới' : 'Create new notebook'}
                            </button>
                            {notebooks.map(nb => (
                                <button key={nb.id} onClick={() => handleImportChooseNotebook(nb.id)} className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                    <div className="font-medium text-sm dark:text-white truncate">{nb.title}</div>
                                    <div className="text-xs text-gray-400 mt-0.5">{nb.nodeCount ?? nb.nodes.length} {language === 'vietnamese' ? 'ghi chú' : 'notes'}</div>
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setPendingImportNotebook(null)} className="mt-4 w-full py-2 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">{language === 'vietnamese' ? 'Hủy' : 'Cancel'}</button>
                    </div>
                </div>
            )}

            {sizeModalNotebookId && (() => {
                const notebook = notebooks.find(nb => nb.id === sizeModalNotebookId);
                if (!notebook) return null;

                return (
                    <NotebookSizeModal
                        notebook={notebook}
                        language={language}
                        onClose={() => setSizeModalNotebookId(null)}
                        onJumpToNode={(nodeId) => {
                            setSizeModalNotebookId(null);
                            if (sizeModalNotebookId !== activeNotebookId) {
                                setActiveNotebookId(sizeModalNotebookId);
                                setTimeout(() => handleJumpToNode(nodeId), 100);
                            } else {
                                handleJumpToNode(nodeId);
                            }
                        }}
                    />
                );
            })()}

            {/* Session expired overlay — shown in-place so unsaved work is preserved */}
            {sessionExpired && user && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-6 text-center">
                        <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mx-auto mb-4">
                            <LogIn className="w-6 h-6 text-indigo-500" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                            {language === 'vietnamese' ? 'Phiên đăng nhập đã hết hạn' : 'Session expired'}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
                            {language === 'vietnamese'
                                ? 'Công việc của bạn được giữ nguyên. Đăng nhập lại để tiếp tục lưu.'
                                : 'Your work is preserved. Sign in again to continue saving.'}
                        </p>
                        <button
                            onClick={async () => {
                                try {
                                    await signInWithGoogle();
                                } catch (e) {
                                    console.error('Re-auth failed:', e);
                                }
                            }}
                            className="w-full py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm transition-colors flex items-center justify-center gap-2"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            {language === 'vietnamese' ? 'Đăng nhập lại với Google' : 'Sign in again with Google'}
                        </button>
                    </div>
                </div>
            )}
            {mobileImageSrc && (
                <MobileImageViewer src={mobileImageSrc} onClose={() => setMobileImageSrc(null)} />
            )}
            {showCollectionsHub && (
                <CollectionsHubModal
                    language={language}
                    onClose={() => setShowCollectionsHub(false)}
                    onNavigateToNote={(notebookId, nodeId) => {
                        setShowCollectionsHub(false);
                        setActiveNotebookId(notebookId);
                        setPendingCrossNav({ notebookId, nodeId });
                    }}
                />
            )}
            {showYouTubeManager && isAdmin && (
                <YouTubeManagerModal
                    allNotebooksMeta={notebooks.filter(nb => !nb.isDeleted).map(nb => ({ id: nb.id, title: nb.title }))}
                    language={language}
                    onClose={() => setShowYouTubeManager(false)}
                    onOpenUpload={(nodeId, notebookId, initialFile, sourceJobId) => {
                        setShowYouTubeManager(false);
                        const nb = notebooks.find(n => n.id === notebookId);
                        if (nb) {
                            setActiveNotebookId(notebookId);
                            // A combined video from the Video tab, or a recovered stuck-job export,
                            // arrives pre-built as a File and becomes the new flow's initialFile.
                            // sourceJobId (present for a recovered job, not a plain Combine) rides
                            // along so a real upload success can mark that job resolved.
                            openUploadSession(nodeId, { notebookId, initialFile, sourceJobId });
                        }
                    }}
                    onOpenNote={(notebookId, nodeId) => {
                        setActiveNotebookId(notebookId);
                        setExpandedNodeId(nodeId);
                    }}
                    onNodePatched={(notebookId, nodeId, patch) => {
                        // Already saved by the Manager; mirror it so an open copy of the note shows it
                        // and a later edit here starts from the new text instead of the old one.
                        setNotebooks(nbs => nbs.map(nb => nb.id !== notebookId || !nb.nodes ? nb
                            : { ...nb, nodes: nb.nodes.map(n => n.id === nodeId ? { ...n, ...patch } : n) }));
                    }}
                />
            )}
            {showThreadsAdvisor && activeNotebook && isAdmin && (
                <LearningShareAdvisorModal
                    notebook={activeNotebook}
                    allNotebooksMeta={notebooks.filter(nb => !nb.isDeleted).map(nb => ({ id: nb.id, title: nb.title, lastPublishUrl: nb.lastPublishUrl }))}
                    language={language}
                    onClose={() => setShowThreadsAdvisor(false)}
                    onSelectDraft={({ postText, hashtags, primaryNodeId }) => {
                        setShowThreadsAdvisor(false);
                        setAdvisorDraft({ postText, hashtags, nodeId: primaryNodeId });
                    }}
                    onOpenNote={(nodeId) => {
                        setExpandedNodeId(nodeId);
                    }}
                />
            )}
            {advisorDraft && activeNotebook && isAdmin && (() => {
                const draftNode = advisorDraft.nodeId
                    ? activeNotebook.nodes.find(n => n.id === advisorDraft.nodeId) ?? activeNotebook.nodes.find(n => n.parentId != null) ?? activeNotebook.nodes[0]
                    : activeNotebook.nodes.find(n => n.parentId != null) ?? activeNotebook.nodes[0];
                return draftNode ? (
                    <SocialPostModal
                        node={draftNode}
                        notebook={activeNotebook}
                        language={language}
                        initialPostText={advisorDraft.postText}
                        initialHashtags={advisorDraft.hashtags}
                        onClose={() => setAdvisorDraft(null)}
                    />
                ) : null;
            })()}
            {showGuideBuilder && activeNotebook && isAdmin && (
                <GuideBuilderModal
                    notebook={activeNotebook}
                    language={language}
                    initialSelectedIds={guideInitialIds}
                    onClose={() => { setShowGuideBuilder(false); setGuideInitialIds([]); }}
                />
            )}
            {podcastHubOpen && activeNotebook && user && (
                <PodcastHub
                    nodes={activeNotebook.nodes}
                    notebookId={activeNotebook.id}
                    notebookTitle={activeNotebook.title}
                    userId={user.id}
                    language={language}
                    activePodcast={activePodcast}
                    onPlayPodcast={setActivePodcast}
                    onQueueChange={setPodcastQueue}
                    onClose={() => setPodcastHubOpen(false)}
                />
            )}
            {activePodcast && (
                <FloatingPodcastPlayer
                    podcast={activePodcast}
                    queue={podcastQueue}
                    language={language}
                    onNavigate={setActivePodcast}
                    onClose={() => setActivePodcast(null)}
                />
            )}

            {activePreviewTag && (
                <TagPreviewStrip
                    tag={activePreviewTag}
                    notes={tagPreviewNotes}
                    language={language}
                    onClose={() => setActivePreviewTag(null)}
                    onNavigate={(nodeId) => {
                        setTagNavPrevNodeId(expandedNodeId);
                        setExpandedNodeIdStable(nodeId);
                        setSelectedNodeId(nodeId);
                    }}
                    onNavigateCrossNotebook={(notebookId, nodeId) => {
                        setActiveNotebookId(notebookId);
                        setTagNavPrevNodeId(expandedNodeId);
                        setTimeout(() => { setExpandedNodeIdStable(nodeId); setSelectedNodeId(nodeId); }, 100);
                        setActivePreviewTag(null);
                    }}
                    onCreateLink={expandedNodeId && !isEffectiveReadOnly ? (targetId) => handleCreateCrossLink(expandedNodeId, targetId) : undefined}
                    linkedNodeIds={expandedNodeId ? new Set((linksByNodeId[expandedNodeId] || []).map(l => l.id)) : undefined}
                />
            )}

        </div>
    );
}